/**
 * FikoModellemeYonetici.ts — CATIA tarzı modelleme orkestratörü (motor çekirdeği)
 *
 * Tek beyin: yüz seç → sketch düzlemi → kamera hizala → kontrol kilitle →
 * çiz (FikoSketchAraci) → Pad/extrude (katı üret). Hem mevcut 🌌 viewer'da
 * hem ayrı workspace'te AYNI sınıf kullanılır → "tamamen aynı sistem".
 *
 * GÜVENLİK: mevcut hot-path'e dokunmaz. Kontrolleri sadece sketch süresince
 * kilitler, çıkışta aynen geri verir. Kendi raycaster'ı ile yüz seçer.
 */

import * as THREE from 'three';
import { mergeVertices } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { FikoSketch, ISketchDuzlem } from './FikoSketch';
import { FikoSketchAraci, SketchArac } from './FikoSketchAraci';
import { FikoStabilTopoloji } from '../fiko-stabil-topoloji/FikoStabilTopoloji';
import { FikoGrup } from '../FikoGrup';
import { FikoKatiMotoru } from './FikoKatiMotoru';
import { FikoKenarIslem } from './FikoKenarIslem';
import { FikoSketchHizliEk } from './FikoSketchHizliEk';
import { FikoFeatureHizliEk } from './FikoFeatureHizliEk';
import { FikoFeatureHizliEkSekmeli } from './FikoFeatureHizliEkSekmeli';
import { FikoPadDialog } from './FikoPadDialog';
import { FikoShaftDialog } from './FikoShaftDialog';
import { FikoHoleDialog } from './FikoHoleDialog';
import { FikoFeatureDialog } from './FikoFeatureDialog';
import { FikoFilletDialog, FikoDraftDialog, FikoShellDialog, FikoPatternDialog, FikoTransformDialog } from './FikoOzellikDialoglari';
import { FikoNoktaDialog } from './FikoNoktaDialog';
import { FikoCizgiDialog } from './FikoCizgiDialog';
import { FikoCemberDialog } from './FikoCemberDialog';
import { FikoSplineDialog } from './FikoSplineDialog';
import { FikoDuzlemDialog } from './FikoDuzlemDialog';
import { FikoGrafikDialog } from './FikoGrafikDialog';
import { yaziGeometri, fontYukle, fontHazirMi } from './fiko-yazi3d';
import { FikoAktiviteGravur } from '../Fiko-2d-Cam/aktiviteler/gravur/FikoAktiviteGravur';
import { yaziGravurUygula } from './yazi-gravur-kopru';
import { faceGruplariUret, edgeGruplariUret } from './fiko-govde-metadata';
import { fikoOCCT } from './fiko-occt-kernel';
import { FikoCizimYalin } from '../FikoCizimYalin';
import { MAKRO_URETECLER, IMakroParam } from './fiko-makro.insaat';
import { MAKRO_URETECLER_MARANGOZ } from './fiko-makro.marangoz';
import { MAKRO_URETECLER_ZENGIN } from './fiko-makro.zengin';
import { fikoCam3D, ICamParam, IToolpath } from '../fiko-cam-3d/fiko-cam3d-motoru';
import { FikoOperasyon } from '../FikoThreeCam/Fikoperasyon';
import { FikoTakimYoluv1 } from '../FikoThreeCam/FikoTakimYoluv1';
import { FikoKnowledgeYonetici } from './knowledge/FikoKnowledgeYonetici';
import { featureAlanlari } from './knowledge/FikoFeatureAlanHaritasi';

/** İnşaat + Marangoz + Zengin ürün makro üreteçleri tek katalogda (id → IOzellik[] üreteci). */
const TUM_MAKRO_URETECLER: Record<string, (p: IMakroParam) => IOzellik[]> = { ...MAKRO_URETECLER, ...MAKRO_URETECLER_MARANGOZ, ...MAKRO_URETECLER_ZENGIN };

export type ModellemeDurum = 'kapali' | 'yuz-secimi' | 'sketch';

interface IKontrol { enabled: boolean; target: THREE.Vector3; object: any; update: () => void; }

export interface ModellemeDeps {
  sahne: THREE.Scene;
  canvasAl: () => HTMLCanvasElement | null | undefined;
  kameraAl: () => THREE.Camera | null | undefined;
  kontrolAl?: () => IKontrol | null | undefined;
  rendererAl?: () => THREE.WebGLRenderer | null | undefined;   // kesit (clipping) için local clipping aç
  ciz: () => void;
  topo: FikoStabilTopoloji;
  degisti?: (durum: ModellemeDurum, sketch: FikoSketch | null) => void;
  agacGuncelle?: () => void;   // ürün ağacını yenile (FikoYT.degisti.emit)
  bildir?: (mesaj: string) => void;   // kullanıcıya kısa bildirim (toast)
  gecmisDegisti?: (geriAlinabilir: boolean, ileriAlinabilir: boolean) => void;   // geri/ileri al buton durumları
}

const RENK_KATI = 0xcfd8e3;

/** Malzeme (CATIA gibi: yoğunluk g/cm³ → kütle hesabı + görsel renk + PBR metalik/pürüz). */
export interface IMalzeme { ad: string; yogunluk: number; renk: number; metalik?: number; puruz?: number; }
export const MALZEMELER: IMalzeme[] = [
  { ad: 'Çelik', yogunluk: 7.85, renk: 0x8a929c, metalik: 0.85, puruz: 0.35 },
  { ad: 'Paslanmaz Çelik', yogunluk: 8.00, renk: 0xb0b6bd, metalik: 0.9, puruz: 0.22 },
  { ad: 'Alüminyum', yogunluk: 2.70, renk: 0xc8ccd0, metalik: 0.85, puruz: 0.4 },
  { ad: 'Pirinç', yogunluk: 8.50, renk: 0xc9a227, metalik: 0.9, puruz: 0.3 },
  { ad: 'Bakır', yogunluk: 8.96, renk: 0xc46a3a, metalik: 0.9, puruz: 0.32 },
  { ad: 'Titanyum', yogunluk: 4.51, renk: 0x9aa0a6, metalik: 0.8, puruz: 0.4 },
  { ad: 'Döküm Demir', yogunluk: 7.20, renk: 0x6e747c, metalik: 0.6, puruz: 0.6 },
  { ad: 'Plastik (ABS)', yogunluk: 1.05, renk: 0x3a7bd5, metalik: 0.0, puruz: 0.5 },
  { ad: 'Ahşap (Meşe)', yogunluk: 0.75, renk: 0xb08968, metalik: 0.0, puruz: 0.75 },
  { ad: 'Cam', yogunluk: 2.50, renk: 0x88c0d0, metalik: 0.1, puruz: 0.05 },
  { ad: 'Beton', yogunluk: 2.40, renk: 0x9e9e9e, metalik: 0.0, puruz: 0.95 },
  { ad: 'Altın', yogunluk: 19.32, renk: 0xd4af37, metalik: 0.95, puruz: 0.2 },
  { ad: 'Tuğla', yogunluk: 1.80, renk: 0x9c3b1b, metalik: 0.0, puruz: 0.9 },
  { ad: 'Sıva', yogunluk: 1.60, renk: 0xe8e2d0, metalik: 0.0, puruz: 0.85 },
  { ad: 'Mermer', yogunluk: 2.70, renk: 0xeeeae3, metalik: 0.05, puruz: 0.25 },
  { ad: 'Bronz', yogunluk: 8.80, renk: 0x8c6a3f, metalik: 0.9, puruz: 0.35 },
];

/** Parametrik feature geçmişi öğesi (rebuild ile sırayla yeniden hesaplanır). */
export interface IOzellik {
  id?: string;                     // kararlı feature kimliği (knowledge/intrinsic backref)
  tip: 'pad' | 'pocket' | 'shaft' | 'groove' | 'mirror' | 'patternDik' | 'patternDairesel' | 'patternIzgara' | 'userPattern' | 'yazi3d' | 'primitif' | 'rib' | 'stiffener' | 'shell' | 'draft' | 'makro' | 'fillet3d' | 'chamfer3d' | 'loft' | 'sweep' | 'slot' | 'combine' | 'hole' | 'translate' | 'rotate' | 'symmetry' | 'scaling' | 'affinity' | 'axisToAxis' | 'thickness' | 'bool' | 'split' | 'yuzey' | 'thickSurface' | 'splitSurface' | 'closeSurface' | 'removeLump' | 'removeFace' | 'faceFillet' | 'triFillet' | 'replaceFace' | 'healing' | 'simplify' | 'untrim' | 'multiExtract' | 'disassemble' | 'offsetShape' | 'pipe3d' | 'nokta3d' | 'cizgi3d' | 'cember3d' | 'spline3d' | 'polyline' | 'curveSmooth' | 'corner' | 'isoparametrik' | 'combine3d' | 'equationCurve' | 'spine3d' | 'helix3d' | 'duzlem3d' | 'spiral3d' | 'paralelEgri' | 'connectEgri' | 'yuzeyKenar' | 'conic3d' | 'yuzeyJoin' | 'kesisim3d' | 'yuzeyTrim' | 'yuzeyExtract' | 'yuzeyBlend' | 'yuzeyExtrapolate';
  secProfiller?: { elemanlar: any[]; duzlem: ISketchDuzlem }[];   // loft (kesitler, sıralı) / sweep ([0]=profil, [1]=yol)
  ekleme: boolean;                 // union mı subtract mı
  // makro (yüksek-seviye meslek komutları: boru/dirsek/yüzük/taş yuvası/bezel):
  makroTip?: 'boru' | 'dirsek' | 'yuzuk' | 'tasYuvasi' | 'bezel';
  mCap?: number; mEtKal?: number; mUzun?: number; mAci?: number; mYaricap?: number;  // tesisat
  mTasCap?: number; mProngAdet?: number; mProngCap?: number; mProngYuk?: number; mBand?: number; // takı
  // 3D yazı (yazi3d):
  metin?: string; fontAd?: string; boyut?: number; harfBosluk?: number;
  // primitif katı (kutu/silindir/küre/koni):
  primitifTip?: string; pEn?: number; pBoy?: number; pYuk?: number; pCap?: number; pTbCap?: number; wedgeLtx?: number;
  // sketch tabanlı (pad/pocket/shaft/groove):
  elemanlar?: any[];               // profil snapshot (deep copy)
  sketchOlculer?: { id: number; tip: string; elemanId: number; deger: number }[];   // eskiz boyutsal ölçüleri (parametrik bağlama için snapshot)
  duzlem?: ISketchDuzlem;          // klon
  param?: number;                  // derinlik/yükseklik (First Limit Length)
  // CATIA Pad Definition — gelişmiş seçenekler:
  limitTip?: 'boyut' | 'sonraki' | 'son' | 'duzleme' | 'yuzeye';   // First Limit Type
  ofset1?: number;                 // up-to-* için limit yüzeyinden ofset
  limitHedefMesafe?: number;       // up-to-plane/surface: seçilen hedefin sketch düzlemine mesafesi (normal boyunca)
  limit2Aktif?: boolean;           // Second Limit (More) açık mı
  limit2Tip?: 'boyut' | 'sonraki' | 'son' | 'duzleme';            // Second Limit Type
  param2?: number;                 // Second Limit Length (ters yön)
  ofset2?: number;
  simetrik?: boolean;              // Mirrored extent (her iki yöne eşit)
  tersYon?: boolean;               // Reverse Direction
  incePad?: boolean;               // Thick / Thin Pad
  padKal1?: number; padKal2?: number;   // Thickness1 (içe) / Thickness2 (dışa)
  notrFiber?: boolean;             // Neutral Fiber: profili ortalayıp eşit iki tarafa kalınlık ver
  tersTaraf?: boolean;             // Reverse Side: açık profilde hangi taraf işlenir
  refYonAktif?: boolean;           // Direction: Normal to profile KAPALI → referans yön
  refYon?: { x: number; y: number; z: number };   // referans uzatma yönü
  draftAci?: number;               // Drafted Filleted Pad: draft açısı (0 = yok)
  yanRadius?: number; ustRadius?: number; altRadius?: number;     // Drafted Filleted Pad radüsleri (0 = yok)
  domainBoy?: number[];            // Multi-Pad: her domain için ayrı boy
  domainKapali?: boolean[];        // Profile Definition Sub: bu domain pad'e dahil DEĞİL (hariç)
  // shaft/groove (revolve): aci=First Angle, aci2=Second Angle, eksen
  aci2?: number;                   // Shaft Second Angle (ters yön)
  shaftEksen?: 'v' | 'u' | 'x' | 'y' | 'z';   // dönme ekseni (default v = sketch dikey)
  // CATIA Hole (delik): yüze tıklanan noktadan içeri matkap. Sketch GEREKTİRMEZ.
  holeMerkez?: { x: number; y: number; z: number };   // delik ekseni başlangıç noktası (tıklanan yüzey noktası)
  holeNormal?: { x: number; y: number; z: number };   // yüzey normali (delik bunun TERSİNE = içeri ilerler)
  holeCap?: number;                // çap (Diameter)
  holeDerinlik?: number;           // Blind derinlik (Depth)
  holeLimit?: 'boyut' | 'sonraki' | 'son' | 'tumu' | 'duzleme';   // Blind / Up To Next/Last / Through All / Up To Plane
  holeAlt?: 'flat' | 'vdip';       // Bottom: düz taban / V-dip (matkap ucu)
  holeAltAci?: number;             // V-dip tepe açısı (default 118°)
  holeTur?: 'simple' | 'counterbored' | 'countersunk' | 'counterdrilled';   // Type
  cbCap?: number; cbDerinlik?: number;   // Counterbored: havşa çapı + derinliği (şişe başı)
  csCap?: number; csAci?: number;        // Countersunk: havşa çapı + koni açısı (havşa başı)
  holeThread?: boolean; holeThreadAd?: string;   // Threaded (M6/M8/M10...) — etiket + tap-drill çapı
  holeXOfset?: number; holeYOfset?: number;       // Positioning: yüzey üzerinde ince konum kaydırma (u/v)
  // mirror / symmetry:
  aynaTip?: 'xy' | 'xz' | 'yz';
  // CATIA Transformations (gövdeyi taşı/döndür/aynala/ölçekle — geçmişte o noktadan sonra uygulanır):
  trX?: number; trY?: number; trZ?: number;   // Translate mesafeleri (koordinat modu)
  trMod?: 'yon' | 'koordinat';                // CATIA Vector Definition: yön+mesafe ya da koordinat
  trYon?: 'x' | 'y' | 'z';                    // Translate yönü (eksenRef set ise 3D çizgi yönü)
  trMesafe?: number;                          // Translate mesafesi (yön modu)
  scaleFaktor?: number;                        // Scaling katsayısı (rotate açısı=aci, ekseni=eksenTip)
  affX?: number; affY?: number; affZ?: number;  // Affinity orantısız ölçek faktörleri
  eksenRef?: number;                           // Rotate ekseni 3D çizgi referansı (-1 = eksenTip kullan)
  symRef?: number;                             // Symmetry referansı (nokta3d=noktasal / cizgi3d=çizgisel; -1 = aynaTip düzlem)
  chamferMod?: 'simetrik' | 'length2' | 'aci'; // Chamfer modu: simetrik / Length-Length / Length-Angle
  chamferAci?: number;                         // Chamfer açısı (Length/Angle modu)
  secNoktalar?: { x: number; y: number; z: number }[];   // User Pattern: 3D'de tıklanan kopya konumları (ilk nokta = çapa)
  splitOfset?: number;                         // Split: kesme düzleminin (aynaTip) normal boyunca ofseti
  // GSD Yüzey (Surface — açık shell):
  yuzeyTip?: 'extrude' | 'revolve' | 'fill' | 'loft' | 'sweep' | 'sphere' | 'cylinder';   // yüzey oluşturma türü
  // GSD Wireframe (tel kafes — construction nokta/çizgi):
  p1x?: number; p1y?: number; p1z?: number;   // nokta / çizgi başlangıç (koordinat modu)
  yuzeyNormal?: { x: number; y: number; z: number };   // yüze tıklayarak konulan noktanın yüzey normali (Point on surface)
  p2x?: number; p2y?: number; p2z?: number;   // çizgi bitiş
  // Point Definition türleri (CATIA): koordinat / arası(between) / eğri üzerinde / referansa göre
  noktaTip?: 'koordinat' | 'arasi' | 'egriUzerinde' | 'extremum' | 'duzlemde' | 'projeksiyon' | 'cemberMerkez' | 'tegetEgri' | 'ortalama' | 'polarExtremum';
  // Düzlem türleri (CATIA Plane): ofset(temel düzlem+mesafe) / 3-nokta / eğriye-normal / paralel-nokta / iki-çizgi
  duzlemTip?: 'ofset' | 'uc-nokta' | 'egriNormal' | 'paralelNokta' | 'iki-cizgi';
  duzlemBoyut?: number;                       // görsel kare yarı-kenarı (mm)
  duzlemRef?: number;                         // referans düzlem feature idx (duzlem3d) — set ise aynaTip yerine kullanılır (CATIA: düzlemi seç)
  yuzIndex?: number;                          // Extract: kaynak shape'in kaçıncı yüzü çıkarılacak
  ref1?: number; ref2?: number;               // referans feature index (başka nokta/çizgi)
  oran?: number;                              // Between/On-Curve oranı (0-1)
  // Çizgi türleri (CATIA): noktadan-noktaya / nokta-yön / açı / açıortay
  cizgiTip?: 'iki-nokta' | 'nokta-yon' | 'aci' | 'bisecting' | 'tanjant' | 'normal';
  // Çember türleri (CATIA): merkez+yarıçap / üç nokta
  cemberTip?: 'merkez' | 'uc-nokta' | 'merkez-nokta' | 'iki-nokta-cap' | 'merkez-eksen' | 'bitangent' | 'merkez-teget' | 'tritangent';
  yaricap?: number; ref3?: number;            // çember yarıçapı / 3. nokta (üç-nokta)
  refler?: number[];                          // Spline: sıralı nokta referansları
  helixYukseklik?: number; helixAdim?: number; helixTur?: number;   // Helix: yükseklik/pitch/tur
  eqX?: string; eqY?: string; eqZ?: string; eqTMin?: number; eqTMax?: number; eqAdim?: number;   // Curve from equation: x(t)/y(t)/z(t)
  // 3D kenar işlemi (fillet3d/chamfer3d): radius/mesafe + keskin kenar eşiği:
  kenarMesafe?: number; kenarAci?: number;
  kenarMesafe2?: number; degiskenFillet?: boolean;   // Variable Fillet: 2. yarıçap (r1→r2 kenar boyunca)
  secKenarlar?: number[];   // CATIA: seçili kenar noktaları [x,y,z,...] — boş ise TÜM kenarlar
  secKenarImza?: number[];  // B-Rep link imzası: her kenar için [mx,my,mz, dx,dy,dz, uzunluk] (7/kenar) — topo eşleştirme
  secYuzler?: number[];     // CATIA: seçili yüz faceId listesi (draft/shell/removeFace) — boş ise tüm/global
  secYuzNok?: number[];     // seçili yüz dünya merkezleri [x,y,z,...] — OCCT yüz eşleştirme için (fallback)
  secYuzKimlik?: number[];  // PERSISTENT NAMING: seçili yüzlerin kalıcı stableId'leri (OCCT history ile taşınır) — birincil
  secYuzImza?: number[];    // B-Rep link imzası: her yüz için [cx,cy,cz, nx,ny,nz, alan] (7/yüz) — topo eşleştirme
  // pattern (önceki feature'ı çoğaltır):
  hedefIndex?: number; adet?: number; aralik?: number; aci?: number;
  adet2?: number; aralik2?: number;  // ızgara (2D) ikinci eksen
  // rib (kaburga): kalınlık + uzatma yönü
  kalinlik?: number; yon?: 'arti' | 'eksi' | 'cift';
  // shell (kabuk): kalinlik alanı paylaşılır (et kalınlığı)
  // draft (eğim): açı (aci) + nötr düzlem + çekme ekseni
  taban?: 'alt' | 'ust' | 'orta'; eksenTip?: 'x' | 'y' | 'z';
  // CATIA Çoklu-Gövde (Multi-Body) + Boolean:
  bodyId?: number;                 // bu feature hangi gövdeye ait (0 = ana PartBody)
  boolOp?: 'assemble' | 'add' | 'remove' | 'intersect';   // bool feature: işlem türü
  kaynakBodyId?: number;           // bool feature: ana gövdeye uygulanacak kaynak gövde
  bastirilmis?: boolean;   // suppress (deactivate) — üretime girmez, gri (CATIA Deactivate)
  hata?: boolean;          // bu özellik son rebuild'de oluşturulamadı (kırmızı uyarı)
  node?: FikoGrup | null;          // ağaç node'u
  // CATIA Graphics Properties — nesne-bazlı görsel özellikler (renk/opaklık/parlaklık/görünüm):
  grafik?: IGrafikOzellik;
}

/** CATIA Graphics Properties: bir nesnenin görsel ayarları. */
export interface IGrafikOzellik {
  renk?: number;            // 0xRRGGBB
  opaklik?: number;         // 0..1
  metalik?: number;         // 0..1 (metalness)
  puruz?: number;           // 0..1 (roughness)
  gorunum?: 'dolu' | 'tel'; // shaded / wireframe
  gizli?: boolean;          // görünürlük
  kenarKalinlik?: number;   // kenar çizgi kalınlığı (px)
  kenarRenk?: number;       // kenar rengi
}

/** Merkezi tür-bazlı seçim talebi (CATIA tarzı). tipler = seçilebilir türler. */
export interface ISecimTalebi {
  tipler: string[];                 // 'nokta3d'|'cizgi3d'|'cember3d'|'spline3d'|'helix3d'|'spiral3d'|'duzlem3d'|'kenar'|'yuz'|'vertex'|'profil'|'govdeNokta'|'govdeNormal'
  feat?: IOzellik;                  // sonucun yazılacağı feature (opsiyonel)
  anahtar?: string;                 // feat üzerinde yazılacak alan (örn. 'ref1','duzlemRef')
  coklu?: boolean;                  // liste seçimi (feat[anahtar] dizisine push)
  geriCagri?: (sonuc?: any) => void; // seçim olunca çağrılır (sonuc = seçilen index/değer)
}

export class FikoModellemeYonetici {
  durum: ModellemeDurum = 'kapali';
  sketch: FikoSketch | null = null;
  private araci: FikoSketchAraci | null = null;
  private aktifDuzlem: ISketchDuzlem | null = null;
  private aktifNode: FikoGrup | null = null;     // aktif sketch'in ağaç node'u
  private eskizGrubu: FikoGrup | null = null;     // "Eskizler" ağaç grubu
  private eskizSayac = 1;
  // Part Design — PartBody (Gövde) + feature geçmişi
  private kati = new FikoKatiMotoru();
  private kenarIslem = new FikoKenarIslem(this.kati);
  private govdeMesh: THREE.Mesh | null = null;
  private govdeKenar: LineSegments2 | null = null;
  private _govdeLineMat: LineMaterial | null = null;   // kenar material reuse (her rebuild'de yeniden yaratma — GC)
  private govdeGrup: FikoGrup | null = null;
  private ozellikSayac = 1;
  private hedefSketch: { sketch: FikoSketch; duzlem: ISketchDuzlem; node: FikoGrup | null } | null = null;
  private ozellikler: IOzellik[] = [];   // parametrik feature geçmişi
  private duzenlenenFeat: IOzellik | null = null;  // feature düzenleme modu
  private yuzSecHandler: ((e: PointerEvent) => void) | null = null;
  gridGoster = true;
  private raycaster = new THREE.Raycaster();
  private mouse = new THREE.Vector2();
  knowledge = new FikoKnowledgeYonetici();

  constructor(private d: ModellemeDeps) {
    this.knowledge.rebuildKopru = (idx) => this.rebuild(idx);
    this.knowledge.agacDegisti = () => { this.parametreAgaciGuncelle(); this.d.agacGuncelle?.(); };
    this.knowledge.featIndexCoz = (featId) => Math.max(0, this.ozellikler.findIndex(f => f.id === featId));
  }

  /** "Eskizler" ağaç grubunu (ürün ağacı altında) lazy oluşturur. */
  private eskizGrubuAl(): FikoGrup {
    if (this.eskizGrubu && this.eskizGrubu.parent) return this.eskizGrubu;
    const kok = (this.d.sahne as any).urunAgaci as FikoGrup | undefined;
    const g = new FikoGrup(this.d.sahne as any, 'Eskizler');
    g.userData['FikoArayuz'].listeGoster = true;
    g.userData['FikoArayuz'].simge = './simgeler/yalinCizim.webp';
    if (kok?.ekle) kok.ekle(g); else this.d.sahne.add(g);
    this.eskizGrubu = g;
    return g;
  }

  // ---- CATIA "PARAMETERS": tüm feature parametrelerini ayrı grupta topla ----
  private parametreGrubu: FikoGrup | null = null;
  private parametreGrubuAl(): FikoGrup {
    if (this.parametreGrubu && this.parametreGrubu.parent) return this.parametreGrubu;
    const kok = (this.d.sahne as any).urunAgaci as FikoGrup | undefined;
    const g = new FikoGrup(this.d.sahne as any, 'Parametreler');
    g.userData['FikoArayuz'].listeGoster = true;
    g.userData['FikoArayuz'].simge = './simgeler/3d.webp';
    if (kok?.ekle) kok.ekle(g); else this.d.sahne.add(g);
    this.parametreGrubu = g;
    return g;
  }
  /** Ürün ağacı "Parametreler" grubunu knowledge gruplarından kur: kullanıcı grupları = klasör, parametreler = chip. */
  private parametreAgaciGuncelle(): void {
    const depo = this.knowledge.depo;
    const params = depo.parametreler;
    if (!params.length) {
      if (this.parametreGrubu?.parent) { this.parametreGrubu.parent.remove(this.parametreGrubu as any); this.parametreGrubu = null; }
      return;
    }
    const kokG = this.parametreGrubuAl();
    for (let i = kokG.children.length - 1; i >= 0; i--) kokG.remove(kokG.children[i]);
    const chipYap = (p: any) => ({
      isim: p.ad.split('\\').pop(),
      turu: p.formul ? 'Formul' : 'Sayı',
      degeri: p.formul ? p.formul : p.deger,
      paramId: p.id, hata: p.hata,
      ayar: { birim: p.tip === 'Aci' ? '°' : (p.tip === 'Uzunluk' ? 'mm' : '') },
      knowledge: this.knowledge,
      degisti: (v: any) => this.knowledge.parametreGuncelle(p.id, v),
    });
    // kullanıcı grupları için node'lar
    const node = new Map<string, FikoGrup>();
    for (const g of depo.gruplar) {
      const n = new FikoGrup(this.d.sahne as any, g.ad);
      n.userData['FikoArayuz'].listeGoster = true;
      n.userData['FikoArayuz'].simge = './simgeler/3d.webp';
      (n as any).dtRename = false;
      node.set(g.id, n);
    }
    for (const g of depo.gruplar) {
      const n = node.get(g.id)!;
      const ust = g.ustGrupId ? node.get(g.ustGrupId) : null;
      (ust || kokG).ekle!(n);
    }
    // grupsuz parametreler → kök "Parametreler" altında chip; gruplular → grup node'unda
    kokG.userData['FikoParametreler'] = params.filter(p => !p.grupId).map(chipYap);
    for (const g of depo.gruplar) {
      node.get(g.id)!.userData['FikoParametreler'] = params.filter(p => p.grupId === g.id).map(chipYap);
    }
  }

  // ---- CATIA GEOMETRİK SET (datum/referans geometri container: düzlem/eksen/nokta) ----
  private geometriSeti: FikoGrup | null = null;
  private geometriSetiAl(): FikoGrup {
    if (this.geometriSeti && this.geometriSeti.parent) return this.geometriSeti;
    const kok = (this.d.sahne as any).urunAgaci as FikoGrup | undefined;
    const g = new FikoGrup(this.d.sahne as any, 'Geometrik Set');
    g.userData['FikoArayuz'].listeGoster = true;
    g.userData['FikoArayuz'].simge = './simgeler/yalinCizim.webp';
    if (kok?.ekle) kok.ekle(g); else this.d.sahne.add(g);
    this.geometriSeti = g;
    return g;
  }
  /** Yeni boş Geometrik Set oluştur (CATIA "Insert > Geometrical Set"). */
  geometriSetiEkle(): void {
    const kok = (this.d.sahne as any).urunAgaci as FikoGrup | undefined;
    const g = new FikoGrup(this.d.sahne as any, 'Geometrik Set.' + (this.refSayac));
    g.userData['FikoArayuz'].listeGoster = true; g.userData['FikoArayuz'].simge = './simgeler/yalinCizim.webp';
    if (kok?.ekle) kok.ekle(g); else this.d.sahne.add(g);
    this.geometriSeti = g; this.d.agacGuncelle?.();
  }

  // ---- mod ----

  /** Modelleme modunu açar; sahnedeki yüzleri stabil ID'ye kaydeder. */
  modBaslat(): void {
    this.d.topo.kaydetSahne(this.d.sahne);
    this.durum = 'kapali';
    this.d.degisti?.(this.durum, null);
    // OCCT'yi ÖNCEDEN yükle (arka plan) → ilk operasyonda hazır, mesh→OCCT race olmaz (B-Rep baştan).
    this.occtYukle().catch(() => { });
  }

  /** "Sketch" → bir sonraki yüz tıklamasında o yüzün düzlemine sketch açar. */
  yuzSecimiBaslat(): void {
    const canvas = this.d.canvasAl();
    if (!canvas) return;
    this.iptalSketch();
    this.durum = 'yuz-secimi';
    this.d.topo.kaydetSahne(this.d.sahne); // yeni gelen parçalar için
    this.yuzSecHandler = (e) => this.yuzSecildi(e, canvas);
    canvas.addEventListener('pointerdown', this.yuzSecHandler, { once: false });
    this.d.degisti?.(this.durum, null);
  }

  /** Temel düzlemde (XY/XZ/YZ) sketch başlatır. */
  temelDuzlem(tip: 'xy' | 'xz' | 'yz'): void {
    this.sketchBaslat(FikoSketch.duzlemHazir(tip));
  }

  // ---- CATIA REFERANS DÜZLEMLER (datum planes): offset düzlem oluştur → üzerine sketch / mirror ----
  private referansDuzlemler: { ad: string; duzlem: ISketchDuzlem; mesh: THREE.Mesh; node: FikoGrup }[] = [];
  private refSayac = 1;
  private taslakRefDuzlem: { duzlem: ISketchDuzlem; mesh: THREE.Mesh; taban: 'xy' | 'xz' | 'yz' } | null = null;
  private taslakMalzeme = false;
  private taslakSketchSec: string | null = null;   // tek sketch komutu: seçilen düzlem tipi
  private taslakCam3d: { op: 'parallel' | 'roughing' | 'waterline' | 'pencil'; p: Record<string, any> } | null = null;
  /** Seçilebilir referans düzlemler (mirror/sketch için). */
  get referansDuzlemListesi(): { ad: string; duzlem: ISketchDuzlem }[] { return this.referansDuzlemler.map(r => ({ ad: r.ad, duzlem: r.duzlem })); }

  /** Bir temel düzlemi normali boyunca ofsetleyerek yeni düzlem (datum) verir. */
  private ofsetDuzlem(taban: 'xy' | 'xz' | 'yz', ofset: number): ISketchDuzlem {
    const b = FikoSketch.duzlemHazir(taban);
    return { origin: b.origin.clone().addScaledVector(b.normal, ofset), normal: b.normal.clone(), eksenU: b.eksenU.clone(), eksenV: b.eksenV.clone() };
  }
  /** Yarı-saydam düzlem görseli (CATIA datum görünümü). */
  private refDuzlemGorsel(d: ISketchDuzlem, boyut = 200, renk = 0x22d3ee, op = 0.13): THREE.Mesh {
    const g = new THREE.PlaneGeometry(boyut, boyut);
    const mat = new THREE.MeshBasicMaterial({ color: renk, transparent: true, opacity: op, side: THREE.DoubleSide, depthWrite: false });
    const mesh = new THREE.Mesh(g, mat);
    mesh.applyMatrix4(new THREE.Matrix4().makeBasis(d.eksenU, d.eksenV, d.normal).setPosition(d.origin));
    mesh.name = 'FikoRefDuzlem'; (mesh as any).raycast = () => { };
    return mesh;
  }

  /** Referans düzlem komutu başlat (canlı önizleme). */
  refDuzlemBaslat(p: Record<string, any>): boolean {
    this.komutIptal();
    const taban = (p.taban || 'xy') as 'xy' | 'xz' | 'yz';
    const d = this.ofsetDuzlem(taban, +p.ofset || 0);
    const mesh = this.refDuzlemGorsel(d); this.d.sahne.add(mesh);
    this.taslakRefDuzlem = { duzlem: d, mesh, taban };
    this.d.ciz();
    return true;
  }
  private refDuzlemGuncelle(anahtar: string, deger: any): void {
    if (!this.taslakRefDuzlem) return;
    if (anahtar === 'taban') this.taslakRefDuzlem.taban = deger;
    const ofset = anahtar === 'ofset' ? +deger : (this.taslakRefDuzlem.duzlem.origin.length() || 0);
    const d = this.ofsetDuzlem(this.taslakRefDuzlem.taban, anahtar === 'ofset' ? +deger : this._sonRefOfset);
    if (anahtar === 'ofset') this._sonRefOfset = +deger;
    this.taslakRefDuzlem.duzlem = d;
    this.d.sahne.remove(this.taslakRefDuzlem.mesh); (this.taslakRefDuzlem.mesh.material as any).dispose?.();
    this.taslakRefDuzlem.mesh = this.refDuzlemGorsel(d); this.d.sahne.add(this.taslakRefDuzlem.mesh);
    this.d.ciz();
  }
  private _sonRefOfset = 0;
  /** Referans düzlemi kalıcı yap (tree node + çift-tık ile sketch). */
  refDuzlemOnayla(): void {
    if (!this.taslakRefDuzlem) return;
    const { duzlem, mesh } = this.taslakRefDuzlem;
    const ad = 'Düzlem.' + (this.refSayac++);
    const node = new FikoGrup(this.d.sahne as any, ad);
    node.userData['refDuzlem'] = duzlem;
    node.userData['FikoArayuz'].simge = './simgeler/yalinCizim.webp';
    (node as any).ciftTiklandi = () => this.sketchBaslat(duzlem);   // çift-tık → bu düzlemde sketch (CATIA)
    node.sagMenu = [
      { baslik: 'Bu Düzlemde Eskiz', simge: 'pencil alternate', renkSinif: 'blue', tiklandi: () => this.sketchBaslat(duzlem) },
      { baslik: 'Sil', simge: 'trash alternate', renkSinif: 'red', tiklandi: () => { (mesh.parent || this.d.sahne).remove(mesh); (mesh.material as any)?.dispose?.(); node.parent?.remove(node as any); this.referansDuzlemler = this.referansDuzlemler.filter(r => r.node !== node); this.d.agacGuncelle?.(); this.d.ciz(); } },
    ];
    this.geometriSetiAl().ekle!(node);   // CATIA: referans düzlem Geometrik Set'te (Eskizler'de değil)
    // görsel mesh'i node altına taşı → ağaçta (sahne kökünde değil); node "Düzlem.N" temsil eder
    if (mesh.userData['FikoArayuz']) mesh.userData['FikoArayuz'].agactaGorunur = false; else mesh.userData['FikoArayuz'] = { agactaGorunur: false };
    (mesh.parent || this.d.sahne).remove(mesh); node.ekle!(mesh as any);
    this.referansDuzlemler.push({ ad, duzlem, mesh, node });
    this.taslakRefDuzlem = null;
    this.d.agacGuncelle?.(); this.d.degisti?.(this.durum, this.sketch);
  }
  refDuzlemIptal(): void {
    if (!this.taslakRefDuzlem) return;
    this.d.sahne.remove(this.taslakRefDuzlem.mesh); (this.taslakRefDuzlem.mesh.material as any).dispose?.();
    this.taslakRefDuzlem = null; this.d.ciz();
  }

  private yuzSecildi(e: PointerEvent, canvas: HTMLCanvasElement): void {
    if (e.button !== 0) return;
    const kamera = this.d.kameraAl();
    if (!kamera) return;
    const rect = canvas.getBoundingClientRect();
    this.mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(this.mouse, kamera as THREE.Camera);

    const hedefler: THREE.Object3D[] = [];
    this.d.sahne.traverse((o) => {
      const m = o as THREE.Mesh;
      if (m.isMesh && o.visible && (m.geometry as THREE.BufferGeometry)?.attributes?.['grup']) hedefler.push(o);
    });
    const hit = this.raycaster.intersectObjects(hedefler, false).find(k => k.faceIndex != null);
    if (!hit || hit.faceIndex == null) return;

    const mesh = hit.object as THREE.Mesh;
    const idx = (mesh.geometry as THREE.BufferGeometry).index;
    const ilkVertex = idx ? idx.getX(hit.faceIndex * 3) : hit.faceIndex * 3;
    const grup = (mesh.geometry as THREE.BufferGeometry).attributes['grup'] as THREE.BufferAttribute;
    const faceId = grup.getZ(ilkVertex) | 0;
    const duzlem = this.d.topo.yuzDuzlemi(mesh, faceId);
    if (!duzlem) return;

    this.yuzSecHandlerKaldir();
    this.sketchBaslat(duzlem);
  }

  // ---- sketch ----

  private sketchBaslat(duzlem: ISketchDuzlem): void {
    // Önceki düzenleme boş kaldıysa node'unu at
    if (this.aktifNode && this.sketch && this.sketch.elemanlar.length === 0) this.nodeKaldir(this.aktifNode);
    this.duzenlemeyiDurdur();
    this.duzenlenenFeat = null;

    this.aktifDuzlem = duzlem;
    this.sketch = new FikoSketch(duzlem);

    // Ürün ağacı node'u (kendi sistemine entegre — çift-tık ile düzenlenebilir)
    const node = new FikoGrup(this.d.sahne as any, 'Eskiz.' + (this.eskizSayac++));
    node.userData['FikoArayuz'].simge = './simgeler/yalinCizim.webp';
    node.userData['FikoArayuz'].listeGoster = true;   // çizgiler sketch altında görünsün (CATIA gibi)
    node.userData['fikoSketch'] = this.sketch;
    node.userData['sketchDuzlem'] = duzlem;
    (node as any).ciftTiklandi = () => this.sketchDuzenle(node);
    node.ekle!(this.sketch.nesne);
    this.eskizGrubuAl().ekle!(node);
    this.aktifNode = node;

    this.aracBaslat(this.sketch);
    this.gridOlustur(duzlem);
    this.kameraHizala(duzlem);
    this.durum = 'sketch';
    if (this.kesitAktif) this.kesitUygula();   // kesit açıksa yeni düzleme hizala
    this.d.agacGuncelle?.();
    this.d.degisti?.(this.durum, this.sketch);
  }

  /** Ağaçtan çift-tıkla → o sketch'i düzenlemeye gir (CATIA "Edit Sketch"). */
  sketchDuzenle(node: FikoGrup): void {
    const sketch = node.userData?.['fikoSketch'] as FikoSketch;
    const duzlem = node.userData?.['sketchDuzlem'] as ISketchDuzlem;
    if (!sketch || !duzlem) return;
    this.duzenlemeyiDurdur();
    this.duzenlenenFeat = (node.userData?.['featObj'] as IOzellik) || null;  // bu sketch bir feature'a aitse
    this.sketch = sketch;
    this.aktifDuzlem = duzlem;
    this.aktifNode = node;
    this.aracBaslat(sketch);
    this.gridOlustur(duzlem);
    this.kameraHizala(duzlem);
    this.durum = 'sketch';
    if (this.kesitAktif) this.kesitUygula();   // kesit açıksa düzenlenen sketch düzlemine hizala
    this.d.degisti?.(this.durum, this.sketch);
  }

  private aracBaslat(sketch: FikoSketch): void {
    sketch.olcuGoster = true;   // bu sketch artık aktif → ölçü/kısıt görselleri görünür
    sketch.ciz();
    this.araci = new FikoSketchAraci(
      sketch, this.d.canvasAl, this.d.kameraAl, this.d.sahne, this.d.ciz,
      () => { this.entityAgaciGuncelle(); this.d.degisti?.(this.durum, this.sketch); this.sketchGecmisKaydet(); },   // yeni eleman → geçmiş
      () => { // sürükleme bitti → feature ise parametrik rebuild
        if (this.duzenlenenFeat) { this.duzenlenenFeat.elemanlar = JSON.parse(JSON.stringify(sketch.elemanlar)); this.rebuild(); }
        this.entityAgaceGuncelleGuvenli();
        this.sketchGecmisKaydet();   // sürükleme sonrası → geçmiş
      },
      (kilit: boolean) => { const k = this.d.kontrolAl?.(); if (k) k.enabled = !kilit; },  // sürüklerken kamera kilidi
    );
    this.araci.sayisalDegisti = () => this.d.degisti?.(this.durum, this.sketch);   // sayısal panel canlı güncelle
    // Kamera serbest (CATIA gibi); sadece sürükleme sırasında kilitlenir.
    this.araci.baslat();
    this.sketchGecmisSifirla();   // sketch'e girince geçmiş tabanı (boş veya mevcut elemanlar)
  }

  /** Sayısal giriş alanları (CATIA Sketch Tools — çizerken uzunluk/açı/yarıçap yaz). */
  get sketchSayisalAlanlar(): { anahtar: string; etiket: string; deger: number; birim: string }[] { return this.araci?.sayisalAlanlar || []; }
  get sketchMouseEkran(): { x: number; y: number } | null { return this.araci?.sonMouseEkran || null; }
  sketchSayisalUygula(vals: Record<string, number>): void { this.araci?.sayisalUygula(vals); }

  /** Sketch elemanlarını (çizgi/çember...) ağaçta sketch node'unun ALTINDA gösterir. */
  private entityAgaciGuncelle(): void {
    if (!this.aktifNode || !this.sketch) return;
    // eski eleman node'larını temizle (sketch.nesne geometri grubu korunur)
    const eskiler = this.aktifNode.children.filter((c: any) => c.userData?.['elemanNode']);
    for (const e of eskiler) this.aktifNode.remove(e);
    const say: Record<string, number> = {};
    for (const el of this.sketch.elemanlar) {
      const ad = (el.yapi ? '⋯ ' : '') + this.elemanAdi(el.tip, say);   // yapı geometrisi işareti
      const n = new FikoGrup(this.d.sahne as any, ad);
      n.userData['elemanNode'] = true;
      n.userData['elemanId'] = el.id;
      n.userData['FikoArayuz'].simge = './simgeler/line.webp';
      this.aktifNode.ekle!(n);
    }
    // BOYUTSAL ÖLÇÜLER de ağaçta görünsün (CATIA spec tree: Sketch > Geometri + Constraints)
    const onek: Record<string, string> = { yaricap: 'R ', cap: 'Ø ', uzunluk: 'L ', genislik: 'W ', yukseklik: 'H ' };
    for (const o of this.sketch.olculer) {
      const n = new FikoGrup(this.d.sahne as any, '⟺ ' + (onek[o.tip] || '') + o.deger.toFixed(1));
      n.userData['elemanNode'] = true;
      n.userData['olcuNode'] = true;
      n.userData['olcuId'] = o.id;
      n.userData['FikoArayuz'].simge = './simgeler/line.webp';
      this.aktifNode.ekle!(n);
    }
    this.d.agacGuncelle?.();
  }

  private elemanAdi(tip: string, say: Record<string, number>): string {
    const etiket: Record<string, string> = { cizgi: 'Çizgi', dikdortgen: 'Dikdörtgen', cember: 'Çember', yay: 'Yay', nokta: 'Nokta' };
    say[tip] = (say[tip] || 0) + 1;
    return `${etiket[tip] || tip}.${say[tip]}`;
  }

  aracSec(a: SketchArac): void { this.islemBitir(); this.araci?.aracSec(a); }
  konikTipiAyarla(t: 'parabol' | 'hiperbol'): void { if (this.araci) this.araci.konikTipi = t; }
  snapAyarla(acik: boolean): void { if (this.araci) this.araci.snapAcik = acik; }

  /** Kontur ötele (curve/contour offset): tüm kapalı konturu `mesafe` kadar paralel kopyalar.
   * Çember→eşmerkez, dikdörtgen→offset dikdörtgen, çizgi-çokgen→bisector offset. */
  konturOtele(mesafe = -10): boolean {
    if (!this.sketch) return false;
    const els = this.sketch.elemanlar.slice();
    let eklendi = false;
    for (const el of els) {
      if (el.tip === 'cember') {
        const m = el.noktalar[0]; const nr = (el.r || 0) + mesafe;
        if (nr > 0.2) { this.sketch.ekle('cember', [{ x: m.x, y: m.y }], nr); eklendi = true; }
      } else if (el.tip === 'dikdortgen') {
        const [a, b] = el.noktalar;
        const minx = Math.min(a.x, b.x), maxx = Math.max(a.x, b.x), miny = Math.min(a.y, b.y), maxy = Math.max(a.y, b.y);
        const na = { x: minx - mesafe, y: miny - mesafe }, nb = { x: maxx + mesafe, y: maxy + mesafe };
        if (nb.x - na.x > 0.2 && nb.y - na.y > 0.2) { this.sketch.ekle('dikdortgen', [na, nb]); eklendi = true; }
      } else if (el.tip === 'cokgen') {   // PARAMETRİK çokgen offset → yine düzgün çokgen (apothem-paralel)
        const n = Math.max(3, Math.round(el.kenarSayisi || 6));
        const nr = (el.r || 0) + mesafe / Math.cos(Math.PI / n);
        if (nr > 0.2) {
          const k1 = el.noktalar[1] ? { x: el.noktalar[1].x, y: el.noktalar[1].y } : { x: el.noktalar[0].x + nr, y: el.noktalar[0].y };
          const ne = this.sketch.ekle('cokgen', [{ x: el.noktalar[0].x, y: el.noktalar[0].y }, k1], nr); ne.kenarSayisi = n; eklendi = true;
        }
      } else if (el.tip === 'slot') {     // PARAMETRİK slot offset → yine slot
        const nr = (el.r || 0) + mesafe;
        if (nr > 0.2) { this.sketch.ekle('slot', [{ x: el.noktalar[0].x, y: el.noktalar[0].y }, { x: el.noktalar[1].x, y: el.noktalar[1].y }], nr); eklendi = true; }
      }
    }
    const cizgiler = els.filter(e => e.tip === 'cizgi');
    if (cizgiler.length >= 1) {
      const pts = [{ x: cizgiler[0].noktalar[0].x, y: cizgiler[0].noktalar[0].y }];
      for (const c of cizgiler) pts.push({ x: c.noktalar[1].x, y: c.noktalar[1].y });
      const ilk = pts[0], son = pts[pts.length - 1];
      const kapali = pts.length >= 4 && Math.hypot(ilk.x - son.x, ilk.y - son.y) < 1.0;
      if (kapali) pts.pop(); // kapalı: son=ilk tekrarını at
      const offs = this.offsetPolygon(pts, mesafe, kapali);
      if (offs && offs.length >= 2) {
        const adet = kapali ? offs.length : offs.length - 1;  // açıkta uçları sarma
        for (let i = 0; i < adet; i++) this.sketch.ekle('cizgi', [offs[i], offs[(i + 1) % offs.length]]);
        eklendi = true;
      }
    }
    if (eklendi) { this.entityAgaceGuncelleGuvenli(); this.d.ciz(); this.d.degisti?.(this.durum, this.sketch); }
    return eklendi;
  }

  private entityAgaceGuncelleGuvenli(): void { try { this.entityAgaciGuncelle(); } catch { } }

  // ---- İNTERAKTİF OFFSET (CATIA: eleman seç → mouse hayalet → tık uygula) ----
  private offsetHayalet: THREE.Line | null = null;

  /** Elemanın `mesafe` kadar offset'lenmiş GEÇİCİ kopyası (gerçek eklemeden — hayalet + uygula ortak). */
  private offsetSahteEleman(el: any, mesafe: number): any {
    if (el.tip === 'cember') { const nr = (el.r || 0) + mesafe; return nr > 0.2 ? { tip: 'cember', noktalar: [{ ...el.noktalar[0] }], r: nr } : null; }
    if (el.tip === 'cokgen') { const n = Math.max(3, Math.round(el.kenarSayisi || 6)); const nr = (el.r || 0) + mesafe / Math.cos(Math.PI / n); return nr > 0.2 ? { tip: 'cokgen', noktalar: [{ ...el.noktalar[0] }, el.noktalar[1] ? { ...el.noktalar[1] } : { x: el.noktalar[0].x + nr, y: el.noktalar[0].y }], r: nr, kenarSayisi: n } : null; }
    if (el.tip === 'slot') { const nr = (el.r || 0) + mesafe; return nr > 0.2 ? { tip: 'slot', noktalar: [{ ...el.noktalar[0] }, { ...el.noktalar[1] }], r: nr } : null; }
    if (el.tip === 'dikdortgen') { const [a, b] = el.noktalar; const minx = Math.min(a.x, b.x), maxx = Math.max(a.x, b.x), miny = Math.min(a.y, b.y), maxy = Math.max(a.y, b.y); const na = { x: minx - mesafe, y: miny - mesafe }, nb = { x: maxx + mesafe, y: maxy + mesafe }; return (nb.x - na.x > 0.2 && nb.y - na.y > 0.2) ? { tip: 'dikdortgen', noktalar: [na, nb] } : null; }
    if (el.tip === 'cizgi') { const offs = this.offsetPolygon([el.noktalar[0], el.noktalar[1]], mesafe, false); return offs && offs.length >= 2 ? { tip: 'cizgi', noktalar: [offs[0], offs[1]] } : null; }
    return null;
  }

  /** İmlecin elemana işaretli mesafesi (dışa +, içe −). */
  private offsetMesafe(el: any, p: { x: number; y: number }): number {
    const cg = this.sketch?.cemberGeo(el);
    if (cg) return Math.hypot(p.x - cg.merkez.x, p.y - cg.merkez.y) - cg.r;
    if (el.tip === 'dikdortgen') { const [a, b] = el.noktalar; const cx = (a.x + b.x) / 2, cy = (a.y + b.y) / 2; return Math.max(Math.abs(p.x - cx) - Math.abs(b.x - a.x) / 2, Math.abs(p.y - cy) - Math.abs(b.y - a.y) / 2); }
    if (el.tip === 'slot') return this.segMesafe(p, el.noktalar[0], el.noktalar[1]) - (el.r || 0);
    if (el.tip === 'cizgi') { const [a, b] = el.noktalar; const dx = b.x - a.x, dy = b.y - a.y, L = Math.hypot(dx, dy) || 1; return (p.x - a.x) * (-dy / L) + (p.y - a.y) * (dx / L); }
    return 0;
  }

  /** Mouse hareketinde hayalet offset konturunu günceller (tıklayana kadar). */
  private offsetHayaletGuncelle(el: any, p: { x: number; y: number }): void {
    const sahte = this.offsetSahteEleman(el, this.offsetMesafe(el, p));
    if (!sahte || !this.sketch) { this.offsetHayaletTemizle(); return; }
    const w = this.elemanNoktalari(sahte).map(q => this.sketch!.duzlem2Dunya(q));
    if (!this.offsetHayalet) {
      const m = new THREE.LineBasicMaterial({ color: 0x00ff88, transparent: true, opacity: 0.85, depthTest: false });
      this.offsetHayalet = new THREE.Line(new THREE.BufferGeometry().setFromPoints(w), m); this.offsetHayalet.renderOrder = 998; (this.offsetHayalet as any).raycast = () => { };
      this.sketch.nesne.add(this.offsetHayalet);
    } else { this.offsetHayalet.geometry.dispose(); this.offsetHayalet.geometry = new THREE.BufferGeometry().setFromPoints(w); }
    this.d.ciz();
  }
  private offsetHayaletTemizle(): void {
    if (this.offsetHayalet) { this.offsetHayalet.parent?.remove(this.offsetHayalet); this.offsetHayalet.geometry.dispose(); (this.offsetHayalet.material as any)?.dispose?.(); this.offsetHayalet = null; this.d.ciz(); }
  }

  /** Tek elemanı offset'ler (yeni parametrik eleman ekler). */
  private elemanOffset(el: any, mesafe: number): void {
    const s = this.offsetSahteEleman(el, mesafe); if (!s || !this.sketch) return;
    const ne = this.sketch.ekle(s.tip, s.noktalar, s.r); if (s.kenarSayisi) ne.kenarSayisi = s.kenarSayisi;
    this.sketch.ciz(); this.entityAgaceGuncelleGuvenli(); this.degistiVeRebuild();
  }

  /** Polygon/polyline'ı `d` kadar öteler. kapali=true → kapalı kontur (köşe kesişimi),
   * kapali=false → açık zincir (uç vertex'ler tek kenar normaliyle, sarma yok). */
  private offsetPolygon(pts: { x: number; y: number }[], d: number, kapali = true): { x: number; y: number }[] | null {
    const n = pts.length; if (n < 2) return null;
    const norm = (ax: number, ay: number) => { const l = Math.hypot(ax, ay) || 1; return { x: ax / l, y: ay / l }; };
    let yon = d;
    if (kapali) { const ccw = this.poliAlan(pts.map(p => new THREE.Vector2(p.x, p.y))) > 0; yon = ccw ? d : -d; }
    const out: { x: number; y: number }[] = [];
    for (let i = 0; i < n; i++) {
      const oncekiVar = kapali || i > 0;
      const sonrakiVar = kapali || i < n - 1;
      const p0 = pts[(i - 1 + n) % n], p1 = pts[i], p2 = pts[(i + 1) % n];
      if (oncekiVar && sonrakiVar) {
        const e1 = norm(p1.x - p0.x, p1.y - p0.y), e2 = norm(p2.x - p1.x, p2.y - p1.y);
        const n1 = { x: -e1.y, y: e1.x }, n2 = { x: -e2.y, y: e2.x };
        const a1 = { x: p1.x + n1.x * yon, y: p1.y + n1.y * yon };
        const a2 = { x: p1.x + n2.x * yon, y: p1.y + n2.y * yon };
        out.push(this.ikiDogru(a1, e1, a2, e2) || { x: p1.x + (n1.x + n2.x) / 2 * yon, y: p1.y + (n1.y + n2.y) / 2 * yon });
      } else if (sonrakiVar) {                         // ilk uç (açık) → sonraki kenar normali
        const e = norm(p2.x - p1.x, p2.y - p1.y); out.push({ x: p1.x - e.y * yon, y: p1.y + e.x * yon });
      } else {                                          // son uç (açık) → önceki kenar normali
        const e = norm(p1.x - p0.x, p1.y - p0.y); out.push({ x: p1.x - e.y * yon, y: p1.y + e.x * yon });
      }
    }
    return out;
  }

  /** (a1,dir1) ve (a2,dir2) doğrularının kesişimi. */
  private ikiDogru(a1: any, d1: any, a2: any, d2: any): { x: number; y: number } | null {
    const den = d1.x * d2.y - d1.y * d2.x;
    if (Math.abs(den) < 1e-9) return null;
    const t = ((a2.x - a1.x) * d2.y - (a2.y - a1.y) * d2.x) / den;
    return { x: a1.x + d1.x * t, y: a1.y + d1.y * t };
  }

  // ---- operasyonlar: aynala/ötele/taşı/fillet/pah/trim + geometrik kısıtlar ----
  private islem: 'yok' | 'aynala' | 'otele' | 'tasi' | 'fillet' | 'pah' | 'trim' | 'quickTrim' | 'corner' | 'olcu' | 'offset' | 'offsetZincir'
    | 'kparalel' | 'kdik' | 'kesit' | 'kcakisik' | 'kesmerkez' | 'kteget' | 'ksimetri' | 'kcontact' | 'ksimetri3' | 'kortanokta' | 'kesitmesafe' | 'kyatay' | 'kdikey' = 'yok';
  private islemHandler: ((e: PointerEvent) => void) | null = null;
  private islemMoveHandler: ((e: PointerEvent) => void) | null = null;
  private secimler: any[] = [];   // seçilen elemanlar
  private nokta1: { x: number; y: number } | null = null;
  cornerR = 5;   // Corner (parametrik fillet) yarıçapı — sayısal panelden ayarlanır

  /** Bir operasyonu başlatır → kullanıcı eleman/nokta seçer (hover vurgulu). */
  islemBaslat(tip: 'aynala' | 'otele' | 'tasi' | 'fillet' | 'pah' | 'trim' | 'quickTrim' | 'corner' | 'olcu' | 'offset' | 'offsetZincir'
    | 'kparalel' | 'kdik' | 'kesit' | 'kcakisik' | 'kesmerkez' | 'kteget' | 'ksimetri' | 'kcontact' | 'ksimetri3' | 'kortanokta' | 'kesitmesafe' | 'kyatay' | 'kdikey'): void {
    if (!this.sketch) return;
    this.araci?.aracSec('sec');
    this.islemBitir();
    this.islem = tip; this.secimler = []; this.nokta1 = null;
    const canvas = this.d.canvasAl();
    if (!canvas) return;
    this.islemHandler = (e) => this.islemTiklama(e, canvas);
    this.islemMoveHandler = (e) => {
      if (this.islem === 'offset' && this.secimler[0]) {   // CATIA: tıklayana kadar mouse'la hayalet offset
        const p = this.duzlemNokta(e, canvas); if (p) this.offsetHayaletGuncelle(this.secimler[0], p);
      } else { const el = this.elemanRaycast(e, canvas); this.sketch?.hover(el ? el.id : null); }
    };
    canvas.addEventListener('pointerdown', this.islemHandler);
    canvas.addEventListener('pointermove', this.islemMoveHandler, { passive: true });
  }

  private islemBitir(): void {
    const canvas = this.d.canvasAl();
    if (this.islemHandler) { canvas?.removeEventListener('pointerdown', this.islemHandler); this.islemHandler = null; }
    if (this.islemMoveHandler) { canvas?.removeEventListener('pointermove', this.islemMoveHandler as any); this.islemMoveHandler = null; }
    this.islem = 'yok'; this.secimler = []; this.nokta1 = null;
    this.offsetHayaletTemizle();
    this.sketch?.hover(null);
    this.sketch?.sec(null);
  }

  private islemBitirVeYenile(): void {
    this.islemBitir();
    if (this.duzenlenenFeat && this.sketch) { this.duzenlenenFeat.elemanlar = JSON.parse(JSON.stringify(this.sketch.elemanlar)); this.rebuild(); }
    this.d.ciz(); this.entityAgaceGuncelleGuvenli(); this.d.degisti?.(this.durum, this.sketch);
    this.sketchGecmisKaydet();   // sketch operasyonu (trim/fillet/aynala/sil...) sonrası → geçmiş
  }

  /** Dikdörtgeni 4 ayrı çizgiye patlatır (CATIA'da dikdörtgen = 4 çizgi). */
  private patlatDikdortgen(el: any): any[] {
    if (!this.sketch || el.tip !== 'dikdortgen') return [];
    const [a, b] = el.noktalar;
    const k = [{ x: a.x, y: a.y }, { x: b.x, y: a.y }, { x: b.x, y: b.y }, { x: a.x, y: b.y }];
    this.sketch.sil(el.id);
    const lns: any[] = [];
    for (let i = 0; i < 4; i++) {
      const ln = this.sketch.ekle('cizgi', [{ x: k[i].x, y: k[i].y }, { x: k[(i + 1) % 4].x, y: k[(i + 1) % 4].y }]);
      if (el.yapi) ln.yapi = true;
      lns.push(ln);
    }
    return lns;
  }

  /** Patlatılmış dikdörtgende tıklanan noktaya en yakın köşeyi paylaşan iki çizgi. */
  private enYakinKose(lns: any[], p: { x: number; y: number }): [any, any] | null {
    if (lns.length !== 4 || !p) return null;
    let best = -1, bestD = Infinity;
    for (let i = 0; i < 4; i++) { const c = lns[i].noktalar[1]; const d = Math.hypot(c.x - p.x, c.y - p.y); if (d < bestD) { bestD = d; best = i; } }
    return best < 0 ? null : [lns[best], lns[(best + 1) % 4]];
  }

  /** Tıklanan noktaya en yakın çizgi (patlatılmış dikdörtgen kenarı). */
  private tiklananCizgi(lns: any[], p: { x: number; y: number }): any | null {
    let en: any = null, enD = Infinity;
    for (const l of lns) {
      const [a, b] = l.noktalar; const dx = b.x - a.x, dy = b.y - a.y; const len2 = dx * dx + dy * dy || 1;
      const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2));
      const d = Math.hypot(a.x + dx * t - p.x, a.y + dy * t - p.y);
      if (d < enD) { enD = d; en = l; }
    }
    return en;
  }

  private islemTiklama(e: PointerEvent, canvas: HTMLCanvasElement): void {
    if (e.button !== 0 || !this.sketch) return;
    const el = this.elemanRaycast(e, canvas);
    const p = this.duzlemNokta(e, canvas);

    switch (this.islem) {
      case 'aynala':
        if (el) { this.aynala(el); this.islemBitirVeYenile(); }
        break;
      case 'otele':
        if (!this.secimler[0]) { if (el) { this.secimler[0] = el; this.sketch.sec(el.id); this.d.ciz(); } }
        else if (p) { this.otele(this.secimler[0], p); this.islemBitirVeYenile(); }
        break;
      case 'offset':   // CATIA: 1.tık eleman seç → mouse hayalet → 2.tık uygula
        if (!this.secimler[0]) { if (el) { this.secimler[0] = el; this.sketch.sec(el.id); this.d.ciz(); } }
        else if (p) { const mes = this.offsetMesafe(this.secimler[0], p); if (Math.abs(mes) > 0.05) this.elemanOffset(this.secimler[0], mes); this.islemBitirVeYenile(); }
        break;
      case 'offsetZincir':   // CATIA offset propagation: teğet zincir boyunca öteler
        if (!this.secimler[0]) { if (el && el.tip === 'cizgi') { this.secimler[0] = el; this.sketch.sec(el.id); this.d.ciz(); } }
        else if (p) { const mes = this.offsetMesafe(this.secimler[0], p); if (Math.abs(mes) > 0.05) this.offsetZincirUygula(this.secimler[0], mes); this.islemBitirVeYenile(); }
        break;
      case 'corner':   // CATIA Corner = parametrik fillet (cornerR yarıçaplı)
        if (el && el.tip === 'dikdortgen' && p) { const lns = this.patlatDikdortgen(el); const kose = this.enYakinKose(lns, p); if (kose) this.fillet(kose[0], kose[1], this.cornerR); this.islemBitirVeYenile(); break; }
        if (el && el.tip === 'cizgi' && !this.secimler.includes(el)) {
          this.secimler.push(el); this.sketch.sec(el.id); this.d.ciz();
          if (this.secimler.length === 2) { this.fillet(this.secimler[0], this.secimler[1], this.cornerR); this.islemBitirVeYenile(); }
        }
        break;
      case 'tasi':
        if (!this.secimler[0]) { if (el) { this.secimler[0] = el; this.sketch.sec(el.id); this.d.ciz(); } }
        else if (!this.nokta1) { this.nokta1 = p; }
        else if (p) { this.tasi(this.secimler[0], this.nokta1, p); this.islemBitirVeYenile(); }
        break;
      case 'fillet':
      case 'pah':
        // Dikdörtgen/çokgen tek elemandır → tıklanan köşeyi bul, 4 çizgiye patlat, o köşeye uygula (CATIA tarzı)
        if (el && el.tip === 'dikdortgen' && p) {
          const lns = this.patlatDikdortgen(el);
          const kose = this.enYakinKose(lns, p);
          if (kose) { if (this.islem === 'fillet') this.fillet(kose[0], kose[1]); else this.pah(kose[0], kose[1]); }
          this.islemBitirVeYenile();
          break;
        }
        if (el && el.tip === 'cizgi' && !this.secimler.includes(el)) {
          this.secimler.push(el); this.sketch.sec(el.id); this.d.ciz();
          if (this.secimler.length === 2) {
            if (this.islem === 'fillet') this.fillet(this.secimler[0], this.secimler[1]);
            else this.pah(this.secimler[0], this.secimler[1]);
            this.islemBitirVeYenile();
          }
        }
        break;
      case 'trim':
        if (el && el.tip === 'dikdortgen' && p) { const lns = this.patlatDikdortgen(el); const en = this.tiklananCizgi(lns, p); if (en) this.trim(en, p); this.islemBitirVeYenile(); break; }
        if (el && p) { this.trim(el, p); this.islemBitirVeYenile(); }
        break;
      case 'quickTrim':   // CATIA QuickTrim: tıklanan parçayı kes, MODDA KAL (zincir budama)
        if (el && el.tip === 'dikdortgen' && p) { const lns = this.patlatDikdortgen(el); const en = this.tiklananCizgi(lns, p); if (en) this.trim(en, p); this.entityAgaceGuncelleGuvenli(); this.d.ciz(); break; }
        if (el && p) { this.trim(el, p); this.entityAgaceGuncelleGuvenli(); this.d.ciz(); this.d.degisti?.(this.durum, this.sketch); }
        break;
      case 'olcu':
        if (el) { this.sketch.olcuEkle(el.id); this.entityAgaceGuncelleGuvenli(); this.d.ciz(); this.d.degisti?.(this.durum, this.sketch); }  // modda kal (çoklu ölçü)
        break;
      // tek-eleman kısıtları
      case 'kyatay': if (el?.tip === 'cizgi') { el.noktalar[1] = { x: el.noktalar[1].x, y: el.noktalar[0].y }; this.sketch.ciz(); this.islemBitirVeYenile(); } break;
      case 'kdikey': if (el?.tip === 'cizgi') { el.noktalar[1] = { x: el.noktalar[0].x, y: el.noktalar[1].y }; this.sketch.ciz(); this.islemBitirVeYenile(); } break;
      // iki-eleman kısıtları (coincidence/parallel/perpendicular/equal/concentric/tangent/symmetric)
      case 'kparalel': case 'kdik': case 'kesit': case 'kcakisik': case 'kesmerkez': case 'kteget': case 'ksimetri': case 'kcontact':
        if (el && !this.secimler.includes(el)) {
          this.secimler.push(el); this.sketch.sec(el.id); this.d.ciz();
          if (this.secimler.length === 2) {
            const [a, b] = this.secimler;
            if (this.islem === 'kparalel') this.kisitParalel(a, b);
            else if (this.islem === 'kdik') this.kisitDik(a, b);
            else if (this.islem === 'kesit') this.kisitEsit(a, b);
            else if (this.islem === 'kcakisik') this.kisitCakisik(a, b);
            else if (this.islem === 'kesmerkez') this.kisitEsmerkez(a, b);
            else if (this.islem === 'kteget') this.kisitTeget(a, b);
            else if (this.islem === 'ksimetri') this.kisitSimetri(a, b);
            else if (this.islem === 'kcontact') this.kisitAkilli(a, b);
            if (this.islem !== 'kcontact') this.kisitKaliciKaydet(this.islem, a, b);   // kalıcı solver kaydı (sürüklemede korunur)
            this.sketch.ciz(); this.islemBitirVeYenile();
          }
        }
        break;
      // üç-öğe kısıtları (symmetry: uç,uç,eksen-çizgi | middlePoint: orta,uçA,uçB | equidistant: uçA,uçB,uçC)
      // tıklanan noktaya en yakın UÇ seçilir (çizgi ucu da seçilebilir, eleman geneli değil)
      case 'ksimetri3': case 'kortanokta': case 'kesitmesafe':
        if (el && p) {
          const uc = this.enYakinUc(el, p);
          if (!this.secimler.some((s: any) => s.el === el && s.uc === uc)) {
            this.secimler.push({ el, uc }); this.sketch.sec(el.id); this.d.ciz();
            if (this.secimler.length === 3) { this.ucOgeKisit(this.islem, this.secimler); this.sketch.ciz(); this.islemBitirVeYenile(); }
          }
        }
        break;
    }
  }

  /** Tıklanan noktaya en yakın uç indeksi (çizgi/yay 0-1, diğer 0). */
  private enYakinUc(el: any, p: { x: number; y: number }): number {
    if (el.tip === 'cizgi' || el.tip === 'yay') {
      const d0 = Math.hypot(el.noktalar[0].x - p.x, el.noktalar[0].y - p.y);
      const d1 = Math.hypot(el.noktalar[1].x - p.x, el.noktalar[1].y - p.y);
      return d0 <= d1 ? 0 : 1;
    }
    return 0;
  }

  /** Üç öğeli kısıtı (symmetry/middlePoint/equidistant) seçili UÇLAR ile kalıcı solver'a uygula + çöz. */
  private ucOgeKisit(islem: string, sec: any[]): void {
    if (!this.sketch || sec.length < 3) return;
    const h = (s: any) => ({ elemanId: s.el.id, uc: s.uc });
    if (islem === 'ksimetri3') {
      this.sketch.kisitEkleKalici('symmetry', [h(sec[0]), h(sec[1]), { elemanId: sec[2].el.id }]);   // 3. = eksen çizgisi
      const ax = sec[2].el.id;
      this.sketch.kisitCoz([`${ax}:px:0`, `${ax}:py:0`, `${ax}:px:1`, `${ax}:py:1`]);   // eksen SABİT → noktalar simetrik olur
      return;
    }
    if (islem === 'kortanokta') this.sketch.kisitEkleKalici('middlePoint', [h(sec[0]), h(sec[1]), h(sec[2])]);
    else if (islem === 'kesitmesafe') this.sketch.kisitEkleKalici('equidistant', [h(sec[0]), h(sec[1]), h(sec[2])]);
    this.sketch.kisitCoz([]);
  }

  /** CATIA Auto Dimension: sketch'i ölçü-kısıtlarıyla TAM TANIMLA (DOF-farkında — sadece serbestlik>0 iken ekler, aşırı yapanı geri alır). */
  otoOlcule(): number {
    if (!this.sketch) return 0;
    const solver = this.sketch.kisitSolver;
    const els = this.sketch.elemanlar.filter((e: any) => !e.yapi);
    let sayi = 0;
    // DOF azaltıyorsa ekle, aşırı (serbestlik<0) yaptıysa geri al
    const dene = (tip: any, hedef: any, param?: number): boolean => {
      if (solver.dof().serbestlik <= 0) return false;   // zaten tam/aşırı tanımlı
      const oncekiSerbest = solver.dof().serbestlik;
      const k = this.sketch!.kisitEkleKalici(tip, hedef, param);
      const yeni = solver.dof().serbestlik;
      if (yeni < 0 || yeni >= oncekiSerbest) { solver.sil(k.id); return false; }   // aşırı yaptı veya azaltmadı → geri al
      sayi++; return true;
    };
    // 1) konum sabitle (translation DOF: fix yoksa ilk elemanın ilk noktası)
    if (els.length && !solver.kisitlar.some(k => k.tip === 'fix')) dene('fix', [{ elemanId: els[0].id, uc: 0 }]);
    // 2) boyutlar — sadece serbestlik kaldıkça
    for (const el of els) {
      if (solver.dof().serbestlik <= 0) break;
      if (el.tip === 'cizgi') {
        const L = Math.hypot(el.noktalar[1].x - el.noktalar[0].x, el.noktalar[1].y - el.noktalar[0].y);
        if (L > 0.1) dene('distance', [{ elemanId: el.id, uc: 0 }, { elemanId: el.id, uc: 1 }], +L.toFixed(2));
      } else if (el.tip === 'cember') {
        dene('radius', [{ elemanId: el.id }], +(el.r || 0).toFixed(2));
      }
    }
    solver.coz([]);
    this.entityAgaceGuncelleGuvenli(); this.sketch.ciz();
    return sayi;
  }

  // ---- geometrik kısıt uygulayıcıları (tek-seferlik; canlı solver gece görevinde gelecek) ----
  private uclar(el: any): { x: number; y: number }[] {
    if (el.tip === 'cizgi' || el.tip === 'dikdortgen') return [el.noktalar[0], el.noktalar[1]];
    return [el.noktalar[0]];
  }
  private cizgiYon(l: any): { x: number; y: number; len: number } {
    const a = l.noktalar[0], b = l.noktalar[1]; const len = Math.hypot(b.x - a.x, b.y - a.y) || 1;
    return { x: (b.x - a.x) / len, y: (b.y - a.y) / len, len };
  }
  private kisitParalel(l1: any, l2: any): void {
    if (l1.tip !== 'cizgi' || l2.tip !== 'cizgi') return;
    const d = this.cizgiYon(l1); const a2 = l2.noktalar[0], b2 = l2.noktalar[1]; const len = Math.hypot(b2.x - a2.x, b2.y - a2.y);
    const isaret = ((b2.x - a2.x) * d.x + (b2.y - a2.y) * d.y) >= 0 ? 1 : -1;
    l2.noktalar[1] = { x: a2.x + d.x * len * isaret, y: a2.y + d.y * len * isaret };
  }
  private kisitDik(l1: any, l2: any): void {
    if (l1.tip !== 'cizgi' || l2.tip !== 'cizgi') return;
    const d = this.cizgiYon(l1); const n = { x: -d.y, y: d.x };
    const a2 = l2.noktalar[0], b2 = l2.noktalar[1]; const len = Math.hypot(b2.x - a2.x, b2.y - a2.y);
    const isaret = ((b2.x - a2.x) * n.x + (b2.y - a2.y) * n.y) >= 0 ? 1 : -1;
    l2.noktalar[1] = { x: a2.x + n.x * len * isaret, y: a2.y + n.y * len * isaret };
  }
  private kisitEsit(e1: any, e2: any): void {
    if (e1.tip === 'cizgi' && e2.tip === 'cizgi') {
      const hedef = Math.hypot(e1.noktalar[1].x - e1.noktalar[0].x, e1.noktalar[1].y - e1.noktalar[0].y);
      const a = e2.noktalar[0], b = e2.noktalar[1]; const cur = Math.hypot(b.x - a.x, b.y - a.y) || 1;
      e2.noktalar[1] = { x: a.x + (b.x - a.x) / cur * hedef, y: a.y + (b.y - a.y) / cur * hedef };
    } else if ((e1.tip === 'cember' || e1.tip === 'yay') && (e2.tip === 'cember' || e2.tip === 'yay')) {
      const c = this.sketch?.cemberGeo(e1); if (c && e2.tip === 'cember') e2.r = c.r;
    }
  }
  private kisitCakisik(e1: any, e2: any): void {
    const p1 = this.uclar(e1), p2 = this.uclar(e2);
    let best: any = null, bd = Infinity;
    for (const a of p1) for (const b of p2) { const d = Math.hypot(a.x - b.x, a.y - b.y); if (d < bd) { bd = d; best = { a, b }; } }
    if (best) { best.b.x = best.a.x; best.b.y = best.a.y; }   // e2 ucu e1 ucuna otur
  }
  private kisitEsmerkez(c1: any, c2: any): void {
    if ((c1.tip === 'cember' || c1.tip === 'yay') && (c2.tip === 'cember' || c2.tip === 'yay')) {
      const g = this.sketch?.cemberGeo(c1); if (g) c2.noktalar[0] = { x: g.merkez.x, y: g.merkez.y };
    }
  }
  private kisitTeget(l: any, c: any): void {
    // çizgiyi çembere teğet yap: çizgiyi merkeze r mesafeli kaydır (yön koru)
    const ciz = l.tip === 'cizgi' ? l : (c.tip === 'cizgi' ? c : null);
    const cem = l.tip === 'cember' ? l : (c.tip === 'cember' ? c : null);
    if (!ciz || !cem) return;
    const m = cem.noktalar[0], r = cem.r || 0;
    const a = ciz.noktalar[0], b = ciz.noktalar[1]; const d = this.cizgiYon(ciz); const n = { x: -d.y, y: d.x };
    const mesafe = (m.x - a.x) * n.x + (m.y - a.y) * n.y;   // çizginin merkeze işaretli dik mesafesi
    const kaydir = mesafe - Math.sign(mesafe || 1) * r;     // r'ye indir
    for (const p of [a, b]) { p.x += n.x * kaydir; p.y += n.y * kaydir; }
  }
  private kisitSimetri(e1: any, e2: any): void {
    // e2'yi e1'in Y eksenine göre aynası yap (basit: x işaretini eşitle, |x| eşitle)
    if (e1.tip === 'cizgi' && e2.tip === 'cizgi') {
      for (let i = 0; i < 2; i++) { e2.noktalar[i] = { x: -e1.noktalar[i].x, y: e1.noktalar[i].y }; }
    }
  }

  /** Ölçü etiketlerinin EKRAN konumları (HTML overlay için, sayfa koordinatı). */
  /** Sketch tanım durumu (DOF rozeti için). */
  get sketchDurumOzeti(): { eleman: number; olcu: number; serbest: number; tamTanimli: boolean; dof?: number; durum?: 'serbest' | 'tam' | 'asiri' } | null {
    return this.sketch?.durumOzeti() || null;
  }

  // ───────────────────── CATIA: AUTO/CONTACT CONSTRAINT + SKETCH ANALYSIS ─────────────────────

  /** Bir uygulanan kısıtı KALICI solver'a da kaydet (sürüklemede korunur — CATIA parametrik). */
  private kisitKaliciKaydet(islem: string, a: any, b: any): void {
    if (!this.sketch) return;
    try {
      if (islem === 'kparalel') this.sketch.kisitEkleKalici('parallel', [{ elemanId: a.id }, { elemanId: b.id }]);
      else if (islem === 'kdik') this.sketch.kisitEkleKalici('perpendicular', [{ elemanId: a.id }, { elemanId: b.id }]);
      else if (islem === 'kesit') this.sketch.kisitEkleKalici('equal', [{ elemanId: a.id }, { elemanId: b.id }]);
      else if (islem === 'kesmerkez') this.sketch.kisitEkleKalici('concentric', [{ elemanId: a.id }, { elemanId: b.id }]);
      else if (islem === 'kteget') this.sketch.kisitEkleKalici('tangent', [{ elemanId: a.id }, { elemanId: b.id }]);
      else if (islem === 'kcakisik') { const uc = this.enYakinUcCift(a, b); this.sketch.kisitEkleKalici('coincidence', uc); }
    } catch { }
  }

  /** İki eleman arasındaki en yakın uç çiftinin {elemanId,uc} hedefleri. */
  private enYakinUcCift(a: any, b: any): { elemanId: number; uc?: number }[] {
    const pa = this.uclar(a), pb = this.uclar(b);
    let bi = 0, bj = 0, bd = Infinity;
    for (let i = 0; i < pa.length; i++) for (let j = 0; j < pb.length; j++) { const d = Math.hypot(pa[i].x - pb[j].x, pa[i].y - pb[j].y); if (d < bd) { bd = d; bi = i; bj = j; } }
    return [{ elemanId: a.id, uc: bi }, { elemanId: b.id, uc: bj }];
  }

  /** İki çizgi arası 0..90° açı farkı. */
  private aciFark2(l1: any, l2: any): number {
    const d1 = this.cizgiYon(l1), d2 = this.cizgiYon(l2);
    let a = Math.abs(Math.atan2(d1.x * d2.y - d1.y * d2.x, d1.x * d2.x + d1.y * d2.y) * 180 / Math.PI);
    if (a > 90) a = 180 - a; return a;
  }

  /** CATIA Contact: iki öğe tipine göre uygun kısıtı seç + uygula + kalıcı kaydet. */
  kisitAkilli(a: any, b: any): string | null {
    if (!this.sketch || !a || !b) return null;
    const cizgi = (x: any) => x.tip === 'cizgi';
    const cember = (x: any) => x.tip === 'cember' || x.tip === 'yay';
    let islem: string | null = null;
    if (cizgi(a) && cizgi(b)) islem = this.aciFark2(a, b) < 45 ? 'kparalel' : 'kdik';
    else if ((cizgi(a) && cember(b)) || (cember(a) && cizgi(b))) islem = 'kteget';
    else if (cember(a) && cember(b)) islem = 'kesmerkez';
    else islem = 'kcakisik';
    if (islem === 'kparalel') this.kisitParalel(a, b);
    else if (islem === 'kdik') this.kisitDik(a, b);
    else if (islem === 'kteget') this.kisitTeget(a, b);
    else if (islem === 'kesmerkez') this.kisitEsmerkez(a, b);
    else this.kisitCakisik(a, b);
    this.kisitKaliciKaydet(islem, a, b);
    this.sketch.kisitCoz?.([]);
    this.sketch.ciz();
    return islem;
  }

  /** CATIA Auto Constraint: tüm üretim geometriye yatay/dikey/çakışık otomatik kısıt çıkar. */
  otoKisitlaTumu(): number {
    if (!this.sketch) return 0;
    const ACI = 2.5, UC = 0.6;
    let sayi = 0;
    const K = this.sketch.kisitSolver.kisitlar;
    const hvVar = (id: number) => K.some(k => (k.tip === 'horizontal' || k.tip === 'vertical') && k.elemanlar.some(x => x.elemanId === id));
    const coincVar = (aId: number, aUc: number, bId: number, bUc: number) => K.some(k => k.tip === 'coincidence' &&
      k.elemanlar.some(x => x.elemanId === aId && (x.uc ?? 0) === aUc) && k.elemanlar.some(x => x.elemanId === bId && (x.uc ?? 0) === bUc));
    const cizgiler = this.sketch.elemanlar.filter((e: any) => e.tip === 'cizgi' && !e.yapi);
    for (const l of cizgiler) {
      if (hvVar(l.id)) continue;   // zaten yatay/dikey kısıtlı → dedup
      const a = l.noktalar[0], b = l.noktalar[1];
      const aci = Math.atan2(Math.abs(b.y - a.y), Math.abs(b.x - a.x)) * 180 / Math.PI;
      if (aci < ACI) { l.noktalar[1] = { x: b.x, y: a.y }; this.sketch.kisitEkleKalici('horizontal', [{ elemanId: l.id }]); sayi++; }
      else if (aci > 90 - ACI) { l.noktalar[1] = { x: a.x, y: b.y }; this.sketch.kisitEkleKalici('vertical', [{ elemanId: l.id }]); sayi++; }
    }
    // uç çakışmaları (dedup: mevcut coincidence varsa ekleme)
    const uclar: { id: number; uc: number; p: any }[] = [];
    for (const e of this.sketch.elemanlar) {
      if ((e as any).yapi) continue;
      const n = e.tip === 'cizgi' || e.tip === 'dikdortgen' ? 2 : 1;
      for (let i = 0; i < n; i++) uclar.push({ id: e.id, uc: i, p: e.noktalar[i] });
    }
    for (let i = 0; i < uclar.length; i++) for (let j = i + 1; j < uclar.length; j++) {
      if (uclar[i].id === uclar[j].id) continue;
      if (Math.hypot(uclar[i].p.x - uclar[j].p.x, uclar[i].p.y - uclar[j].p.y) < UC && !coincVar(uclar[i].id, uclar[i].uc, uclar[j].id, uclar[j].uc)) {
        this.sketch.kisitEkleKalici('coincidence', [{ elemanId: uclar[i].id, uc: uclar[i].uc }, { elemanId: uclar[j].id, uc: uclar[j].uc }]); sayi++;
      }
    }
    this.sketch.kisitCoz?.([]);
    this.entityAgaceGuncelleGuvenli(); this.sketch.ciz(); this.d.ciz();
    return sayi;
  }

  /** CATIA Sketch Analysis: açık profil tespiti — kapanmamış zincirlerin serbest uçlarını bul. */
  sketchSaglik(): { kapaliLoop: number; acikUc: number; acikUcDunya: { x: number; y: number; z: number }[]; padOlur: boolean; mesaj: string } | null {
    if (!this.sketch) return null;
    const els = this.sketch.elemanlar.filter((e: any) => !e.yapi);
    // çizgi/yay segment uçları topla
    const uclar: { x: number; y: number }[] = [];
    for (const el of els) {
      if (el.tip === 'cizgi') uclar.push(el.noktalar[0], el.noktalar[1]);
      else if (el.tip === 'yay') uclar.push(el.noktalar[0], el.noktalar[1]);
    }
    const tol = 0.8;   // zincirle() ile aynı tolerans
    const acikUc: { x: number; y: number }[] = [];
    for (let i = 0; i < uclar.length; i++) {
      let eslesme = 0;
      for (let j = 0; j < uclar.length; j++) { if (i === j) continue; if (Math.hypot(uclar[i].x - uclar[j].x, uclar[i].y - uclar[j].y) < tol) eslesme++; }
      if (eslesme === 0) acikUc.push(uclar[i]);
    }
    const shapes = this.profilShapelar(els);
    const padOlur = shapes.length > 0;
    const acikUcDunya = acikUc.map(p => { const w = this.sketch!.duzlem2Dunya(p); return { x: w.x, y: w.y, z: w.z }; });
    const mesaj = padOlur
      ? (acikUc.length ? `Profil kapalı (${shapes.length} bölge) ama ${acikUc.length} serbest uç var (artık çizgi).` : `Profil sağlıklı: ${shapes.length} kapalı bölge, pad/pocket yapılabilir.`)
      : (acikUc.length ? `Açık profil: ${acikUc.length} serbest uç birleşmemiş → pad/pocket yapılamaz. Uçları birleştir.` : 'Kapalı profil bulunamadı (en az 1 kapalı kontur gerekli).');
    return { kapaliLoop: shapes.length, acikUc: acikUc.length, acikUcDunya, padOlur, mesaj };
  }

  /** Açık uçları kırmızı daire ile işaretle (fatVurgu altyapısı). */
  private acikUcVurgu: THREE.Object3D | null = null;
  sketchAcikUclariIsaretle(): number {
    this.acikUcVurguTemizle();
    const sag = this.sketchSaglik(); if (!sag) return 0;
    if (sag.acikUcDunya.length) {
      const seg: number[] = []; const R = 2.2;
      for (const u of sag.acikUcDunya) {
        const w = this.sketch!.duzlem2Dunya(this.sketch!.dunya2Duzlem(new THREE.Vector3(u.x, u.y, u.z)));   // düzlem üstü
        for (let i = 0; i < 12; i++) {
          const t0 = i / 12 * Math.PI * 2, t1 = (i + 1) / 12 * Math.PI * 2;
          const eU = this.sketch!.duzlem.eksenU, eV = this.sketch!.duzlem.eksenV;
          const p0 = w.clone().addScaledVector(eU, Math.cos(t0) * R).addScaledVector(eV, Math.sin(t0) * R);
          const p1 = w.clone().addScaledVector(eU, Math.cos(t1) * R).addScaledVector(eV, Math.sin(t1) * R);
          seg.push(p0.x, p0.y, p0.z, p1.x, p1.y, p1.z);
        }
      }
      this.acikUcVurgu = this.fatVurgu(seg, 0xff3b30, 3, 1001);
      this.d.sahne.add(this.acikUcVurgu); this.d.ciz();
    }
    if (this.d.bildir) this.d.bildir(sag.mesaj);
    return sag.acikUc;
  }
  private acikUcVurguTemizle(): void {
    if (this.acikUcVurgu) { this.d.sahne.remove(this.acikUcVurgu); (this.acikUcVurgu as any).geometry?.dispose?.(); (this.acikUcVurgu as any).material?.dispose?.(); this.acikUcVurgu = null; }
  }

  /** CATIA offset propagation: bir çizgiden başlayıp uç-paylaşan tüm çizgileri aynı mesafede öteler. */
  private offsetZincirUygula(bas: any, mesafe: number): void {
    if (!this.sketch) return;
    const zincir = this.sketchTegetZincir2D(bas);
    for (const l of zincir) this.sketch.otele(l.id, mesafe);
    this.entityAgaceGuncelleGuvenli();
  }
  /** 2D sketch teğet/bağlı çizgi zinciri (uç paylaşımı, tol=0.8). */
  private sketchTegetZincir2D(bas: any): any[] {
    if (!this.sketch) return [bas];
    const cizgiler = this.sketch.elemanlar.filter((e: any) => e.tip === 'cizgi' && !e.yapi);
    const tol = 0.8;
    const ayni = (a: any, b: any) => Math.hypot(a.x - b.x, a.y - b.y) < tol;
    const sonuc = new Set<any>([bas]); const kuyruk = [bas];
    while (kuyruk.length) {
      const cur = kuyruk.pop();
      for (const o of cizgiler) {
        if (sonuc.has(o)) continue;
        const paylas = [0, 1].some(i => [0, 1].some(j => ayni(cur.noktalar[i], o.noktalar[j])));
        if (paylas) { sonuc.add(o); kuyruk.push(o); }
      }
    }
    return [...sonuc];
  }

  // ───────────────────── CATIA: ANIMATE CONSTRAINT + SKETCH POSITIONING ─────────────────────
  private animTimer: any = null;
  get animasyonAktif(): boolean { return !!this.animTimer; }

  /** CATIA Animate Constraint: seçili (veya verilen) ölçüyü min↔max aralığında yumuşak oynat (mekanizma önizleme). Tekrar çağrı = durdur. */
  olcuAnimasyon(olcuId?: number): void {
    if (this.animTimer) { clearInterval(this.animTimer); this.animTimer = null; this.d.degisti?.(this.durum, this.sketch); return; }
    if (!this.sketch) return;
    let id = olcuId;
    if (id == null) { const sel = (this.sketch as any).seciliId; const o = sel != null ? this.sketch.olculer.find(x => x.elemanId === sel) : this.sketch.olculer[0]; id = o?.id; }
    const o = id != null ? this.sketch.olculer.find(x => x.id === id) : null;
    if (!o) { this.d.bildir?.('Animasyon için bir ölçülü eleman seç'); return; }
    const baz = o.deger || 10, min = baz * 0.6, max = baz * 1.4, sure = 2400, dt = 40; let t = 0;
    this.animTimer = setInterval(() => {
      t += dt; const f = (Math.sin(t / sure * Math.PI * 2 - Math.PI / 2) + 1) / 2;   // 0→1→0 yumuşak salınım
      this.olcuDegistir(o.id, +(min + (max - min) * f).toFixed(2));
      if (t >= sure * 2) { clearInterval(this.animTimer); this.animTimer = null; this.olcuDegistir(o.id, baz); this.d.degisti?.(this.durum, this.sketch); }
    }, dt);
    this.d.bildir?.('Animasyon: ölçü oynatılıyor (durdurmak için tekrar bas)');
    this.d.degisti?.(this.durum, this.sketch);
  }
  private animasyonDurdur(): void { if (this.animTimer) { clearInterval(this.animTimer); this.animTimer = null; } }

  /** CATIA Change Support / Sketch Positioning: aktif eskizi başka temel düzleme taşı (elemanlar 2D korunur). */
  eskizDuzlemeTasi(tip: 'xy' | 'xz' | 'yz'): boolean {
    if (this.durum !== 'sketch' || !this.sketch || !this.aktifNode) return false;
    const yeni = FikoSketch.duzlemHazir(tip);
    this.sketch.duzlem = yeni; this.aktifDuzlem = yeni;
    this.aktifNode.userData['sketchDuzlem'] = yeni;
    const feat = this.aktifNode.userData?.['featObj'] as IOzellik;
    if (feat) feat.duzlem = this.duzlemKlon(yeni);
    this.gridOlustur(yeni); this.kameraHizala(yeni);
    this.sketch.ciz(); if (feat) this.rebuild();
    if (this.kesitAktif) this.kesitUygula();
    this.d.degisti?.(this.durum, this.sketch);
    return true;
  }

  /** CATIA hazır profil/konik şablonu — eskiz merkezine (0,0) yerleştir (sonra sürüklenebilir/düzenlenebilir). */
  sablonEkle(tip: 'yonluDikdortgen' | 'paralelkenar' | 'keyhole' | 'parabol' | 'hiperbol'): boolean {
    if (this.durum !== 'sketch' || !this.sketch) return false;
    const o = { x: 0, y: 0 };
    if (tip === 'yonluDikdortgen') this.sketch.sablonYonluDikdortgen(o, 60, 40, Math.PI / 12);
    else if (tip === 'paralelkenar') this.sketch.sablonParalelkenar({ x: -30, y: -20 }, 60, 40, 0, Math.PI / 3);
    else if (tip === 'keyhole') this.sketch.sablonAnahtarDeligi({ x: 0, y: 20 }, 15, 40, 6);
    else if (tip === 'parabol') { const el = this.sketch.ekle('konik', [o], 8); el.r2 = 12; el.konikTip = 'parabol'; }
    else if (tip === 'hiperbol') { const el = this.sketch.ekle('konik', [o], 12); el.r2 = 12; el.konikTip = 'hiperbol'; }
    this.entityAgaceGuncelleGuvenli(); this.sketch.ciz(); this.d.degisti?.(this.durum, this.sketch);
    return true;
  }

  olcuEkranListesi(canvas: HTMLCanvasElement): { id: number; x: number; y: number; metin: string; deger: number; tip: string }[] {
    if (!this.sketch?.olculer?.length) return [];
    const kamera = this.d.kameraAl(); if (!kamera) return [];
    const rect = canvas.getBoundingClientRect();
    const w = new THREE.Vector3();
    const out: any[] = [];
    for (const o of this.sketch.olculer) {
      const yer = this.sketch.olcuYerlesim(o); const anchor = yer?.metin || this.sketch.olcuAnchor(o); if (!anchor) continue;
      this.sketch.duzlem2Dunya(anchor, w); w.project(kamera as THREE.Camera);
      if (w.z > 1) continue; // kamera arkası
      const onek = o.tip === 'yaricap' ? 'R ' : o.tip === 'cap' ? 'Ø ' : '';
      out.push({
        id: o.id,
        x: Math.round(rect.left + (w.x * 0.5 + 0.5) * rect.width),
        y: Math.round(rect.top + (-w.y * 0.5 + 0.5) * rect.height),
        metin: onek + o.deger.toFixed(1), deger: o.deger, tip: o.tip,
      });
    }
    return out;
  }

  /** Ölçü değerini değiştir → geometriyi yeniden boyutlandır (+feature ise rebuild). */
  olcuDegistir(id: number, deger: number): boolean {
    if (!this.sketch) return false;
    const ok = this.sketch.olcuUygula(id, deger);
    if (ok && this.duzenlenenFeat) { this.duzenlenenFeat.elemanlar = JSON.parse(JSON.stringify(this.sketch.elemanlar)); this.rebuild(); }
    this.entityAgaceGuncelleGuvenli(); this.d.ciz(); this.d.degisti?.(this.durum, this.sketch);
    return ok;
  }

  get olcuVar(): boolean { return !!this.sketch?.olculer?.length; }

  // ---- context menü (sağ tık / uzun bas) ----
  /** İmleç altındaki sketch elemanı (context menü için). */
  elemanAltindaki(e: PointerEvent | MouseEvent): any | null {
    const canvas = this.d.canvasAl(); if (!canvas) return null;
    return this.elemanRaycast(e as PointerEvent, canvas);
  }
  menuSil(id: number): void {
    if (!this.sketch) return;
    this.sketch.sil(id);
    if (this.duzenlenenFeat) { this.duzenlenenFeat.elemanlar = JSON.parse(JSON.stringify(this.sketch.elemanlar)); this.rebuild(); }
    this.entityAgaceGuncelleGuvenli(); this.d.ciz(); this.d.degisti?.(this.durum, this.sketch);
  }
  menuOlcule(id: number): void { this.sketch?.olcuEkle(id); this.entityAgaceGuncelleGuvenli(); this.d.ciz(); this.d.degisti?.(this.durum, this.sketch); }
  menuYapi(id: number): void {
    if (!this.sketch) return;
    this.sketch.yapiToggle(id);
    if (this.duzenlenenFeat) { this.duzenlenenFeat.elemanlar = JSON.parse(JSON.stringify(this.sketch.elemanlar)); this.rebuild(); }
    this.d.ciz(); this.d.degisti?.(this.durum, this.sketch);
  }
  olcuSil(id: number): void { this.sketch?.olcuSil(id); this.entityAgaceGuncelleGuvenli(); this.d.ciz(); this.d.degisti?.(this.durum, this.sketch); }

  /** MEVCUT ileri seviye 3dHizliMenu (Fiko3DHizliMenu) — icon toolbar + bilgi. Yeni menü YOK. */
  sketchSagMenu(e: PointerEvent | MouseEvent): boolean {
    if (!this.sketch) return false;
    const el = this.elemanAltindaki(e); if (!el) return false;
    const adlar: Record<string, string> = { cizgi: 'Çizgi', dikdortgen: 'Dikdörtgen', cember: 'Çember', yay: 'Yay', nokta: 'Nokta' };
    const olculu = this.sketch.olculer.some(o => o.elemanId === el.id);
    const obj: any = {
      name: adlar[el.tip] || 'Eleman',
      userData: { FikoArayuz: { simge: './simgeler/line.webp' } },
      hizliMenu3DBilgi: this.elemanBilgi(el),
      hizliMenu3DEk: FikoFeatureHizliEkSekmeli,
      hizliMenu3DEkParametreler: { element: el, yonetici: this, icBilesen: FikoSketchHizliEk },
      hizli3DMenu: [
        { baslik: olculu ? 'Ölçü var' : 'Ölçülendir', simge: 'arrows alternate horizontal', renkSinif: 'teal', tiklandi: () => this.menuOlcule(el.id) },
        { baslik: el.yapi ? 'Normale çevir' : 'Yapı geometrisi', simge: el.yapi ? 'minus' : 'ellipsis horizontal', renkSinif: 'grey', tiklandi: () => this.menuYapi(el.id) },
        { tipi: 'Ayraç' },
        { baslik: 'Sil', simge: 'trash alternate', renkSinif: 'red', tiklandi: () => this.menuSil(el.id) },
      ],
    };
    const hm = (this.d.sahne as any).anaGosterim?.Dialog3DHizliMenu;
    if (hm?.ac) { hm.ac(obj, { type: 'pointerdown', clientX: (e as any).clientX, clientY: (e as any).clientY }); return true; }
    // fallback: itemMenu sağ-menü
    obj.sagMenu = obj.hizli3DMenu;
    (this.d.sahne as any).secimler?.sagMenuAc(e, obj);
    return true;
  }

  // ---- gelişmiş hızlı menü paneli (FikoSketchHizliEk) API ----
  private degistiVeRebuild(): void {
    if (this.duzenlenenFeat && this.sketch) { this.duzenlenenFeat.elemanlar = JSON.parse(JSON.stringify(this.sketch.elemanlar)); this.rebuild(); }
    // bağlı ölçüleri güncel değere senkronla
    if (this.sketch) for (const o of this.sketch.olculer) { const el = this.sketch.elemanlar.find((e: any) => e.id === o.elemanId); if (el) o.deger = this.sketch.elemanDeger(el, o.tip); }
    this.entityAgaceGuncelleGuvenli(); this.d.ciz(); this.d.degisti?.(this.durum, this.sketch);
  }
  /** Elemanı doğrudan boyutla (panel input'tan). */
  panelBoyut(el: any, tip: string, deger: number): void {
    if (!this.sketch || !el || !(deger > 0)) return;
    const a = el.noktalar[0], b = el.noktalar[1];
    if (tip === 'kenar' && el.tip === 'cokgen') { el.kenarSayisi = Math.max(3, Math.round(deger)); }   // PARAMETRİK kenar sayısı
    else if (tip === 'uzunluk' && el.tip === 'cizgi') { const len = Math.hypot(b.x - a.x, b.y - a.y) || 1; el.noktalar[1] = { x: a.x + (b.x - a.x) / len * deger, y: a.y + (b.y - a.y) / len * deger }; }
    else if (tip === 'yaricap') el.r = deger;
    else if (tip === 'cap') el.r = deger / 2;
    else if (tip === 'genislik' && el.tip === 'dikdortgen') el.noktalar[1] = { x: a.x + Math.sign(b.x - a.x || 1) * deger, y: b.y };
    else if (tip === 'yukseklik' && el.tip === 'dikdortgen') el.noktalar[1] = { x: b.x, y: a.y + Math.sign(b.y - a.y || 1) * deger };
    else if (tip === 'slotuzun' && el.tip === 'slot') { const len = Math.hypot(b.x - a.x, b.y - a.y) || 1; const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2, ux = (b.x - a.x) / len, uy = (b.y - a.y) / len; el.noktalar[0] = { x: mx - ux * deger / 2, y: my - uy * deger / 2 }; el.noktalar[1] = { x: mx + ux * deger / 2, y: my + uy * deger / 2 }; }
    else if (tip === 'slotgen' && el.tip === 'slot') el.r = deger / 2;
    this.sketch.ciz(); this.degistiVeRebuild();
  }
  panelYatay(el: any): void { if (el?.tip === 'cizgi') { el.noktalar[1] = { x: el.noktalar[1].x, y: el.noktalar[0].y }; this.sketch?.ciz(); this.degistiVeRebuild(); } }
  panelDikey(el: any): void { if (el?.tip === 'cizgi') { el.noktalar[1] = { x: el.noktalar[0].x, y: el.noktalar[1].y }; this.sketch?.ciz(); this.degistiVeRebuild(); } }
  panelYapi(el: any): void { if (el) this.menuYapi(el.id); }
  panelSil(el: any): void { if (el) { this.menuSil(el.id); (this.d.sahne as any).anaGosterim?.Dialog3DHizliMenu?.kapa?.(); } }
  /** Parametrik şekil patlatılabilir mi (çokgen/slot/dikdörtgen → ayrı çizgiler). */
  patlatilabilir(el: any): boolean { return !!el && (el.tip === 'cokgen' || el.tip === 'slot' || el.tip === 'dikdortgen'); }
  /** CATIA "patlat / bağı kopar": parametrik şekli ayrı çizgilere böler (artık tek tek düzenlenir). */
  panelPatlat(el: any): void {
    if (!this.sketch || !this.patlatilabilir(el)) return;
    let pts: { x: number; y: number }[] = [];
    if (el.tip === 'cokgen') pts = FikoSketch.cokgenNoktalari(el);
    else if (el.tip === 'slot') pts = FikoSketch.slotNoktalari(el);
    else if (el.tip === 'dikdortgen') { const [a, b] = el.noktalar; pts = [{ x: a.x, y: a.y }, { x: b.x, y: a.y }, { x: b.x, y: b.y }, { x: a.x, y: b.y }]; }
    if (pts.length < 2) return;
    this.sketch.sil(el.id);
    for (let i = 0; i < pts.length; i++) this.sketch.ekle('cizgi', [{ x: pts[i].x, y: pts[i].y }, { x: pts[(i + 1) % pts.length].x, y: pts[(i + 1) % pts.length].y }]);
    this.sketch.ciz(); this.entityAgaceGuncelleGuvenli(); this.degistiVeRebuild();
    (this.d.sahne as any).anaGosterim?.Dialog3DHizliMenu?.kapa?.();
  }
  panelOlcule(el: any): void { if (el) this.menuOlcule(el.id); }

  private elemanBilgi(el: any): string {
    if (el.tip === 'cizgi') { const [a, b] = el.noktalar; return 'Uzunluk: ' + Math.hypot(b.x - a.x, b.y - a.y).toFixed(1); }
    if (el.tip === 'cember' || el.tip === 'yay') return 'Yarıçap: ' + (el.r || 0).toFixed(1);
    if (el.tip === 'dikdortgen') { const [a, b] = el.noktalar; return `${Math.abs(b.x - a.x).toFixed(0)} × ${Math.abs(b.y - a.y).toFixed(0)}`; }
    return el.tip;
  }

  /** EKRAN-UZAYI seçim: her elemanı ekrana izdüşür, imlece en yakın (<14px) elemanı döndür.
   * Ortografik kamerada raycast eşiği zoom'a uyumsuz olduğu için bu yöntem güvenilir. */
  private elemanRaycast(e: PointerEvent, canvas: HTMLCanvasElement): any | null {
    const kamera = this.d.kameraAl();
    if (!kamera || !this.sketch) return null;
    const rect = canvas.getBoundingClientRect();
    const fare = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    const w = new THREE.Vector3();
    const ekran = (p2d: { x: number; y: number }) => {
      this.sketch!.duzlem2Dunya(p2d, w); w.project(kamera as THREE.Camera);
      return { x: (w.x * 0.5 + 0.5) * rect.width, y: (-w.y * 0.5 + 0.5) * rect.height };
    };
    let en: any = null, enD = 14;
    for (const el of this.sketch.elemanlar) {
      const pts = this.elemanNoktalari(el).map(ekran);
      let d = Infinity;
      if (pts.length === 1) d = Math.hypot(pts[0].x - fare.x, pts[0].y - fare.y);
      else for (let i = 0; i < pts.length - 1; i++) d = Math.min(d, this.segMesafe(fare, pts[i], pts[i + 1]));
      if (d < enD) { enD = d; en = el; }
    }
    return en;
  }

  /** Elemanın 2D düzlem noktaları (kapalılarda son=ilk). */
  private elemanNoktalari(el: any): { x: number; y: number }[] {
    if (el.tip === 'cizgi') return [el.noktalar[0], el.noktalar[1]];
    if (el.tip === 'dikdortgen') { const [a, b] = el.noktalar; return [{ x: a.x, y: a.y }, { x: b.x, y: a.y }, { x: b.x, y: b.y }, { x: a.x, y: b.y }, { x: a.x, y: a.y }]; }
    if (el.tip === 'cember') { const m = el.noktalar[0], r = el.r || 0; const o = []; for (let i = 0; i <= 48; i++) { const t = i / 48 * Math.PI * 2; o.push({ x: m.x + Math.cos(t) * r, y: m.y + Math.sin(t) * r }); } return o; }
    if (el.tip === 'yay') return FikoSketch.yayNoktalari(el.noktalar[0], el.noktalar[1], el.noktalar[2]);
    if (el.tip === 'cokgen') { const p = FikoSketch.cokgenNoktalari(el); return p.length ? [...p, p[0]] : []; }   // tüm köşeler (kapalı) → komple seçim
    if (el.tip === 'slot') { const p = FikoSketch.slotNoktalari(el); return p.length ? [...p, p[0]] : []; }
    return [el.noktalar[0]];
  }

  /** Noktanın ekran-segmentine en kısa mesafesi (px). */
  private segMesafe(p: { x: number; y: number }, a: { x: number; y: number }, b: { x: number; y: number }): number {
    const dx = b.x - a.x, dy = b.y - a.y; const l2 = dx * dx + dy * dy;
    let t = l2 > 0 ? ((p.x - a.x) * dx + (p.y - a.y) * dy) / l2 : 0; t = Math.max(0, Math.min(1, t));
    return Math.hypot(p.x - (a.x + dx * t), p.y - (a.y + dy * t));
  }

  private cizgiEsigi(): number {
    const k = this.d.kontrolAl?.(); const kam = this.d.kameraAl();
    if (k && kam) return Math.max(0.4, kam.position.distanceTo(k.target) * 0.012);
    return 2;
  }

  private duzlemNokta(e: PointerEvent, canvas: HTMLCanvasElement): { x: number; y: number } | null {
    const kamera = this.d.kameraAl();
    if (!kamera || !this.aktifDuzlem || !this.sketch) return null;
    const rect = canvas.getBoundingClientRect();
    this.mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(this.mouse, kamera as THREE.Camera);
    const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(this.aktifDuzlem.normal, this.aktifDuzlem.origin);
    const hit = new THREE.Vector3();
    if (!this.raycaster.ray.intersectPlane(plane, hit)) return null;
    return this.sketch.dunya2Duzlem(hit);
  }

  /** Bir çizgiyi eksen alıp diğer tüm elemanları yansıtır. */
  private aynala(eksen: any): void {
    if (!this.sketch || eksen.tip !== 'cizgi') return;
    const A = eksen.noktalar[0], B = eksen.noktalar[1];
    const yansit = (p: { x: number; y: number }) => {
      const dx = B.x - A.x, dy = B.y - A.y; const len2 = dx * dx + dy * dy;
      if (len2 < 1e-9) return { x: p.x, y: p.y };
      const t = ((p.x - A.x) * dx + (p.y - A.y) * dy) / len2;
      const px = A.x + t * dx, py = A.y + t * dy;
      return { x: 2 * px - p.x, y: 2 * py - p.y };
    };
    const kopya = this.sketch.elemanlar.filter((x: any) => x.id !== eksen.id);
    for (const el of kopya) this.sketch.ekle(el.tip, el.noktalar.map(yansit), el.r);
  }

  /** Bir elemanı hedef noktaya göre paralel/eşmerkez kopyalar (offset). */
  private otele(el: any, hedef: { x: number; y: number }): void {
    if (!this.sketch) return;
    if (el.tip === 'cizgi') {
      const A = el.noktalar[0], B = el.noktalar[1];
      const dx = B.x - A.x, dy = B.y - A.y; const len = Math.hypot(dx, dy) || 1;
      const nx = -dy / len, ny = dx / len;                       // dik normal
      const d = (hedef.x - A.x) * nx + (hedef.y - A.y) * ny;     // işaretli mesafe
      this.sketch.ekle('cizgi', [{ x: A.x + nx * d, y: A.y + ny * d }, { x: B.x + nx * d, y: B.y + ny * d }]);
    } else if (el.tip === 'cember') {
      const m = el.noktalar[0]; const yeniR = Math.hypot(hedef.x - m.x, hedef.y - m.y);
      this.sketch.ekle('cember', [{ x: m.x, y: m.y }], yeniR);
    }
  }

  /** Elemanı yerinde taşır (translate): from→to vektörü kadar. */
  private tasi(el: any, from: { x: number; y: number } | null, to: { x: number; y: number }): void {
    if (!this.sketch || !from) return;
    const dv = { x: to.x - from.x, y: to.y - from.y };
    for (const p of el.noktalar) { p.x += dv.x; p.y += dv.y; }
    this.sketch.ciz();
  }

  /** İki çizginin ortak köşesi {C, i1, i2} (paylaşılan uç) — yoksa null. */
  private ortakKose(l1: any, l2: any): { C: any; i1: number; i2: number; tol: number } | null {
    const tol = Math.max(0.5, this.cizgiEsigi());
    for (let i = 0; i < 2; i++) for (let j = 0; j < 2; j++) {
      const a = l1.noktalar[i], b = l2.noktalar[j];
      if (Math.hypot(a.x - b.x, a.y - b.y) < tol) return { C: { x: a.x, y: a.y }, i1: i, i2: j, tol };
    }
    return null;
  }

  /** Köşe yuvarlatma (fillet): iki çizgi arasına teğet yay. */
  private fillet(l1: any, l2: any, rIstek?: number): void {
    const k = this.ortakKose(l1, l2); if (!k || !this.sketch) return;
    const C = k.C;
    const u = (l: any, i: number) => { const f = l.noktalar[1 - i]; const d = { x: f.x - C.x, y: f.y - C.y }; const len = Math.hypot(d.x, d.y) || 1; return { x: d.x / len, y: d.y / len, len }; };
    const u1 = u(l1, k.i1), u2 = u(l2, k.i2);
    const cosT = Math.max(-1, Math.min(1, u1.x * u2.x + u1.y * u2.y));
    const theta = Math.acos(cosT);
    if (theta < 0.05 || theta > Math.PI - 0.05) return;       // kollinear
    let R = rIstek && rIstek > 0 ? rIstek : Math.min(u1.len, u2.len) * 0.25;   // Corner: kullanıcı yarıçapı (CATIA)
    let t = R / Math.tan(theta / 2);
    if (t > u1.len * 0.9 || t > u2.len * 0.9) { t = Math.min(u1.len, u2.len) * 0.45; R = t * Math.tan(theta / 2); }
    const T1 = { x: C.x + u1.x * t, y: C.y + u1.y * t };
    const T2 = { x: C.x + u2.x * t, y: C.y + u2.y * t };
    let bx = u1.x + u2.x, by = u1.y + u2.y; const bl = Math.hypot(bx, by) || 1; bx /= bl; by /= bl;
    const distC = R / Math.sin(theta / 2);
    const merkez = { x: C.x + bx * distC, y: C.y + by * distC };
    const pmid = { x: merkez.x - bx * R, y: merkez.y - by * R };  // yay üstü (köşeye en yakın)
    l1.noktalar[k.i1] = T1; l2.noktalar[k.i2] = T2;
    this.sketch.ekle('yay', [T1, T2, pmid]);
    this.sketch.ciz();
  }

  /** Pah (chamfer): iki çizgi köşesini eğik kesimle kapat. */
  private pah(l1: any, l2: any): void {
    const k = this.ortakKose(l1, l2); if (!k || !this.sketch) return;
    const C = k.C;
    const u = (l: any, i: number) => { const f = l.noktalar[1 - i]; const d = { x: f.x - C.x, y: f.y - C.y }; const len = Math.hypot(d.x, d.y) || 1; return { x: d.x / len, y: d.y / len, len }; };
    const u1 = u(l1, k.i1), u2 = u(l2, k.i2);
    const d = Math.min(u1.len, u2.len) * 0.25;
    const T1 = { x: C.x + u1.x * d, y: C.y + u1.y * d };
    const T2 = { x: C.x + u2.x * d, y: C.y + u2.y * d };
    l1.noktalar[k.i1] = T1; l2.noktalar[k.i2] = T2;
    this.sketch.ekle('cizgi', [T1, T2]);
    this.sketch.ciz();
  }

  /** Buda (trim): çizgiyi en yakın kesişimlerden tıklanan parçayı silerek kırpar. */
  private trim(el: any, p: { x: number; y: number }): void {
    if (!this.sketch || el.tip !== 'cizgi') return;
    const A = el.noktalar[0], B = el.noktalar[1];
    const dx = B.x - A.x, dy = B.y - A.y; const len2 = dx * dx + dy * dy;
    if (len2 < 1e-9) return;
    const param = (q: { x: number; y: number }) => ((q.x - A.x) * dx + (q.y - A.y) * dy) / len2;
    const tp = Math.max(0, Math.min(1, param(p)));
    // diğer çizgilerle kesişim parametreleri
    const ts: number[] = [];
    for (const o of this.sketch.elemanlar) {
      if (o === el || o.tip !== 'cizgi') continue;
      const ip = this.cizgiKesisim(A, B, o.noktalar[0], o.noktalar[1]);
      if (ip != null && ip > 0.001 && ip < 0.999) ts.push(ip);
    }
    if (!ts.length) return;
    ts.sort((a, b) => a - b);
    const tlo = Math.max(0, ...ts.filter(t => t <= tp));
    const thiArr = ts.filter(t => t >= tp); const thi = thiArr.length ? Math.min(...thiArr) : 1;
    const nokta = (t: number) => ({ x: A.x + dx * t, y: A.y + dy * t });
    if (tlo <= 0.001 && thi >= 0.999) { this.sketch.sil(el.id); }
    else if (tlo <= 0.001) { el.noktalar[0] = nokta(thi); this.sketch.ciz(); }
    else if (thi >= 0.999) { el.noktalar[1] = nokta(tlo); this.sketch.ciz(); }
    else { const ucB = { x: B.x, y: B.y }; el.noktalar[1] = nokta(tlo); this.sketch.ekle('cizgi', [nokta(thi), ucB]); }
  }

  /** İki doğru parçası kesişiminin l1 üzerindeki parametresi (yoksa null). */
  private cizgiKesisim(a: any, b: any, c: any, d: any): number | null {
    const r = { x: b.x - a.x, y: b.y - a.y };
    const s = { x: d.x - c.x, y: d.y - c.y };
    const rxs = r.x * s.y - r.y * s.x;
    if (Math.abs(rxs) < 1e-9) return null;
    const qp = { x: c.x - a.x, y: c.y - a.y };
    const t = (qp.x * s.y - qp.y * s.x) / rxs;
    const u = (qp.x * r.y - qp.y * r.x) / rxs;
    if (u < -0.001 || u > 1.001) return null;   // diğer segment dışında
    return t;
  }

  /** Sketch düzlemine dik bakış (CATIA "Normal View"). */
  dikBak(): void { if (this.aktifDuzlem) this.kameraHizala(this.aktifDuzlem); }

  /** Sketch grid'ini aç/kapat. */
  gridToggle(goster?: boolean): void {
    this.gridGoster = goster ?? !this.gridGoster;
    if (this.gridGrubu) this.gridGrubu.visible = this.gridGoster;
    else if (this.gridGoster && this.aktifDuzlem) this.gridOlustur(this.aktifDuzlem);
    this.d.ciz();
  }

  /** Sketch'i bitirir → ağaçta kalır (boşsa atılır). Part Design hedefi olur. */
  sketchBitir(): void {
    // FEATURE DÜZENLEME: bu sketch bir feature'a aitse profilini güncelle + rebuild (parametrik)
    if (this.duzenlenenFeat && this.sketch) {
      this.duzenlenenFeat.elemanlar = JSON.parse(JSON.stringify(this.sketch.elemanlar));
      this.duzenlenenFeat = null;
      this.duzenlemeyiDurdur();
      this.aktifNode = null; this.sketch = null;
      this.rebuild();
      this.durum = 'kapali';
      this.d.agacGuncelle?.();
      this.d.degisti?.(this.durum, null);
      return;
    }
    if (this.aktifNode && this.sketch && this.sketch.elemanlar.length === 0) { this.nodeKaldir(this.aktifNode); this.hedefSketch = null; }
    else if (this.sketch && this.aktifDuzlem) { this.hedefSketch = { sketch: this.sketch, duzlem: this.aktifDuzlem, node: this.aktifNode }; }
    this.duzenlemeyiDurdur();
    this.aktifNode = null; this.sketch = null;
    this.durum = 'kapali';
    this.d.agacGuncelle?.();
    this.d.degisti?.(this.durum, null);
  }

  /** Aktif sketch'i tamamen siler (ağaçtan da kaldırır). Feature düzenlemede sadece çıkar. */
  iptalSketch(): void {
    this.yuzSecHandlerKaldir();
    if (this.duzenlenenFeat) { this.duzenlenenFeat = null; }   // düzenleme iptali → node'u silme
    else if (this.aktifNode) this.nodeKaldir(this.aktifNode);
    this.duzenlemeyiDurdur();
    this.aktifNode = null; this.sketch = null;
    this.durum = 'kapali';
    this.d.agacGuncelle?.();
  }

  /** Düzenlemeyi durdurur (node'u ağaçta bırakır). */
  private duzenlemeyiDurdur(): void {
    this.islemBitir();
    if (this.sketch) { this.sketch.olcuGoster = false; this.sketch.ciz(); }  // sketch pasifleşti → ölçü/kısıt görsellerini gizle
    if (this.araci) { this.araci.dispose(); this.araci = null; }
    this.gridKaldir();
    this.aktifDuzlem = null;
    this.animasyonDurdur();
    if (this.kesitAktif) this.kesitKaldir();   // sketch'ten çıkınca kesit görseli kalkar (flag korunur)
  }

  private nodeKaldir(node: FikoGrup): void {
    const sketch = node.userData?.['fikoSketch'] as FikoSketch | undefined;
    node.parent?.remove(node);
    sketch?.dispose();
  }

  // ---- grid (düzleme hizalı) ----
  /** Grup: ince (minor) + kalın (major) iki grid, sabit kare boyutu, geniş kapsama. */
  private gridGrubu: THREE.Group | null = null;
  private gridOlustur(d: ISketchDuzlem): void {
    this.gridKaldir();
    if (!this.gridGoster) return;
    const KARE = 10;       // sabit kare boyutu (birim)
    const BOYUT = 4000;    // geniş kapsama (ekranı kaplar)
    const grup = new THREE.Group();
    // ince ızgara (her 10 birim)
    const ince = new THREE.GridHelper(BOYUT, BOYUT / KARE, 0x2a3354, 0x2a3354);
    // kalın ızgara (her 100 birim) — major çizgiler
    const kalin = new THREE.GridHelper(BOYUT, BOYUT / (KARE * 10), 0x5a7bd8, 0x46568f);
    for (const g of [ince, kalin]) {
      const mat = g.material as THREE.Material | THREE.Material[];
      (Array.isArray(mat) ? mat : [mat]).forEach(m => { m.opacity = g === ince ? 0.22 : 0.5; m.transparent = true; (m as any).depthWrite = false; });
      grup.add(g);
    }
    const M = new THREE.Matrix4().makeBasis(d.eksenU, d.normal, d.eksenV).setPosition(d.origin);
    grup.applyMatrix4(M);
    grup.name = 'FikoSketchGrid';
    grup.renderOrder = -1;
    this.gridGrubu = grup;
    this.d.sahne.add(grup);
  }
  private gridKaldir(): void {
    if (this.gridGrubu) {
      this.d.sahne.remove(this.gridGrubu);
      this.gridGrubu.traverse((o: any) => o.geometry?.dispose?.());
      this.gridGrubu = null;
    }
  }

  // ---- Part Design feature'ları (Pad/Pocket/Shaft/Groove) ----

  /** Part Design feature'ı için kullanılabilir bir sketch var mı? */
  get hedefVar(): boolean { return !!(this.sketch || this.hedefSketch); }
  /** Bir PartBody (gövde) oluştu mu? */
  get govdeVar(): boolean { return !!this.govdeMesh; }

  pad(yukseklik = 20): THREE.Mesh | null { return this.sketchOzellik('pad', yukseklik); }
  pocket(derinlik = 20): THREE.Mesh | null { return this.sketchOzellik('pocket', derinlik); }
  shaft(): THREE.Mesh | null { return this.sketchOzellik('shaft', 0); }
  groove(): THREE.Mesh | null { return this.sketchOzellik('groove', 0); }

  /** Aktif/hedef sketch'i bir feature olarak geçmişe ekler ve rebuild eder. */
  private sketchOzellik(tip: 'pad' | 'pocket' | 'shaft' | 'groove', param: number): THREE.Mesh | null {
    const s = this.sketch || this.hedefSketch?.sketch || null;
    const d = this.aktifDuzlem || this.hedefSketch?.duzlem || null;
    const sketchNode = this.aktifNode || this.hedefSketch?.node || null;
    if (!s || !d || !s.elemanlar.length) return null;
    const ekleme = (tip === 'pad' || tip === 'shaft');
    if (!ekleme && !this.govdeMesh) return null;  // pocket/groove için gövde şart

    const feat: IOzellik = {
      tip, ekleme, param,
      elemanlar: JSON.parse(JSON.stringify(s.elemanlar)),  // derin kopya (snapshot)
      sketchOlculer: this.olculerKopyala(s),
      duzlem: this.duzlemKlon(d),
      node: this.ozellikNode(tip, sketchNode),
    };
    if (sketchNode) sketchNode.userData['featObj'] = feat;  // sketch↔feature bağı (düzenleme için)
    this.ozellikler.push(feat);
    this.featureNodeBagla(feat);
    this.rebuild();

    this.duzenlemeyiDurdur();
    this.aktifNode = null; this.sketch = null; this.hedefSketch = null;
    this.durum = 'kapali';
    this.d.agacGuncelle?.();
    this.d.degisti?.(this.durum, null);
    return this.govdeMesh;
  }

  /** Gövdeyi bir temel düzleme göre aynalar (mirror) — gövde şart. */
  aynalaGovde(tip: 'xy' | 'xz' | 'yz'): THREE.Mesh | null {
    if (!this.govdeMesh) return null;
    const node = this.ozellikNode('mirror', null);
    const feat: IOzellik = { tip: 'mirror', ekleme: true, aynaTip: tip, node };
    this.ozellikler.push(feat); this.featureNodeBagla(feat);
    this.rebuild();
    this.d.agacGuncelle?.(); this.d.degisti?.(this.durum, null);
    return this.govdeMesh;
  }

  /** Son feature'ı doğrusal/dairesel çoğaltır (pattern) — son feature sketch tabanlı olmalı. */
  pattern(dairesel: boolean, adet = 4, aralikVeyaAci = dairesel ? 90 : 40): THREE.Mesh | null {
    const sonIdx = this.sonSketchFeatureIndex();
    if (sonIdx < 0 || !this.govdeMesh) return null;
    const node = this.ozellikNode(dairesel ? 'patternDairesel' : 'patternDik', null);
    const feat: IOzellik = {
      tip: dairesel ? 'patternDairesel' : 'patternDik', ekleme: this.ozellikler[sonIdx].ekleme,
      hedefIndex: sonIdx, adet, aci: dairesel ? aralikVeyaAci : undefined, aralik: dairesel ? undefined : aralikVeyaAci, node,
    };
    this.ozellikler.push(feat); this.featureNodeBagla(feat);
    this.rebuild();
    this.d.agacGuncelle?.(); this.d.degisti?.(this.durum, null);
    return this.govdeMesh;
  }

  /** Son feature'ı 2D ızgara (MxN) olarak çoğaltır. */
  patternIzgara(adetU = 3, adetV = 3, aralikU = 40, aralikV = 40): THREE.Mesh | null {
    const sonIdx = this.sonSketchFeatureIndex();
    if (sonIdx < 0 || !this.govdeMesh) return null;
    const node = this.ozellikNode('patternIzgara', null);
    const feat: IOzellik = {
      tip: 'patternIzgara', ekleme: this.ozellikler[sonIdx].ekleme, hedefIndex: sonIdx,
      adet: adetU, adet2: adetV, aralik: aralikU, aralik2: aralikV, node,
    };
    this.ozellikler.push(feat); this.featureNodeBagla(feat);
    this.rebuild();
    this.d.agacGuncelle?.(); this.d.degisti?.(this.durum, null);
    return this.govdeMesh;
  }

  // ---- KOMUT PENCERESİ + CANLI ÖNİZLEME (taslak feature → rebuild = önizleme; CATIA OK/İptal) ----
  private taslakFeat: IOzellik | null = null;
  private taslakSketchNode: FikoGrup | null = null;
  // ÇOKLU-FEATURE MAKRO (mimarlık/çelik): tek komut → birden çok feature (önizleme = hepsi rebuild)
  private taslakFeatler: IOzellik[] | null = null;
  private taslakMakroId: string | null = null;
  private taslakMakroParam: Record<string, any> | null = null;
  private taslakMakroNode: FikoGrup | null = null;
  get komutAktif(): boolean { return !!this.taslakFeat || !!this.taslakFeatler; }

  /** Komut penceresi açılınca taslak feature kurar → rebuild ile CANLI önizleme.
   * Makro komut ise (MAKRO_URETECLER) çoklu-feature yolu, aksi tek-feature yolu çalışır. */
  komutBaslat(komutId: string, p: Record<string, any>): boolean {
    this.komutIptal();
    if (komutId.startsWith('cam3d_')) {   // 3D CAM op (parametre dialogu + canlı toolpath önizleme)
      if (!this.govdeMesh) return false;
      const op = komutId.slice(6) as any;
      this.taslakCam3d = { op, p: { ...p } };
      this.cam3dCalistir(op, this.cam3dParamYap(op, p));
      return true;
    }
    if (komutId.startsWith('analiz_')) {   // GSD Yüzey Analizi (overlay — feature değil)
      const t = komutId.slice(7);
      if (t === 'kapat') { this.analizKapat(); return true; }
      if (t === 'draft') return this.draftAnaliz((p.eksen as any) || 'z', p.minAci ?? 3);
      if (t === 'egrilik') return this.egrilikAnaliz();
      if (t === 'connect') return this.connectChecker();
      if (t === 'egriBaglanti') return this.egriBaglantiChecker();
      if (t === 'porcupine') return this.porcupineAnaliz(p.olcek ?? 30);
      if (t === 'reflect') return this.reflectLineAnaliz((p.eksen as any) || 'z');
      if (t === 'kutle') return this.kutleAnaliz();
      if (t === 'mesafe') return this.mesafeAnaliz();
      if (t === 'kontrol') return this.kontrolAnaliz();
      if (t === 'bbox') return this.bboxAnaliz();
      return false;
    }
    if (komutId === 'sketch') { this.taslakSketchSec = (p.duzlemTip || 'xy'); return true; }   // tek sketch komutu (dialog'dan düzlem)
    if (komutId === 'refDuzlem') { this._sonRefOfset = +p.ofset || 0; return this.refDuzlemBaslat(p); }   // referans düzlem (datum)
    if (komutId === 'malzeme') { if (!this.govdeMesh) return false; this.taslakMalzeme = true; if (p.malzeme) this.malzemeAta(p.malzeme); return true; }   // malzeme ata
    if (TUM_MAKRO_URETECLER[komutId]) return this.makroBaslat(komutId, p);
    const feat = this.taslakFeatKur(komutId, p);
    if (!feat) {
      const sketchLi = ['pad', 'pocket', 'shaft', 'groove', 'rib', 'loft', 'sweep', 'slot', 'combine'].includes(komutId) || komutId.startsWith('yuzey');
      this.d.bildir?.(sketchLi ? 'Önce kapalı bir profil çiz (aktif eskiz gerekli) — sonra komutu çalıştır' : 'Bu komut için önce bir katı/gövde oluştur');
      return false;
    }
    this.taslakFeat = feat;
    this.ozellikler.push(feat);
    this.featureNodeBagla(feat);
    // Yüz/kenar seçimli komutlar: oto-seçim moduna GİRME (hiçbir komut kendi kendine yüz/kenar seçmez).
    // Seçim listesi komut penceresinde (FikoKomutIcerik) gösterilir; kullanıcı "🎯 Ekle" ile bizzat seçer.
    if (feat.tip === 'userPattern') this.userPatternSecBaslat(feat);   // CATIA User Pattern: 3D'de noktalara tıkla
    const yuzeyCoklu = feat.tip === 'yuzey' && (feat.yuzeyTip === 'loft' || feat.yuzeyTip === 'sweep');
    if (feat.tip === 'pad' || feat.tip === 'pocket' || feat.tip === 'shaft' || feat.tip === 'groove' || feat.tip === 'rib' || feat.tip === 'stiffener' || (feat.tip === 'yuzey' && !yuzeyCoklu)) this.profilSecBaslat();   // CATIA: profil/sketch seçim modu
    if (feat.tip === 'loft' || feat.tip === 'sweep' || feat.tip === 'slot' || feat.tip === 'combine' || yuzeyCoklu) this.profilSecBaslat(true);   // çoklu profil
    // B-REP: TÜM OCCT-uyumlu gövdeler OpenCascade ile (doğru topoloji — yüz/kenar/çember grupları). Uyumsuz (makro/yazi3d/rib) → mesh.
    if (!this.occtModu && this.occtUyumluMu()) {
      if (fikoOCCT.hazirMi()) this.occtModu = true;   // OCCT zaten yüklü (modBaslat ön-yükledi) → SENKRON geç, mesh fazı yok
      else this.occtYukle().then(ok => { if (ok && this.occtUyumluMu()) { this.occtModu = true; if (this.kenarSecModu) this.kenarSecBaslat(); this.rebuild(); } });
    } else if (!this.occtModu && (feat.tip === 'fillet3d' || feat.tip === 'chamfer3d')) {
      this.d.bildir?.('Not: gövde makro/yazı içerdiğinden mesh fillet (köşe yaklaşık)');
    }
    this.rebuild();
    return true;
  }

  /** Makro taslağını kurar: tek bir ürün-ağacı node'u altında çoklu feature → canlı önizleme. */
  private makroBaslat(komutId: string, p: Record<string, any>): boolean {
    const feats = TUM_MAKRO_URETECLER[komutId](p as IMakroParam);
    if (!feats?.length) return false;
    const node = this.ozellikNode('makro', null);
    this.taslakMakroId = komutId;
    this.taslakMakroParam = { ...p };
    this.taslakMakroNode = node;
    this.taslakFeatler = feats;
    for (const f of feats) { f.node = node; this.ozellikler.push(f); }   // hepsi tek node altında (ağaç sade)
    this.makroNodeBagla();
    this.rebuild();
    this.d.agacGuncelle?.();
    return true;
  }

  /** Makro parametresi değişince: feature'ları sil → yeni param ile yeniden üret (tek node korunur). */
  private makroGuncelle(anahtar: string, deger: any): void {
    if (!this.taslakFeatler || !this.taslakMakroId || !this.taslakMakroParam) return;
    const sayisal = !['profilTip', 'yuvarlak', 'sirtVar'].includes(anahtar);
    this.taslakMakroParam[anahtar] = sayisal ? +deger : deger;
    // eski taslak feature'larını listeden çıkar (node korunur — yeniden doldurulur)
    this.ozellikler = this.ozellikler.filter(f => !this.taslakFeatler!.includes(f));
    const feats = TUM_MAKRO_URETECLER[this.taslakMakroId](this.taslakMakroParam as IMakroParam);
    this.taslakFeatler = feats;
    for (const f of feats) { f.node = this.taslakMakroNode; this.ozellikler.push(f); }
    this.makroNodeBagla();
    this.rebuild();
    this.d.agacGuncelle?.();
  }

  /** Makro node'unu ad + sağ-menü ile bağlar (parametre düzenleme tek noktadan). */
  private makroNodeBagla(): void {
    const node: any = this.taslakMakroNode; if (!node) return;
    const ad: Record<string, string> = { duvar: 'Duvar', doseme: 'Döşeme', kolon: 'Kolon', kiris: 'Kiriş', celikProfil: 'Çelik Profil', birlesimPlakasi: 'Birleşim Plakası',
      panel: 'Panel', karkas: 'Karkas', rafliDolap: 'Raflı Dolap', kinisliPano: 'Kinişli Pano', kavelaliPano: 'Kavelalı Pano', kapak: 'Kapak', cekmece: 'Çekmece' };
    node._taban = ad[this.taslakMakroId || ''] || 'Makro';
    node.name = node._taban;
  }

  /** Parametre değişince taslağı güncelle → canlı önizleme. */
  komutGuncelle(anahtar: string, deger: any): void {
    if (this.taslakCam3d) { this.taslakCam3d.p[anahtar] = deger; this.cam3dCalistir(this.taslakCam3d.op, this.cam3dParamYap(this.taslakCam3d.op, this.taslakCam3d.p)); return; }   // canlı toolpath
    if (this.taslakSketchSec !== null) { if (anahtar === 'duzlemTip') this.taslakSketchSec = deger; return; }
    if (this.taslakMalzeme) { if (anahtar === 'malzeme') this.malzemeAta(deger); return; }
    if (this.taslakRefDuzlem) { this.refDuzlemGuncelle(anahtar, deger); return; }
    if (this.taslakFeatler) { this.makroGuncelle(anahtar, deger); return; }
    if (!this.taslakFeat) return;
    if (anahtar === 'aynaTip') { this.taslakFeat.aynaTip = deger; this.rebuild(); this.featureNodeAdGuncelle(this.taslakFeat); this.d.agacGuncelle?.(); return; }
    if (anahtar === 'metin') { this.taslakFeat.metin = String(deger); this.rebuild(); this.featureNodeAdGuncelle(this.taslakFeat); this.d.agacGuncelle?.(); return; }
    if (anahtar === 'fontAd') { this.taslakFeat.fontAd = deger; if (fontHazirMi(deger)) this.rebuild(); else this.yaziFontHazirla(deger); return; }
    if (anahtar === 'ekleme') { this.taslakFeat.ekleme = !!deger; this.rebuild(); this.featureNodeAdGuncelle(this.taslakFeat); this.d.agacGuncelle?.(); return; }
    if (anahtar === 'tersYon') { this.taslakFeat.tersYon = !!deger; this.rebuild(); this.featureNodeAdGuncelle(this.taslakFeat); this.d.agacGuncelle?.(); return; }
    if (anahtar === 'yon' || anahtar === 'taban' || anahtar === 'eksenTip' || anahtar === 'trMod' || anahtar === 'trYon' || anahtar === 'chamferMod') { (this.taslakFeat as any)[anahtar] = deger; this.rebuild(); this.featureNodeAdGuncelle(this.taslakFeat); this.d.agacGuncelle?.(); return; }
    this.featureParamGuncelle(this.taslakFeat, anahtar, +deger);
  }
  /** Tamam → taslağı kalıcı yap (sketch tabanlıysa sketch'i tüket; makroysa node ağaçta kalır). */
  komutOnayla(): void {
    Promise.resolve().then(() => this.gecmisKaydet());   // komut tamamlanınca (taslak temizlenince) geçmişe kaydet
    if (this.taslakCam3d) {   // toolpath sahnede kalır + CATIA NC Program node'u olarak ağaca eklenir
      const adlar: Record<string, string> = { parallel: 'Parallel Finiş', roughing: 'Z-Level Kaba', waterline: 'Waterline', pencil: 'Pencil' };
      this.cam3dAgacaEkle((adlar[this.taslakCam3d.op] || 'CAM 3D') + '.' + (this.cam3dProgSayac++));
      this.taslakCam3d = null; return;
    }
    if (this.taslakSketchSec !== null) {   // tek sketch komutu → seçilen düzlem/yüzde sketch başlat
      const tip = this.taslakSketchSec; this.taslakSketchSec = null;
      if (tip === 'yuz') this.yuzSecimiBaslat(); else this.sketchBaslat(FikoSketch.duzlemHazir(tip as any));
      return;
    }
    if (this.taslakMalzeme) { this.taslakMalzeme = false; return; }   // malzeme zaten uygulandı
    if (this.taslakRefDuzlem) { this.refDuzlemOnayla(); return; }   // referans düzlem kalıcı
    if (this.taslakFeatler) {   // makro: feature'lar zaten ozellikler[]'de + node ağaçta → sadece taslak referanslarını bırak
      this.taslakFeatler = null; this.taslakMakroId = null; this.taslakMakroParam = null; this.taslakMakroNode = null;
      this.d.agacGuncelle?.(); this.d.degisti?.(this.durum, this.sketch);
      return;
    }
    if (!this.taslakFeat) return;
    // CATIA: profil seçilmeden onaylanırsa → iptal et (boş feature kalmasın)
    if (this.profilSecModu) {
      const yeterli = this.profilCoklu ? ((this.taslakFeat.secProfiller?.length || 0) >= 2)
        : (this.taslakFeat.elemanlar === undefined || this.taslakFeat.elemanlar.length > 0);
      if (!yeterli) { this.profilSecBitir(); this.komutIptal(); return; }
    }
    // CATIA: pah/yuvarlatma kenar seçilmeden onaylanırsa → iptal (sessiz no-op yerine net uyarı)
    if (this.kenarSecModu && (this.taslakFeat.tip === 'chamfer3d' || this.taslakFeat.tip === 'fillet3d') && this.seciliKenarIdx.size === 0) {
      this.d.bildir?.(`⚠️ Hiç kenar seçilmedi — önce ${this.taslakFeat.tip === 'chamfer3d' ? 'pah kırılacak' : 'yuvarlatılacak'} bir KENARA (veya yüze) tıkla`);
      this.kenarSecBitir(); this.komutIptal(); return;
    }
    if (this.kenarSecModu) this.kenarSecBitir();   // CATIA kenar seçimini bitir
    if (this.yuzSecModu) this.yuzSecBitir();       // CATIA yüz seçimini bitir
    if (this.profilSecModu) this.profilSecBitir(); // CATIA profil seçimini bitir
    if (this.aktifSecim) this.secimBitir();        // merkezi seçim (User Pattern nokta tıklama) bitir
    const feat = this.taslakFeat; this.taslakFeat = null;
    if (feat.bodyId === undefined) feat.bodyId = this.aktifBodyId;   // çoklu-gövde damgası
    if (this.taslakSketchNode && feat.node) {   // sketch node'unu feature altına nest et + sketch'i tüket
      this.taslakSketchNode.parent?.remove(this.taslakSketchNode as any);
      feat.node.ekle!(this.taslakSketchNode);
      this.taslakSketchNode.userData['featObj'] = feat;
      this.taslakSketchNode = null;
      this.duzenlemeyiDurdur();
      this.aktifNode = null; this.sketch = null; this.hedefSketch = null;
      this.durum = 'kapali';
    }
    this.d.agacGuncelle?.(); this.d.degisti?.(this.durum, this.sketch);
    if (['pad', 'pocket', 'shaft', 'groove'].includes(feat.tip)) setTimeout(() => this.featureMenu(feat), 60);   // CATIA: onaylanınca Definition penceresi açılır
  }
  /** İptal → taslağı geri al (sketch düzenlemede KALIR). */
  komutIptal(): void {
    if (this.taslakCam3d) { this.cam3dYolTemizle(); this.taslakCam3d = null; return; }
    if (this.taslakSketchSec !== null) { this.taslakSketchSec = null; return; }
    if (this.taslakMalzeme) { this.taslakMalzeme = false; return; }
    if (this.taslakRefDuzlem) { this.refDuzlemIptal(); return; }   // referans düzlem önizleme iptal
    if (this.taslakFeatler) {   // makro iptal: tüm taslak feature'ları + node'u kaldır
      const feats = this.taslakFeatler; const node = this.taslakMakroNode;
      this.taslakFeatler = null; this.taslakMakroId = null; this.taslakMakroParam = null; this.taslakMakroNode = null;
      this.ozellikler = this.ozellikler.filter(f => !feats.includes(f));
      node?.parent?.remove(node as any);
      this.rebuild(); this.d.agacGuncelle?.();
      return;
    }
    if (!this.taslakFeat) return;
    if (this.kenarSecModu) this.kenarSecBitir();   // CATIA kenar seçimini bitir
    if (this.yuzSecModu) this.yuzSecBitir();       // CATIA yüz seçimini bitir
    if (this.profilSecModu) this.profilSecBitir(); // CATIA profil seçimini bitir
    if (this.aktifSecim) this.secimBitir();        // merkezi seçim (User Pattern nokta tıklama) bitir
    const feat = this.taslakFeat; this.taslakFeat = null; this.taslakSketchNode = null;
    this.featureSil(feat);
  }
  private taslakFeatKur(komutId: string, p: Record<string, any>): IOzellik | null {
    if (komutId === 'pad' || komutId === 'pocket' || komutId === 'shaft' || komutId === 'groove') {
      const s = this.sketch || this.hedefSketch?.sketch || null;
      const d = this.aktifDuzlem || this.hedefSketch?.duzlem || null;
      const sketchNode = this.aktifNode || this.hedefSketch?.node || null;
      const ekleme = (komutId === 'pad' || komutId === 'shaft');
      if (!ekleme && !this.govdeMesh) return null;   // pocket/groove için gövde şart
      // CATIA: aktif sketch yoksa PROFİLSİZ başla → kullanıcı 3D'den profil seçer (profilSecBaslat)
      this.taslakSketchNode = sketchNode;
      return {
        tip: komutId as any, ekleme, param: p.param ?? 20,
        elemanlar: (s && s.elemanlar.length) ? JSON.parse(JSON.stringify(s.elemanlar)) : [],
        sketchOlculer: this.olculerKopyala(s),
        duzlem: d ? this.duzlemKlon(d) : undefined,
        node: this.ozellikNode(komutId, null),
      };
    }
    if (komutId === 'loft' || komutId === 'sweep' || komutId === 'slot' || komutId === 'combine' || komutId === 'removedLoft') {   // OCCT-only: çoklu profil seçimi (loft kesitler / sweep+slot profil+yol / combine 2 profil / removedLoft=loft çıkar)
      if (komutId === 'removedLoft' && !this.govdeMesh) return null;   // çıkarma için gövde şart
      const tip = komutId === 'removedLoft' ? 'loft' : komutId;
      return { tip: tip as any, ekleme: komutId !== 'slot' && komutId !== 'removedLoft', secProfiller: [], node: this.ozellikNode(tip, null) };
    }
    if (komutId === 'yuzeyExtrude' || komutId === 'yuzeyRevolve' || komutId === 'yuzeyFill') {   // GSD Yüzey (sketch profili → açık shell)
      const s = this.sketch || this.hedefSketch?.sketch || null;
      const d = this.aktifDuzlem || this.hedefSketch?.duzlem || null;
      this.taslakSketchNode = this.aktifNode || this.hedefSketch?.node || null;
      return {
        tip: 'yuzey', ekleme: true, yuzeyTip: komutId.replace('yuzey', '').toLowerCase() as any,
        param: p.param ?? 40, aci: p.aci ?? 360, shaftEksen: p.shaftEksen ?? 'v',
        elemanlar: (s && s.elemanlar.length) ? JSON.parse(JSON.stringify(s.elemanlar)) : [],
        duzlem: d ? this.duzlemKlon(d) : undefined,
        node: this.ozellikNode('yuzey', null),
      };
    }
    if (komutId === 'yuzeyLoft' || komutId === 'yuzeySweep') {   // GSD Multi-section / Sweep yüzey (çoklu profil → açık shell)
      return { tip: 'yuzey', ekleme: true, yuzeyTip: komutId === 'yuzeySweep' ? 'sweep' : 'loft', secProfiller: [], node: this.ozellikNode('yuzey', null) };
    }
    if (komutId === 'yuzeySphere' || komutId === 'yuzeyCylinder') {   // GSD Yüzey primitif (sketch'siz) — küre/silindir yan yüzey
      return {
        tip: 'yuzey', ekleme: true, yuzeyTip: komutId === 'yuzeySphere' ? 'sphere' : 'cylinder',
        pCap: p.pCap ?? 50, pYuk: p.pYuk ?? 60, aynaTip: p.aynaTip ?? 'xy',
        ref1: -1, p1x: 0, p1y: 0, p1z: 0, node: this.ozellikNode('yuzey', null),
      };
    }
    if (komutId === 'rib') {   // sketch tabanlı: profili kalınlaştırıp normal boyunca uzat → ekle
      const s = this.sketch || this.hedefSketch?.sketch || null;
      const d = this.aktifDuzlem || this.hedefSketch?.duzlem || null;
      const sketchNode = this.aktifNode || this.hedefSketch?.node || null;
      if (!s || !d || !s.elemanlar.length) return null;
      this.taslakSketchNode = sketchNode;
      return {
        tip: 'rib', ekleme: true, param: p.param ?? 30, kalinlik: p.kalinlik ?? 6, yon: p.yon ?? 'arti',
        elemanlar: JSON.parse(JSON.stringify(s.elemanlar)),
        sketchOlculer: this.olculerKopyala(s),
        duzlem: this.duzlemKlon(d),
        node: this.ozellikNode('rib', null),
      };
    }
    if (komutId === 'stiffener') {   // CATIA Stiffener (Feder): açık profili kalınlaştırıp Z=0 tabana kadar indir → gövdeye ekle
      const s = this.sketch || this.hedefSketch?.sketch || null;
      const d = this.aktifDuzlem || this.hedefSketch?.duzlem || null;
      const sketchNode = this.aktifNode || this.hedefSketch?.node || null;
      if (!this.govdeMesh) return null;
      this.taslakSketchNode = sketchNode;
      return {
        tip: 'stiffener', ekleme: true, kalinlik: p.kalinlik ?? 6,
        elemanlar: (s && s.elemanlar.length) ? JSON.parse(JSON.stringify(s.elemanlar)) : [],
        duzlem: d ? this.duzlemKlon(d) : undefined,
        node: this.ozellikNode('stiffener', null),
      };
    }
    if (komutId === 'shell') {   // gövde değiştirici: gövde − içe ölçekli gövde (kabuk)
      if (!this.govdeMesh) return null;
      return { tip: 'shell', ekleme: false, kalinlik: p.kalinlik ?? 4, node: this.ozellikNode('shell', null) };
    }
    if (komutId === 'thickness') {   // CATIA Thickness: seçili yüz(ler)e kalınlık (yüz seçilmezse tüm gövde)
      if (!this.govdeMesh) return null;
      return { tip: 'thickness', ekleme: true, kalinlik: p.kalinlik ?? 2, tersYon: false, secYuzler: [], node: this.ozellikNode('thickness', null) };
    }
    if (komutId === 'split') {   // gövde değiştirici: düzlemle böl, bir tarafı tut
      if (!this.govdeMesh) return null;
      return { tip: 'split', ekleme: true, aynaTip: p.aynaTip ?? 'xy', splitOfset: p.splitOfset ?? 0, tersYon: false, node: this.ozellikNode('split', null) };
    }
    if (komutId === 'closeSurface') {   // CLOSE SURFACE: tüm yüzeyleri dik → katı
      if (!this.ozellikler.some(o => o.tip === 'yuzey' && !o.bastirilmis)) { this.d.bildir?.('Önce yüzey(ler) oluştur (Surface → Yüzey Oluştur)'); return null; }
      return { tip: 'closeSurface', ekleme: true, node: this.ozellikNode('closeSurface', null) };
    }
    if (komutId === 'removeLump') {   // REMOVE LUMP: kopuk solid parçalardan en büyüğünü tut
      if (!this.govdeMesh) return null;
      return { tip: 'removeLump', ekleme: true, node: this.ozellikNode('removeLump', null) };
    }
    if (komutId === 'healing') {   // HEALING: ShapeFix ile onar
      if (!this.govdeMesh) return null;
      return { tip: 'healing', ekleme: true, param: p.param ?? 0.01, node: this.ozellikNode('healing', null) };
    }
    if (komutId === 'simplify') {   // SURFACE SIMPLIFICATION
      if (!this.govdeMesh) return null;
      return { tip: 'simplify', ekleme: true, node: this.ozellikNode('simplify', null) };
    }
    if (komutId === 'offsetShape') {   // OCCT 3D Offset Shape (büyüt/küçült)
      if (!this.govdeMesh) return null;
      return { tip: 'offsetShape', ekleme: true, param: p.param ?? 5, node: this.ozellikNode('offsetShape', null) };
    }
    if (komutId === 'untrim' || komutId === 'multiExtract' || komutId === 'disassemble') {   // GSD yüzey ayırma op
      let ref = -1; this.ozellikler.forEach((s, i) => { if (s.tip === 'yuzey' && !s.bastirilmis) ref = i; });
      if (ref < 0 && this.govdeMesh) ref = -2;   // gövde kaynağı
      if (ref === -1) { this.d.bildir?.('Önce bir yüzey veya gövde oluştur'); return null; }
      return { tip: komutId as any, ekleme: true, ref1: ref, yuzIndex: 0, node: this.ozellikNode(komutId, null) };
    }
    if (komutId === 'removeFace') {   // REMOVE FACE: seçili yüzleri kaldır (defeaturing)
      if (!this.govdeMesh) return null;
      return { tip: 'removeFace', ekleme: true, secYuzler: [], node: this.ozellikNode('removeFace', null) };
    }
    if (komutId === 'faceFillet') {   // FACE-FACE FILLET: komşu 2+ yüz paylaşılan kenarlarına fillet
      if (!this.govdeMesh) return null;
      return { tip: 'faceFillet', ekleme: true, kenarMesafe: p.kenarMesafe ?? 4, secYuzler: [], node: this.ozellikNode('faceFillet', null) };
    }
    if (komutId === 'triFillet') {   // TRITANGENT: orta/şerit yüzü oto-yarıçapla yuvarla (0 = oto)
      if (!this.govdeMesh) return null;
      return { tip: 'triFillet', ekleme: true, kenarMesafe: p.kenarMesafe ?? 0, secYuzler: [], node: this.ozellikNode('triFillet', null) };
    }
    if (komutId === 'replaceFace') {   // REPLACE FACE: seçili yüzü hedef düzleme taşı
      if (!this.govdeMesh) return null;
      return { tip: 'replaceFace', ekleme: true, aynaTip: p.aynaTip ?? 'xy', splitOfset: p.splitOfset ?? 0, secYuzler: [], node: this.ozellikNode('replaceFace', null) };
    }
    if (komutId === 'draft') {   // gövde değiştirici: yüzeyleri çekme ekseni boyunca eğ (vertex taper)
      if (!this.govdeMesh) return null;
      return { tip: 'draft', ekleme: true, aci: p.aci ?? 5, taban: p.taban ?? 'alt', eksenTip: p.eksenTip ?? 'z', node: this.ozellikNode('draft', null) };
    }
    if (komutId === 'patternDik' || komutId === 'patternDairesel' || komutId === 'patternIzgara') {
      const sonIdx = this.sonSketchFeatureIndex();
      if (sonIdx < 0 || !this.govdeMesh) return null;
      return {
        tip: komutId as any, ekleme: this.ozellikler[sonIdx].ekleme, hedefIndex: sonIdx,
        adet: p.adet, adet2: p.adet2, aralik: p.aralik, aralik2: p.aralik2, aci: p.aci,
        node: this.ozellikNode(komutId, null),
      };
    }
    if (komutId === 'mirror') {
      if (!this.govdeMesh) return null;
      return { tip: 'mirror', ekleme: true, aynaTip: (p.aynaTip || 'yz'), node: this.ozellikNode('mirror', null) };
    }
    if (komutId === 'userPattern') {   // CATIA User Pattern: 3D'de tıklanan noktalarda gövde çoğalt (interaktif)
      if (!this.govdeMesh) return null;
      return { tip: 'userPattern', ekleme: true, secNoktalar: [], node: this.ozellikNode('userPattern', null) };
    }
    if (komutId === 'translate' || komutId === 'rotate' || komutId === 'symmetry' || komutId === 'scaling' || komutId === 'affinity' || komutId === 'axisToAxis') {   // CATIA Transformations
      if (!this.govdeMesh) return null;
      return {
        tip: komutId as any, ekleme: true,
        trX: p.trX ?? 50, trY: p.trY ?? 0, trZ: p.trZ ?? 0,
        trMod: p.trMod ?? 'yon', trYon: p.trYon ?? 'x', trMesafe: p.trMesafe ?? 100, eksenRef: -1, symRef: -1, ref1: -1,
        aci: p.aci ?? 90, eksenTip: p.eksenTip ?? 'z', aynaTip: p.aynaTip ?? 'yz', scaleFaktor: p.scaleFaktor ?? 1.5,
        affX: p.affX ?? 1.5, affY: p.affY ?? 1, affZ: p.affZ ?? 1,
        node: this.ozellikNode(komutId, null),
      };
    }
    if (komutId === 'yazi3d') {
      const d = this.aktifDuzlem || this.hedefSketch?.duzlem || FikoSketch.duzlemHazir('xy');
      return {
        tip: 'yazi3d', ekleme: true,
        metin: p.metin || 'FikoYT', fontAd: p.fontAd || 'helvetiker', boyut: p.boyut || 10, param: p.param || 4, harfBosluk: p.harfBosluk ?? 0,
        duzlem: this.duzlemKlon(d), node: this.ozellikNode('yazi3d', null),
      };
    }
    if (komutId === 'fillet3d' || komutId === 'chamfer3d') {
      if (!this.govdeMesh) return null;
      return { tip: komutId, ekleme: true, kenarMesafe: p.kenarMesafe ?? 4, kenarAci: p.kenarAci ?? 30, secKenarlar: [], node: this.ozellikNode(komutId, null) };
    }
    if (komutId === 'kutu' || komutId === 'silindir' || komutId === 'kure' || komutId === 'koni' || komutId === 'torus' || komutId === 'wedge') {
      const d = this.aktifDuzlem || this.hedefSketch?.duzlem || FikoSketch.duzlemHazir('xy');
      return {
        tip: 'primitif', ekleme: p.ekleme !== false, primitifTip: komutId,
        pEn: p.pEn || 40, pBoy: p.pBoy || 40, pYuk: p.pYuk || 30, pCap: p.pCap || 40, pTbCap: p.pTbCap || 12, wedgeLtx: p.wedgeLtx ?? 15,
        duzlem: this.duzlemKlon(d), node: this.ozellikNode('primitif', null),
      };
    }
    if (komutId === 'boru' || komutId === 'dirsek' || komutId === 'yuzuk' || komutId === 'tasYuvasi' || komutId === 'bezel') {
      const d = this.aktifDuzlem || this.hedefSketch?.duzlem || FikoSketch.duzlemHazir('xy');
      return {
        tip: 'makro', ekleme: p.ekleme !== false, makroTip: komutId as any,
        mCap: p.mCap, mEtKal: p.mEtKal, mUzun: p.mUzun, mAci: p.mAci, mYaricap: p.mYaricap,
        mTasCap: p.mTasCap, mProngAdet: p.mProngAdet, mProngCap: p.mProngCap, mProngYuk: p.mProngYuk, mBand: p.mBand,
        duzlem: this.duzlemKlon(d), node: this.ozellikNode('makro', null),
      };
    }
    return null;
  }

  // ---- CAM: yazı gravür — MEVCUT FikoAktiviteGravur + DialogFikoGravurComponent sistemine entegre ----
  private yaziGravurObje: any = null;

  /**
   * Yazı feature'ından MEVCUT gravür aktivitesi kurar + MEVCUT gravür dialog'unu açar.
   * Tüm CAM parametreleri (4 gravür tipi, takım, dolgu/V/çizgi/nokta, hız, giriş-çıkış…),
   * toolpath hesabı, G-code ve simülasyon mevcut CAM sisteminden gelir — paralel UI YOK.
   */
  yaziGravurAc(feat: IOzellik, $event?: any): void {
    if (feat?.tip !== 'yazi3d' || !feat.metin) return;
    try {
      this.yaziGravurTemizle();
      const grv = new FikoAktiviteGravur(this.d.sahne as any);
      // Yazı outline'ını profillere yükle + takıma-göre makul başlangıç (kullanıcı dialog'da değiştirir)
      yaziGravurUygula(grv, feat.metin, {
        fontId: feat.fontAd || 'helvetiker', boyut: feat.boyut || 10, harfBosluk: feat.harfBosluk || 0,
        takimTipi: 'VKesici', gravurTipi: 'DolguGravur', dolguDeseni: 'CrossHatch',
        derinlik: Math.max(0.2, (feat.param || 4) * 0.5),
      });
      if (feat.duzlem) (grv as any).applyMatrix4?.(new THREE.Matrix4().makeBasis(feat.duzlem.eksenU, feat.duzlem.eksenV, feat.duzlem.normal).setPosition(feat.duzlem.origin));
      this.d.sahne.add(grv as any);
      this.yaziGravurObje = grv;
      const ana: any = (this.d.sahne as any).anaGosterim;
      if (ana) ana.aktifItem = grv;
      this.d.ciz();
      (grv as any).duzenle?.($event);   // MEVCUT DialogFikoGravurComponent (tüm parametreler + tab'lar + simülasyon)
    } catch (e) { console.warn('[Modelleme] yaziGravurAc hatası', e); }
  }

  /** Aktif gravür aktivitesini sahneden kaldırır. */
  yaziGravurTemizle(): void {
    if (this.yaziGravurObje) { this.yaziGravurObje.parent?.remove(this.yaziGravurObje); this.yaziGravurObje = null; this.d.ciz(); }
  }

  // ---- CATIA KENAR SEÇİMİ: fillet/chamfer SADECE seçili kenarlara (komle gövdeye değil) ----
  private kenarSecModu = false;
  private seciliKenarIdx = new Set<number>();
  private kenarVurguObj: THREE.Object3D | null = null;
  private kenarCache: any[] = [];   // fillet ÖNCESİ kenar grupları — seçim boyunca SABİT (rebuild kenarları değiştirse de pick/highlight tutarlı)
  tangencyAcik = false;   // VARSAYILAN: tek B-Rep edge seç (her edge ayrı grup). CATIA "Propagation: Tangency" istenirse toggle ile aç
  get tangencyDurum(): boolean { return this.tangencyAcik; }
  tangencyToggle(): void { this.tangencyAcik = !this.tangencyAcik; this.d.degisti?.(this.durum, this.sketch); }

  get kenarSecimAktif(): boolean { return this.kenarSecModu; }
  get seciliKenarSayisi(): number { return this.seciliKenarIdx.size; }
  /** Seçim boyunca kullanılacak kenar grupları: kenar modunda SABİT cache, aksi canlı. */
  private get aktifKenarGruplari(): any[] {
    if (this.kenarSecModu && this.kenarCache.length) return this.kenarCache;
    return (this.govdeMesh as any)?.occt_edgeGroups as any[] || [];
  }

  private kenarSecBaslat(): void {
    this.kenarSecModu = true; this.seciliKenarIdx.clear(); this.seciliKenarVurguTemizle(); this.hoverKenarIdx = -1;
    this.kenarCache = ((this.govdeMesh as any)?.occt_edgeGroups as any[] || []).slice();   // fillet öncesi kenarları dondur
    const f = this.taslakFeat;   // EKLE modu: mevcut secKenarlar midpoint'lerini eşleştirip koru
    if (f?.secKenarlar && f.secKenarlar.length >= 3) {
      for (let i = 0; i * 3 + 2 < f.secKenarlar.length; i++) {
        const px = f.secKenarlar[i * 3], py = f.secKenarlar[i * 3 + 1], pz = f.secKenarlar[i * 3 + 2];
        let best = -1, bestD = Infinity;
        for (let g = 0; g < this.kenarCache.length; g++) { const np = this.edgeGroupNoktalari(this.kenarCache[g]); if (np.length < 2) continue; const mid = np[Math.floor(np.length / 2)]; const d = (mid.x - px) ** 2 + (mid.y - py) ** 2 + (mid.z - pz) ** 2; if (d < bestD) { bestD = d; best = g; } }
        if (best >= 0 && bestD < 16) this.seciliKenarIdx.add(best);   // 4mm tol
      }
      if (this.seciliKenarIdx.size) this.kenarSecGuncelle();   // highlight + secKenarlar tazele
    }
  }
  kenarSecBitir(): void { this.kenarSecModu = false; this.seciliKenarVurguTemizle(); this.hoverTemizle(); this.hoverKenarIdx = -1; this.kenarCache = []; }

  /** İmlece en yakın kenar index'i (ekran-uzayı, px eşiği). Hover + pick ortak kullanır. */
  private enYakinKenar(e: PointerEvent | MouseEvent, thr = 22): number {
    if (!this.govdeMesh) return -1;
    const eg = this.aktifKenarGruplari; if (!eg?.length) return -1;
    const canvas = this.d.canvasAl(); const kamera = this.d.kameraAl(); if (!canvas || !kamera) return -1;
    const r = canvas.getBoundingClientRect();
    const mx = (e as any).clientX - r.left, my = (e as any).clientY - r.top;
    let enYakin = -1, enMesafe = thr; const tmp = new THREE.Vector3();
    for (let i = 0; i < eg.length; i++) {
      for (const p of this.edgeGroupNoktalari(eg[i])) {
        tmp.copy(p).project(kamera as any);
        if (tmp.z > 1) continue;   // kamera arkasındaki noktalar
        const sx = (tmp.x * 0.5 + 0.5) * r.width, sy = (-tmp.y * 0.5 + 0.5) * r.height;
        const d = Math.hypot(sx - mx, sy - my);
        if (d < enMesafe) { enMesafe = d; enYakin = i; }
      }
    }
    return enYakin;
  }

  /** İmlece en yakın gövde kenarını (occt_edgeGroups) seç/kaldır → canlı fillet/chamfer önizleme + vurgu. */
  kenarPick(e: PointerEvent | MouseEvent): boolean {
    if (!this.kenarSecModu || !this.govdeMesh) return false;
    const enYakin = this.enYakinKenar(e);
    if (enYakin >= 0) {   // doğrudan kenara tıklandı → TANGENCY propagation: teğet zincir toggle (CATIA)
      const zincir = this.tangencyAcik ? this.tegetZincir(enYakin) : [enYakin];
      const hepsiSecili = zincir.every(k => this.seciliKenarIdx.has(k));
      for (const k of zincir) { if (hepsiSecili) this.seciliKenarIdx.delete(k); else this.seciliKenarIdx.add(k); }
      this.kenarSecGuncelle();
      return true;
    }
    // CATIA: kenara değil YÜZE tıklandı → o yüzün TÜM sınır kenarlarını otomatik seç
    const faceId = this.yuzPickFaceId(e);
    if (faceId >= 0) {
      const yuzKenar = this.yuzKenarlariBul(faceId);
      if (yuzKenar.length) {
        const hepsiSecili = yuzKenar.every(k => this.seciliKenarIdx.has(k));
        for (const k of yuzKenar) { if (hepsiSecili) this.seciliKenarIdx.delete(k); else this.seciliKenarIdx.add(k); }  // yüz toggle
        this.kenarSecGuncelle();
        return true;
      }
    }
    return false;
  }

  // ───────────────────── CATIA: PROJECT 3D ELEMENT (katı kenarını aktif eskize izdüşür) ─────────────────────
  private projekteModu = false;
  get projekteAktif(): boolean { return this.projekteModu; }

  /** Aktif eskiz + katı varken kenar seçimini başlat → tıklanan kenar düzleme inşa çizgisi olarak izdüşer. */
  projekte3dBaslat(): boolean {
    if (this.durum !== 'sketch' || !this.sketch || !this.aktifDuzlem || !this.govdeMesh) return false;
    this.projekteModu = true;
    this.kenarSecBaslat();   // occt_edgeGroups'u dondur, kenar modu + hover vurgu
    this.d.degisti?.(this.durum, this.sketch);
    return true;
  }
  projekte3dBitir(): void {
    if (!this.projekteModu) return;
    this.projekteModu = false;
    this.kenarSecBitir();
  }

  /** Kenara tıklanınca o kenarı (tangency açıksa teğet zinciriyle) aktif eskize izdüşür. */
  projekte3dPick(e: PointerEvent | MouseEvent): boolean {
    if (!this.projekteModu || !this.sketch) return false;
    const idx = this.enYakinKenar(e); if (idx < 0) return false;
    const eg = this.aktifKenarGruplari;
    const zincir = this.tangencyAcik ? this.tegetZincir(idx) : [idx];
    let eklendi = 0;
    for (const i of zincir) if (this.kenariEskizeProjektele(eg[i])) eklendi++;
    if (eklendi) { this.entityAgaceGuncelleGuvenli(); this.d.ciz(); this.d.degisti?.(this.durum, this.sketch); }
    return eklendi > 0;
  }

  /** Tek occt_edgeGroup'u aktif düzleme izdüşürüp eskize inşa elemanı ekler. */
  private kenariEskizeProjektele(eg: any): boolean {
    if (!this.sketch || !eg) return false;
    const np = this.edgeGroupNoktalari(eg); if (np.length < 2) return false;
    return this.noktalariEskizeProjektele(np);
  }

  /** Dünya noktalarını aktif düzleme izdüşürüp eskize inşa elemanı (çember/çizgi/spline) ekler — Project+Intersect ortak. */
  private noktalariEskizeProjektele(np: THREE.Vector3[]): boolean {
    if (!this.sketch || np.length < 2) return false;
    const p2d = np.map(w => this.sketch!.dunya2Duzlem(w));
    const c = this.cember2dTespit(p2d);
    if (c) { const el = this.sketch.ekle('cember', [{ x: c.cx, y: c.cy }], c.r); el.yapi = true; return true; }
    if (p2d.length === 2 || this.dogrusal2dMi(p2d)) {
      const el = this.sketch.ekle('cizgi', [p2d[0], p2d[p2d.length - 1]]); el.yapi = true; return true;
    }
    const el = this.sketch.ekle('spline', this.seyrelt2d(p2d, 12)); el.yapi = true; return true;
  }

  /** CATIA Intersect 3D Element: aktif eskiz düzlemiyle katının kesişim eğrilerini eskize düşür (OCCT section). */
  intersect3dUygula(): number {
    if (this.durum !== 'sketch' || !this.sketch || !this.aktifDuzlem) return 0;
    const shape = this.occtSonShapeRef || this.occtBazShape;
    if (!shape) { this.d.bildir?.('Intersect için OCCT B-Rep gövde gerekli (önce bir katı oluştur)'); return 0; }
    const o = this.aktifDuzlem.origin, n = this.aktifDuzlem.normal, u = this.aktifDuzlem.eksenU, v = this.aktifDuzlem.eksenV;
    let kenarlar: { pts: { x: number; y: number; z: number }[] }[] = [];
    try { kenarlar = fikoOCCT.kesitKenarlari(shape, { x: o.x, y: o.y, z: o.z }, { x: n.x, y: n.y, z: n.z }, { x: u.x, y: u.y, z: u.z }, { x: v.x, y: v.y, z: v.z }); } catch { }
    let eklendi = 0;
    for (const k of kenarlar) {
      const np = k.pts.map(p => new THREE.Vector3(p.x, p.y, p.z));
      if (this.noktalariEskizeProjektele(np)) eklendi++;
    }
    if (eklendi) { this.entityAgaceGuncelleGuvenli(); this.sketch.ciz(); this.d.degisti?.(this.durum, this.sketch); }
    else this.d.bildir?.('Kesişim eğrisi bulunamadı (düzlem katıyı kesmiyor olabilir)');
    return eklendi;
  }

  /** 2D nokta dizisi sabit-yarıçaplı kapalı çember mi? (çevre çember + sapma testi) */
  private cember2dTespit(p: { x: number; y: number }[]): { cx: number; cy: number; r: number } | null {
    if (p.length < 8) return null;
    const cc = FikoSketch.cevreCember(p[0], p[Math.floor(p.length / 3)], p[Math.floor(2 * p.length / 3)]);
    if (!cc) return null;
    let maxSapma = 0;
    for (const q of p) { const dr = Math.abs(Math.hypot(q.x - cc.merkez.x, q.y - cc.merkez.y) - cc.r); if (dr > maxSapma) maxSapma = dr; }
    if (maxSapma > cc.r * 0.03 + 0.05) return null;
    if (Math.hypot(p[0].x - p[p.length - 1].x, p[0].y - p[p.length - 1].y) > cc.r * 0.2) return null;
    return { cx: cc.merkez.x, cy: cc.merkez.y, r: cc.r };
  }

  /** 2D polyline neredeyse düz mü? (uç-uca çizgiye max dik sapma) */
  private dogrusal2dMi(p: { x: number; y: number }[]): boolean {
    const a = p[0], b = p[p.length - 1]; const dx = b.x - a.x, dy = b.y - a.y; const L = Math.hypot(dx, dy) || 1;
    let maxd = 0;
    for (const q of p) { const d = Math.abs((q.x - a.x) * dy - (q.y - a.y) * dx) / L; if (d > maxd) maxd = d; }
    return maxd < L * 0.01 + 0.02;
  }

  /** 2D nokta dizisini ~n kontrol noktasına seyrelt (spline için). */
  private seyrelt2d(p: { x: number; y: number }[], n: number): { x: number; y: number }[] {
    if (p.length <= n) return p.slice();
    const out: { x: number; y: number }[] = []; const adim = (p.length - 1) / (n - 1);
    for (let i = 0; i < n; i++) out.push(p[Math.round(i * adim)]);
    return out;
  }

  // ───────────────────── CATIA: CUT PART BY SKETCH PLANE (görsel kesit — clipping plane) ─────────────────────
  private kesitDuzlemi: THREE.Plane | null = null;
  private kesitYon = 1;                 // 1 / -1 → düzlemin hangi yarısı gizlenir
  kesitAktif = false;
  get kesitDurum(): boolean { return this.kesitAktif; }

  /** Aktif eskiz düzleminden görsel kesit (clipping) üret — üretim/topoloji DEĞİŞMEZ, yalnız govdeMesh materyali. */
  private kesitUygula(): void {
    if (!this.kesitAktif || !this.aktifDuzlem || !this.govdeMesh) { this.kesitKaldir(); return; }
    const n = this.aktifDuzlem.normal.clone().multiplyScalar(this.kesitYon);
    if (!this.kesitDuzlemi) this.kesitDuzlemi = new THREE.Plane();
    this.kesitDuzlemi.setFromNormalAndCoplanarPoint(n, this.aktifDuzlem.origin);
    const mat = this.govdeMesh.material as THREE.MeshStandardMaterial;
    const ilk = !mat.clippingPlanes || mat.clippingPlanes.length === 0;
    mat.clippingPlanes = [this.kesitDuzlemi];
    mat.clipShadows = true;
    mat.side = THREE.DoubleSide;          // kesik içi yüzeyler görünsün
    if (ilk) mat.needsUpdate = true;      // shader recompile yalnız 0↔1 geçişte
    this.kesitRendererAc();
    this.d.ciz();
  }

  /** Görsel kesiti kaldır (materyali normale döndür). */
  kesitKaldir(): void {
    if (this.govdeMesh) {
      const mat = this.govdeMesh.material as THREE.MeshStandardMaterial;
      if (mat.clippingPlanes?.length) { mat.clippingPlanes = null; mat.side = THREE.FrontSide; mat.needsUpdate = true; }
    }
    this.kesitDuzlemi = null;
    this.d.ciz();
  }

  /** UI'dan: kesiti aç/kapat. */
  kesitToggle(): boolean {
    this.kesitAktif = !this.kesitAktif;
    this.kesitAktif ? this.kesitUygula() : this.kesitKaldir();
    return this.kesitAktif;
  }

  /** Kesit yönünü çevir (düzlemin diğer yarısını gizle). */
  kesitYonToggle(): void {
    this.kesitYon = -this.kesitYon;
    if (this.kesitAktif) this.kesitUygula();
  }

  /** Renderer'da local clipping'i (bir kez) aç — yoksa clippingPlanes etkisiz. */
  private kesitRendererAc(): void {
    try { const r = this.d.rendererAl?.(); if (r && !r.localClippingEnabled) r.localClippingEnabled = true; } catch { }
  }

  /** TANGENCY propagation: bir kenardan başlayıp teğet (smooth, açı<15°) komşu kenarları zincirler. */
  private tegetZincir(bas: number): number[] {
    const eg = this.aktifKenarGruplari; if (!eg?.length) return [bas];
    const Q = 0.5, key = (p: THREE.Vector3) => `${Math.round(p.x / Q)},${Math.round(p.y / Q)},${Math.round(p.z / Q)}`;
    const uc = (i: number) => {
      const np = this.edgeGroupNoktalari(eg[i]); if (np.length < 2) return null;
      return { a: np[0], b: np[np.length - 1], ta: np[1].clone().sub(np[0]).normalize(), tb: np[np.length - 2].clone().sub(np[np.length - 1]).normalize() };
    };
    const cosThr = Math.cos(15 * Math.PI / 180);
    const sonuc = new Set<number>([bas]); const kuyruk = [bas];
    while (kuyruk.length) {
      const c = kuyruk.pop()!; const uc_c = uc(c); if (!uc_c) continue;
      for (let i = 0; i < eg.length; i++) {
        if (sonuc.has(i)) continue; const uc_i = uc(i); if (!uc_i) continue;
        const ciftler: [THREE.Vector3, THREE.Vector3, THREE.Vector3, THREE.Vector3][] = [
          [uc_c.a, uc_c.ta, uc_i.a, uc_i.ta], [uc_c.a, uc_c.ta, uc_i.b, uc_i.tb],
          [uc_c.b, uc_c.tb, uc_i.a, uc_i.ta], [uc_c.b, uc_c.tb, uc_i.b, uc_i.tb],
        ];
        for (const [pc, tc, pi, ti] of ciftler) {
          if (key(pc) === key(pi) && tc.dot(ti) < -cosThr) { sonuc.add(i); kuyruk.push(i); break; }  // ortak uç + düz devam = teğet
        }
      }
    }
    return [...sonuc];
  }

  /** İmlecin altındaki gövde yüzünün faceId'si (raycast → grup.z). */
  private static _pickNM = new THREE.Matrix3();
  private static _pickWN = new THREE.Vector3();
  private yuzPickFaceId(e: PointerEvent | MouseEvent): number {
    const gm = this.govdeMesh; if (!gm) return -1;
    const canvas = this.d.canvasAl(); const kamera = this.d.kameraAl(); if (!canvas || !kamera) return -1;
    const r = canvas.getBoundingClientRect();
    const ndc = new THREE.Vector2((((e as any).clientX - r.left) / r.width) * 2 - 1, -(((e as any).clientY - r.top) / r.height) * 2 + 1);
    const rc = new THREE.Raycaster(); rc.setFromCamera(ndc, kamera as any);
    const grup = gm.geometry.attributes['grup'] as THREE.BufferAttribute | undefined;
    if (!grup) return -1;
    // En yakın KAMERAYA BAKAN (ön) yüzü seç — gövdenin arkasındaki/gizli yüzler seçilemez.
    const hits = rc.intersectObject(gm, true) as any[];
    const nm = FikoModellemeYonetici._pickNM, wn = FikoModellemeYonetici._pickWN;
    nm.getNormalMatrix(gm.matrixWorld);
    let enYakin = -1;
    for (const hit of hits) {
      if (hit.faceIndex == null || !hit.face || hit.object !== gm) continue;
      const fid = grup.getZ(hit.faceIndex * 3);
      if (enYakin < 0) enYakin = fid;   // winding tutarsızsa fallback
      wn.copy(hit.face.normal).applyMatrix3(nm).normalize();
      if (wn.dot(rc.ray.direction) < 0) return fid;   // normal kameraya dönük = görünür ön yüz
    }
    return enYakin;
  }

  // ───────────────────── CATIA Pad "Up to Plane/Surface": 3D hedef seçimi ─────────────────────
  private limitSecModu = false;
  private limitSecFeat: IOzellik | null = null;
  get limitSecAktif(): boolean { return this.limitSecModu; }
  /** Pad penceresinden "Hedef Seç" → gövde yüzü/düzlemi tıklama modu (up-to-plane mesafesi). */
  limitHedefSecBaslat(feat: IOzellik): boolean {
    if (!this.govdeMesh) { this.d.bildir?.('Hedef için bir katı gerekli'); return false; }
    this.limitSecModu = true; this.limitSecFeat = feat;
    this.d.bildir?.('Hedef yüze/düzleme tıkla (pad oraya kadar uzar)');
    return true;
  }
  limitHedefSecBitir(): void { this.limitSecModu = false; this.limitSecFeat = null; }
  /** Hedef tıklaması: ışın gövdeye çarptığı nokta → sketch düzlemine normal mesafe → up-to mesafesi. */
  limitHedefPick(e: PointerEvent | MouseEvent): boolean {
    if (!this.limitSecModu || !this.limitSecFeat) return false;
    const gm = this.govdeMesh; const canvas = this.d.canvasAl(); const kamera = this.d.kameraAl();
    if (!gm || !canvas || !kamera) return false;
    const r = canvas.getBoundingClientRect();
    const ndc = new THREE.Vector2((((e as any).clientX - r.left) / r.width) * 2 - 1, -(((e as any).clientY - r.top) / r.height) * 2 + 1);
    const rc = new THREE.Raycaster(); rc.setFromCamera(ndc, kamera as any);
    const hit = rc.intersectObject(gm, true)[0] as any;
    if (!hit?.point) return false;
    // HOLE: sketch düzlemi yok → delik ekseni boyunca mesafe (holeMerkez'den içeri)
    if (this.limitSecFeat.tip === 'hole' && this.limitSecFeat.holeMerkez && this.limitSecFeat.holeNormal) {
      const c = this.limitSecFeat.holeMerkez, nh = this.limitSecFeat.holeNormal;
      const Lh = Math.hypot(nh.x, nh.y, nh.z) || 1;
      const dx = hit.point.x - c.x, dy = hit.point.y - c.y, dz = hit.point.z - c.z;
      const mes = -(dx * nh.x + dy * nh.y + dz * nh.z) / Lh;   // içeri (-normal) yönünde derinlik
      this.limitSecFeat.holeLimit = 'duzleme'; this.limitSecFeat.limitHedefMesafe = Math.max(0.1, Math.abs(mes));
      this.rebuild(); this.featureNodeAdGuncelle(this.limitSecFeat); this.d.agacGuncelle?.();
      this.limitHedefSecBitir(); return true;
    }
    if (!this.limitSecFeat.duzlem) return false;
    const d = this.limitSecFeat.duzlem; const n = d.normal;
    const mes = (hit.point.x - d.origin.x) * n.x + (hit.point.y - d.origin.y) * n.y + (hit.point.z - d.origin.z) * n.z;   // normal boyunca işaretli mesafe
    this.limitSecFeat.limitHedefMesafe = Math.abs(mes);
    if (mes < 0) this.limitSecFeat.tersYon = !this.limitSecFeat.tersYon;   // hedef ters yöndeyse yönü çevir
    this.rebuild(); this.featureNodeAdGuncelle(this.limitSecFeat); this.d.agacGuncelle?.();
    this.limitHedefSecBitir();
    return true;
  }

  // ───────────────────── CATIA Pad Direction Reference: 3D yüzeyden uzatma yönü ─────────────────────
  private yonSecModu = false;
  private yonSecFeat: IOzellik | null = null;
  get yonSecAktif(): boolean { return this.yonSecModu; }
  /** Pad penceresinden "3D'den Seç" → bir yüze tıkla, yüzey normali uzatma yönü olur. */
  yonHedefSecBaslat(feat: IOzellik): boolean {
    if (!this.govdeMesh) { this.d.bildir?.('Yön için bir katı gerekli'); return false; }
    this.yonSecModu = true; this.yonSecFeat = feat;
    this.d.bildir?.('Uzatma yönü için bir yüze tıkla (yüzey normali = yön)');
    return true;
  }
  yonHedefSecBitir(): void { this.yonSecModu = false; this.yonSecFeat = null; }
  /** Yön tıklaması: ışın gövdeye çarptığı yüzün NORMALİ → uzatma yönü (refYon). */
  yonHedefPick(e: PointerEvent | MouseEvent): boolean {
    if (!this.yonSecModu || !this.yonSecFeat) return false;
    const gm = this.govdeMesh; const canvas = this.d.canvasAl(); const kamera = this.d.kameraAl();
    if (!gm || !canvas || !kamera) return false;
    const r = canvas.getBoundingClientRect();
    const ndc = new THREE.Vector2((((e as any).clientX - r.left) / r.width) * 2 - 1, -(((e as any).clientY - r.top) / r.height) * 2 + 1);
    const rc = new THREE.Raycaster(); rc.setFromCamera(ndc, kamera as any);
    const hit = rc.intersectObject(gm, true)[0] as any;
    if (!hit?.face) return false;
    const n = hit.face.normal.clone().transformDirection(gm.matrixWorld).normalize();   // yüz normali → dünya
    this.yonSecFeat.refYon = { x: n.x, y: n.y, z: n.z };
    this.yonSecFeat.refYonAktif = true;
    this.rebuild(); this.featureNodeAdGuncelle(this.yonSecFeat); this.d.agacGuncelle?.();
    this.yonHedefSecBitir();
    return true;
  }

  /** CATIA "yüz seç → kenarları otomatik": faceId'nin sınır kenarlarındaki occt_edgeGroups index'leri. */
  private yuzKenarlariBul(faceId: number): number[] {
    const gm = this.govdeMesh; if (!gm) return [];
    const grup = gm.geometry.attributes['grup'] as THREE.BufferAttribute | undefined;
    const pos = gm.geometry.attributes['position'] as THREE.BufferAttribute | undefined;
    const eg = this.aktifKenarGruplari; if (!grup || !pos || !eg?.length) return [];
    const Q = 0.5, key = (x: number, y: number, z: number) => `${Math.round(x / Q)},${Math.round(y / Q)},${Math.round(z / Q)}`;
    const yuzPts = new Set<string>();
    for (let i = 0; i < grup.count; i++) if (grup.getZ(i) === faceId) yuzPts.add(key(pos.getX(i), pos.getY(i), pos.getZ(i)));
    const out: number[] = [];
    for (let i = 0; i < eg.length; i++) {
      const np = this.edgeGroupNoktalari(eg[i]); if (np.length < 2) continue;
      const bas = np[0], son = np[np.length - 1];
      if (yuzPts.has(key(bas.x, bas.y, bas.z)) && yuzPts.has(key(son.x, son.y, son.z))) out.push(i);  // her iki uç yüzde → yüz sınır kenarı
    }
    return out;
  }

  /** occt_edgeGroup'u 3D noktalara açar (LINE: örnekle, CIRCLE: çember). */
  private edgeGroupNoktalari(eg: any): THREE.Vector3[] {
    const out: THREE.Vector3[] = [];
    if (eg.pts && eg.pts.length >= 2) { for (const p of eg.pts) out.push(new THREE.Vector3(p.x, p.y, p.z)); return out; }   // OCCT B-Rep edge polyline
    if (eg.curveType === 'CIRCLE' && eg.center && eg.axis) {
      const c = new THREE.Vector3(eg.center.x, eg.center.y, eg.center.z);
      const ax = new THREE.Vector3(eg.axis.x, eg.axis.y, eg.axis.z).normalize();
      const u = new THREE.Vector3(1, 0, 0); if (Math.abs(ax.dot(u)) > 0.9) u.set(0, 1, 0);
      const e1 = new THREE.Vector3().crossVectors(ax, u).normalize();
      const e2 = new THREE.Vector3().crossVectors(ax, e1).normalize();
      const rad = eg.radius || 1;
      for (let i = 0; i <= 32; i++) { const a = (i / 32) * Math.PI * 2; out.push(c.clone().addScaledVector(e1, Math.cos(a) * rad).addScaledVector(e2, Math.sin(a) * rad)); }
    } else if (eg.start && eg.end) {
      const a = new THREE.Vector3(eg.start.x, eg.start.y, eg.start.z), b = new THREE.Vector3(eg.end.x, eg.end.y, eg.end.z);
      for (let i = 0; i <= 8; i++) out.push(a.clone().lerp(b, i / 8));
    }
    return out;
  }

  private seciliKenarVurguPts: number[] = [];   // rebuild ÖNCESİ yakalanan highlight segmentleri (index geçersizleşmesini önler)
  private kenarSecGuncelle(): void {
    const feat = this.taslakFeat; if (!feat) return;
    const eg = this.aktifKenarGruplari;
    // feature için tüm seçili kenar noktaları + highlight segmentleri (rebuild ÖNCESİ — index'ler hâlâ geçerli)
    const nokta: number[] = [], segPts: number[] = [], imza: number[] = [];
    for (const i of this.seciliKenarIdx) {
      const np = this.edgeGroupNoktalari(eg[i]); if (np.length < 2) continue;
      const mid = np[Math.floor(np.length / 2)];   // kenar-başına BİR temsilci (orta nokta) → listelenebilir + eşleştirme
      nokta.push(mid.x, mid.y, mid.z);
      // B-Rep link imzası: yön (uç→uç) + zincir uzunluğu → parametre değişse de skorla eşleşir
      const yon = np[np.length - 1].clone().sub(np[0]); const uz0 = yon.length(); if (uz0 > 1e-6) yon.multiplyScalar(1 / uz0);
      let uzun = 0; for (let k = 1; k < np.length; k++) uzun += np[k].distanceTo(np[k - 1]);
      imza.push(mid.x, mid.y, mid.z, yon.x, yon.y, yon.z, uzun);
      for (let k = 1; k < np.length; k++) segPts.push(np[k - 1].x, np[k - 1].y, np[k - 1].z, np[k].x, np[k].y, np[k].z);   // highlight tüm segment (ayrı)
    }
    feat.secKenarlar = nokta;
    feat.secKenarImza = imza;
    this.seciliKenarVurguPts = segPts;
    this.rebuild();   // fillet uygulanır → eg yeniden üretilir, ama highlight segPts (dünya koord) sabit
    this.seciliKenarVurgula();
    this.d.degisti?.(this.durum, this.sketch);
    this.secimBildir();   // açık seçim listelerini canlı tazele
  }

  private seciliKenarVurgula(): void {
    this.seciliKenarVurguTemizle();
    const pts = this.seciliKenarVurguPts;
    if (!pts.length) { this.d.ciz(); return; }
    this.kenarVurguObj = this.fatVurgu(pts, 0x00ff88, 4, 999);   // parlak yeşil kalın çizgi (seçili)
    this.d.sahne.add(this.kenarVurguObj); this.d.ciz();
  }
  private seciliKenarVurguTemizle(): void {
    if (this.kenarVurguObj) { this.d.sahne.remove(this.kenarVurguObj); (this.kenarVurguObj as any).geometry?.dispose?.(); (this.kenarVurguObj as any).material?.dispose?.(); this.kenarVurguObj = null; }
  }

  // ---- PROFESYONEL HOVER ÖNİZLEME (CATIA/Fusion tarzı — imleç altındaki kenar/yüz/vertex anlık vurgu) ----
  private hoverObj: THREE.Object3D | null = null;
  private hoverKenarIdx = -1;
  private hoverFaceId = -2;
  private hoverVertexIdx = -1;
  private _hoverSon = 0;

  /** Herhangi bir topoloji seçim modu aktif mi (pointermove hover için). */
  get topoSecimAktif(): boolean { return this.kenarSecModu || this.yuzSecModu || this.vertexSecModu || this.profilSecModu; }

  /** pointermove → imleç altındaki kenar/yüz/vertex/profil'i anlık vurgular (seçimden önce görsel geri bildirim). */
  topoHover(e: PointerEvent | MouseEvent): void {
    if (this.profilSecModu) return this.profilHover(e);
    const now = (e as any).timeStamp || performance.now();
    if (now - this._hoverSon < 16) return;   // ~60fps throttle
    this._hoverSon = now;
    if (this.vertexSecModu) return this.vertexHover(e);
    if (this.kenarSecModu) return this.kenarHover(e);
    if (this.yuzSecModu) return this.yuzHover(e);
  }

  private hoverTemizle(): void {
    if (this.hoverObj) { this.d.sahne.remove(this.hoverObj); (this.hoverObj as any).geometry?.dispose?.(); (this.hoverObj as any).material?.dispose?.(); this.hoverObj = null; }
  }

  /** PROFESYONEL kalın parlak vurgu çizgisi (LineSegments2 fat-line — LineBasicMaterial 1px değil, GÖRÜNÜR). */
  private fatVurgu(segPts: number[], renk: number, kalinlik: number, renderOrder: number): LineSegments2 {
    const lsg = new LineSegmentsGeometry(); lsg.setPositions(segPts);
    const canvas = this.d.canvasAl();
    const mat = new LineMaterial({ color: renk, linewidth: kalinlik, worldUnits: false, depthTest: false, transparent: true, dashed: false, alphaToCoverage: false });
    mat.resolution.set(canvas?.width || 1920, canvas?.height || 1080);
    const ls = new LineSegments2(lsg, mat); ls.renderOrder = renderOrder; (ls as any).raycast = () => { };
    return ls;
  }

  private kenarHover(e: PointerEvent | MouseEvent): void {
    const idx = this.enYakinKenar(e);
    if (idx === this.hoverKenarIdx) return;
    this.hoverKenarIdx = idx; this.hoverTemizle();
    if (idx < 0) { this.d.ciz(); return; }
    const eg = this.aktifKenarGruplari;
    const zincir = this.tangencyAcik ? this.tegetZincir(idx) : [idx];   // teğet zincirin tamamını önizle
    const pts: number[] = [];
    for (const i of zincir) { const np = this.edgeGroupNoktalari(eg[i]); for (let k = 1; k < np.length; k++) pts.push(np[k - 1].x, np[k - 1].y, np[k - 1].z, np[k].x, np[k].y, np[k].z); }
    if (!pts.length) { this.d.ciz(); return; }
    const obj = this.fatVurgu(pts, 0x22d3ee, 5, 998);   // parlak cyan kalın çizgi (hover)
    this.hoverObj = obj; this.d.sahne.add(obj); this.d.ciz();
  }

  private yuzHover(e: PointerEvent | MouseEvent): void {
    const faceId = this.yuzPickFaceId(e);
    if (faceId === this.hoverFaceId) return;
    this.hoverFaceId = faceId; this.hoverTemizle();
    if (faceId < 0) { this.d.ciz(); return; }
    if (this.seciliYuzIdx.has(faceId)) { this.d.ciz(); return; }   // zaten seçili → hover gösterme
    const obj = this.yuzGorselMesh(fid => fid === faceId, 0x4db8ff, 0.42, 996);   // CATIA hover: açık mavi
    if (obj) { this.hoverObj = obj; this.d.sahne.add(obj); }
    this.d.ciz();
  }

  /** CATIA-profesyonel yüz vurgu mesh'i: depthTest+polygonOffset (gövdeden geçmez, yüzeye oturur). */
  private yuzGorselMesh(secili: (fid: number) => boolean, renk: number, op: number, ro: number): THREE.Mesh | null {
    const gm = this.govdeMesh; if (!gm) return null;
    const grup = gm.geometry.attributes['grup'] as THREE.BufferAttribute | undefined;
    const pos = gm.geometry.attributes['position'] as THREE.BufferAttribute | undefined;
    if (!grup || !pos) return null;
    const arr: number[] = [];
    for (let i = 0; i < grup.count; i++) if (secili(grup.getZ(i))) arr.push(pos.getX(i), pos.getY(i), pos.getZ(i));
    if (!arr.length) return null;
    const g = new THREE.BufferGeometry(); g.setAttribute('position', new THREE.Float32BufferAttribute(arr, 3)); g.computeVertexNormals();
    const m = new THREE.MeshBasicMaterial({ color: renk, transparent: true, opacity: op, side: THREE.DoubleSide, depthTest: true, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 });
    const mesh = new THREE.Mesh(g, m); mesh.renderOrder = ro; (mesh as any).raycast = () => { };
    return mesh;
  }

  // ---- CATIA VERTEX (köşe) SEÇİMİ: gövde kenar uç noktaları ----
  private vertexSecModu = false;
  private seciliVertexler: THREE.Vector3[] = [];
  private vertexVurguObj: THREE.Points | null = null;
  get vertexSecimAktif(): boolean { return this.vertexSecModu; }
  get seciliVertexSayisi(): number { return this.seciliVertexler.length; }
  vertexSecBaslat(): void { this.vertexSecModu = true; this.seciliVertexler = []; this.vertexVurguTemizle(); }
  vertexSecBitir(): void { this.vertexSecModu = false; this.vertexVurguTemizle(); this.hoverTemizle(); }

  private _govdeVertexCache: THREE.Vector3[] | null = null;   // gövde değişince invalidate (govdeMeshKur)
  /** Gövdenin benzersiz köşe noktaları (occt_edgeGroups uçları, Q=0.3 dedup). Cache'li — hover'da yeniden hesaplanmaz. */
  private govdeVertexler(): THREE.Vector3[] {
    if (this._govdeVertexCache) return this._govdeVertexCache;
    const eg = (this.govdeMesh as any)?.occt_edgeGroups as any[] || [];
    const Q = 0.3, seen = new Map<string, THREE.Vector3>();
    for (const g of eg) {
      const np = this.edgeGroupNoktalari(g); if (np.length < 2) continue;
      for (const p of [np[0], np[np.length - 1]]) {
        const k = `${Math.round(p.x / Q)},${Math.round(p.y / Q)},${Math.round(p.z / Q)}`;
        if (!seen.has(k)) seen.set(k, p.clone());
      }
    }
    this._govdeVertexCache = [...seen.values()];
    return this._govdeVertexCache;
  }

  /** İmlece en yakın köşe noktası (ekran-uzayı). */
  private enYakinVertex(e: PointerEvent | MouseEvent, thr = 18): THREE.Vector3 | null {
    const canvas = this.d.canvasAl(); const kamera = this.d.kameraAl(); if (!canvas || !kamera) return null;
    const r = canvas.getBoundingClientRect();
    const mx = (e as any).clientX - r.left, my = (e as any).clientY - r.top;
    let en: THREE.Vector3 | null = null, enM = thr; const tmp = new THREE.Vector3();
    for (const v of this.govdeVertexler()) {
      tmp.copy(v).project(kamera as any); if (tmp.z > 1) continue;
      const sx = (tmp.x * 0.5 + 0.5) * r.width, sy = (-tmp.y * 0.5 + 0.5) * r.height;
      const d = Math.hypot(sx - mx, sy - my);
      if (d < enM) { enM = d; en = v; }
    }
    return en;
  }

  vertexPick(e: PointerEvent | MouseEvent): boolean {
    if (!this.vertexSecModu) return false;
    const v = this.enYakinVertex(e); if (!v) return false;
    const Q = 0.3, key = (p: THREE.Vector3) => `${Math.round(p.x / Q)},${Math.round(p.y / Q)},${Math.round(p.z / Q)}`;
    const vk = key(v);
    const idx = this.seciliVertexler.findIndex(s => key(s) === vk);
    if (idx >= 0) this.seciliVertexler.splice(idx, 1); else this.seciliVertexler.push(v);
    this.vertexVurgula();
    this.d.degisti?.(this.durum, this.sketch);
    return true;
  }

  private vertexHover(e: PointerEvent | MouseEvent): void {
    const v = this.enYakinVertex(e);
    const vi = v ? `${v.x.toFixed(1)},${v.y.toFixed(1)},${v.z.toFixed(1)}` : '';
    if ((vi ? 1 : 0) === this.hoverVertexIdx && !v) return;
    this.hoverVertexIdx = v ? 1 : 0; this.hoverTemizle();
    if (!v) { this.d.ciz(); return; }
    const g = new THREE.BufferGeometry(); g.setAttribute('position', new THREE.Float32BufferAttribute([v.x, v.y, v.z], 3));
    const obj = new THREE.Points(g, new THREE.PointsMaterial({ color: 0x22d3ee, size: 12, sizeAttenuation: false, depthTest: false }));
    obj.renderOrder = 999; (obj as any).raycast = () => { };
    this.hoverObj = obj; this.d.sahne.add(obj); this.d.ciz();
  }

  private vertexVurgula(): void {
    this.vertexVurguTemizle();
    if (!this.seciliVertexler.length) { this.d.ciz(); return; }
    const arr: number[] = [];
    for (const v of this.seciliVertexler) arr.push(v.x, v.y, v.z);
    const g = new THREE.BufferGeometry(); g.setAttribute('position', new THREE.Float32BufferAttribute(arr, 3));
    this.vertexVurguObj = new THREE.Points(g, new THREE.PointsMaterial({ color: 0xffaa00, size: 14, sizeAttenuation: false, depthTest: false }));
    this.vertexVurguObj.renderOrder = 1000; (this.vertexVurguObj as any).raycast = () => { };
    this.d.sahne.add(this.vertexVurguObj); this.d.ciz();
  }
  private vertexVurguTemizle(): void {
    if (this.vertexVurguObj) { this.d.sahne.remove(this.vertexVurguObj); this.vertexVurguObj.geometry.dispose(); (this.vertexVurguObj.material as any)?.dispose?.(); this.vertexVurguObj = null; }
  }
  /** Seçili köşe noktaları (dünya koord) — ölçüm/referans için. */
  get seciliVertexNoktalar(): { x: number; y: number; z: number }[] { return this.seciliVertexler.map(v => ({ x: v.x, y: v.y, z: v.z })); }

  // ---- CATIA PROFİL SEÇİMİ: Pad/Pocket/Shaft/Groove için 3D'den sketch (profil) seç ----
  private profilSecModu = false;
  private profilCoklu = false;   // loft/sweep: birden çok profil sıralı seçilir
  private seciliProfilIdx: number[] = [];   // çoklu modda seçili aday index'leri (sıralı)
  private profilAdaylar: { sketch: FikoSketch; duzlem: ISketchDuzlem; node: FikoGrup | null; pts: { x: number; y: number; z: number }[] }[] = [];
  private profilVurguObj: THREE.Object3D | null = null;
  get profilSecimAktif(): boolean { return this.profilSecModu; }
  get profilAdaySayisi(): number { return this.profilAdaylar.length; }
  get profilSecildiMi(): boolean {
    if (this.profilCoklu) return (this.taslakFeat?.secProfiller?.length || 0) >= (this.taslakFeat?.tip === 'loft' ? 2 : 2);
    return !!(this.taslakFeat?.elemanlar && this.taslakFeat.elemanlar.length);
  }
  get seciliProfilSayisi(): number { return this.seciliProfilIdx.length; }

  /** Eskiz grubundaki seçilebilir tüm sketch'ler (eleman içerenler). */
  private tumSketchler(): { sketch: FikoSketch; duzlem: ISketchDuzlem; node: FikoGrup | null }[] {
    const out: { sketch: FikoSketch; duzlem: ISketchDuzlem; node: FikoGrup | null }[] = [];
    const grup = this.eskizGrubuAl();
    for (const n of ((grup as any)?.children || [])) {
      const sk = n.userData?.['fikoSketch'] as FikoSketch;
      const dz = n.userData?.['sketchDuzlem'] as ISketchDuzlem;
      if (sk && dz && sk.elemanlar?.length) out.push({ sketch: sk, duzlem: dz, node: n });
    }
    return out;
  }

  private profilSecBaslat(coklu = false): void {
    this.profilSecModu = true; this.profilCoklu = coklu; this.seciliProfilIdx = []; this.profilVurguTemizle(); this.hoverTemizle();
    this.profilAdaylar = this.tumSketchler().map(s => {
      const loops = this.looplar(s.sketch.elemanlar);
      const big = loops.length ? loops.reduce((a, b) => b.alan > a.alan ? b : a) : null;
      const pts = big ? this.noktaDunya(big.pts, s.duzlem) : [];
      return { ...s, pts };
    }).filter(a => a.pts.length >= 3);
  }
  profilSecBitir(): void { this.profilSecModu = false; this.profilCoklu = false; this.seciliProfilIdx = []; this.profilVurguTemizle(); this.hoverTemizle(); this.profilAdaylar = []; }

  /** İmlece en yakın profil adayı index'i (ekran-uzayı). */
  private enYakinProfil(e: PointerEvent | MouseEvent, thr = 26): number {
    const canvas = this.d.canvasAl(); const kamera = this.d.kameraAl(); if (!canvas || !kamera) return -1;
    const r = canvas.getBoundingClientRect();
    const mx = (e as any).clientX - r.left, my = (e as any).clientY - r.top;
    let en = -1, enM = thr; const tmp = new THREE.Vector3();
    for (let i = 0; i < this.profilAdaylar.length; i++) {
      for (const p of this.profilAdaylar[i].pts) {
        tmp.set(p.x, p.y, p.z).project(kamera as any); if (tmp.z > 1) continue;
        const sx = (tmp.x * 0.5 + 0.5) * r.width, sy = (-tmp.y * 0.5 + 0.5) * r.height;
        const d = Math.hypot(sx - mx, sy - my);
        if (d < enM) { enM = d; en = i; }
      }
    }
    return en;
  }

  /** 3D'de bir profile tıkla → Pad/Pocket profili (tek) veya loft/sweep kesiti (çoklu, sıralı). */
  profilPick(e: PointerEvent | MouseEvent): boolean {
    if (!this.profilSecModu || !this.taslakFeat) return false;
    const idx = this.enYakinProfil(e); if (idx < 0) return false;
    const a = this.profilAdaylar[idx];
    if (this.profilCoklu) {   // loft/sweep: sıralı liste (tekrar tıkla→çıkar)
      const yer = this.seciliProfilIdx.indexOf(idx);
      if (yer >= 0) this.seciliProfilIdx.splice(yer, 1); else this.seciliProfilIdx.push(idx);
      this.taslakFeat.secProfiller = this.seciliProfilIdx.map(i => ({
        elemanlar: JSON.parse(JSON.stringify(this.profilAdaylar[i].sketch.elemanlar)),
        duzlem: this.duzlemKlon(this.profilAdaylar[i].duzlem),
      }));
      this.rebuild();
      this.profilVurguCoklu();
      this.d.degisti?.(this.durum, this.sketch);
      return true;
    }
    this.taslakFeat.elemanlar = JSON.parse(JSON.stringify(a.sketch.elemanlar));
    this.taslakFeat.duzlem = this.duzlemKlon(a.duzlem);
    this.hedefSketch = { sketch: a.sketch, duzlem: a.duzlem, node: a.node };
    this.taslakSketchNode = a.node;
    this.rebuild();
    this.profilVurgula(a);
    this.d.degisti?.(this.durum, this.sketch);
    return true;
  }

  /** Çoklu seçili profilleri sıra numarasıyla vurgular (loft/sweep). */
  private profilVurguCoklu(): void {
    this.profilVurguTemizle();
    const seg: number[] = [];
    for (const i of this.seciliProfilIdx) { const pts = this.profilAdaylar[i].pts; for (let k = 0; k < pts.length; k++) { const p = pts[k], q = pts[(k + 1) % pts.length]; seg.push(p.x, p.y, p.z, q.x, q.y, q.z); } }
    if (!seg.length) { this.d.ciz(); return; }
    this.profilVurguObj = this.fatVurgu(seg, 0x00ff88, 4, 999); this.d.sahne.add(this.profilVurguObj); this.d.ciz();
  }

  /** pointermove: imleç altındaki profili anlık vurgula (cyan). */
  profilHover(e: PointerEvent | MouseEvent): void {
    const now = (e as any).timeStamp || performance.now();
    if (now - this._hoverSon < 16) return; this._hoverSon = now;
    const idx = this.enYakinProfil(e);
    this.hoverTemizle();
    if (idx < 0) { this.d.ciz(); return; }
    const a = this.profilAdaylar[idx], seg: number[] = [];
    for (let k = 0; k < a.pts.length; k++) { const p = a.pts[k], q = a.pts[(k + 1) % a.pts.length]; seg.push(p.x, p.y, p.z, q.x, q.y, q.z); }
    const obj = this.fatVurgu(seg, 0x22d3ee, 5, 998); this.hoverObj = obj; this.d.sahne.add(obj); this.d.ciz();
  }

  private profilVurgula(a: { pts: { x: number; y: number; z: number }[] }): void {
    this.profilVurguTemizle();
    const seg: number[] = [];
    for (let k = 0; k < a.pts.length; k++) { const p = a.pts[k], q = a.pts[(k + 1) % a.pts.length]; seg.push(p.x, p.y, p.z, q.x, q.y, q.z); }
    this.profilVurguObj = this.fatVurgu(seg, 0x00ff88, 4, 999); this.d.sahne.add(this.profilVurguObj); this.d.ciz();
  }
  private profilVurguTemizle(): void {
    if (this.profilVurguObj) { this.d.sahne.remove(this.profilVurguObj); (this.profilVurguObj as any).geometry?.dispose?.(); (this.profilVurguObj as any).material?.dispose?.(); this.profilVurguObj = null; }
  }

  // ---- CATIA YÜZ SEÇİMİ: draft/shell/removeFace SADECE seçili yüzlere (fillet kenar seçiminin yüz versiyonu) ----
  private yuzSecModu = false;
  private seciliYuzIdx = new Set<number>();
  private yuzVurguObj: THREE.Mesh | null = null;

  get yuzSecimAktif(): boolean { return this.yuzSecModu; }
  get seciliYuzSayisi(): number { return this.seciliYuzIdx.size; }
  private yuzSecBaslat(): void {
    this.yuzSecModu = true; this.seciliYuzIdx.clear(); this.yuzVurguTemizle(); this.hoverFaceId = -2;
    const f = this.taslakFeat;   // EKLE modu: mevcut seçili yüzleri koru (CATIA — listeye ekleme)
    if (f && FikoModellemeYonetici.SEC_YUZ_OP_TIPLER.has(f.tip)) this.rebuild();   // op'u geri al → sabit gövde üzerinde seç (faceId kaymaz)
    if (f?.secYuzler?.length) { for (const id of f.secYuzler) this.seciliYuzIdx.add(id); this.yuzVurgula(); }
  }
  yuzSecBitir(): void {
    const yuzOp = !!this.taslakFeat && FikoModellemeYonetici.SEC_YUZ_OP_TIPLER.has(this.taslakFeat.tip);
    this.yuzSecModu = false; this.yuzVurguTemizle(); this.hoverTemizle(); this.hoverFaceId = -2;
    if (yuzOp) { this.rebuild(); this.secimBildir(); }   // seçim sırasında atlanan op'u şimdi (mod kapalı) UYGULA → düzenleme/Tamam'da komut işlenir
  }

  /** İmlecin altındaki gövde yüzünü (faceId) seç/kaldır → canlı draft/shell önizleme + vurgu. */
  yuzPick(e: PointerEvent | MouseEvent): boolean {
    if (!this.yuzSecModu) return false;
    const faceId = this.yuzPickFaceId(e);
    if (faceId < 0) return false;
    if (this.seciliYuzIdx.has(faceId)) this.seciliYuzIdx.delete(faceId); else this.seciliYuzIdx.add(faceId);
    this.yuzSecGuncelle();
    return true;
  }
  private yuzSecGuncelle(): void {
    const feat = this.taslakFeat; if (!feat) return;
    feat.secYuzler = [...this.seciliYuzIdx];
    feat.secYuzNok = this.yuzMerkezleri(this.seciliYuzIdx);   // OCCT yüz eşleştirme için dünya merkezleri (fallback)
    // PERSISTENT NAMING: seçili faceId'lerin kalıcı stableId'sini SON rebuild kimliğinden yakala (kullanıcının
    // gördüğü gövdeyle aynı). Sonraki rebuild'lerde radius/op eklense de bu kimlik doğru yüze çözülür.
    feat.secYuzKimlik = feat.secYuzler.map(fid => fikoOCCT.kimlikYuzId(fid));
    this.rebuild();
    this.yuzVurgula();
    this.d.degisti?.(this.durum, this.sketch);
    this.secimBildir();   // açık seçim listelerini canlı tazele
  }
  /** Seçili faceId'lerin grup attribute'undan dünya merkezlerini hesaplar [x,y,z,...] (OCCT shell açık yüz için). */
  private yuzMerkezleri(faceIdSet: Set<number>): number[] {
    const gm = this.govdeMesh; if (!gm || !faceIdSet.size) return [];
    const grup = gm.geometry.attributes['grup'] as THREE.BufferAttribute | undefined;
    const pos = gm.geometry.attributes['position'] as THREE.BufferAttribute | undefined;
    if (!grup || !pos) return [];
    const acc = new Map<number, { x: number; y: number; z: number; n: number }>();
    for (let i = 0; i < grup.count; i++) {
      const id = grup.getZ(i); if (!faceIdSet.has(id)) continue;
      const a = acc.get(id) || { x: 0, y: 0, z: 0, n: 0 };
      a.x += pos.getX(i); a.y += pos.getY(i); a.z += pos.getZ(i); a.n++; acc.set(id, a);
    }
    const out: number[] = [];
    for (const a of acc.values()) if (a.n) out.push(a.x / a.n, a.y / a.n, a.z / a.n);
    return out;
  }
  /** Seçili yüzlerin geometrisini (grup attribute → o faceId üçgenleri) sarı highlight ile gösterir. */
  private yuzVurgula(): void {
    this.yuzVurguTemizle();
    if (!this.seciliYuzIdx.size) { this.d.ciz(); return; }
    const obj = this.yuzGorselMesh(fid => this.seciliYuzIdx.has(fid), 0xff8c1a, 0.5, 997);   // CATIA seçim: turuncu
    if (obj) { this.yuzVurguObj = obj; this.d.sahne.add(obj); }
    this.d.ciz();
  }
  private yuzVurguTemizle(): void {
    if (this.yuzVurguObj) { this.d.sahne.remove(this.yuzVurguObj); this.yuzVurguObj.geometry.dispose(); (this.yuzVurguObj.material as any)?.dispose?.(); this.yuzVurguObj = null; }
  }

  /** Part Design 3D gövdeye sağ-tık / uzun-bas → MEVCUT 3dHizliMenu ile CAM operasyon menüsü (geometriye göre). */
  govdeSagMenu(e: PointerEvent | MouseEvent): boolean {
    if (!this.govdeAltinda(e)) return false;
    const sf = () => this.sonDuzenlenebilirFeat();
    // CATIA: 3D gövdeye sağ-tık → feature düzenle/bastır/sil (+ varsa CAM)
    const menu: any[] = [
      { baslik: 'Düzenle (Pencere)', simge: 'sliders horizontal', renkSinif: 'blue', tiklandi: () => { const f = sf(); if (f) this.featureMenu(f); } },
      { baslik: 'Hızlı Ayar', simge: 'bolt', renkSinif: 'teal', tiklandi: () => { const f = sf(); if (f) this.featureHizliMenu(f); } },
      { tipi: 'Ayraç' },
      { baslik: 'Bastır / Aktifleştir', simge: 'ban', renkSinif: 'orange', tiklandi: () => { const f = sf(); if (f) this.featureBastirToggle(f); } },
      { baslik: 'Son Özelliği Sil', simge: 'trash alternate', renkSinif: 'red', tiklandi: () => { const f = sf(); if (f) this.featureSil(f); } },
    ];
    const camMenu = this.camOperasyonMenusu();
    if (camMenu.length) { menu.push({ tipi: 'Ayraç' }, ...camMenu); }
    // Sağ-tık → NORMAL context menü (sagMenuAc / itemMenu overlay). Hızlı3D menü AÇILMAZ (o uzun-bas içindir).
    const obj: any = {
      name: 'Katı (PartBody)',
      userData: { FikoArayuz: { simge: './simgeler/3d.webp' } },
      sagMenu: menu,
    };
    (this.d.sahne as any).secimler?.sagMenuAc(e, obj);
    return true;
  }

  /** Seçili geometriye göre CAM operasyon menüsü (CATIA: yazı→Gravür; ileride profil→Cep/Profil). */
  private camOperasyonMenusu(): any[] {
    const menu: any[] = [];
    for (const y of this.ozellikler.filter(f => f.tip === 'yazi3d')) {
      menu.push({ baslik: 'Gravür: ' + (y.metin || 'Yazı'), simge: 'cut', renkSinif: 'orange', tiklandi: () => this.yaziGravurAc(y) });
    }
    return menu;
  }

  /** İmlecin altında modelleme gövdesi var mı (raycast)? */
  private govdeAltinda(e: any): boolean {
    if (!this.govdeMesh) return false;
    const canvas = this.d.canvasAl(); const kamera = this.d.kameraAl();
    if (!canvas || !kamera) return false;
    const r = canvas.getBoundingClientRect();
    const ndc = new THREE.Vector2(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1);
    const rc = new THREE.Raycaster(); rc.setFromCamera(ndc, kamera as any);
    return rc.intersectObject(this.govdeMesh, true).length > 0;
  }

  /** Yazı feature'ı için fontu (asenkron) hazırlar → yüklenince önizlemeyi tazeler. */
  yaziFontHazirla(fontAd: string): void {
    if (fontHazirMi(fontAd)) return;
    fontYukle(fontAd).then(() => { if (this.taslakFeat?.tip === 'yazi3d' || this.ozellikler.some(f => f.tip === 'yazi3d')) this.rebuild(); });
  }

  private sonSketchFeatureIndex(): number {
    for (let i = this.ozellikler.length - 1; i >= 0; i--)
      if (['pad', 'pocket', 'shaft', 'groove'].includes(this.ozellikler[i].tip)) return i;
    return -1;
  }

  /** Tüm feature geçmişini sıfırdan yeniden hesaplar (parametrik rebuild). */
  govdeGrafik: IGrafikOzellik = {};   // CATIA: PartBody (katı) body-level grafik özellikleri
  /** CATIA Graphics Properties: mesh material'ına görsel özellikleri uygula (renk/opaklık/parlaklık/görünüm/görünürlük). */
  grafikUygula(mesh: THREE.Mesh | null | undefined, grafik?: IGrafikOzellik, varsayilanRenk?: number): void {
    if (!mesh) return; const m = mesh.material as any; if (!m) return;
    if (grafik?.renk != null) m.color?.setHex(grafik.renk);
    else if (varsayilanRenk != null) m.color?.setHex(varsayilanRenk);
    if (grafik?.opaklik != null) { m.opacity = grafik.opaklik; m.transparent = grafik.opaklik < 0.999; }
    if (m.isMeshStandardMaterial) {
      if (grafik?.metalik != null) m.metalness = grafik.metalik;
      if (grafik?.puruz != null) m.roughness = grafik.puruz;
    }
    m.wireframe = grafik?.gorunum === 'tel';
    mesh.visible = !grafik?.gizli;
    m.needsUpdate = true;
  }
  /** Dialog'dan: bir feature'ın (veya gövdenin, feat=null) grafik özelliğini güncelle → canlı yeniden uygula. */
  grafikGuncelle(feat: IOzellik | null, anahtar: keyof IGrafikOzellik, deger: any): void {
    const g = feat ? (feat.grafik ||= {}) : this.govdeGrafik;
    (g as any)[anahtar] = deger;
    if (feat) this.rebuild();
    else if (anahtar === 'kenarKalinlik' || anahtar === 'kenarRenk') this.kenarGuncelle();   // kenar = LineSegments2 fat-line yeniden kur
    else this.grafikUygula(this.govdeMesh, this.govdeGrafik, this.aktifMalzeme?.renk);
    this.d.ciz?.(); this.d.agacGuncelle?.();
  }
  /** CATIA Graphics Properties penceresini aç (feat=null → gövde/PartBody). */
  grafikDialogAc(feat: IOzellik | null): void {
    const df = (this.d.sahne as any).anaGosterim?.DialogFiko;
    if (df?.ac) df.ac({ bilesen: FikoGrafikDialog, parametreler: { element: feat, yonetici: this }, ayarlar: { baslik: 'Grafik Özellikleri', simgeResim: './simgeler/3d.webp' } });
  }
  grafikSifirla(feat: IOzellik | null): void {
    if (feat) { feat.grafik = {}; this.rebuild(); } else { this.govdeGrafik = {}; this.grafikUygula(this.govdeMesh, {}, this.aktifMalzeme?.renk); this.kenarGuncelle(); }
    this.d.ciz?.(); this.d.agacGuncelle?.();
  }
  /** CATIA View > Render Style: tüm sahne görünüm modu (geçici, grafik özelliğini değiştirmez). */
  sahneGorunumModu(mod: 'dolu' | 'tel' | 'seffaf'): void {
    const meshler = [this.govdeMesh, ...(this.yuzeyMeshler || []), ...(this.telMeshler || [])].filter(Boolean) as THREE.Mesh[];
    for (const me of meshler) {
      const mat: any = me.material; if (!mat) continue;
      if (mod === 'tel') { mat.wireframe = true; mat.transparent = false; mat.opacity = 1; }
      else if (mod === 'seffaf') { mat.wireframe = false; mat.transparent = true; mat.opacity = 0.35; }
      else { mat.wireframe = false; mat.transparent = (mat.opacity ?? 1) < 0.999; }
      mat.needsUpdate = true;
    }
    if (this.govdeKenar) this.govdeKenar.visible = mod !== 'tel';
    this.d.ciz?.();
  }
  /** Çoklu (toplu) grafik: gövde + tüm yüzey/wireframe feature'lara aynı grafik özelliği uygula. */
  grafikTumuneUygula(grafik: IGrafikOzellik): void {
    Object.assign(this.govdeGrafik, grafik);
    for (const f of this.ozellikler) {
      if (['yuzey', 'yuzeyJoin', 'yuzeyTrim', 'yuzeyExtract', 'yuzeyBlend', 'yuzeyExtrapolate', 'nokta3d', 'cizgi3d', 'cember3d', 'spline3d', 'helix3d', 'spiral3d', 'duzlem3d', 'conic3d', 'paralelEgri', 'connectEgri'].includes(f.tip)) {
        f.grafik = { ...(f.grafik || {}), ...grafik };
      }
    }
    this.rebuild(); this.grafikUygula(this.govdeMesh, this.govdeGrafik, this.aktifMalzeme?.renk);
    this.d.ciz?.(); this.d.agacGuncelle?.();
  }
  private _sonFeatSayisi = 0;
  /** Kamerayı son geometriye sığdır — MEVCUT bakış yönünü KORUR (yalnız zoom/merkez), açıyı döndürmez. */
  kameraSigdir(): void {
    try {
      const kam: any = this.d.kameraAl(); if (!kam) return;
      (this.d.sahne as any)?.updateMatrixWorld?.(true);
      // TÜM modelleme içeriğini kapsa: gövde + makrolar + yüzeyler + tel-kafes (nokta/çizgi/eğri)
      const box = new THREE.Box3(); const tmp = new THREE.Box3();
      const hedefler: any[] = [];
      if (this.govdeMesh) hedefler.push(this.govdeMesh);
      (this.makroMeshler || []).forEach(o => hedefler.push(o));
      (this.yuzeyMeshler || []).forEach(o => hedefler.push(o));
      (this.telMeshler || []).forEach(o => hedefler.push(o));
      for (const o of hedefler) { try { tmp.setFromObject(o); if (!tmp.isEmpty()) box.union(tmp); } catch (e) { } }
      if (box.isEmpty()) return;
      const merkez = box.getCenter(new THREE.Vector3());
      const r = Math.max(box.getSize(new THREE.Vector3()).length() * 0.5, 8);
      const ctrl: any = this.d.kontrolAl?.() || (this.d as any).controls || (this.d as any).orbit;
      // Mevcut bakış yönünü koru — yön sıfırlama YOK (kullanıcının açısı bozulmaz)
      const eskiHedef: THREE.Vector3 = ctrl?.target ? ctrl.target.clone() : merkez.clone();
      const yon = kam.position.clone().sub(eskiHedef);
      if (yon.lengthSq() < 1e-6) yon.set(1, -1, 0.8);   // ilk açılış için makul varsayılan
      yon.normalize();
      kam.position.copy(merkez).addScaledVector(yon, r * 1.6);
      if (kam.isPerspectiveCamera) { kam.near = Math.max(0.1, r * 0.05); kam.far = r * 60; kam.updateProjectionMatrix?.(); }
      kam.lookAt(merkez);
      if (ctrl?.target) { ctrl.target.copy(merkez); ctrl.update?.(); }
      this.d.ciz?.();
    } catch (e) { }
  }
  private rebuild(baslangicIndex: number = 0): void {
    this.intrinsikSenkron();
    this.parametreCozUygula();
    if (!this.occtModu && this.occtUyumluMu() && fikoOCCT.hazirMi()) this.occtModu = true;   // OCCT hazırsa B-Rep modu (kaydet/aç sonrası Part Design B-Rep'i mesh-CSG'ye düşmesin)
    const ilkFeat = this._sonFeatSayisi === 0 && this.ozellikler.length > 0;   // SADECE boş sahneye ilk feature → odakla (sonrakilerde kullanıcı açısı korunur)
    this._sonFeatSayisi = this.ozellikler.length;
    if (ilkFeat && !this._gecmisKilit) [350, 900].forEach(ms => setTimeout(() => this.kameraSigdir(), ms));   // occt async — mesh hazır olunca bir kez odakla
    if (this.occtModu) { this.rebuildOCCT(); return; }   // GERÇEK kernel modu (async, arka planda) — mesh-CSG'ye dokunmaz
    let govdeGeo: THREE.BufferGeometry | null = null;
    for (let i = 0; i < this.ozellikler.length; i++) {
      const f = this.ozellikler[i];
      if (f.bastirilmis) { this.featureDurumGuncelle(f); continue; }   // suppress: üretime girmez
      try {
        if (f.tip === 'fillet3d' || f.tip === 'chamfer3d') {
          if (!govdeGeo) continue;
          const calis = this.kati.finalize(govdeGeo.clone());   // kenar çıkarımı için pürüzsüz/grup'lu kopya
          // secKenarlar TANIMLIYSA seçim-modu (boş→hiç, dolu→o kenarlar); undefined→tüm kenarlar (eski/geriye uyumlu)
          const secNok = f.secKenarlar !== undefined
            ? Array.from({ length: Math.floor(f.secKenarlar.length / 3) }, (_, i) => new THREE.Vector3(f.secKenarlar![i * 3], f.secKenarlar![i * 3 + 1], f.secKenarlar![i * 3 + 2]))
            : undefined;
          // B-Rep topo imzası (varsa): merkez+yön+uzunluk skoruyla kenar eşleştir (parametrik-dayanıklı link)
          const secImza = (f.secKenarImza && f.secKenarImza.length >= 7)
            ? Array.from({ length: Math.floor(f.secKenarImza.length / 7) }, (_, i) => {
                const o = i * 7, a = f.secKenarImza!;
                return { merkez: [a[o], a[o + 1], a[o + 2]] as [number, number, number], yon: [a[o + 3], a[o + 4], a[o + 5]] as [number, number, number], olcu: a[o + 6] };
              })
            : undefined;
          const sonuc = this.kenarIslem.uygula(calis, f.tip === 'fillet3d' ? 'fillet' : 'chamfer', f.kenarMesafe ?? 4, f.kenarAci ?? 30, secNok, 1.5, secImza);
          calis.dispose();
          if (sonuc) { govdeGeo.dispose(); govdeGeo = sonuc; }    // başarısızsa gövdeyi koru
        } else if (f.tip === 'mirror') {
          if (!govdeGeo) continue;
          const m = this.aynaGeo(govdeGeo, f.aynaTip!);
          const r = this.kati.birlestirHam(govdeGeo, m); m.dispose(); govdeGeo.dispose(); govdeGeo = r;
        } else if (f.tip === 'shell') {
          if (!govdeGeo) continue;
          // GERÇEK SHELL: vertex-normal offset iç gövde; secYuzler verilmişse o yüzler AÇIK kabuk
          let acikNok: Set<string> | undefined;
          if (f.secYuzler?.length) {
            if (!govdeGeo.attributes['grup']) { const tmp = this.kati.finalize(govdeGeo); govdeGeo.dispose(); govdeGeo = tmp; }
            acikNok = this.yuzNoktaSeti(govdeGeo, f.secYuzler);
          }
          const ic = this.kati.kabukIcGeoGercek(govdeGeo, f.kalinlik || 4, acikNok);
          if (ic) { const r = this.kati.cikarHam(govdeGeo, ic); ic.dispose(); govdeGeo.dispose(); govdeGeo = r; }
        } else if (f.tip === 'draft') {
          if (!govdeGeo) continue;
          // seçili yüz (secYuzler) için faceId gerekli → grup yoksa geçici finalize (CATIA yüz-bazlı draft)
          if (f.secYuzler?.length && !govdeGeo.attributes['grup']) { const tmp = this.kati.finalize(govdeGeo); govdeGeo.dispose(); govdeGeo = tmp; }
          this.draftUygula(govdeGeo, f);   // vertex deformasyonu (yerinde, CSG yok)
        } else if (f.tip === 'patternIzgara') {
          if (!govdeGeo || f.hedefIndex == null) continue;
          const hedef = this.ozellikler[f.hedefIndex];
          const tool0 = this.toolGeo(hedef); if (!tool0) continue;
          const dU = hedef.duzlem?.eksenU || new THREE.Vector3(1, 0, 0);
          const dV = hedef.duzlem?.eksenV || new THREE.Vector3(0, 1, 0);
          for (let iu = 0; iu < (f.adet || 1); iu++) for (let iv = 0; iv < (f.adet2 || 1); iv++) {
            if (iu === 0 && iv === 0) continue;
            const t = tool0.clone();
            const ou = (f.aralik || 0) * iu, ov = (f.aralik2 || 0) * iv;
            t.translate(dU.x * ou + dV.x * ov, dU.y * ou + dV.y * ov, dU.z * ou + dV.z * ov);
            const r = f.ekleme ? this.kati.birlestirHam(govdeGeo, t) : this.kati.cikarHam(govdeGeo, t);
            t.dispose(); govdeGeo.dispose(); govdeGeo = r;
          }
          tool0.dispose();
        } else if (f.tip === 'patternDik' || f.tip === 'patternDairesel') {
          if (!govdeGeo || f.hedefIndex == null) continue;
          const hedef = this.ozellikler[f.hedefIndex];
          const tool0 = this.toolGeo(hedef); if (!tool0) continue;
          for (let k = 1; k < (f.adet || 1); k++) {
            const t = tool0.clone();
            if (f.tip === 'patternDik') {
              const dir = hedef.duzlem?.eksenU || new THREE.Vector3(1, 0, 0);
              t.translate(dir.x * (f.aralik || 0) * k, dir.y * (f.aralik || 0) * k, dir.z * (f.aralik || 0) * k);
            } else {
              const eks = hedef.duzlem?.normal || new THREE.Vector3(0, 0, 1);
              const o = hedef.duzlem?.origin || new THREE.Vector3();
              const mm = new THREE.Matrix4().makeTranslation(o.x, o.y, o.z)
                .multiply(new THREE.Matrix4().makeRotationAxis(eks, (f.aci || 0) * Math.PI / 180 * k))
                .multiply(new THREE.Matrix4().makeTranslation(-o.x, -o.y, -o.z));
              t.applyMatrix4(mm);
            }
            const r = f.ekleme ? this.kati.birlestirHam(govdeGeo, t) : this.kati.cikarHam(govdeGeo, t);
            t.dispose(); govdeGeo.dispose(); govdeGeo = r;
          }
          tool0.dispose();
        } else {
          const tool = this.toolGeo(f); if (!tool) continue;
          if (!govdeGeo) { govdeGeo = tool; }   // ilk feature: ham (finalize döngü sonunda)
          else { const r = f.ekleme ? this.kati.birlestirHam(govdeGeo, tool) : this.kati.cikarHam(govdeGeo, tool); tool.dispose(); govdeGeo.dispose(); govdeGeo = r; }
        }
        f.hata = false; this.featureDurumGuncelle(f);
      } catch (e) { console.warn('[Modelleme] rebuild feature hatası', f.tip, e); f.hata = true; this.featureDurumGuncelle(f); }
    }

    // TEK SEFER pürüzsüz normal + yüz gruplama (zincir/pattern boyunca tekrar tekrar değil)
    if (govdeGeo) { try { govdeGeo = this.kati.finalize(govdeGeo); } catch (e) { console.warn('[Modelleme] finalize hatası', e); } }

    if (!govdeGeo) {
      if (this.govdeMesh) { (this.govdeMesh.parent||this.d.sahne).remove(this.govdeMesh); this.govdeMesh.geometry.dispose(); this.govdeMesh = null; }
      this.d.ciz(); return;
    }
    this.govdeMeshKur(govdeGeo);
  }

  // ---- GERÇEK B-Rep KERNEL (OpenCascade.js) — kusursuz fillet/shell/chamfer ----
  private occtHazirMi = false;
  private occtModu = false;
  /** OCCT B-Rep rebuild'in desteklediği feature tipleri (diğerleri mesh-only: mirror/pattern/makro/yazi3d/rib). */
  private static OCCT_DESTEKLI = new Set(['primitif', 'pad', 'pocket', 'shaft', 'groove', 'hole', 'loft', 'sweep', 'slot', 'combine', 'rib', 'fillet3d', 'chamfer3d', 'shell', 'thickness', 'split', 'yuzey', 'yuzeyJoin', 'yuzeyTrim', 'yuzeyExtract', 'yuzeyBlend', 'yuzeyExtrapolate', 'thickSurface', 'splitSurface', 'nokta3d', 'cizgi3d', 'cember3d', 'spline3d', 'polyline', 'curveSmooth', 'corner', 'isoparametrik', 'combine3d', 'equationCurve', 'spine3d', 'helix3d', 'duzlem3d', 'spiral3d', 'paralelEgri', 'connectEgri', 'yuzeyKenar', 'conic3d', 'kesisim3d', 'draft', 'mirror', 'symmetry', 'translate', 'rotate', 'scaling', 'patternDik', 'patternDairesel', 'patternIzgara', 'userPattern', 'stiffener', 'closeSurface', 'removeLump', 'removeFace', 'faceFillet', 'triFillet', 'replaceFace', 'healing', 'simplify', 'untrim', 'multiExtract', 'disassemble', 'affinity', 'axisToAxis', 'offsetShape', 'pipe3d', 'makro']);
  /** Gövde OCCT'ye geçebilir mi (tüm özellikler desteklenir veya import B-Rep var). */
  private occtUyumluMu(): boolean { return !!this.occtBazShape || this.ozellikler.every(f => FikoModellemeYonetici.OCCT_DESTEKLI.has(f.tip)); }
  private occtBazShape: any = null;   // import edilen STEP'in GERÇEK B-Rep gövdesi (pocket/fillet bunun üstüne)
  private occtBazStepBytes: Uint8Array | null = null;   // CATIA "Result of import": import B-Rep'i kalıcı sakla (kaydet/aç → birebir geri yüklenir)
  get occtModuAktif(): boolean { return this.occtModu; }
  get disGovdeVar(): boolean { return !!this.occtBazShape; }

  /**
   * KUSURSUZ STEP IMPORT: STEP dosyasını GERÇEK B-Rep shape olarak okur → modelleme baz gövdesi yapar.
   * Artık üstüne sketch+pocket/pad/fillet/shell uygulanabilir (occt-import-js mesh DEĞİL — kusursuz).
   */
  async disStepAl(bytes: Uint8Array): Promise<boolean> {
    if (!await this.occtYukle()) return false;
    try {
      const shape = await fikoOCCT.stepOku(bytes);
      if (!shape) { console.warn('[OCCT] STEP okunamadı'); return false; }
      this.parametrikTemizle();
      this.modBaslat();
      this.occtBazShape = shape;
      this.occtBazStepBytes = bytes.slice();   // kalıcılık için ham STEP'i sakla (kaydet → birebir geri yüklenir)
      this.occtModu = true;          // B-Rep gövde → kusursuz kernel zorunlu
      this.rebuild();                // rebuildOCCT bazShape'i tessellate eder
      this.ithalGovdeNodeKur();      // CATIA: ürün ağacında "İçe Aktarılan Gövde" öğesi
      this.d.degisti?.(this.durum, this.sketch);
      return true;
    } catch (e) { console.warn('[OCCT] disStepAl hatası', e); return false; }
  }

  /** Aktif OCCT gövdesini (modelleme sonucu) STEP olarak dışa aktarır (kusursuz B-Rep export). */
  async stepDisaAktar(): Promise<Uint8Array | null> {
    if (!this.occtSonShapeRef) return null;
    try { return await fikoOCCT.stepYaz(this.occtSonShapeRef); } catch (e) { console.warn('[OCCT] export hatası', e); return null; }
  }
  private occtSonShapeRef: any = null;   // rebuildOCCT'nin ürettiği son B-Rep shape (export için)
  private _occtIslemBasarisiz: '' | 'fillet' | 'chamfer' = '';   // fillet/chamfer OCCT'de başarısız oldu mu (yarıçap çok büyük) → kullanıcı uyarısı
  private _sonBasarisizUyari = '';   // aynı uyarıyı canlı önizlemede tekrar tekrar gösterme (debounce)

  // ───────── B-REP KÖPRÜSÜ (CATIA modeli: mesh sadece görsel, hesap B-Rep) ─────────
  /** Aktif gövdenin canlı OCCT TopoDS_Shape'i (modelleme/STEP-import). null → B-Rep yok (sadece mesh). */
  get brepShape(): any { return this.occtSonShapeRef || this.occtBazShape || null; }
  brepVarMi(): boolean { return !!this.brepShape; }
  /** Mesh grup.z=faceId → o yüzün GERÇEK B-Rep dış kontur segmentleri (çizgi/yay analitik). B-Rep yoksa null → çağıran mesh'e düşer. */
  yuzBrepKontur(faceId: number): { segmentler: any[]; kapali: boolean } | null {
    const s = this.brepShape; if (s == null || faceId == null || faceId < 0) return null;
    try { return fikoOCCT.yuzKonturBrep(s, faceId); } catch (e) { console.warn('[OCCT] yuzBrepKontur hatası', e); return null; }
  }
  /** Ölçüm için GERÇEK analitik snap noktaları (köşe/merkez/orta + yarıçap) — tessellation değil. B-Rep yoksa boş. */
  brepSnapNoktalari(): { tip: 'vertex' | 'merkez' | 'orta'; x: number; y: number; z: number; r?: number }[] {
    const s = this.brepShape; if (!s) return [];
    try { return fikoOCCT.brepSnapNoktalari(s); } catch (e) { console.warn('[OCCT] brepSnapNoktalari hatası', e); return []; }
  }
  /** CATIA Dynamic Section: düzlemle GERÇEK B-Rep kesit profili (BRepAlgoAPI_Section) — analitik kenarlar (curveType/center/radius). B-Rep yoksa null → mesh clipping. */
  kesitProfiliBrep(origin: { x: number; y: number; z: number }, normal: { x: number; y: number; z: number }): any[] | null {
    const s = this.brepShape; if (!s) return null;
    try { const e = fikoOCCT.kesitKenarlari(s, origin, normal); return e && e.length ? e : null; } catch (err) { console.warn('[OCCT] kesitProfiliBrep hatası', err); return null; }
  }
  /** CATIA Teknik Resim HLR (OCCT HLRBRep bu build'de YOK → kendi HLR'imiz): analitik B-Rep kenarlarını görünüş yönüne projekte +
   * mesh occlusion (raycast) ile görünür(düz)/gizli(kesik) ayırır. yon = bakış doğrultusu (örn {0,0,-1} üstten). 2D segmentler döndürür. */
  hlrTeknikResim(yon: { x: number; y: number; z: number } = { x: 0, y: 0, z: -1 }): { gorunur: { x: number; y: number }[][]; gizli: { x: number; y: number }[][] } | null {
    const s = this.brepShape; const gm = this.govdeMesh; if (!s || !gm) return null;
    let edges: any[] = []; try { edges = fikoOCCT.shapeToEdges(s) || []; } catch { } if (!edges.length) return null;
    const d = new THREE.Vector3(yon.x, yon.y, yon.z).normalize();
    const up = Math.abs(d.z) < 0.9 ? new THREE.Vector3(0, 0, 1) : new THREE.Vector3(0, 1, 0);
    const u = new THREE.Vector3().crossVectors(up, d).normalize();
    const v = new THREE.Vector3().crossVectors(d, u).normalize();
    gm.geometry.computeBoundingBox?.(); const bb = gm.geometry.boundingBox;
    const diag = bb ? bb.min.distanceTo(bb.max) : 1000; const BIG = diag * 2 + 100; const eps = Math.max(0.02, diag * 0.0006);
    const rc = new THREE.Raycaster(); const _o = new THREE.Vector3(), _p = new THREE.Vector3();
    const gizliMi = (p: any): boolean => {
      _o.set(p.x - d.x * BIG, p.y - d.y * BIG, p.z - d.z * BIG); rc.set(_o, d);
      const hit = rc.intersectObject(gm, true)[0]; if (!hit) return false;   // ışın geçti → dış hat (görünür)
      return hit.distance < BIG - eps;   // P'den ÖNCE yüzey → gizli
    };
    const prj = (p: any) => { _p.set(p.x, p.y, p.z); return { x: _p.dot(u), y: _p.dot(v) }; };
    const gorunur: any[][] = [], gizli: any[][] = [];
    for (const e of edges) {
      const pts = e?.pts; if (!pts || pts.length < 2) continue;
      let cur: any[] = [], curGizli: boolean | null = null;
      for (const p of pts) {
        const g = gizliMi(p), pr = prj(p);
        if (curGizli === null) { curGizli = g; cur = [pr]; }
        else if (g === curGizli) cur.push(pr);
        else { if (cur.length >= 2) (curGizli ? gizli : gorunur).push(cur); cur = [pr]; curGizli = g; }
      }
      if (cur.length >= 2) (curGizli ? gizli : gorunur).push(cur);
    }
    return { gorunur, gizli };
  }

  /** HLR (3D varyant): kenarları görünür/gizli ayırır ama 3D WORLD noktaları döndürür — teknik resim modu kendi projeksiyon/ölçeğini uygular (koordinat uyumu). */
  hlrSegmentler3D(yon: { x: number; y: number; z: number } = { x: 0, y: 0, z: -1 }): { gorunur: { x: number; y: number; z: number }[][]; gizli: { x: number; y: number; z: number }[][] } | null {
    const s = this.brepShape; const gm = this.govdeMesh; if (!s || !gm) return null;
    let edges: any[] = []; try { edges = fikoOCCT.shapeToEdges(s) || []; } catch { } if (!edges.length) return null;
    const d = new THREE.Vector3(yon.x, yon.y, yon.z).normalize();
    gm.geometry.computeBoundingBox?.(); const bb = gm.geometry.boundingBox;
    const diag = bb ? bb.min.distanceTo(bb.max) : 1000; const BIG = diag * 2 + 100; const eps = Math.max(0.02, diag * 0.0006);
    const rc = new THREE.Raycaster(); const _o = new THREE.Vector3();
    const gizliMi = (p: any): boolean => {
      _o.set(p.x - d.x * BIG, p.y - d.y * BIG, p.z - d.z * BIG); rc.set(_o, d);
      const hit = rc.intersectObject(gm, true)[0]; if (!hit) return false;
      return hit.distance < BIG - eps;
    };
    const gorunur: any[][] = [], gizli: any[][] = [];
    for (const e of edges) {
      const pts = e?.pts; if (!pts || pts.length < 2) continue;
      let cur: any[] = [], curGizli: boolean | null = null;
      for (const p of pts) {
        const g = gizliMi(p), np = { x: p.x, y: p.y, z: p.z };
        if (curGizli === null) { curGizli = g; cur = [np]; }
        else if (g === curGizli) cur.push(np);
        else { if (cur.length >= 2) (curGizli ? gizli : gorunur).push(cur); cur = [np]; curGizli = g; }
      }
      if (cur.length >= 2) (curGizli ? gizli : gorunur).push(cur);
    }
    return { gorunur, gizli };
  }

  // ───────── CATIA "3D Accuracy" — gösterim mesh kalitesi (B-Rep'ten yeniden tessellate) ─────────
  private static readonly DISPLAY_KALITE = {
    kaba:    { oran: 0.012,  aci: 1.0 },    // hızlı, az üçgen
    orta:    { oran: 0.004,  aci: 0.5 },    // varsayılan (mevcut davranış)
    ince:    { oran: 0.0015, aci: 0.3 },
    cokInce: { oran: 0.0006, aci: 0.15 },   // pürüzsüz, çok üçgen
  } as const;
  displayKaliteSeviye: 'kaba' | 'orta' | 'ince' | 'cokInce' = 'orta';
  /** Sayısal override (CATIA "sag" oranı). null → preset (displayKaliteSeviye) kullanılır. */
  displayKaliteOran: number | null = null;
  static readonly KALITE_ORAN_MIN = 0.0002;
  static readonly KALITE_ORAN_MAX = 0.02;
  get displayKalite(): { oran: number; aci: number } {
    if (this.displayKaliteOran != null) {
      const oran = this.displayKaliteOran;
      const aci = Math.min(1.2, Math.max(0.1, Math.sqrt(oran) * 9));   // orana göre açısal sapma türet
      return { oran, aci };
    }
    return FikoModellemeYonetici.DISPLAY_KALITE[this.displayKaliteSeviye];
  }
  /** CATIA 3D Accuracy: gösterim kalitesini değiştir + mevcut OCCT gövdeyi YENİDEN TESSELLATE et (B-Rep yeniden hesaplanmaz — hızlı). */
  displayKaliteAyarla(seviye: 'kaba' | 'orta' | 'ince' | 'cokInce'): boolean {
    if (!FikoModellemeYonetici.DISPLAY_KALITE[seviye]) return false;
    this.displayKaliteSeviye = seviye;
    this.displayKaliteOran = null;   // preset seçildi → sayısal override kalksın
    return this.yenidenGoster();
  }
  /** CATIA 3D Accuracy: sayısal oran (küçük=hassas). Slider/input için. */
  displayKaliteSayisalAyarla(oran: number): boolean {
    if (!(oran > 0)) return false;
    this.displayKaliteOran = Math.min(FikoModellemeYonetici.KALITE_ORAN_MAX, Math.max(FikoModellemeYonetici.KALITE_ORAN_MIN, oran));
    return this.yenidenGoster();
  }
  /** Aktif OCCT gövdeyi mevcut displayKalite ile yeniden tessellate eder (B-Rep korunur, sadece mesh yenilenir). */
  yenidenGoster(): boolean {
    const s = this.brepShape; if (!s) return false;
    try {
      const k = this.displayKalite;
      const geo = fikoOCCT.shapeToGeo(s, 0.05, k.aci, k.oran);
      if (!geo) return false;
      if (!(geo as any)._occtFaceGruplu) this.kati.yuzGrupla(geo);
      this.govdeMeshKur(geo); this.d.ciz();
      return true;
    } catch (e) { console.warn('[OCCT] yenidenGoster hatası', e); return false; }
  }

  /** OCCT kernel'i yükler (lazy, ~3-8sn ilk). */
  async occtYukle(): Promise<boolean> {
    try { await fikoOCCT.init(); this.occtHazirMi = true; return true; }
    catch (e) { console.warn('[OCCT] yüklenemedi', e); return false; }
  }

  /** Kernel modu: mesh-CSG (hızlı/yaklaşık) ↔ OCCT (kusursuz B-Rep). Mevcut feature'lar korunur, yeniden hesaplanır. */
  async occtModuToggle(): Promise<boolean> {
    if (!this.occtModu) { if (!await this.occtYukle()) return false; }
    this.occtModu = !this.occtModu;
    this.rebuild();
    this.d.degisti?.(this.durum, this.sketch);
    return this.occtModu;
  }

  /** Feature'ın profil dünya-uzayı kapalı kontur noktaları (sketch elemanlar → düzlem → 3D). */
  /** Sketch 2D noktalarını düzlem üzerinden 3D dünya koordinatına taşır. */
  private noktaDunya(pts: THREE.Vector2[], d: ISketchDuzlem): { x: number; y: number; z: number }[] {
    return pts.map(p => ({
      x: d.origin.x + d.eksenU.x * p.x + d.eksenV.x * p.y,
      y: d.origin.y + d.eksenU.y * p.x + d.eksenV.y * p.y,
      z: d.origin.z + d.eksenU.z * p.x + d.eksenV.z * p.y,
    }));
  }

  /** Profil → dış kontur (CCW) + iç delikler (CW), 3D dünya koord. OCCT delikli prism için. */
  private occtProfilDunya(f: IOzellik): { dis: { x: number; y: number; z: number }[]; delikler: { x: number; y: number; z: number }[][] } | null {
    if (!f.elemanlar || !f.duzlem) return null;
    const loops = this.looplar(f.elemanlar);
    if (!loops.length) return null;
    loops.sort((a, b) => b.alan - a.alan);   // en büyük = dış kontur
    const d = f.duzlem;
    const disPts = loops[0].pts.slice();
    if (this.poliAlan(disPts) < 0) disPts.reverse();   // dış CCW
    const dis = this.noktaDunya(disPts, d);
    const delikler: { x: number; y: number; z: number }[][] = [];
    for (let i = 1; i < loops.length; i++) {
      if (!this.icerdeMi(loops[i].pts[0], loops[0].pts)) continue;   // dış kontur içinde değilse delik değil
      const hp = loops[i].pts.slice();
      if (this.poliAlan(hp) > 0) hp.reverse();   // delik CW (dışın tersi)
      delikler.push(this.noktaDunya(hp, d));
    }
    return { dis, delikler };
  }

  /** Gövde kapsam ölçüsü (through-all uzunluğu için). */
  private govdeKapsam(): number {
    const m = this.govdeMesh; if (!m) return 500;
    m.geometry.computeBoundingBox(); const b = m.geometry.boundingBox; if (!b) return 500;
    return Math.max(b.max.x - b.min.x, b.max.y - b.min.y, b.max.z - b.min.z) * 2.2 + 50;
  }

  /** Sketch'in dolu bölgeleri (even-odd domain'ler) — her biri dış kontur + iç delikler (Multi-Pad domain'leri). */
  private padDomainlar(els: any[]): { dis: THREE.Vector2[]; delikler: THREE.Vector2[][] }[] {
    const loops = this.looplar(els); if (!loops.length) return [];
    const derinlik = loops.map((L, i) => loops.filter((O, j) => j !== i && O.alan > L.alan && this.icerdeMi(L.pts[0], O.pts)).length);
    const out: { dis: THREE.Vector2[]; delikler: THREE.Vector2[][] }[] = [];
    loops.forEach((L, i) => {
      if (derinlik[i] % 2 !== 0) return;   // tek derinlik = delik
      const dis = L.pts.slice(); if (this.poliAlan(dis) < 0) dis.reverse();
      const delikler: THREE.Vector2[][] = [];
      loops.forEach((H, j) => { if (i !== j && derinlik[j] === derinlik[i] + 1 && this.icerdeMi(H.pts[0], L.pts)) { const hp = H.pts.slice(); if (this.poliAlan(hp) > 0) hp.reverse(); delikler.push(hp); } });
      out.push({ dis, delikler });
    });
    return out;
  }

  /** Pad profilindeki dolu bölge (domain) sayısı — Multi-Pad tablosu için. */
  padDomainSayisi(feat: IOzellik): number { return feat?.elemanlar ? this.padDomainlar(feat.elemanlar).length : 0; }

  /** CATIA Pad/Pocket geometri motoru — TÜM bölgeler (Multi-Pad: her bölge ayrı boy) + tüm seçenekler. */
  /** Profil GENUINELY açık mı (kapalı standalone eleman yok + zincir uçları ayrı)? CATIA s29 thin duvar için. */
  private profilAcikMi(els: any[]): boolean {
    if ((els || []).some(e => !e.yapi && (['cember', 'dikdortgen', 'cokgen', 'slot', 'elips'].includes(e.tip) || (e.tip === 'spline' && e.kapali)))) return false;
    const z = this.acikZincir2D(els); if (z.length < 2) return false;
    return Math.hypot(z[0].x - z[z.length - 1].x, z[0].y - z[z.length - 1].y) > 0.8;
  }

  private occtPadShape(f: IOzellik): any {
    if (!f.elemanlar || !f.duzlem) return null;
    if (f.incePad && this.profilAcikMi(f.elemanlar)) return this.occtAcikProfilDuvar(f);   // CATIA s29: AÇIK profil + thin → çizgi-boyu ince duvar
    const domains = this.padDomainlar(f.elemanlar);
    if (!domains.length) { if (f.incePad) return this.occtAcikProfilDuvar(f); return null; }
    domains.sort((a, b) => Math.abs(this.poliAlan(b.dis)) - Math.abs(this.poliAlan(a.dis)));   // büyük → küçük
    const cokBoy = domains.length > 1 && !!f.domainBoy && f.domainBoy.some(b => b != null && Math.abs((b as number) - (f.param ?? 20)) > 0.01);
    let shape: any = null;
    for (let i = 0; i < domains.length; i++) {
      if (f.domainKapali?.[i]) continue;   // Profile Definition Sub: bu bölge hariç
      const h1 = cokBoy ? ((f.domainBoy![i] ?? f.param ?? 20)) : (f.param ?? 20);
      const s = this.occtBolgePrism(domains[i], f, h1);
      if (s) shape = shape ? fikoOCCT.birlestir(shape, s) : s;
    }
    return shape;
  }

  /** Tek bölgeyi (dış+delik) verilen h1 boyuyla prism yap — thin/yön/simetri/second-limit seçenekleriyle. */
  private occtBolgePrism(domain: { dis: THREE.Vector2[]; delikler: THREE.Vector2[][] }, f: IOzellik, h1Boyut: number): any {
    const d = f.duzlem!;
    let disP = domain.dis.slice();
    let delikP: THREE.Vector2[][] = domain.delikler.map(h => h.slice());
    // THIN PAD (CATIA Thin Solid): Thickness1 (içe +k1) + Thickness2 (dışa -k2) + Neutral Fiber (eşit böl)
    if (f.incePad) {
      const orijinal = disP.map(p => ({ x: p.x, y: p.y }));
      let k1 = Math.max(0, f.padKal1 ?? 2);   // içe
      let k2 = Math.max(0, f.padKal2 ?? 0);   // dışa
      if (f.notrFiber) { const t = k1 + (k2 || k1); k1 = t / 2; k2 = t / 2; }   // profili ortala (eşit iki taraf)
      // dış kontur = orijinal DIŞA offset (-k2)
      if (k2 > 0.01) { const o = this.offsetPolygon(orijinal, -k2, true); if (o && o.length >= 3) disP = o.map(p => new THREE.Vector2(p.x, p.y)); }
      // iç delik = orijinal İÇE offset (+k1), CW
      delikP = [];
      if (k1 > 0.01) {
        const ic = this.offsetPolygon(orijinal, k1, true);
        if (ic && ic.length >= 3) { const icV = ic.map(p => new THREE.Vector2(p.x, p.y)); const da = Math.abs(this.poliAlan(disP)), ia = Math.abs(this.poliAlan(icV)); if (ia > 0.01 && ia < da) { if (this.poliAlan(icV) > 0) icV.reverse(); delikP = [icV]; } }
      }
    }
    // YÖN (normal/referans + reverse)
    let nx = d.normal.x, ny = d.normal.y, nz = d.normal.z;
    if (f.refYonAktif && f.refYon) { const r = f.refYon; const l = Math.hypot(r.x, r.y, r.z) || 1; nx = r.x / l; ny = r.y / l; nz = r.z / l; }
    const ters = f.tersYon ? -1 : 1; nx *= ters; ny *= ters; nz *= ters;
    // LİMİTLER
    const through = (t?: string) => t === 'son' || t === 'sonraki' || t === 'duzleme' || t === 'yuzeye';
    let h1 = h1Boyut;
    if ((f.limitTip === 'duzleme' || f.limitTip === 'yuzeye') && f.limitHedefMesafe != null) h1 = Math.max(0.01, f.limitHedefMesafe + (f.ofset1 ?? 0));   // seçilen hedefe kadar
    else if (through(f.limitTip)) h1 = this.govdeKapsam() + (f.ofset1 ?? 0);
    let h2 = 0;
    if (f.simetrik) h2 = h1;
    else if (f.limit2Aktif) h2 = through(f.limit2Tip) ? this.govdeKapsam() : (f.param2 ?? 0);
    const bas = -h2, toplam = h1 + h2;
    if (toplam <= 0.01) return null;
    const off = (pts: THREE.Vector2[]) => this.noktaDunya(pts, d).map(p => ({ x: p.x + nx * bas, y: p.y + ny * bas, z: p.z + nz * bas }));
    const dis = off(disP);
    const delikler = delikP.map(off);
    const yon = { x: nx * toplam, y: ny * toplam, z: nz * toplam };
    return delikler.length ? fikoOCCT.makePrismDelikli(dis, delikler, yon) : fikoOCCT.makePrism(dis, yon);
  }

  /** CATIA Shaft/Groove geometri motoru — First/Second Angle + eksen seçimi + reverse. */
  private occtShaftShape(f: IOzellik): any {
    const prof = this.occtProfilDunya(f); if (!prof || prof.dis.length < 3) return null;
    const d = f.duzlem!; const o = d.origin;
    // dönme ekseni (default sketch dikey V; U/X/Y/Z seçilebilir)
    let aks = d.eksenV;
    if (f.shaftEksen === 'u') aks = d.eksenU;
    else if (f.shaftEksen === 'x') aks = new THREE.Vector3(1, 0, 0);
    else if (f.shaftEksen === 'y') aks = new THREE.Vector3(0, 1, 0);
    else if (f.shaftEksen === 'z') aks = new THREE.Vector3(0, 0, 1);
    const aci1 = (f.aci != null ? f.aci : 360);
    const aci2 = f.limit2Aktif ? (f.aci2 ?? 0) : 0;
    let toplam = aci1 + aci2; if (f.tersYon) toplam = -toplam;
    toplam = Math.max(-360, Math.min(360, toplam)); if (Math.abs(toplam) < 0.5) return null;
    return fikoOCCT.makeRevolve(prof.dis, prof.delikler, { x: o.x, y: o.y, z: o.z }, { x: aks.x, y: aks.y, z: aks.z }, toplam * Math.PI / 180);
  }

  /** CATIA Hole: tıklanan yüzey noktasından içeri matkap aracı (silindir + havşa/V-dip/diş). */
  private occtHoleTool(f: IOzellik): any {
    const c = f.holeMerkez, nrm = f.holeNormal; if (!c || !nrm) return null;
    const L = Math.hypot(nrm.x, nrm.y, nrm.z) || 1;
    const dir = { x: -nrm.x / L, y: -nrm.y / L, z: -nrm.z / L };   // delik içeri (yüzey normalinin tersi)
    const r = Math.max(0.1, (f.holeCap ?? 8) / 2);
    const eps = Math.max(2, r);   // yüzeyden yukarı pay (temiz boolean kesim)
    const through = f.holeLimit === 'son' || f.holeLimit === 'sonraki' || f.holeLimit === 'tumu';
    let h = through ? this.govdeKapsam() : Math.max(0.1, f.holeDerinlik ?? 20);
    if (f.holeLimit === 'duzleme' && f.limitHedefMesafe != null) h = Math.max(0.1, f.limitHedefMesafe);
    const bas = { x: c.x - dir.x * eps, y: c.y - dir.y * eps, z: c.z - dir.z * eps };   // yüzeyin biraz üstünden başla
    let tool = fikoOCCT.makeSilindirEksen(bas, dir, r, h + eps);
    // V-dip (matkap ucu konisi) — sadece kör delikte anlamlı
    if (f.holeAlt === 'vdip' && !through) {
      const aci = (f.holeAltAci ?? 118) * Math.PI / 180;
      const koniH = r / Math.tan(aci / 2);
      const dip = { x: c.x + dir.x * h, y: c.y + dir.y * h, z: c.z + dir.z * h };
      try { tool = fikoOCCT.birlestir(tool, fikoOCCT.makeKoniEksen(dip, dir, r, 0, koniH)); } catch { }
    }
    // Counterbored (şişe başı): üstte geniş silindir
    if (f.holeTur === 'counterbored') {
      const cbR = Math.max(r + 0.2, (f.cbCap ?? (f.holeCap ?? 8) * 1.8) / 2);
      const cbH = Math.max(0.1, f.cbDerinlik ?? 5);
      try { tool = fikoOCCT.birlestir(tool, fikoOCCT.makeSilindirEksen(bas, dir, cbR, cbH + eps)); } catch { }
    } else if (f.holeTur === 'countersunk') {   // Countersunk (havşa başı): yüzeyde csR, içeride r → koni
      const csR = Math.max(r + 0.2, (f.csCap ?? (f.holeCap ?? 8) * 2) / 2);
      const csH = Math.max(0.1, (csR - r) / Math.tan(((f.csAci ?? 90) / 2) * Math.PI / 180));
      const tanH = (csR - r) / csH;
      // konik ağzı yüzeyin biraz üstünden başlat (temiz kesim): yüzeyde tam csR olur
      try { tool = fikoOCCT.birlestir(tool, fikoOCCT.makeKoniEksen(bas, dir, csR + eps * tanH, r, csH + eps)); } catch { }
    } else if (f.holeTur === 'counterdrilled') {   // Counterdrilled: counterbore + altında havşa konisi
      const cbR = Math.max(r + 0.2, (f.cbCap ?? (f.holeCap ?? 8) * 1.8) / 2);
      const cbH = Math.max(0.1, f.cbDerinlik ?? 5);
      const cdip = { x: c.x + dir.x * cbH, y: c.y + dir.y * cbH, z: c.z + dir.z * cbH };
      try {
        tool = fikoOCCT.birlestir(tool, fikoOCCT.makeSilindirEksen(bas, dir, cbR, cbH + eps));
        tool = fikoOCCT.birlestir(tool, fikoOCCT.makeKoniEksen(cdip, dir, cbR, r, Math.max(0.1, cbR - r)));
      } catch { }
    }
    return tool;
  }

  // ───────────────────── CATIA Hole: yüze tıkla → delik aç (sketch'siz) ─────────────────────
  private holeSecModu = false;
  get holeSecAktif(): boolean { return this.holeSecModu; }
  /** Ribbon "Delik" → yüze tıklama moduna gir. Tıklanan nokta delik merkezi, yüz normali eksen. */
  holeBaslat(): boolean {
    if (!this.govdeMesh) { this.d.bildir?.('Delik için önce bir katı gerekli'); return false; }
    this.holeSecModu = true;
    this.d.bildir?.('Delik açmak için bir yüzeye tıkla');
    return true;
  }
  holeSecBitir(): void { this.holeSecModu = false; }
  /** Yüze tıklama: nokta = delik merkezi, yüz normali = eksen → yeni hole feature + pencere. */
  holeYerPick(e: PointerEvent | MouseEvent): boolean {
    if (!this.holeSecModu) return false;
    const gm = this.govdeMesh; const canvas = this.d.canvasAl(); const kamera = this.d.kameraAl();
    if (!gm || !canvas || !kamera) { this.holeSecBitir(); return false; }
    const r = canvas.getBoundingClientRect();
    const ndc = new THREE.Vector2((((e as any).clientX - r.left) / r.width) * 2 - 1, -(((e as any).clientY - r.top) / r.height) * 2 + 1);
    const rc = new THREE.Raycaster(); rc.setFromCamera(ndc, kamera as any);
    const hit = rc.intersectObject(gm, true)[0] as any;
    if (!hit?.point || !hit.face) { this.d.bildir?.('Yüzey bulunamadı, tekrar dene'); return false; }
    const n = hit.face.normal.clone().transformDirection(gm.matrixWorld).normalize();
    const node = this.ozellikNode('hole', null);
    const feat: IOzellik = {
      tip: 'hole', ekleme: false, node,
      holeMerkez: { x: hit.point.x, y: hit.point.y, z: hit.point.z },
      holeNormal: { x: n.x, y: n.y, z: n.z },
      holeCap: 10, holeDerinlik: 20, holeLimit: 'boyut', holeAlt: 'flat', holeAltAci: 118, holeTur: 'simple',
      cbCap: 18, cbDerinlik: 6, csCap: 20, csAci: 90,
    } as IOzellik;
    this.holeSecBitir();
    this.ozellikler.push(feat); this.featureNodeBagla(feat);
    // Delik B-Rep (OCCT) zorunlu — mesh modundaysak OCCT'ye geç (yoksa delik kesmez)
    if (!this.occtModuAktif && this.occtUyumluMu()) {
      if (fikoOCCT.hazirMi()) this.occtModu = true;
      else { this.occtYukle().then(ok => { if (ok) { this.occtModu = true; this.rebuild(); this.d.agacGuncelle?.(); } }); }
    }
    this.rebuild(); this.d.agacGuncelle?.(); this.d.degisti?.(this.durum, null);
    setTimeout(() => this.featureMenu(feat), 60);
    return true;
  }

  /** Açık profil 2D zinciri (çizgi/yay uç-uca, sıralı) — Thin Solid duvarı için. */
  private acikZincir2D(els: any[]): { x: number; y: number }[] {
    const segs: { a: any; b: any; pts: { x: number; y: number }[] }[] = [];
    for (const el of (els || [])) {
      if (el.yapi) continue;
      if (el.tip === 'cizgi') segs.push({ a: el.noktalar[0], b: el.noktalar[1], pts: [el.noktalar[0], el.noktalar[1]] });
      else if (el.tip === 'yay') { const yp = FikoSketch.yayNoktalari(el.noktalar[0], el.noktalar[1], el.noktalar[2]); segs.push({ a: el.noktalar[0], b: el.noktalar[1], pts: yp }); }
    }
    if (!segs.length) return [];
    const tol = 0.8, ayni = (p: any, q: any) => Math.hypot(p.x - q.x, p.y - q.y) < tol;
    const kullanildi = new Array(segs.length).fill(false); kullanildi[0] = true;
    let zincir = segs[0].pts.slice(); let son = segs[0].b, bas = segs[0].a;
    let ilerle = true;
    while (ilerle) {
      ilerle = false;
      for (let j = 0; j < segs.length; j++) {
        if (kullanildi[j]) continue; const s = segs[j];
        if (ayni(s.a, son)) { zincir = zincir.concat(s.pts.slice(1)); son = s.b; kullanildi[j] = true; ilerle = true; break; }
        if (ayni(s.b, son)) { zincir = zincir.concat(s.pts.slice().reverse().slice(1)); son = s.a; kullanildi[j] = true; ilerle = true; break; }
        if (ayni(s.b, bas)) { zincir = s.pts.slice().concat(zincir.slice(1)); bas = s.a; kullanildi[j] = true; ilerle = true; break; }
        if (ayni(s.a, bas)) { zincir = s.pts.slice().reverse().concat(zincir.slice(1)); bas = s.b; kullanildi[j] = true; ilerle = true; break; }
      }
    }
    return zincir;
  }

  /** CATIA Thin Solid (açık profil): açık zinciri iki tarafa offsetleyip kapalı şerit yap → prism (duvar). */
  private occtAcikProfilDuvar(f: IOzellik): any {
    const pts = this.acikZincir2D(f.elemanlar); if (pts.length < 2) return null;
    const d = f.duzlem!;
    let k1 = Math.max(0.1, f.padKal1 ?? 2);   // bir taraf
    let k2 = Math.max(0, f.padKal2 ?? 0);     // diğer taraf
    if (f.notrFiber) { const t = k1 + (k2 || k1); k1 = t / 2; k2 = t / 2; }
    const s1 = this.offsetPolygon(pts, k1, false);                     // bir tarafa offset
    const s2 = this.offsetPolygon(pts, -(k2 > 0.01 ? k2 : 0.0001), false);   // diğer taraf (k2=0 → profil çizgisi)
    if (!s1 || s1.length < 2 || !s2 || s2.length < 2) return null;
    let strip = s1.concat(s2.slice().reverse());                       // kapalı şerit (CCW garanti et)
    const sv = strip.map(p => new THREE.Vector2(p.x, p.y)); if (this.poliAlan(sv) < 0) strip = strip.slice().reverse();
    // yön + limit (occtBolgePrism ile aynı mantık, tek bölge)
    let nx = d.normal.x, ny = d.normal.y, nz = d.normal.z;
    if (f.refYonAktif && f.refYon) { const r = f.refYon; const l = Math.hypot(r.x, r.y, r.z) || 1; nx = r.x / l; ny = r.y / l; nz = r.z / l; }
    const ters = f.tersYon ? -1 : 1; nx *= ters; ny *= ters; nz *= ters;
    const through = (t?: string) => t === 'son' || t === 'sonraki' || t === 'duzleme' || t === 'yuzeye';
    let h1 = f.param ?? 20;
    if ((f.limitTip === 'duzleme' || f.limitTip === 'yuzeye') && f.limitHedefMesafe != null) h1 = Math.max(0.01, f.limitHedefMesafe + (f.ofset1 ?? 0));
    else if (through(f.limitTip)) h1 = this.govdeKapsam() + (f.ofset1 ?? 0);
    let h2 = f.simetrik ? h1 : (f.limit2Aktif ? (through(f.limit2Tip) ? this.govdeKapsam() : (f.param2 ?? 0)) : 0);
    const bas = -h2, toplam = h1 + h2; if (toplam <= 0.01) return null;
    const dis = this.noktaDunya(strip.map(p => new THREE.Vector2(p.x, p.y)), d).map(p => ({ x: p.x + nx * bas, y: p.y + ny * bas, z: p.z + nz * bas }));
    return fikoOCCT.makePrism(dis, { x: nx * toplam, y: ny * toplam, z: nz * toplam });
  }

  /** Solid Combine: profili sketch normali boyunca iki yöne tam-extrude (kesişim için). */
  private occtProfilExtrudeFull(sp: { elemanlar: any[]; duzlem: ISketchDuzlem }): any {
    const pts = this.profilSetiDunya(sp.elemanlar, sp.duzlem); if (pts.length < 3) return null;
    const n = sp.duzlem.normal; const L = this.govdeKapsam();
    const bas = pts.map(p => ({ x: p.x - n.x * L / 2, y: p.y - n.y * L / 2, z: p.z - n.z * L / 2 }));
    return fikoOCCT.makePrism(bas, { x: n.x * L, y: n.y * L, z: n.z * L });
  }

  /** CATIA Rib (OCCT): profili kalınlaştırıp (açık→duvar, kapalı→dolu) normal boyunca uzat → katı. */
  private occtRibShape(f: IOzellik): any {
    if (!f.elemanlar || !f.duzlem) return null;
    const d = f.duzlem; const n = d.normal;
    const k = Math.max(0.1, f.kalinlik || 6);
    const uz = Math.max(0.2, f.param || 30);
    const cift = f.yon === 'cift';
    const bas = cift ? -uz / 2 : (f.yon === 'eksi' ? -uz : 0);
    if (this.profilAcikMi(f.elemanlar)) {   // açık profil → iki tarafa k/2 offset şerit (duvar)
      const pts = this.acikZincir2D(f.elemanlar); if (pts.length < 2) return null;
      const s1 = this.offsetPolygon(pts, k / 2, false), s2 = this.offsetPolygon(pts, -k / 2, false);
      if (!s1 || s1.length < 2 || !s2 || s2.length < 2) return null;
      let strip = s1.concat(s2.slice().reverse());
      const sv = strip.map(p => new THREE.Vector2(p.x, p.y)); if (this.poliAlan(sv) < 0) strip = strip.slice().reverse();
      const dis = this.noktaDunya(strip.map(p => new THREE.Vector2(p.x, p.y)), d).map(p => ({ x: p.x + n.x * bas, y: p.y + n.y * bas, z: p.z + n.z * bas }));
      return fikoOCCT.makePrism(dis, { x: n.x * uz, y: n.y * uz, z: n.z * uz });
    }
    // kapalı profil → profili doldur, normal boyunca uzat (Pad gibi)
    const prof = this.occtProfilDunya(f); if (!prof || prof.dis.length < 3) return null;
    const ofs = (a: { x: number; y: number; z: number }[]) => a.map(p => ({ x: p.x + n.x * bas, y: p.y + n.y * bas, z: p.z + n.z * bas }));
    return fikoOCCT.makePrismDelikli(ofs(prof.dis), (prof.delikler || []).map(ofs), { x: n.x * uz, y: n.y * uz, z: n.z * uz });
  }

  /** STIFFENER (Feder): açık profili (üst kenar) Z=0 tabana indirip kapalı bölge yap → düzlem normali boyunca kalınlık ver. */
  private occtStiffenerShape(f: IOzellik): any {
    if (!f.elemanlar || !f.duzlem) return null;
    const ust = this.sketchYolDunya(f.elemanlar, f.duzlem);   // açık profil dünya noktaları (sıralı)
    if (ust.length < 2) return null;
    const n = f.duzlem.normal.clone().normalize();
    // -Z'nin düzlem-içi izdüşümü (planarlık korunur): her üst noktayı bu yön boyunca z=0'a indir
    const d = new THREE.Vector3(0, 0, -1).addScaledVector(n, -(new THREE.Vector3(0, 0, -1).dot(n)));
    if (Math.abs(d.z) < 1e-3) return null;   // yatay düzlem → stiffener tanımsız
    d.normalize();
    const alt = ust.map(p => { const t = p.z / -d.z; return { x: p.x + d.x * t, y: p.y + d.y * t, z: 0 }; });
    const loop = [...ust, ...alt.slice().reverse()];   // kapalı planar bölge (üst profil + z=0 taban çizgisi)
    const k = Math.max(0.1, f.kalinlik || 6);
    const slab = fikoOCCT.makePrism(loop, { x: n.x * k, y: n.y * k, z: n.z * k });
    return fikoOCCT.oteleKopya(slab, -n.x * k / 2, -n.y * k / 2, -n.z * k / 2);   // neutral fiber (iki yana eşit)
  }

  /** Yüzey profili dünya noktaları (açık→zincir, kapalı→en büyük loop). ofset1 varsa profili o kadar kaydırır (Offset Surface). */
  private yuzeyProfil3d(f: IOzellik): { x: number; y: number; z: number }[] {
    if (!f.elemanlar || !f.duzlem) return [];
    const acik = this.profilAcikMi(f.elemanlar);
    let pts2d: { x: number; y: number }[] = acik
      ? this.acikZincir2D(f.elemanlar)
      : (this.looplar(f.elemanlar).reduce((a, b) => b.alan > (a?.alan ?? -1) ? b : a, null as any)?.pts.map((p: any) => ({ x: p.x, y: p.y })) || []);
    if (f.ofset1 && pts2d.length >= 2) { const o = this.offsetPolygon(pts2d, f.ofset1, !acik); if (o && o.length >= 2) pts2d = o; }   // Offset Surface
    return this.noktaDunya(pts2d.map(p => new THREE.Vector2(p.x, p.y)), f.duzlem);
  }

  /** GSD Yüzey shape'i (açık shell): extrude/revolve/fill/loft. */
  private occtYuzeyShape(f: IOzellik): any {
    if (f.yuzeyTip === 'sphere') {
      const r = (f.pCap ?? 50) / 2;
      const sh = fikoOCCT.kureYuzey(r);
      const o = (f.ref1 != null && f.ref1 >= 0) ? this.refNoktaKonum(f.ref1) : new THREE.Vector3(f.p1x ?? 0, f.p1y ?? 0, f.p1z ?? 0);
      return (o.lengthSq() > 1e-9) ? fikoOCCT.oteleKopya(sh, o.x, o.y, o.z) : sh;
    }
    if (f.yuzeyTip === 'cylinder') {
      const r = (f.pCap ?? 50) / 2, h = f.pYuk ?? 60;
      const o = (f.ref1 != null && f.ref1 >= 0) ? this.refNoktaKonum(f.ref1) : new THREE.Vector3(f.p1x ?? 0, f.p1y ?? 0, f.p1z ?? 0);
      const ax = this.aynaNormal(f);
      return fikoOCCT.silindirYanYuzey({ x: o.x, y: o.y, z: o.z }, { x: ax.x, y: ax.y, z: ax.z }, r, h);
    }
    if (f.yuzeyTip === 'loft') {
      const profiller = (f.secProfiller || []).map(p => this.profilSetiDunya(p.elemanlar, p.duzlem)).filter(a => a.length >= 3);
      return profiller.length >= 2 ? fikoOCCT.makeLoft(profiller, false) : null;
    }
    if (f.yuzeyTip === 'sweep') {
      const sp = f.secProfiller || []; if (sp.length < 2) return null;
      const acikP = this.profilAcikMi(sp[0].elemanlar);
      const profil = acikP ? this.noktaDunya(this.acikZincir2D(sp[0].elemanlar).map(p => new THREE.Vector2(p.x, p.y)), sp[0].duzlem) : this.profilSetiDunya(sp[0].elemanlar, sp[0].duzlem);
      const yol = this.sketchYolDunya(sp[1].elemanlar, sp[1].duzlem);
      return (profil.length >= 2 && yol.length >= 2) ? fikoOCCT.makeYuzeySweep(profil, yol, !acikP) : null;
    }
    const prof = this.yuzeyProfil3d(f); if (prof.length < 2 || !f.duzlem) return null;
    const acik = this.profilAcikMi(f.elemanlar);
    const d = f.duzlem;
    if (f.yuzeyTip === 'fill') return acik ? null : fikoOCCT.makeYuzeyFill(prof);
    if (f.yuzeyTip === 'revolve') {
      const aks = d.eksenV, o = d.origin;
      return fikoOCCT.makeYuzeyRevolve(prof, { x: o.x, y: o.y, z: o.z }, { x: aks.x, y: aks.y, z: aks.z }, (f.aci ?? 360) * Math.PI / 180, !acik);
    }
    const n = d.normal, uz = f.param ?? 40;   // extrude (varsayılan)
    return fikoOCCT.makeYuzeyExtrude(prof, { x: n.x * uz, y: n.y * uz, z: n.z * uz }, !acik);
  }

  /** GSD Thick Surface (GÜVENİLİR): profili t/2 iki tarafa offsetle → kalın profil → aynı işlem (extrude→duvar / revolve→tüp / fill→pad) katı olarak. */
  private occtYuzeyKalinlikShape(hedef: IOzellik, t: number): any {
    if (!hedef.duzlem || !hedef.elemanlar) return null;
    const d = hedef.duzlem;
    const acik = this.profilAcikMi(hedef.elemanlar);
    let pts2d: { x: number; y: number }[];
    if (acik) pts2d = this.acikZincir2D(hedef.elemanlar);
    else { const loops = this.looplar(hedef.elemanlar); if (!loops.length) return null; const big = loops.reduce((a, b) => b.alan > a.alan ? b : a); pts2d = big.pts.map((p: any) => ({ x: p.x, y: p.y })); }
    if (pts2d.length < 2) return null;
    // FILL: kapalı yüzeyi normal boyunca t kadar pad et (ortala)
    if (hedef.yuzeyTip === 'fill') {
      const loop3d = this.noktaDunya(pts2d.map(p => new THREE.Vector2(p.x, p.y)), d); const n = d.normal;
      const bas = loop3d.map(p => ({ x: p.x - n.x * t / 2, y: p.y - n.y * t / 2, z: p.z - n.z * t / 2 }));
      return fikoOCCT.makePrism(bas, { x: n.x * t, y: n.y * t, z: n.z * t });
    }
    // EXTRUDE/REVOLVE: profili t/2 iki tarafa offsetle → kalın kapalı profil
    const s1 = this.offsetPolygon(pts2d, t / 2, !acik), s2 = this.offsetPolygon(pts2d, -t / 2, !acik);
    if (!s1 || s1.length < 2 || !s2 || s2.length < 2) return null;
    let strip = s1.concat(s2.slice().reverse());
    const sv = strip.map(p => new THREE.Vector2(p.x, p.y)); if (this.poliAlan(sv) < 0) strip = strip.slice().reverse();
    const dis3d = this.noktaDunya(strip.map(p => new THREE.Vector2(p.x, p.y)), d);
    if (hedef.yuzeyTip === 'revolve') {
      const aks = d.eksenV, o = d.origin;
      return fikoOCCT.makeRevolve(dis3d, [], { x: o.x, y: o.y, z: o.z }, { x: aks.x, y: aks.y, z: aks.z }, (hedef.aci ?? 360) * Math.PI / 180);
    }
    const n = d.normal, uz = hedef.param ?? 40;   // extrude → duvar prism
    return fikoOCCT.makePrism(dis3d, { x: n.x * uz, y: n.y * uz, z: n.z * uz });
  }

  /** GSD Split-by-Surface: extrude yüzeyini yarı-uzaya genişletip kesme aracı (katıyı yüzeyle böl). */
  private occtSplitSurfaceTool(hedef: IOzellik, ters: boolean): any {
    if (hedef.yuzeyTip !== 'extrude' || !hedef.elemanlar || !hedef.duzlem) return null;   // şimdilik düz/extrude yüzey
    const d = hedef.duzlem;
    const acik = this.profilAcikMi(hedef.elemanlar);
    let pts: { x: number; y: number }[];
    if (acik) pts = this.acikZincir2D(hedef.elemanlar);
    else { const loops = this.looplar(hedef.elemanlar); if (!loops.length) return null; pts = loops.reduce((a, b) => b.alan > a.alan ? b : a).pts.map((p: any) => ({ x: p.x, y: p.y })); }
    if (pts.length < 2) return null;
    const big = this.govdeKapsam();
    const outer = this.offsetPolygon(pts, ters ? -big : big, false);   // profili bir tarafa devasa offsetle
    if (!outer || outer.length < 2) return null;
    let band = pts.concat(outer.slice().reverse());                     // profil + ofset → kapalı bant (yarı-uzay)
    const sv = band.map(p => new THREE.Vector2(p.x, p.y)); if (this.poliAlan(sv) < 0) band = band.slice().reverse();
    const n = d.normal;
    const bas = this.noktaDunya(band.map(p => new THREE.Vector2(p.x, p.y)), d).map(p => ({ x: p.x - n.x * big, y: p.y - n.y * big, z: p.z - n.z * big }));
    return fikoOCCT.makePrism(bas, { x: n.x * 2 * big, y: n.y * 2 * big, z: n.z * 2 * big });
  }

  /** GSD Yüzeyleri — govdeMesh gibi DOĞRUDAN PartBody altına ayrı DoubleSide mesh'ler (ara grup YOK → raycast object=mesh). */
  private yuzeyMeshler: THREE.Mesh[] = [];
  private _yardimciKenarMat: LineMaterial | null = null;
  /** Bir mesh'e gerçek B-Rep kenarları (OCCT shapeToEdges) ya da geometrik 30° kenar (mesh) — CATIA part-edge görünümü. */
  private meshKenarEkle(parent: THREE.Mesh, geo: THREE.BufferGeometry, occtShape?: any): void {
    try {
      let segPts: number[] = [];
      if (occtShape) {
        // GERÇEK B-Rep kenarları (çember boundary + seam) — CATIA gibi
        const edges = fikoOCCT.shapeToEdges(occtShape) || [];
        for (const e of edges) { const p = e.pts; for (let k = 1; k < p.length; k++) segPts.push(p[k - 1].x, p[k - 1].y, p[k - 1].z, p[k].x, p[k].y, p[k].z); }
      }
      if (!segPts.length) {
        const eg = this.kati.kenarCikar(geo, 30); const pa = eg.getAttribute('position') as THREE.BufferAttribute | undefined;
        if (pa && pa.count) segPts = Array.from(pa.array as Float32Array); eg.dispose();
      }
      if (!segPts.length) return;
      const lsg = new LineSegmentsGeometry(); lsg.setPositions(segPts);
      const canvas = this.d.canvasAl();
      if (!this._yardimciKenarMat) this._yardimciKenarMat = new LineMaterial({ worldUnits: false, transparent: false, dashed: false, alphaToCoverage: false });
      const mat = this._yardimciKenarMat;
      mat.color.setHex(this.govdeGrafik?.kenarRenk ?? 0x0b1119); mat.linewidth = this.govdeGrafik?.kenarKalinlik ?? 2;
      mat.resolution.set(canvas?.width || 1920, canvas?.height || 1080);
      const ls = new LineSegments2(lsg, mat); ls.name = 'FikoKenar'; ls.renderOrder = 1; (ls as any).raycast = () => { };
      parent.add(ls);
    } catch (e) { }
  }
  private makroMeshler: THREE.Mesh[] = [];
  /** OCCT modunda eski tek-parça makroları (boru/dirsek/yüzük/taş yuvası/bezel) ayrı mesh olarak çiz (rebuildOCCT'de makro dalı yok). */
  private makroMeshKur(): void {
    for (const o of this.makroMeshler) { (o.parent || this.d.sahne).remove(o); o.geometry?.dispose?.(); }
    this.makroMeshler = [];
    const par = this.govdeGrupAl();
    const mat = new THREE.MeshStandardMaterial({ color: 0xb0bec5, metalness: 0.3, roughness: 0.5, side: THREE.DoubleSide });
    this.ozellikler.forEach(f => {
      if (f.tip !== 'makro' || f.bastirilmis) return;
      try {
        // GERÇEK B-Rep: makroyu OCCT şekli olarak kur → çember boundary + seam kenarları (CATIA gibi)
        let occtSh = this.occtMakroShape(f);
        let geo: THREE.BufferGeometry | null = null;
        if (occtSh) { geo = fikoOCCT.shapeToGeo(occtSh, 0.2); if (geo) geo.computeVertexNormals(); }
        if (!geo) { geo = this.makroGeo(f); occtSh = null; }   // OCCT olmazsa legacy mesh
        if (!geo) return;
        const mesh = new THREE.Mesh(geo, f.grafik ? mat.clone() : mat); mesh.name = 'FikoMakro';
        if (f.grafik) this.grafikUygula(mesh, f.grafik, 0xb0bec5);
        (mesh as any).ciftTiklandi = () => this.featureMenu(f);
        (mesh as any).uzunBasildi = () => this.featureHizliMenu(f);
        (mesh as any).userData['FikoArayuz'] = { ad: 'Makro', agactaGorunur: false, listeGoster: false };
        par.ekle!(mesh as any); this.makroMeshler.push(mesh);
        this.meshKenarEkle(mesh, geo, occtSh);   // GERÇEK B-Rep part-edge'leri
        try { this.d.topo.kaydetMesh(mesh); } catch (e) { }
      } catch (e) { }
    });
  }
  /** Makro feature'ı GERÇEK OCCT katı olarak kur (boru/dirsek/yüzük/taş yuvası/bezel) → temiz B-Rep kenarları. Yerel→dünya f.duzlem ile. */
  private occtMakroShape(f: IOzellik): any {
    try {
      const t = f.makroTip; let s: any = null;
      if (t === 'boru') {
        const R = Math.max(2, f.mCap || 30) / 2, et = Math.max(0.5, f.mEtKal ?? 3), uz = Math.max(2, f.mUzun || 120);
        s = fikoOCCT.cikar(fikoOCCT.makeCylinder(R, uz), fikoOCCT.makeCylinder(Math.max(0.5, R - et), uz));
      } else if (t === 'yuzuk') {
        const ri = Math.max(2, f.mCap || 18) / 2, band = Math.max(0.5, f.mBand ?? 4), gen = Math.max(1, f.mUzun || 5);
        const dis = fikoOCCT.oteleKopya(fikoOCCT.makeCylinder(ri + band, gen), 0, 0, -gen / 2);
        const ic = fikoOCCT.oteleKopya(fikoOCCT.makeCylinder(ri, gen), 0, 0, -gen / 2);
        s = fikoOCCT.cikar(dis, ic);
      } else if (t === 'bezel') {
        const R = Math.max(2, f.mTasCap || 8) / 2 + Math.max(0.3, f.mEtKal ?? 1), ri = Math.max(1, f.mTasCap || 8) / 2, h = Math.max(1, f.mProngYuk || 6);
        s = fikoOCCT.cikar(fikoOCCT.makeCylinder(R, h), fikoOCCT.oteleKopya(fikoOCCT.makeCylinder(ri, h), 0, 0, h * 0.3));
      } else if (t === 'dirsek') {
        const R = Math.max(2, f.mCap || 30) / 2, et = Math.max(0.5, f.mEtKal ?? 3), kol = Math.max(R, f.mUzun || 60), aci = (f.mAci || 90) * Math.PI / 180;
        const boru = fikoOCCT.cikar(fikoOCCT.makeCylinder(R, kol), fikoOCCT.makeCylinder(Math.max(0.5, R - et), kol));
        const kol2 = fikoOCCT.dondurKopya(boru, { x: 1, y: 0, z: 0 }, aci, { x: 0, y: 0, z: 0 });
        s = fikoOCCT.birlestir(boru, kol2);
      } else if (t === 'tasYuvasi') {
        const rb = Math.max(1, f.mTasCap || 8) / 2, n = Math.max(3, Math.round(f.mProngAdet || 4)), pr = Math.max(0.4, f.mProngCap || 1.2) / 2, ph = Math.max(1, f.mProngYuk || 6);
        s = fikoOCCT.makeCone(rb * 0.6, rb, ph * 0.35);   // taban koni
        for (let i = 0; i < n; i++) { const a = (i / n) * Math.PI * 2; const prong = fikoOCCT.oteleKopya(fikoOCCT.makeCylinder(pr, ph), rb * Math.cos(a), rb * Math.sin(a), 0); s = fikoOCCT.birlestir(s, prong); }
      }
      if (!s) return null;
      const d = f.duzlem;
      if (d) s = fikoOCCT.duzlemeYerlestir(s, { x: d.origin.x, y: d.origin.y, z: d.origin.z }, { x: d.eksenU.x, y: d.eksenU.y, z: d.eksenU.z }, { x: d.eksenV.x, y: d.eksenV.y, z: d.eksenV.z }, { x: d.normal.x, y: d.normal.y, z: d.normal.z });
      return s;
    } catch (e) { return null; }
  }
  private yuzeyMeshKur(): void {
    for (const o of this.yuzeyMeshler) { (o.parent || this.d.sahne).remove(o); o.geometry?.dispose?.(); }
    this.yuzeyMeshler = [];
    const kalinlasan = new Set(this.ozellikler.filter(f => (f.tip === 'thickSurface' || f.tip === 'splitSurface') && !f.bastirilmis).map(f => f.hedefIndex));
    const joinTuketilen = new Set<number>();   // Join/Trim'e dahil yüzeyler ayrı render edilmez (işlenmiş hali gösterilir)
    this.ozellikler.forEach(f => {
      if (f.bastirilmis) return;
      if (f.tip === 'yuzeyJoin') (f.refler || []).forEach(i => joinTuketilen.add(i));
      if ((f.tip === 'yuzeyTrim' || f.tip === 'yuzeyExtrapolate') && f.ref1 != null) joinTuketilen.add(f.ref1);
    });
    const par = this.govdeGrupAl();
    const mat = new THREE.MeshStandardMaterial({ color: 0x22d3ee, metalness: 0.1, roughness: 0.5, side: THREE.DoubleSide });   // varsayılan OPAK (şeffaf değil)
    this.ozellikler.forEach((f, i) => {
      const yuzeyTipleri = ['yuzey', 'yuzeyJoin', 'yuzeyTrim', 'yuzeyExtract', 'yuzeyBlend', 'yuzeyExtrapolate', 'untrim', 'multiExtract', 'disassemble'];
      if (!yuzeyTipleri.includes(f.tip) || f.bastirilmis || kalinlasan.has(i) || joinTuketilen.has(i)) return;
      try {
        const sh = f.tip === 'yuzeyJoin' ? this.occtYuzeyJoinShape(f) : f.tip === 'yuzeyTrim' ? this.occtYuzeyTrimShape(f) : f.tip === 'yuzeyExtract' ? this.occtExtractShape(f) : f.tip === 'yuzeyBlend' ? this.occtBlendShape(f) : f.tip === 'yuzeyExtrapolate' ? this.occtExtrapolateShape(f) : f.tip === 'untrim' ? this.occtUntrimShape(f) : f.tip === 'multiExtract' ? this.occtMultiExtractShape(f) : f.tip === 'disassemble' ? this.occtDisassembleShape(f) : this.occtYuzeyShape(f); if (!sh) return;
        const geo = fikoOCCT.shapeToGeo(sh, 0.3); if (!geo) return;
        geo.computeVertexNormals();
        if (!(geo as any)._occtFaceGruplu) this.kati.yuzGrupla(geo);
        const mesh = new THREE.Mesh(geo, f.grafik ? mat.clone() : mat); mesh.name = 'FikoYuzey';
        if (f.grafik) this.grafikUygula(mesh, f.grafik, 0x22d3ee);   // CATIA Graphics Properties (yüzey-bazlı)
        // CATIA: 3D yüzeyden etkileşim — çift-tık→Pencere, uzun-bas→Hızlı param, sağ-tık→context (FikoSecililer dispatch)
        (mesh as any).ciftTiklandi = () => this.featureMenu(f);
        (mesh as any).uzunBasildi = () => this.featureHizliMenu(f);
        (mesh as any).sagMenu = this.yuzeySagMenu(f);
        (mesh as any)._telFeat = f;   // 3D'den referans seçimi (yuzeyTrim/Extract/Kenar/Blend ref pick)
        (mesh as any).userData['FikoArayuz'] = { ad: 'Yüzey', agactaGorunur: false, listeGoster: false };
        par.ekle!(mesh as any);   // DOĞRUDAN PartBody altına (govdeMesh gibi) → enrich object=mesh
        try { this.d.topo.kaydetMesh(mesh); const fg = faceGruplariUret(geo); (mesh as any).occt_faceGroups = fg; (mesh as any).brep_faces = fg; } catch (e) { }
        this.meshKenarEkle(mesh, geo, sh);   // GERÇEK B-Rep kenarları (çember boundary + seam)
        this.yuzeyMeshler.push(mesh);
      } catch (e) { }
    });
  }

  /** Yarı-uzay kesici kutu (split/trim ortak): aynaTip düzlemi+ofset'in bir tarafını kapsayan büyük kutu. */
  private yariUzayTool(aynaTip: string, ofset: number, alt: boolean): any {
    const K = this.govdeKapsam(); const box = fikoOCCT.makeBox(2 * K, 2 * K, 2 * K);
    return aynaTip === 'yz' ? fikoOCCT.oteleKopya(box, alt ? ofset - 2 * K : ofset, -K, -K)
      : aynaTip === 'xz' ? fikoOCCT.oteleKopya(box, -K, alt ? ofset - 2 * K : ofset, -K)
        : fikoOCCT.oteleKopya(box, -K, -K, alt ? ofset - 2 * K : ofset);
  }
  /** Surface Extrapolate: yüzeyi(ref1) sınırlarından d kadar uzat (NurbsConvert+ExtendSurfByLength). */
  private occtExtrapolateShape(f: IOzellik): any {
    const surf = this.ozellikler[f.ref1 ?? -1];
    let sh = surf && (surf.tip === 'yuzey' ? this.occtYuzeyShape(surf) : surf.tip === 'yuzeyJoin' ? this.occtYuzeyJoinShape(surf) : surf.tip === 'yuzeyTrim' ? this.occtYuzeyTrimShape(surf) : null);
    if (!sh) return null;
    return fikoOCCT.yuzeyUzat(sh, f.param ?? 20);
  }
  /** Surface Extrapolate ekle: son yüzeyi uzat. */
  yuzeyExtrapolateEkle(): void {
    let surf = -1; this.ozellikler.forEach((s, i) => { if (s.tip === 'yuzey' && !s.bastirilmis) surf = i; });
    if (surf < 0) { this.d.bildir?.('Uzatmak için bir yüzey gerekli'); return; }
    const node = this.ozellikNode('yuzeyExtrapolate', null);
    const feat: IOzellik = { tip: 'yuzeyExtrapolate', ekleme: true, ref1: surf, param: 20, node };
    this.ozellikler.push(feat); this.featureNodeBagla(feat); this.rebuild(); this.d.agacGuncelle?.(); this.gecmisKaydet();
    setTimeout(() => this.featureMenu(feat), 60);
  }
  /** OCCT Pipe: bir 3D eğri (ref1) boyunca dairesel kesitli boru (solid). Profil eğri başlangıç teğetine dik. */
  private pipe3dShape(f: IOzellik): any {
    const yol = this.egriNoktalari(this.ozellikler[f.ref1 ?? -1]); if (yol.length < 2) return null;
    const r = Math.max(0.1, (f.pCap ?? 16) / 2);
    const teget = yol[1].clone().sub(yol[0]); if (teget.lengthSq() < 1e-9) return null; teget.normalize();
    const u = Math.abs(teget.x) > 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0);
    const e1 = new THREE.Vector3().crossVectors(teget, u).normalize(), e2 = new THREE.Vector3().crossVectors(teget, e1).normalize();
    const profil: { x: number; y: number; z: number }[] = [];
    for (let i = 0; i < 32; i++) { const a = (i / 32) * Math.PI * 2; const p = yol[0].clone().addScaledVector(e1, Math.cos(a) * r).addScaledVector(e2, Math.sin(a) * r); profil.push({ x: p.x, y: p.y, z: p.z }); }
    try { return fikoOCCT.makeSweep(profil, yol.map(p => ({ x: p.x, y: p.y, z: p.z })), true); } catch (e) { return null; }
  }
  /** Surface Blend: iki eğri (ref1,ref2) arasında pürüzsüz yüzey (ThruSections açık profil). */
  private occtBlendShape(f: IOzellik): any {
    const p1 = this.egriNoktalari(this.ozellikler[f.ref1 ?? -1]).map(p => ({ x: p.x, y: p.y, z: p.z }));
    const p2 = this.egriNoktalari(this.ozellikler[f.ref2 ?? -1]).map(p => ({ x: p.x, y: p.y, z: p.z }));
    if (p1.length < 2 || p2.length < 2) return null;
    return fikoOCCT.makeBlend(p1, p2);
  }
  /** Surface Blend ekle: ilk iki eğri arası yüzey. */
  yuzeyBlendEkle(): void {
    const eg: number[] = []; this.ozellikler.forEach((s, i) => { if (['cizgi3d', 'cember3d', 'spline3d', 'helix3d', 'spiral3d', 'conic3d', 'paralelEgri'].includes(s.tip) && !s.bastirilmis) eg.push(i); });
    if (eg.length < 2) { this.d.bildir?.('Blend için en az 2 eğri gerekli'); return; }
    const node = this.ozellikNode('yuzeyBlend', null);
    const feat: IOzellik = { tip: 'yuzeyBlend', ekleme: true, ref1: eg[0], ref2: eg[1], node };
    this.ozellikler.push(feat); this.featureNodeBagla(feat); this.rebuild(); this.d.agacGuncelle?.(); this.gecmisKaydet();
    setTimeout(() => this.featureMenu(feat), 60);
  }
  /** Surface Extract: kaynak shape'ten (gövde/yüzey) bir yüzü ayrı yüzey olarak çıkar (TopExp_Explorer FACE). */
  private occtExtractShape(f: IOzellik): any {
    const src = this.kesisimShapeAl(f.ref1); if (!src) return null;
    const faces = fikoOCCT.yuzler(src); if (!faces.length) return null;
    const i = Math.min(Math.max(f.yuzIndex ?? 0, 0), faces.length - 1);
    return faces[i];
  }
  /** GSD Untrim: kaynak yüzün (yuzIndex) altındaki tam yüzeyi geri getir. */
  private occtUntrimShape(f: IOzellik): any {
    const src = this.kesisimShapeAl(f.ref1); if (!src) return null;
    const faces = fikoOCCT.yuzler(src); if (!faces.length) return null;
    const i = Math.min(Math.max(f.yuzIndex ?? 0, 0), faces.length - 1);
    return fikoOCCT.untrimYuzey(faces[i]);
  }
  /** GSD Multiple Extract: kaynaktaki TÜM yüzleri tek yüzey (dikilmiş kabuk) olarak çıkar. */
  private occtMultiExtractShape(f: IOzellik): any {
    const src = this.kesisimShapeAl(f.ref1); if (!src) return null;
    const faces = fikoOCCT.yuzler(src); if (!faces.length) return null;
    return faces.length === 1 ? faces[0] : (fikoOCCT.dikis(faces) || faces[0]);
  }
  /** GSD Disassemble: kaynağı bağımsız yüzlere ayır (compound olarak göster). */
  private occtDisassembleShape(f: IOzellik): any {
    const src = this.kesisimShapeAl(f.ref1); if (!src) return null;
    const faces = fikoOCCT.parcala(src); return faces.length ? (fikoOCCT.dikis(faces) || faces[0]) : src;
  }
  /** Surface Extract ekle: kaynak (gövde/son yüzey) yüzünü çıkar. */
  yuzeyExtractEkle(): void {
    let ref = -1; this.ozellikler.forEach((s, i) => { if (s.tip === 'yuzey' && !s.bastirilmis) ref = i; });
    if (ref < 0 && this.occtSonShapeRef) ref = -2;
    if (ref === -1) { this.d.bildir?.('Çıkarmak için bir yüzey veya gövde gerekli'); return; }
    const node = this.ozellikNode('yuzeyExtract', null);
    const feat: IOzellik = { tip: 'yuzeyExtract', ekleme: true, ref1: ref, yuzIndex: 0, node };
    this.ozellikler.push(feat); this.featureNodeBagla(feat); this.rebuild(); this.d.agacGuncelle?.(); this.gecmisKaydet();
    setTimeout(() => this.featureMenu(feat), 60);
  }
  /** Surface Trim: yüzeyi (ref1) düzlemle kırp → tutulan taraf (BRepAlgoAPI_Common ile gerçek B-Rep). */
  private occtYuzeyTrimShape(f: IOzellik): any {
    const surf = this.ozellikler[f.ref1 ?? -1]; if (!surf || surf.tip !== 'yuzey') return null;
    const sh = this.occtYuzeyShape(surf); if (!sh) return null;
    const tool = this.yariUzayTool(f.aynaTip || 'xy', f.splitOfset ?? 0, !f.tersYon);   // tutulan taraf
    try { return tool ? fikoOCCT.kesistir(sh, tool) : sh; } catch (e) { return sh; }
  }
  /** Surface Trim ekle: son yüzeyi düzlemle kırp. */
  yuzeyTrimEkle(): void {
    let surf = -1; this.ozellikler.forEach((s, i) => { if (s.tip === 'yuzey' && !s.bastirilmis) surf = i; });
    if (surf < 0) { this.d.bildir?.('Kırpmak için bir yüzey gerekli'); return; }
    const node = this.ozellikNode('yuzeyTrim', null);
    const feat: IOzellik = { tip: 'yuzeyTrim', ekleme: true, ref1: surf, aynaTip: 'yz', splitOfset: 0, tersYon: false, node };
    this.ozellikler.push(feat); this.featureNodeBagla(feat); this.rebuild(); this.d.agacGuncelle?.(); this.gecmisKaydet();
    setTimeout(() => this.featureMenu(feat), 60);
  }
  /** Surface Join/Sew: referans yüzeyleri GERÇEK B-Rep dikişle birleştir (BRepBuilderAPI_Sewing). */
  private occtYuzeyJoinShape(f: IOzellik): any {
    const shapes = (f.refler || []).map(i => this.ozellikler[i]).filter(s => s && s.tip === 'yuzey' && !s.bastirilmis).map(s => this.occtYuzeyShape(s)).filter(Boolean);
    if (!shapes.length) return null;
    if (shapes.length === 1) return shapes[0];
    return fikoOCCT.dikis(shapes);
  }
  /** Kesişim referansının OCCT shape'i: -2=ana gövde, yüzey/yuzeyJoin feature, veya null. */
  private kesisimShapeAl(ref?: number): any {
    if (ref === -2) return this.occtSonShapeRef || null;
    const o = this.ozellikler[ref ?? -1]; if (!o) return null;
    if (o.tip === 'yuzey') return this.occtYuzeyShape(o);
    if (o.tip === 'yuzeyJoin') return this.occtYuzeyJoinShape(o);
    return null;
  }
  /** Kesişim eğrisi ekle (iki yüzey/gövde). */
  kesisim3dEkle(): void {
    const yz: number[] = []; this.ozellikler.forEach((s, i) => { if (s.tip === 'yuzey' && !s.bastirilmis) yz.push(i); });
    const node = this.ozellikNode('kesisim3d', null);
    const feat: IOzellik = { tip: 'kesisim3d', ekleme: true, ref1: yz[0] ?? (this.occtSonShapeRef ? -2 : -1), ref2: yz[1] ?? (this.occtSonShapeRef ? -2 : -1), node };
    this.ozellikler.push(feat); this.featureNodeBagla(feat); this.rebuild(); this.d.agacGuncelle?.(); this.gecmisKaydet();
    setTimeout(() => this.featureMenu(feat), 60);
  }
  /** Kesişim/Trim için ref seçenekleri: yüzeyler + Ana Gövde. */
  kesisimSecenekleri(feat: IOzellik): { deger: number; etiket: string }[] {
    const out: { deger: number; etiket: string }[] = [];
    if (this.occtSonShapeRef) out.push({ deger: -2, etiket: 'Ana Gövde' });
    this.ozellikler.forEach((f, i) => { if ((f.tip === 'yuzey' || f.tip === 'yuzeyJoin') && f !== feat) out.push({ deger: i, etiket: (f.node as any)?.name || 'Yüzey.' + (i + 1) }); });
    return out;
  }
  /** Surface Join ekle: mevcut tüm yüzeyleri birleştir. */
  yuzeyJoinEkle(): void {
    const refler: number[] = []; this.ozellikler.forEach((s, i) => { if (s.tip === 'yuzey' && !s.bastirilmis) refler.push(i); });
    if (refler.length < 2) { this.d.bildir?.('Birleştirmek için en az 2 yüzey gerekli'); return; }
    const node = this.ozellikNode('yuzeyJoin', null);
    const feat: IOzellik = { tip: 'yuzeyJoin', ekleme: true, refler, node };
    this.ozellikler.push(feat); this.featureNodeBagla(feat); this.rebuild(); this.d.agacGuncelle?.(); this.gecmisKaydet();
    setTimeout(() => this.featureMenu(feat), 60);
  }
  /** GSD yüzey sağ-tık menüsü: düzenle/hızlı/katıya çevir/böl/sil. */
  private yuzeySagMenu(f: IOzellik): any[] {
    const menu: any[] = [
      { baslik: 'Düzenle (Pencere)', simge: 'sliders horizontal', renkSinif: 'blue', tiklandi: () => this.featureMenu(f) },
      { baslik: 'Hızlı Ayar', simge: 'bolt', renkSinif: 'teal', tiklandi: () => this.featureHizliMenu(f) },
      { baslik: 'Grafik Özellikleri', simge: 'paint brush', renkSinif: 'purple', tiklandi: () => this.grafikDialogAc(f) },
      { tipi: 'Ayraç' },
    ];
    if (f.tip === 'yuzey' && f.yuzeyTip !== 'sweep' && f.yuzeyTip !== 'loft') {   // profil-tabanlı yüzeyler katıya çevrilebilir/bölebilir
      menu.push({ baslik: 'Paralel Yüzey (Offset)', simge: 'clone outline', renkSinif: 'teal', tiklandi: () => this.yuzeyOffsetFor(f) });
      menu.push({ baslik: 'Kalınlaştır (Katıya Çevir)', simge: 'clone', renkSinif: 'green', tiklandi: () => this.yuzeyKalinlikFor(f) });
      if (f.yuzeyTip === 'extrude' && this.govdeMesh) menu.push({ baslik: 'Bu Yüzeyle Böl', simge: 'cut', renkSinif: 'orange', tiklandi: () => this.yuzeyBolFor(f) });
    }
    menu.push({ tipi: 'Ayraç' }, { baslik: 'Sil', simge: 'trash alternate', renkSinif: 'red', tiklandi: () => this.featureSil(f) });
    return menu;
  }
  /** Paralel yüzey (Offset Surface): kaynak yüzeyin kopyasını profili kaydırarak oluştur. */
  private yuzeyOffsetFor(f: IOzellik): void {
    const node = this.ozellikNode('yuzey', null);
    const yeni: IOzellik = {
      tip: 'yuzey', ekleme: true, yuzeyTip: f.yuzeyTip, param: f.param, aci: f.aci, shaftEksen: f.shaftEksen,
      elemanlar: f.elemanlar ? JSON.parse(JSON.stringify(f.elemanlar)) : undefined, duzlem: f.duzlem, secProfiller: f.secProfiller,
      ofset1: 5, node,
    };
    this.ozellikler.push(yeni); this.featureNodeBagla(yeni); this.rebuild(); this.d.agacGuncelle?.();
    setTimeout(() => this.featureMenu(yeni), 60);
  }
  /** Belirli bir yüzey feature'ını kalınlaştır (sağ-tık menüsünden). */
  private yuzeyKalinlikFor(f: IOzellik): void {
    const idx = this.ozellikler.indexOf(f); if (idx < 0) return;
    const node = this.ozellikNode('thickSurface', null);
    const feat: IOzellik = { tip: 'thickSurface', ekleme: true, hedefIndex: idx, kalinlik: 3, node };
    this.ozellikler.push(feat); this.featureNodeBagla(feat); this.rebuild(); this.d.agacGuncelle?.(); this.gecmisKaydet();
    setTimeout(() => this.featureMenu(feat), 60);
  }
  /** Belirli bir extrude yüzey feature'ıyla böl (sağ-tık menüsünden). */
  private yuzeyBolFor(f: IOzellik): void {
    const idx = this.ozellikler.indexOf(f); if (idx < 0 || !this.govdeMesh) return;
    const node = this.ozellikNode('splitSurface', null);
    const feat: IOzellik = { tip: 'splitSurface', ekleme: false, hedefIndex: idx, tersYon: false, node };
    this.ozellikler.push(feat); this.featureNodeBagla(feat); this.rebuild(); this.d.agacGuncelle?.(); this.gecmisKaydet();
    setTimeout(() => this.featureMenu(feat), 60);
  }

  // ───────────────────── GSD Wireframe (tel kafes — 3D nokta/çizgi) ─────────────────────
  private telMeshler: THREE.Mesh[] = [];
  private telKafesKur(): void {
    for (const o of this.telMeshler) { (o.parent || this.d.sahne).remove(o); o.geometry?.dispose?.(); }
    this.telMeshler = [];
    const par = this.govdeGrupAl();
    const mat = new THREE.MeshBasicMaterial({ color: 0xfacc15 });   // sarı tel kafes
    for (const f of this.ozellikler) {
      if (f.bastirilmis) continue;
      try {
        if (f.tip === 'nokta3d') {
          const pos = this.noktaKonum(f);
          const geo = new THREE.SphereGeometry(1.4, 14, 10); geo.translate(pos.x, pos.y, pos.z);
          const mesh = new THREE.Mesh(geo, mat); mesh.name = 'FikoTelNokta';
          this.telEtkilesim(mesh, f); par.ekle!(mesh as any); this.telMeshler.push(mesh);
        } else if (f.tip === 'cizgi3d') {
          const [a, b] = this.cizgiUclar(f);
          const len = a.distanceTo(b); if (len < 0.01) continue;
          const geo = new THREE.CylinderGeometry(0.5, 0.5, len, 8);
          const mesh = new THREE.Mesh(geo, mat); mesh.name = 'FikoTelCizgi';
          mesh.position.copy(a).add(b).multiplyScalar(0.5);
          mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), b.clone().sub(a).normalize());
          this.telEtkilesim(mesh, f); par.ekle!(mesh as any); this.telMeshler.push(mesh);
        } else if (f.tip === 'cember3d') {
          const cb = this.cemberBilgi(f); if (!cb || cb.r < 0.01) continue;
          const geo = new THREE.TorusGeometry(cb.r, 0.5, 8, 48);
          const mesh = new THREE.Mesh(geo, mat); mesh.name = 'FikoTelCember';
          mesh.position.copy(cb.merkez);
          mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), cb.normal);
          this.telEtkilesim(mesh, f); par.ekle!(mesh as any); this.telMeshler.push(mesh);
        } else if (f.tip === 'polyline') {   // GSD Polyline: noktalardan düz çizgi segmentleri
          const pts = this.egriNoktalari(f);
          if (pts.length < 2) continue;
          const yol = new THREE.CurvePath<THREE.Vector3>();
          for (let k = 0; k < pts.length - 1; k++) yol.add(new THREE.LineCurve3(pts[k], pts[k + 1]));
          const geo = new THREE.TubeGeometry(yol as any, Math.max(8, (pts.length - 1) * 2), 0.5, 7, false);
          const mesh = new THREE.Mesh(geo, mat); mesh.name = 'FikoTelPolyline';
          this.telEtkilesim(mesh, f); par.ekle!(mesh as any); this.telMeshler.push(mesh);
        } else if (f.tip === 'spline3d' || f.tip === 'helix3d' || f.tip === 'spiral3d' || f.tip === 'paralelEgri' || f.tip === 'connectEgri' || f.tip === 'conic3d' || f.tip === 'curveSmooth' || f.tip === 'corner' || f.tip === 'isoparametrik' || f.tip === 'combine3d' || f.tip === 'equationCurve' || f.tip === 'spine3d') {
          const pts = this.egriNoktalari(f);
          if (pts.length < 2) continue;
          const curve = new THREE.CatmullRomCurve3(pts, false, 'catmullrom', 0.5);
          const geo = new THREE.TubeGeometry(curve, Math.max(16, pts.length * 8), 0.5, 7, false);
          const adH: Record<string, string> = { helix3d: 'FikoTelHelix', spiral3d: 'FikoTelSpiral', paralelEgri: 'FikoTelParalel', connectEgri: 'FikoTelConnect', conic3d: 'FikoTelConic', spline3d: 'FikoTelSpline', curveSmooth: 'FikoTelSmooth', corner: 'FikoTelCorner', isoparametrik: 'FikoTelIso', combine3d: 'FikoTelCombine', equationCurve: 'FikoTelEquation', spine3d: 'FikoTelSpine' };
          const mesh = new THREE.Mesh(geo, mat); mesh.name = adH[f.tip] || 'FikoTelSpline';
          this.telEtkilesim(mesh, f); par.ekle!(mesh as any); this.telMeshler.push(mesh);
        } else if (f.tip === 'duzlem3d') {
          const db = this.duzlemBilgi(f); if (!db) continue;
          const s = Math.max(2, f.duzlemBoyut ?? 50);
          const geo = new THREE.PlaneGeometry(s * 2, s * 2);
          const pmat = new THREE.MeshBasicMaterial({ color: 0x38bdf8, transparent: true, opacity: 0.22, side: THREE.DoubleSide, depthWrite: false });
          const mesh = new THREE.Mesh(geo, pmat); mesh.name = 'FikoTelDuzlem';
          mesh.position.copy(db.merkez);
          const m = new THREE.Matrix4().makeBasis(db.e1, db.e2, db.normal); mesh.quaternion.setFromRotationMatrix(m);
          this.telEtkilesim(mesh, f); par.ekle!(mesh as any); this.telMeshler.push(mesh);
        } else if (f.tip === 'kesisim3d') {   // GSD Intersection: iki shape'in GERÇEK kesişim eğrileri (BRepAlgoAPI_Section)
          const s1 = this.kesisimShapeAl(f.ref1), s2 = this.kesisimShapeAl(f.ref2);
          if (!s1 || !s2) continue;
          const kenarlar = fikoOCCT.kesit(s1, s2) || [];
          for (const k of kenarlar) {
            if (!k.pts || k.pts.length < 2) continue;
            const pts = k.pts.map(p => new THREE.Vector3(p.x, p.y, p.z));
            const curve = new THREE.CatmullRomCurve3(pts, false, 'catmullrom', 0.5);
            const geo = new THREE.TubeGeometry(curve, Math.max(8, pts.length * 4), 0.6, 6, false);
            const mesh = new THREE.Mesh(geo, mat); mesh.name = 'FikoTelKesisim';
            this.telEtkilesim(mesh, f); par.ekle!(mesh as any); this.telMeshler.push(mesh);
          }
        } else if (f.tip === 'yuzeyKenar') {   // GSD Boundary: bir yüzeyin sınır eğrileri
          const surf = this.ozellikler[f.ref1 ?? -1];
          if (!surf || surf.tip !== 'yuzey') continue;
          const sh = this.occtYuzeyShape(surf); if (!sh) continue;
          const kenarlar = fikoOCCT.shapeToEdges(sh) || [];
          for (const k of kenarlar) {
            if (!k.pts || k.pts.length < 2) continue;
            const pts = k.pts.map(p => new THREE.Vector3(p.x, p.y, p.z));
            const curve = new THREE.CatmullRomCurve3(pts, false, 'catmullrom', 0.5);
            const geo = new THREE.TubeGeometry(curve, Math.max(8, pts.length * 4), 0.6, 6, false);
            const mesh = new THREE.Mesh(geo, mat); mesh.name = 'FikoTelYuzeyKenar';
            this.telEtkilesim(mesh, f); par.ekle!(mesh as any); this.telMeshler.push(mesh);
          }
        }
      } catch (e) { }
    }
  }
  /** Çember bilgisi: merkez + normal + yarıçap (merkez+yarıçap veya üç-nokta circumcircle). */
  cemberBilgi(f: IOzellik): { merkez: THREE.Vector3; normal: THREE.Vector3; r: number } | null {
    if (f.cemberTip === 'uc-nokta') {
      const a = this.refNoktaKonum(f.ref1), b = this.refNoktaKonum(f.ref2), c = this.refNoktaKonum(f.ref3);
      const ab = b.clone().sub(a), ac = c.clone().sub(a); const n = ab.clone().cross(ac);
      if (n.lengthSq() < 1e-9) return null; n.normalize();
      // circumcenter (3D düzlemde)
      const ab2 = ab.lengthSq(), ac2 = ac.lengthSq();
      const abxac = ab.clone().cross(ac);
      const d = 2 * abxac.lengthSq();
      // circumcenter - a = ((|AB|²·AC − |AC|²·AB) × (AB×AC)) / (2|AB×AC|²)
      const u = ac.clone().multiplyScalar(ab2).sub(ab.clone().multiplyScalar(ac2)).cross(abxac).divideScalar(d);
      const merkez = a.clone().add(u);
      return { merkez, normal: n, r: merkez.distanceTo(a) };
    }
    if (f.cemberTip === 'merkez-nokta') {   // merkez (ref1/koord) + çevre noktası (ref2) → yarıçap = mesafe
      const mk = (f.ref1 != null && f.ref1 >= 0) ? this.refNoktaKonum(f.ref1) : new THREE.Vector3(f.p1x ?? 0, f.p1y ?? 0, f.p1z ?? 0);
      const cevre = this.refNoktaKonum(f.ref2);
      return { merkez: mk, normal: this.aynaNormal(f), r: Math.max(0.01, mk.distanceTo(cevre)) };
    }
    if (f.cemberTip === 'iki-nokta-cap') {   // iki nokta = çap uçları → merkez orta, yarıçap mesafe/2
      const a = this.refNoktaKonum(f.ref1), b = this.refNoktaKonum(f.ref2);
      return { merkez: a.clone().lerp(b, 0.5), normal: this.aynaNormal(f), r: Math.max(0.01, a.distanceTo(b) / 2) };
    }
    if (f.cemberTip === 'merkez-teget') {   // merkez (ref1) + bir çizgiye (ref2) teğet → r = merkez-çizgi mesafesi
      const mk = (f.ref1 != null && f.ref1 >= 0) ? this.refNoktaKonum(f.ref1) : new THREE.Vector3(f.p1x ?? 0, f.p1y ?? 0, f.p1z ?? 0);
      const ln = this.ozellikler[f.ref2 ?? -1];
      if (ln?.tip === 'cizgi3d') { const [a, b] = this.cizgiUclar(ln); const d = b.clone().sub(a).normalize(); const ap = mk.clone().sub(a); const dik = ap.clone().sub(d.clone().multiplyScalar(ap.dot(d))); const r = dik.length(); const n = d.clone().cross(dik).normalize(); return { merkez: mk, normal: n.lengthSq() > 1e-6 ? n : this.aynaNormal(f), r: Math.max(0.01, r) }; }
      return { merkez: mk, normal: this.aynaNormal(f), r: Math.max(0.01, f.yaricap ?? 20) };
    }
    if (f.cemberTip === 'bitangent') {   // iki çizgiye (ref1,ref2) teğet + yarıçap → köşe bisektörü üzerinde merkez
      const l1 = this.ozellikler[f.ref1 ?? -1], l2 = this.ozellikler[f.ref2 ?? -1];
      if (l1?.tip === 'cizgi3d' && l2?.tip === 'cizgi3d') {
        const cc = this.bitangentMerkez(l1, l2, Math.max(0.1, f.yaricap ?? 15));
        if (cc) return cc;
      }
      return { merkez: new THREE.Vector3(f.p1x ?? 0, f.p1y ?? 0, f.p1z ?? 0), normal: this.aynaNormal(f), r: Math.max(0.01, f.yaricap ?? 15) };
    }
    if (f.cemberTip === 'tritangent') {   // üç çizgiye teğet → oluşan üçgenin iç teğet çemberi
      const ls = [this.ozellikler[f.ref1 ?? -1], this.ozellikler[f.ref2 ?? -1], this.ozellikler[f.ref3 ?? -1]];
      if (ls.every(l => l?.tip === 'cizgi3d')) { const cc = this.tritangentMerkez(ls[0], ls[1], ls[2]); if (cc) return cc; }
      return { merkez: new THREE.Vector3(0, 0, 0), normal: this.aynaNormal(f), r: 10 };
    }
    if (f.cemberTip === 'merkez-eksen') {   // merkez (ref1/koord) + eksen çizgisi (eksenRef) → normal = çizgi yönü
      const mk = (f.ref1 != null && f.ref1 >= 0) ? this.refNoktaKonum(f.ref1) : new THREE.Vector3(f.p1x ?? 0, f.p1y ?? 0, f.p1z ?? 0);
      let n = this.aynaNormal(f);
      if (f.eksenRef != null && f.eksenRef >= 0 && this.ozellikler[f.eksenRef]?.tip === 'cizgi3d') { const [a, b] = this.cizgiUclar(this.ozellikler[f.eksenRef]); const d = b.clone().sub(a); if (d.lengthSq() > 1e-9) n = d.normalize(); }
      return { merkez: mk, normal: n, r: Math.max(0.01, f.yaricap ?? 20) };
    }
    const merkez = (f.ref1 != null && f.ref1 >= 0) ? this.refNoktaKonum(f.ref1) : new THREE.Vector3(f.p1x ?? 0, f.p1y ?? 0, f.p1z ?? 0);
    const normal = this.aynaNormal(f);
    return { merkez, normal, r: Math.max(0.01, f.yaricap ?? 20) };
  }
  /** İki çizgiye teğet, yarıçap r çemberin merkezi (köşe bisektörü üzerinde). */
  private bitangentMerkez(l1: IOzellik, l2: IOzellik, r: number): { merkez: THREE.Vector3; normal: THREE.Vector3; r: number } | null {
    const [a1, b1] = this.cizgiUclar(l1), [a2, b2] = this.cizgiUclar(l2);
    const d1 = b1.clone().sub(a1), d2 = b2.clone().sub(a2);
    const e = d1.dot(d1), g = d2.dot(d2), ff = d1.dot(d2), r12 = a1.clone().sub(a2), c1 = d1.dot(r12), c2 = d2.dot(r12);
    const den = e * g - ff * ff; if (Math.abs(den) < 1e-9) return null;
    const t = (ff * c2 - g * c1) / den; const P = a1.clone().addScaledVector(d1, t);
    const u1 = (b1.distanceTo(P) > a1.distanceTo(P) ? b1 : a1).clone().sub(P).normalize();
    const u2 = (b2.distanceTo(P) > a2.distanceTo(P) ? b2 : a2).clone().sub(P).normalize();
    const teta = Math.acos(Math.max(-1, Math.min(1, u1.dot(u2)))); if (teta < 1e-3 || teta > Math.PI - 1e-3) return null;
    const bis = u1.clone().add(u2).normalize(); const merkez = P.clone().addScaledVector(bis, r / Math.sin(teta / 2));
    const n = d1.clone().cross(d2).normalize();
    return { merkez, normal: n.lengthSq() > 1e-6 ? n : new THREE.Vector3(0, 0, 1), r };
  }
  /** Üç çizginin oluşturduğu üçgenin iç teğet çemberi (incircle). */
  private tritangentMerkez(l1: IOzellik, l2: IOzellik, l3: IOzellik): { merkez: THREE.Vector3; normal: THREE.Vector3; r: number } | null {
    const kesis = (la: IOzellik, lb: IOzellik): THREE.Vector3 | null => {
      const [a1, b1] = this.cizgiUclar(la), [a2, b2] = this.cizgiUclar(lb);
      const d1 = b1.clone().sub(a1), d2 = b2.clone().sub(a2);
      const e = d1.dot(d1), g = d2.dot(d2), ff = d1.dot(d2), r12 = a1.clone().sub(a2), c1 = d1.dot(r12), c2 = d2.dot(r12);
      const den = e * g - ff * ff; if (Math.abs(den) < 1e-9) return null;
      return a1.clone().addScaledVector(d1, (ff * c2 - g * c1) / den);
    };
    const A = kesis(l2, l3), B = kesis(l1, l3), C = kesis(l1, l2); if (!A || !B || !C) return null;
    const a = B.distanceTo(C), b = A.distanceTo(C), c = A.distanceTo(B); const per = a + b + c; if (per < 1e-6) return null;
    const merkez = A.clone().multiplyScalar(a).add(B.clone().multiplyScalar(b)).add(C.clone().multiplyScalar(c)).divideScalar(per);
    const s = per / 2; const alan = Math.sqrt(Math.max(0, s * (s - a) * (s - b) * (s - c))); const r = alan / s;
    const n = B.clone().sub(A).cross(C.clone().sub(A)).normalize();
    return { merkez, normal: n.lengthSq() > 1e-6 ? n : new THREE.Vector3(0, 0, 1), r: Math.max(0.01, r) };
  }
  /** Düzlem bilgisi: merkez + normal + iki dik eksen (e1,e2). CATIA Plane türleri. */
  duzlemBilgi(f: IOzellik): { merkez: THREE.Vector3; normal: THREE.Vector3; e1: THREE.Vector3; e2: THREE.Vector3 } | null {
    const t = f.duzlemTip || 'ofset';
    const eksenler = (n: THREE.Vector3) => {
      const u = Math.abs(n.x) > 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0);
      const e1 = new THREE.Vector3().crossVectors(n, u).normalize();
      const e2 = new THREE.Vector3().crossVectors(n, e1).normalize();
      return { e1, e2 };
    };
    if (t === 'uc-nokta') {
      const a = this.refNoktaKonum(f.ref1), b = this.refNoktaKonum(f.ref2), c = this.refNoktaKonum(f.ref3);
      const n = b.clone().sub(a).cross(c.clone().sub(a)); if (n.lengthSq() < 1e-9) return null; n.normalize();
      const merkez = a.clone().add(b).add(c).divideScalar(3);
      return { merkez, normal: n, ...eksenler(n) };
    }
    if (t === 'egriNormal') {   // eğriye dik düzlem (ref1=eğri, oran=konum) — normal = teğet
      const eg = this.ozellikler[f.ref1 ?? -1]; const pts = this.egriNoktalari(eg); if (pts.length < 2) return null;
      const o = Math.min(Math.max(f.oran ?? 0.5, 0), 1) * (pts.length - 1);
      const i = Math.floor(o), j = Math.min(i + 1, pts.length - 1);
      const merkez = pts[i].clone().lerp(pts[j], o - i);
      const n = pts[j].clone().sub(pts[Math.max(i, 0)]).normalize(); if (n.lengthSq() < 1e-9) return null;
      return { merkez, normal: n, ...eksenler(n) };
    }
    if (t === 'iki-cizgi') {   // iki çizgiyi içeren düzlem
      const l1 = this.ozellikler[f.ref1 ?? -1], l2 = this.ozellikler[f.ref2 ?? -1];
      if (!l1 || !l2) return null;
      const [a1, b1] = this.cizgiUclar(l1), [a2] = this.cizgiUclar(l2);
      const d1 = b1.clone().sub(a1), d2 = a2.clone().sub(a1);
      const n = d1.clone().cross(d2); if (n.lengthSq() < 1e-9) return null; n.normalize();
      return { merkez: a1.clone(), normal: n, ...eksenler(n) };
    }
    // ofset / paralelNokta: temel düzlem (aynaTip veya seçili duzlemRef) + ofset mesafesi VEYA nokta üzerinden
    const normal = this.aynaNormal(f);
    let merkez: THREE.Vector3;
    if (t === 'paralelNokta' && f.ref1 != null && f.ref1 >= 0) {
      merkez = this.refNoktaKonum(f.ref1);   // noktadan geçen paralel düzlem
    } else {
      merkez = normal.clone().multiplyScalar(f.splitOfset ?? 0);   // temel düzlemden ofset
    }
    return { merkez, normal, ...eksenler(normal) };
  }
  /** Bir feature'ın seçili düzlem normali: duzlemRef(seçilmiş düzlem) öncelikli, yoksa aynaTip(XY/XZ/YZ). */
  aynaNormal(f: IOzellik): THREE.Vector3 {
    if (f.duzlemRef != null && f.duzlemRef >= 0) {
      const d = this.ozellikler[f.duzlemRef];
      if (d && d.tip === 'duzlem3d') { const db = this.duzlemBilgi(d); if (db) return db.normal.clone(); }
    }
    const at = f.aynaTip || 'xy';
    return at === 'yz' ? new THREE.Vector3(1, 0, 0) : at === 'xz' ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(0, 0, 1);
  }
  /** Seçili düzlemin {merkez, normal} kökü: duzlemRef varsa o düzlemin, yoksa aynaTip standart (origin). */
  duzlemKok(f: IOzellik): { merkez: THREE.Vector3; normal: THREE.Vector3 } {
    if (f.duzlemRef != null && f.duzlemRef >= 0) {
      const d = this.ozellikler[f.duzlemRef];
      if (d && d.tip === 'duzlem3d') { const db = this.duzlemBilgi(d); if (db) return { merkez: db.merkez.clone(), normal: db.normal.clone() }; }
    }
    return { merkez: new THREE.Vector3(0, 0, 0), normal: this.aynaNormal(f) };
  }
  /** Dialog etiketi: seçili düzlem adı (XY/XZ/YZ veya Düzlem.N). */
  duzlemAdi(f: IOzellik): string {
    if (f.duzlemRef != null && f.duzlemRef >= 0) { const d = this.ozellikler[f.duzlemRef]; return (((d?.node as any)?.name || 'Düzlem') as string).split(' · ')[0]; }
    return (f.aynaTip || 'xy').toUpperCase();
  }
  /** Düzlem standart seçimi (XY/XZ/YZ) — duzlemRef temizlenir. */
  duzlemStandartSec(f: IOzellik, tip: 'xy' | 'xz' | 'yz'): void {
    f.duzlemRef = -1; this.padGuncelle(f, 'aynaTip', tip);
  }
  /** Wireframe çember ekle. */
  cember3dEkle(): void {
    const node = this.ozellikNode('cember3d', null);
    const feat: IOzellik = { tip: 'cember3d', ekleme: true, cemberTip: 'merkez', ref1: -1, p1x: 0, p1y: 0, p1z: 0, yaricap: 20, aynaTip: 'xy', ref2: -1, ref3: -1, node };
    this.ozellikler.push(feat); this.featureNodeBagla(feat); this.rebuild(); this.d.agacGuncelle?.(); this.gecmisKaydet();
    setTimeout(() => this.featureMenu(feat), 60);
  }
  /** Spline noktaları: refler[] nokta referanslarını dünya konumlarına çözer. */
  splineNoktalar(f: IOzellik): THREE.Vector3[] { return (f.refler || []).map(i => this.refNoktaKonum(i)); }
  /** Helix noktaları: merkez(p1/ref1)+aynaTip ekseni etrafında yaricap, helixYukseklik, helixTur. */
  helixNoktalar(f: IOzellik): THREE.Vector3[] {
    const o = (f.ref1 != null && f.ref1 >= 0) ? this.refNoktaKonum(f.ref1) : new THREE.Vector3(f.p1x ?? 0, f.p1y ?? 0, f.p1z ?? 0);
    const ax = this.aynaNormal(f);
    const u = Math.abs(ax.x) > 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0);
    const e1 = new THREE.Vector3().crossVectors(ax, u).normalize(), e2 = new THREE.Vector3().crossVectors(ax, e1).normalize();
    const r = Math.max(0.1, f.yaricap ?? 20), h = f.helixYukseklik ?? 60, tur = Math.max(0.1, f.helixTur ?? 3);
    const N = Math.max(24, Math.round(tur * 24)); const out: THREE.Vector3[] = [];
    for (let i = 0; i <= N; i++) { const t = i / N; const a = t * tur * Math.PI * 2; out.push(o.clone().addScaledVector(e1, Math.cos(a) * r).addScaledVector(e2, Math.sin(a) * r).addScaledVector(ax, t * h)); }
    return out;
  }
  /** Spiral noktaları: düzlemsel/konik Arşimet sarmalı. yaricap=başlangıç R, helixAdim=tur başına radyal artış, helixYukseklik=eksen boyu (0=düz). */
  spiralNoktalar(f: IOzellik): THREE.Vector3[] {
    const o = (f.ref1 != null && f.ref1 >= 0) ? this.refNoktaKonum(f.ref1) : new THREE.Vector3(f.p1x ?? 0, f.p1y ?? 0, f.p1z ?? 0);
    const ax = this.aynaNormal(f);
    const u = Math.abs(ax.x) > 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0);
    const e1 = new THREE.Vector3().crossVectors(ax, u).normalize(), e2 = new THREE.Vector3().crossVectors(ax, e1).normalize();
    const r0 = Math.max(0, f.yaricap ?? 5), adim = f.helixAdim ?? 10, h = f.helixYukseklik ?? 0, tur = Math.max(0.1, f.helixTur ?? 3);
    const N = Math.max(48, Math.round(tur * 48)); const out: THREE.Vector3[] = [];
    for (let i = 0; i <= N; i++) { const t = i / N; const a = t * tur * Math.PI * 2; const r = Math.max(0.01, r0 + adim * (a / (Math.PI * 2))); out.push(o.clone().addScaledVector(e1, Math.cos(a) * r).addScaledVector(e2, Math.sin(a) * r).addScaledVector(ax, t * h)); }
    return out;
  }
  /** Wireframe spline ekle (mevcut tüm noktaları sırayla referans alır). */
  spline3dEkle(): void {
    const refler: number[] = []; this.ozellikler.forEach((f, i) => { if (f.tip === 'nokta3d' && !f.bastirilmis) refler.push(i); });
    const node = this.ozellikNode('spline3d', null);
    const feat: IOzellik = { tip: 'spline3d', ekleme: true, refler, node };
    this.ozellikler.push(feat); this.featureNodeBagla(feat); this.rebuild(); this.d.agacGuncelle?.(); this.gecmisKaydet();
    setTimeout(() => this.featureMenu(feat), 60);
  }
  /** Wireframe polyline ekle (noktalardan düz segmentler). */
  polyline3dEkle(): void {
    const refler: number[] = []; this.ozellikler.forEach((f, i) => { if (f.tip === 'nokta3d' && !f.bastirilmis) refler.push(i); });
    const node = this.ozellikNode('polyline', null);
    const feat: IOzellik = { tip: 'polyline', ekleme: true, refler, node };
    this.ozellikler.push(feat); this.featureNodeBagla(feat); this.rebuild(); this.d.agacGuncelle?.(); this.gecmisKaydet();
    setTimeout(() => this.featureMenu(feat), 60);
  }
  /** GSD Curve Smooth ekle: son eğriyi yumuşat. */
  curveSmoothEkle(): void {
    let ref = -1; const egri = new Set(['cizgi3d', 'cember3d', 'spline3d', 'polyline', 'helix3d', 'spiral3d', 'conic3d', 'paralelEgri', 'connectEgri']);
    this.ozellikler.forEach((s, i) => { if (egri.has(s.tip) && !s.bastirilmis) ref = i; });
    if (ref < 0) { this.d.bildir?.('Yumuşatmak için bir eğri oluştur'); return; }
    const feat: IOzellik = { tip: 'curveSmooth', ekleme: true, ref1: ref, param: 3, node: this.ozellikNode('curveSmooth', null) };
    this.ozellikler.push(feat); this.featureNodeBagla(feat); this.rebuild(); this.d.agacGuncelle?.(); this.gecmisKaydet();
    setTimeout(() => this.featureMenu(feat), 60);
  }
  /** GSD Corner ekle: ilk iki çizgi arası yarıçaplı köşe. */
  cornerEkle(): void {
    const ln: number[] = []; this.ozellikler.forEach((s, i) => { if (s.tip === 'cizgi3d' && !s.bastirilmis) ln.push(i); });
    if (ln.length < 2) { this.d.bildir?.('Köşe için en az 2 çizgi gerekli'); return; }
    const feat: IOzellik = { tip: 'corner', ekleme: true, ref1: ln[0], ref2: ln[1], yaricap: 10, node: this.ozellikNode('corner', null) };
    this.ozellikler.push(feat); this.featureNodeBagla(feat); this.rebuild(); this.d.agacGuncelle?.(); this.gecmisKaydet();
    setTimeout(() => this.featureMenu(feat), 60);
  }
  /** GSD Isoparametric ekle: son yüzey/gövdeden iso eğri. */
  isoparametrikEkle(): void {
    let ref = -1; this.ozellikler.forEach((s, i) => { if (s.tip === 'yuzey' && !s.bastirilmis) ref = i; });
    if (ref < 0 && this.govdeMesh) ref = -2;
    if (ref === -1) { this.d.bildir?.('Iso eğri için bir yüzey/gövde gerekli'); return; }
    const feat: IOzellik = { tip: 'isoparametrik', ekleme: true, ref1: ref, oran: 0.5, tersYon: false, node: this.ozellikNode('isoparametrik', null) };
    this.ozellikler.push(feat); this.featureNodeBagla(feat); this.rebuild(); this.d.agacGuncelle?.(); this.gecmisKaydet();
    setTimeout(() => this.featureMenu(feat), 60);
  }
  /** GSD Combine ekle: iki eğrinin uzatma kesişimi → 3D eğri. */
  combine3dEkle(): void {
    const eg: number[] = []; const egri = new Set(['cizgi3d', 'cember3d', 'spline3d', 'polyline', 'helix3d', 'spiral3d', 'conic3d']);
    this.ozellikler.forEach((s, i) => { if (egri.has(s.tip) && !s.bastirilmis) eg.push(i); });
    if (eg.length < 2) { this.d.bildir?.('Combine için en az 2 eğri gerekli'); return; }
    const feat: IOzellik = { tip: 'combine3d', ekleme: true, ref1: eg[0], ref2: eg[1], node: this.ozellikNode('combine3d', null) };
    this.ozellikler.push(feat); this.featureNodeBagla(feat); this.rebuild(); this.d.agacGuncelle?.(); this.gecmisKaydet();
    setTimeout(() => this.featureMenu(feat), 60);
  }
  /** GSD Curve from Equation ekle: parametrik f(t) eğrisi. */
  equationCurveEkle(): void {
    const feat: IOzellik = { tip: 'equationCurve', ekleme: true, eqX: '40*cos(t)', eqY: '40*sin(t)', eqZ: '6*t', eqTMin: 0, eqTMax: Math.PI * 2, eqAdim: 120, node: this.ozellikNode('equationCurve', null) };
    this.ozellikler.push(feat); this.featureNodeBagla(feat); this.rebuild(); this.d.agacGuncelle?.(); this.gecmisKaydet();
    setTimeout(() => this.featureMenu(feat), 60);
  }
  /** OCCT Pipe ekle: son 3D eğri boyunca dairesel boru (solid). */
  pipe3dEkle(): void {
    let ref = -1; const egri = new Set(['cizgi3d', 'cember3d', 'spline3d', 'polyline', 'curveSmooth', 'helix3d', 'spiral3d', 'conic3d', 'combine3d', 'spine3d', 'equationCurve']);
    this.ozellikler.forEach((s, i) => { if (egri.has(s.tip) && !s.bastirilmis) ref = i; });
    if (ref < 0) { this.d.bildir?.('Boru için önce bir 3D eğri (spline/helis/spine/denklem) oluştur'); return; }
    const feat: IOzellik = { tip: 'pipe3d', ekleme: true, ref1: ref, pCap: 16, node: this.ozellikNode('pipe3d', null) };
    this.ozellikler.push(feat); this.featureNodeBagla(feat); this.rebuild(); this.d.agacGuncelle?.(); this.gecmisKaydet();
    setTimeout(() => this.featureMenu(feat), 60);
  }
  /** GSD Spine ekle: mevcut eğri/düzlem kesitlerinin merkezlerinden omurga. */
  spine3dEkle(): void {
    const refler: number[] = []; const tipler = new Set(['cember3d', 'duzlem3d', 'spline3d', 'cizgi3d', 'polyline', 'conic3d']);
    this.ozellikler.forEach((s, i) => { if (tipler.has(s.tip) && !s.bastirilmis) refler.push(i); });
    if (refler.length < 2) { this.d.bildir?.('Omurga için en az 2 kesit (çember/düzlem/eğri) gerekli'); return; }
    const feat: IOzellik = { tip: 'spine3d', ekleme: true, refler, node: this.ozellikNode('spine3d', null) };
    this.ozellikler.push(feat); this.featureNodeBagla(feat); this.rebuild(); this.d.agacGuncelle?.(); this.gecmisKaydet();
    setTimeout(() => this.featureMenu(feat), 60);
  }
  /** Wireframe helix ekle. */
  helix3dEkle(): void {
    const node = this.ozellikNode('helix3d', null);
    const feat: IOzellik = { tip: 'helix3d', ekleme: true, ref1: -1, p1x: 0, p1y: 0, p1z: 0, yaricap: 20, helixYukseklik: 60, helixTur: 3, aynaTip: 'xy', node };
    this.ozellikler.push(feat); this.featureNodeBagla(feat); this.rebuild(); this.d.agacGuncelle?.(); this.gecmisKaydet();
    setTimeout(() => this.featureMenu(feat), 60);
  }
  /** Paralel eğri: kaynak eğriyi (ref1) seçili düzlem içinde mesafe(param) kadar ofsetler. */
  paralelEgriNoktalari(f: IOzellik): THREE.Vector3[] {
    const kaynak = this.egriNoktalari(this.ozellikler[f.ref1 ?? -1]); if (kaynak.length < 2) return [];
    const n = this.aynaNormal(f), d = f.param ?? 10; const out: THREE.Vector3[] = [];
    for (let i = 0; i < kaynak.length; i++) {
      const prev = kaynak[Math.max(i - 1, 0)], next = kaynak[Math.min(i + 1, kaynak.length - 1)];
      const teg = next.clone().sub(prev).normalize();
      const ofsYon = new THREE.Vector3().crossVectors(teg, n).normalize();
      if (ofsYon.lengthSq() < 1e-9) { out.push(kaynak[i].clone()); continue; }
      out.push(kaynak[i].clone().addScaledVector(ofsYon, d));
    }
    return out;
  }
  /** Connect eğrisi: iki eğrinin (ref1,ref2) yakın uçlarını teğet-sürekli cubic Bézier ile bağlar. */
  connectNoktalari(f: IOzellik): THREE.Vector3[] {
    const e1 = this.egriNoktalari(this.ozellikler[f.ref1 ?? -1]), e2 = this.egriNoktalari(this.ozellikler[f.ref2 ?? -1]);
    if (e1.length < 2 || e2.length < 2) return [];
    // e1'in son ucu ile e2'nin (en yakın) ucunu bağla
    const p0 = e1[e1.length - 1];
    const e2Bas = e2[0], e2Son = e2[e2.length - 1];
    const p3 = p0.distanceTo(e2Bas) <= p0.distanceTo(e2Son) ? e2Bas : e2Son;
    const t0 = p0.clone().sub(e1[e1.length - 2]).normalize();   // e1 çıkış teğeti
    const t3 = (p3 === e2Bas ? e2[1] : e2[e2.length - 2]).clone().sub(p3).normalize();   // e2 giriş teğeti
    const L = p0.distanceTo(p3) * (f.param ?? 0.4);   // teğet kolu uzunluğu (gerginlik)
    const c1 = p0.clone().addScaledVector(t0, L), c2 = p3.clone().addScaledVector(t3, -L);
    const out: THREE.Vector3[] = []; const N = 40;
    for (let i = 0; i <= N; i++) {
      const t = i / N, u = 1 - t;
      out.push(p0.clone().multiplyScalar(u * u * u)
        .addScaledVector(c1, 3 * u * u * t).addScaledVector(c2, 3 * u * t * t).addScaledVector(p3, t * t * t));
    }
    return out;
  }
  /** Konik eğri: başlangıç(ref1) + bitiş(ref2) + tepe/kontrol(ref3) — rational quadratic Bézier.
   *  ρ(param): 0.5=parabol, <0.5=elips, >0.5=hiperbol. */
  conicNoktalari(f: IOzellik): THREE.Vector3[] {
    const p0 = this.refNoktaKonum(f.ref1), p2 = this.refNoktaKonum(f.ref2), pk = this.refNoktaKonum(f.ref3);
    if (p0.distanceTo(p2) < 1e-6) return [];
    const ro = Math.min(Math.max(f.param ?? 0.5, 0.05), 0.95); const w = ro / (1 - ro);   // ağırlık (parabol w=1)
    const out: THREE.Vector3[] = []; const N = 48;
    for (let i = 0; i <= N; i++) {
      const t = i / N, u = 1 - t;
      const b0 = u * u, b1 = 2 * u * t * w, b2 = t * t; const pay = b0 + b1 + b2;
      out.push(p0.clone().multiplyScalar(b0 / pay).addScaledVector(pk, b1 / pay).addScaledVector(p2, b2 / pay));
    }
    return out;
  }
  /** Wireframe spiral ekle (Arşimet sarmalı). */
  spiral3dEkle(): void {
    const node = this.ozellikNode('spiral3d', null);
    const feat: IOzellik = { tip: 'spiral3d', ekleme: true, ref1: -1, p1x: 0, p1y: 0, p1z: 0, yaricap: 5, helixAdim: 12, helixYukseklik: 0, helixTur: 3, aynaTip: 'xy', node };
    this.ozellikler.push(feat); this.featureNodeBagla(feat); this.rebuild(); this.d.agacGuncelle?.(); this.gecmisKaydet();
    setTimeout(() => this.featureMenu(feat), 60);
  }
  /** Paralel eğri ekle (ilk eğriyi varsayılan kaynak alır). */
  paralelEgri3dEkle(): void {
    let kaynak = -1; this.ozellikler.forEach((f, i) => { if (kaynak < 0 && ['cizgi3d', 'cember3d', 'spline3d', 'helix3d', 'spiral3d'].includes(f.tip) && !f.bastirilmis) kaynak = i; });
    const node = this.ozellikNode('paralelEgri', null);
    const feat: IOzellik = { tip: 'paralelEgri', ekleme: true, ref1: kaynak, param: 10, aynaTip: 'xy', node };
    this.ozellikler.push(feat); this.featureNodeBagla(feat); this.rebuild(); this.d.agacGuncelle?.(); this.gecmisKaydet();
    setTimeout(() => this.featureMenu(feat), 60);
  }
  /** Connect eğrisi ekle (ilk iki eğriyi varsayılan alır). */
  connectEgri3dEkle(): void {
    const egriler: number[] = []; this.ozellikler.forEach((f, i) => { if (['cizgi3d', 'cember3d', 'spline3d', 'helix3d', 'spiral3d'].includes(f.tip) && !f.bastirilmis) egriler.push(i); });
    const node = this.ozellikNode('connectEgri', null);
    const feat: IOzellik = { tip: 'connectEgri', ekleme: true, ref1: egriler[0] ?? -1, ref2: egriler[1] ?? -1, param: 0.4, node };
    this.ozellikler.push(feat); this.featureNodeBagla(feat); this.rebuild(); this.d.agacGuncelle?.(); this.gecmisKaydet();
    setTimeout(() => this.featureMenu(feat), 60);
  }
  /** Konik eğri ekle (ilk 3 noktayı varsayılan alır). */
  conic3dEkle(): void {
    const nk: number[] = []; this.ozellikler.forEach((f, i) => { if (f.tip === 'nokta3d' && !f.bastirilmis) nk.push(i); });
    const node = this.ozellikNode('conic3d', null);
    const feat: IOzellik = { tip: 'conic3d', ekleme: true, ref1: nk[0] ?? -1, ref2: nk[1] ?? -1, ref3: nk[2] ?? -1, param: 0.5, node };
    this.ozellikler.push(feat); this.featureNodeBagla(feat); this.rebuild(); this.d.agacGuncelle?.(); this.gecmisKaydet();
    setTimeout(() => this.featureMenu(feat), 60);
  }
  /** Yüzey sınırı (Boundary): son yüzeyin kenarlarını eğri olarak çıkar. */
  yuzeyKenar3dEkle(): void {
    let surf = -1; this.ozellikler.forEach((f, i) => { if (f.tip === 'yuzey' && !f.bastirilmis) surf = i; });
    if (surf < 0) { this.d.bildir?.('Önce bir yüzey oluştur'); return; }
    const node = this.ozellikNode('yuzeyKenar', null);
    const feat: IOzellik = { tip: 'yuzeyKenar', ekleme: true, ref1: surf, node };
    this.ozellikler.push(feat); this.featureNodeBagla(feat); this.rebuild(); this.d.agacGuncelle?.(); this.gecmisKaydet();
    setTimeout(() => this.featureMenu(feat), 60);
  }
  /** Dialog için: yüzey feature'ları {idx,etiket}. */
  yuzeySecenekleri(feat: IOzellik): { deger: number; etiket: string }[] {
    const out: { deger: number; etiket: string }[] = [];
    this.ozellikler.forEach((f, i) => { if (f.tip === 'yuzey' && f !== feat) out.push({ deger: i, etiket: (f.node as any)?.name || 'Yüzey.' + (i + 1) }); });
    return out;
  }
  /** Wireframe düzlem ekle (CATIA Plane). */
  duzlem3dEkle(): void {
    const node = this.ozellikNode('duzlem3d', null);
    const feat: IOzellik = { tip: 'duzlem3d', ekleme: true, duzlemTip: 'ofset', aynaTip: 'xy', splitOfset: 30, duzlemBoyut: 50, ref1: -1, ref2: -1, ref3: -1, oran: 0.5, node };
    this.ozellikler.push(feat); this.featureNodeBagla(feat); this.rebuild(); this.d.agacGuncelle?.(); this.gecmisKaydet();
    setTimeout(() => this.featureMenu(feat), 60);
  }
  /** Bir nokta feature'ının dünya konumu (koordinat / arası / eğri üzerinde — referanslı çözüm). */
  noktaKonum(f: IOzellik): THREE.Vector3 {
    if (f.noktaTip === 'arasi') {
      const a = this.refNoktaKonum(f.ref1), b = this.refNoktaKonum(f.ref2);
      return a.clone().lerp(b, f.oran ?? 0.5);
    }
    if (f.noktaTip === 'egriUzerinde') {
      const ln = this.ozellikler[f.ref1 ?? -1];
      if (ln) {
        const pts = this.egriNoktalari(ln);
        if (pts.length >= 2) {   // oran [0..1] eğri boyunca enterpolasyon (çizgi/çember/spline/helis)
          const t = Math.min(Math.max(f.oran ?? 0.5, 0), 1) * (pts.length - 1);
          const i = Math.floor(t), j = Math.min(i + 1, pts.length - 1);
          return pts[i].clone().lerp(pts[j], t - i);
        }
      }
    }
    if (f.noktaTip === 'duzlemde') {   // bir düzlem üzerinde (u,v) konumunda nokta
      const db = this.duzlemBilgi(this.ozellikler[f.ref1 ?? -1]);
      if (db) return db.merkez.clone().addScaledVector(db.e1, f.p1x ?? 0).addScaledVector(db.e2, f.p1y ?? 0);
    }
    if (f.noktaTip === 'projeksiyon') {   // bir noktayı (ref1) seçili düzleme dik izdüşür
      const P = this.refNoktaKonum(f.ref1); const dk = this.duzlemKok(f);
      return P.clone().sub(dk.normal.clone().multiplyScalar(P.clone().sub(dk.merkez).dot(dk.normal)));
    }
    if (f.noktaTip === 'extremum') {   // referans eğrinin bir eksen boyunca min/max noktası
      const pts = this.egriNoktalari(this.ozellikler[f.ref1 ?? -1]);
      if (pts.length) {
        const ek = f.eksenTip || 'z'; const bb = (p: THREE.Vector3) => ek === 'x' ? p.x : ek === 'y' ? p.y : p.z;
        return pts.reduce((best, p) => (f.tersYon ? bb(p) < bb(best) : bb(p) > bb(best)) ? p : best).clone();
      }
    }
    if (f.noktaTip === 'cemberMerkez') {   // bir çemberin/eğrinin merkez noktası
      const eg = this.ozellikler[f.ref1 ?? -1];
      if (eg?.tip === 'cember3d') { const cb = this.cemberBilgi(eg); if (cb) return cb.merkez.clone(); }
      const pts = this.egriNoktalari(eg); if (pts.length) { const c = new THREE.Vector3(); pts.forEach(p => c.add(p)); return c.divideScalar(pts.length); }
    }
    if (f.noktaTip === 'ortalama') {   // N noktanın ağırlık merkezi (mean through points)
      const ids = (f.refler && f.refler.length) ? f.refler : [f.ref1, f.ref2, f.ref3].filter(i => i != null && i >= 0) as number[];
      if (ids.length) { const c = new THREE.Vector3(); ids.forEach(i => c.add(this.refNoktaKonum(i))); return c.divideScalar(ids.length); }
    }
    if (f.noktaTip === 'tegetEgri') {   // eğri üzerinde teğeti belirtilen eksene paralel olan nokta
      const pts = this.egriNoktalari(this.ozellikler[f.ref1 ?? -1]);
      if (pts.length >= 2) {
        const ek = f.eksenTip || 'x'; const ax = ek === 'x' ? new THREE.Vector3(1, 0, 0) : ek === 'y' ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(0, 0, 1);
        let best = pts[0], bestDot = -1;
        for (let i = 0; i < pts.length; i++) { const a = pts[Math.max(i - 1, 0)], b = pts[Math.min(i + 1, pts.length - 1)]; const t = b.clone().sub(a).normalize(); const d = Math.abs(t.dot(ax)); if (d > bestDot) { bestDot = d; best = pts[i]; } }
        return best.clone();
      }
    }
    if (f.noktaTip === 'polarExtremum') {   // eğri üzerinde bir eksene en uzak/yakın (polar yarıçap) nokta
      const pts = this.egriNoktalari(this.ozellikler[f.ref1 ?? -1]);
      if (pts.length) {
        const ek = f.eksenTip || 'z'; const ax = ek === 'x' ? new THREE.Vector3(1, 0, 0) : ek === 'y' ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(0, 0, 1);
        const rad = (p: THREE.Vector3) => p.clone().sub(ax.clone().multiplyScalar(p.dot(ax))).length();   // eksene dik mesafe
        return pts.reduce((best, p) => (f.tersYon ? rad(p) < rad(best) : rad(p) > rad(best)) ? p : best).clone();
      }
    }
    return new THREE.Vector3(f.p1x ?? 0, f.p1y ?? 0, f.p1z ?? 0);
  }
  /** Bir eğri feature'ının örnek noktaları (çizgi/çember/spline/helix) — extremum/projeksiyon için. */
  egriNoktalari(f?: IOzellik): THREE.Vector3[] {
    if (!f) return [];
    if (f.tip === 'cizgi3d') { const [a, b] = this.cizgiUclar(f); return [a, b]; }
    if (f.tip === 'spline3d' || f.tip === 'polyline') return this.splineNoktalar(f);
    if (f.tip === 'curveSmooth') return this.curveSmoothNoktalari(f);
    if (f.tip === 'corner') return this.cornerNoktalari(f);
    if (f.tip === 'isoparametrik') return this.isoNoktalari(f);
    if (f.tip === 'combine3d') return this.combineNoktalari(f);
    if (f.tip === 'equationCurve') return this.equationNoktalari(f);
    if (f.tip === 'spine3d') return this.spineNoktalari(f);
    if (f.tip === 'helix3d') return this.helixNoktalar(f);
    if (f.tip === 'spiral3d') return this.spiralNoktalar(f);
    if (f.tip === 'paralelEgri') return this.paralelEgriNoktalari(f);
    if (f.tip === 'connectEgri') return this.connectNoktalari(f);
    if (f.tip === 'conic3d') return this.conicNoktalari(f);
    if (f.tip === 'cember3d') {
      const cb = this.cemberBilgi(f); if (!cb) return [];
      const u = Math.abs(cb.normal.x) > 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0);
      const e1 = new THREE.Vector3().crossVectors(cb.normal, u).normalize(), e2 = new THREE.Vector3().crossVectors(cb.normal, e1).normalize();
      const out: THREE.Vector3[] = []; for (let i = 0; i < 48; i++) { const a = (i / 48) * Math.PI * 2; out.push(cb.merkez.clone().addScaledVector(e1, Math.cos(a) * cb.r).addScaledVector(e2, Math.sin(a) * cb.r)); } return out;
    }
    return [];
  }
  /** GSD Curve Smooth: kaynak eğriyi Laplacian yumuşatma ile düzleştir (uçlar sabit). */
  private curveSmoothNoktalari(f: IOzellik): THREE.Vector3[] {
    const src = this.egriNoktalari(this.ozellikler[f.ref1 ?? -1]); if (src.length < 3) return src.map(p => p.clone());
    let pts = src.map(p => p.clone()); const iter = Math.max(1, Math.round(f.param ?? 3));
    for (let k = 0; k < iter; k++) { const np = pts.map(p => p.clone()); for (let i = 1; i < pts.length - 1; i++) np[i] = pts[i - 1].clone().add(pts[i + 1]).add(pts[i]).divideScalar(3); pts = np; }
    return pts;
  }
  /** GSD Corner: iki çizgi arasında yarıçaplı teğet yay (köşe yuvarlama). */
  private cornerNoktalari(f: IOzellik): THREE.Vector3[] {
    const l1 = this.ozellikler[f.ref1 ?? -1], l2 = this.ozellikler[f.ref2 ?? -1];
    if (l1?.tip !== 'cizgi3d' || l2?.tip !== 'cizgi3d') return [];
    const [a1, b1] = this.cizgiUclar(l1), [a2, b2] = this.cizgiUclar(l2);
    const d1 = b1.clone().sub(a1), d2 = b2.clone().sub(a2);
    // iki doğrunun en yakın noktası (kesişim ~ P)
    const r12 = a1.clone().sub(a2); const e = d1.dot(d1), g = d2.dot(d2), ff = d1.dot(d2), c1 = d1.dot(r12), c2 = d2.dot(r12);
    const den = e * g - ff * ff; if (Math.abs(den) < 1e-9) return [];
    const t = (ff * c2 - g * c1) / den; const P = a1.clone().addScaledVector(d1, t);
    // P'den çizgi gövdelerine doğru yön: uzak uç tarafı
    const u1 = (b1.distanceTo(P) > a1.distanceTo(P) ? b1 : a1).clone().sub(P).normalize();
    const u2 = (b2.distanceTo(P) > a2.distanceTo(P) ? b2 : a2).clone().sub(P).normalize();
    const teta = Math.acos(Math.max(-1, Math.min(1, u1.dot(u2)))); if (teta < 1e-3 || teta > Math.PI - 1e-3) return [];
    const r = Math.max(0.1, f.yaricap ?? 10);
    const tanMes = r / Math.tan(teta / 2);
    const T1 = P.clone().addScaledVector(u1, tanMes), T2 = P.clone().addScaledVector(u2, tanMes);
    const bis = u1.clone().add(u2).normalize(); const C = P.clone().addScaledVector(bis, r / Math.sin(teta / 2));
    const v1 = T1.clone().sub(C), v2 = T2.clone().sub(C); const yayAci = v1.angleTo(v2);
    const eksen = v1.clone().cross(v2).normalize();
    const out: THREE.Vector3[] = []; const N = 24;
    for (let i = 0; i <= N; i++) { const q = v1.clone().applyAxisAngle(eksen, yayAci * i / N); out.push(C.clone().add(q)); }
    return out;
  }
  /** GSD Isoparametric: kaynak yüzeyin sabit-u/sabit-v iso eğrisi (düzlemle kesit yaklaşımı). */
  private isoNoktalari(f: IOzellik): THREE.Vector3[] {
    const src = this.kesisimShapeAl(f.ref1); if (!src) return [];
    try {
      // yüzeyin kapsamı içinde, seçili eksene dik düzlemle keserek iso-benzeri kesit eğrisi
      const faces = fikoOCCT.yuzler(src); if (!faces.length) return [];
      const mc = fikoOCCT.yuzMerkez(faces[0]); const K = this.govdeKapsam() || 100;
      const t = Math.min(Math.max(f.oran ?? 0.5, 0), 1);
      const isU = !f.tersYon;   // u→ X düzlemi, v→ Y düzlemi
      const o = isU ? { x: mc.x + (t - 0.5) * 2 * K, y: mc.y, z: mc.z } : { x: mc.x, y: mc.y + (t - 0.5) * 2 * K, z: mc.z };
      const n = isU ? { x: 1, y: 0, z: 0 } : { x: 0, y: 1, z: 0 };
      const segs = fikoOCCT.kesitKenarlari(src, o, n); if (!segs?.length) return [];
      return segs[0].pts.map((p: any) => new THREE.Vector3(p.x, p.y, p.z));
    } catch (e) { return []; }
  }
  /** GSD Combine: iki eğrinin (kendi düzlem normalleri boyunca uzatılıp) kesişimi → 3D eğri. */
  private combineNoktalari(f: IOzellik): THREE.Vector3[] {
    const c1 = this.egriNoktalari(this.ozellikler[f.ref1 ?? -1]), c2 = this.egriNoktalari(this.ozellikler[f.ref2 ?? -1]);
    if (c1.length < 2 || c2.length < 2) return [];
    const norm = (pts: THREE.Vector3[]) => { const a = pts[0], b = pts[Math.floor(pts.length / 2)], c = pts[pts.length - 1]; const n = b.clone().sub(a).cross(c.clone().sub(a)); return n.lengthSq() > 1e-9 ? n.normalize() : new THREE.Vector3(0, 0, 1); };
    const K = this.govdeKapsam() || 200;
    try {
      const s1 = fikoOCCT.makeYuzeyExtrude(c1.map(p => ({ x: p.x, y: p.y, z: p.z })), this.vecObj(norm(c1).multiplyScalar(2 * K)), false);
      const s2 = fikoOCCT.makeYuzeyExtrude(c2.map(p => ({ x: p.x, y: p.y, z: p.z })), this.vecObj(norm(c2).multiplyScalar(2 * K)), false);
      const segs = fikoOCCT.kesit(s1, s2); if (!segs?.length) return [];
      return segs[0].pts.map((p: any) => new THREE.Vector3(p.x, p.y, p.z));
    } catch (e) { return []; }
  }
  private vecObj(v: THREE.Vector3): { x: number; y: number; z: number } { return { x: v.x, y: v.y, z: v.z }; }
  /** Parametrik denklemi t fonksiyonuna çevir (Math fonksiyonları açık: sin/cos/PI...). */
  private eqFn(expr: string): (t: number) => number {
    const pre = 'const {sin,cos,tan,asin,acos,atan,atan2,sinh,cosh,tanh,abs,sqrt,cbrt,pow,exp,log,log2,log10,floor,ceil,round,sign,min,max,PI,E,hypot}=Math;';
    return new Function('t', pre + 'return (' + (expr || '0') + ');') as any;
  }
  /** GSD Curve from Equation: x(t),y(t),z(t) parametrik eğri (t ∈ [tMin,tMax]). */
  private equationNoktalari(f: IOzellik): THREE.Vector3[] {
    const ex = f.eqX || '40*cos(t)', ey = f.eqY || '40*sin(t)', ez = f.eqZ || '6*t';
    const tMin = f.eqTMin ?? 0, tMax = f.eqTMax ?? (Math.PI * 2), N = Math.max(8, Math.round(f.eqAdim ?? 120));
    let fx: any, fy: any, fz: any;
    try { fx = this.eqFn(ex); fy = this.eqFn(ey); fz = this.eqFn(ez); } catch (e) { return []; }
    const out: THREE.Vector3[] = [];
    for (let i = 0; i <= N; i++) {
      const t = tMin + (tMax - tMin) * i / N;
      try { const x = +fx(t), y = +fy(t), z = +fz(t); if (isFinite(x) && isFinite(y) && isFinite(z)) out.push(new THREE.Vector3(x, y, z)); } catch (e) { }
    }
    return out;
  }
  /** GSD Spine: seçili eğri/düzlem "kesitlerinin" merkezlerinden geçen pürüzsüz omurga eğrisi. */
  private spineNoktalari(f: IOzellik): THREE.Vector3[] {
    const ids = (f.refler && f.refler.length) ? f.refler : [];
    const merkezler: THREE.Vector3[] = [];
    for (const i of ids) {
      const o = this.ozellikler[i]; if (!o) continue;
      if (o.tip === 'duzlem3d') { const db = this.duzlemBilgi(o); if (db) merkezler.push(db.merkez.clone()); }
      else if (o.tip === 'cember3d') { const cb = this.cemberBilgi(o); if (cb) merkezler.push(cb.merkez.clone()); }
      else { const pts = this.egriNoktalari(o); if (pts.length) { const c = new THREE.Vector3(); pts.forEach(p => c.add(p)); merkezler.push(c.divideScalar(pts.length)); } }
    }
    if (merkezler.length < 2) return merkezler;
    // merkezlerden geçen pürüzsüz eğri (Catmull-Rom örnekle)
    const curve = new THREE.CatmullRomCurve3(merkezler, false, 'centripetal');
    return curve.getPoints(Math.max(24, merkezler.length * 12));
  }
  private refNoktaKonum(idx?: number): THREE.Vector3 {
    const f = this.ozellikler[idx ?? -1];
    return f && f.tip === 'nokta3d' ? this.noktaKonum(f) : new THREE.Vector3();
  }
  /** Çizgi feature'ının iki ucu (dünya). Tür: iki-nokta (ref/koordinat) veya nokta-yön. */
  cizgiUclar(f: IOzellik): [THREE.Vector3, THREE.Vector3] {
    const a = (f.ref1 != null && f.ref1 >= 0) ? this.refNoktaKonum(f.ref1) : new THREE.Vector3(f.p1x ?? 0, f.p1y ?? 0, f.p1z ?? 0);
    if (f.cizgiTip === 'nokta-yon') {
      const ek = f.eksenTip || 'x';
      const dir = ek === 'x' ? new THREE.Vector3(1, 0, 0) : ek === 'y' ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(0, 0, 1);
      return [a, a.clone().addScaledVector(dir, f.param ?? 40)];
    }
    if (f.cizgiTip === 'aci') {   // referans çizginin yönünü açı kadar döndür → yeni çizgi (referansın başından)
      const rl = this.ozellikler[f.ref1 ?? -1];
      if (rl && rl.tip === 'cizgi3d') {
        const [ra, rb] = this.cizgiUclar(rl); const dir = rb.clone().sub(ra).normalize();
        const ek = f.eksenTip || 'z'; const ax = ek === 'x' ? new THREE.Vector3(1, 0, 0) : ek === 'y' ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(0, 0, 1);
        dir.applyAxisAngle(ax, (f.aci ?? 45) * Math.PI / 180);
        return [ra.clone(), ra.clone().addScaledVector(dir, f.param ?? 40)];
      }
    }
    if (f.cizgiTip === 'bisecting') {   // iki referans çizginin açıortayı (ortak başlangıçtan)
      const l1 = this.ozellikler[f.ref1 ?? -1], l2 = this.ozellikler[f.ref2 ?? -1];
      if (l1?.tip === 'cizgi3d' && l2?.tip === 'cizgi3d') {
        const [a1, b1] = this.cizgiUclar(l1), [a2, b2] = this.cizgiUclar(l2);
        const d1 = b1.clone().sub(a1).normalize(), d2 = b2.clone().sub(a2).normalize();
        const bis = d1.clone().add(d2).normalize(); if (bis.lengthSq() < 1e-6) bis.copy(d1);
        return [a1.clone(), a1.clone().addScaledVector(bis, f.param ?? 40)];
      }
    }
    if (f.cizgiTip === 'tanjant') {   // bir eğriye (ref1) bir konumda (oran) teğet çizgi
      const pts = this.egriNoktalari(this.ozellikler[f.ref1 ?? -1]);
      if (pts.length >= 2) {
        const o = Math.min(Math.max(f.oran ?? 0.5, 0), 1) * (pts.length - 1);
        const i = Math.floor(o), j = Math.min(i + 1, pts.length - 1);
        const base = pts[i].clone().lerp(pts[j], o - i);
        const dir = pts[j].clone().sub(pts[Math.max(i - 1, 0)]).normalize();
        if (dir.lengthSq() < 1e-9) dir.set(1, 0, 0);
        return [base, base.clone().addScaledVector(dir, f.param ?? 40)];
      }
    }
    if (f.cizgiTip === 'normal') {   // bir düzleme/yüzeye normal çizgi (başlangıç noktasından)
      const dir = (f.yuzeyNormal ? new THREE.Vector3(f.yuzeyNormal.x, f.yuzeyNormal.y, f.yuzeyNormal.z) : this.aynaNormal(f)).normalize();
      return [a, a.clone().addScaledVector(dir, f.param ?? 40)];
    }
    const b = (f.ref2 != null && f.ref2 >= 0) ? this.refNoktaKonum(f.ref2) : new THREE.Vector3(f.p2x ?? 30, f.p2y ?? 0, f.p2z ?? 0);
    return [a, b];
  }
  /** Dialog için: bu feature'dan ÖNCEKİ nokta feature'ları {idx,etiket}. */
  noktaSecenekleri(feat: IOzellik): { deger: number; etiket: string }[] {
    const son = this.ozellikler.indexOf(feat); const out: { deger: number; etiket: string }[] = [];
    this.ozellikler.forEach((f, i) => { if (f.tip === 'nokta3d' && f !== feat && (son < 0 || i < son)) out.push({ deger: i, etiket: (f.node as any)?.name || 'Nokta.' + (i + 1) }); });
    return out;
  }
  /** Dialog için: bu feature'dan ÖNCEKİ çizgi feature'ları {idx,etiket}. */
  egriSecenekleri(feat: IOzellik): { deger: number; etiket: string }[] {
    const son = this.ozellikler.indexOf(feat); const out: { deger: number; etiket: string }[] = [];
    this.ozellikler.forEach((f, i) => { if (f.tip === 'cizgi3d' && (son < 0 || i < son)) out.push({ deger: i, etiket: (f.node as any)?.name || 'Çizgi.' + (i + 1) }); });
    return out;
  }
  /** Dialog için: bu feature'dan ÖNCEKİ TÜM eğri feature'ları (çizgi/çember/spline/helis). */
  tumEgriSecenekleri(feat: IOzellik): { deger: number; etiket: string }[] {
    const son = this.ozellikler.indexOf(feat); const out: { deger: number; etiket: string }[] = [];
    const egri = new Set(['cizgi3d', 'cember3d', 'spline3d', 'polyline', 'curveSmooth', 'corner', 'isoparametrik', 'combine3d', 'equationCurve', 'spine3d', 'helix3d', 'spiral3d']);
    this.ozellikler.forEach((f, i) => { if (egri.has(f.tip) && f !== feat && (son < 0 || i < son)) out.push({ deger: i, etiket: (f.node as any)?.name || f.tip }); });
    return out;
  }
  /** Dialog için: bu feature'dan ÖNCEKİ düzlem feature'ları. */
  duzlemSecenekleri(feat: IOzellik): { deger: number; etiket: string }[] {
    const son = this.ozellikler.indexOf(feat); const out: { deger: number; etiket: string }[] = [];
    this.ozellikler.forEach((f, i) => { if (f.tip === 'duzlem3d' && f !== feat && (son < 0 || i < son)) out.push({ deger: i, etiket: (f.node as any)?.name || 'Düzlem.' + (i + 1) }); });
    return out;
  }

  private telEtkilesim(mesh: THREE.Mesh, f: IOzellik): void {
    if (f.grafik) { mesh.material = (mesh.material as any).clone(); this.grafikUygula(mesh, f.grafik); }   // CATIA Graphics Properties (wireframe-bazlı)
    (mesh as any)._telFeat = f;   // 3D referans seçimi için (refSecPick)
    (mesh as any).ciftTiklandi = () => this.featureMenu(f);
    (mesh as any).uzunBasildi = () => this.featureHizliMenu(f);
    (mesh as any).sagMenu = [
      { baslik: 'Düzenle (Pencere)', simge: 'sliders horizontal', renkSinif: 'blue', tiklandi: () => this.featureMenu(f) },
      { baslik: 'Grafik Özellikleri', simge: 'paint brush', renkSinif: 'purple', tiklandi: () => this.grafikDialogAc(f) },
      { tipi: 'Ayraç' },
      { baslik: 'Sil', simge: 'trash alternate', renkSinif: 'red', tiklandi: () => this.featureSil(f) },
    ];
    const telAd: Record<string, string> = { nokta3d: 'Nokta', cizgi3d: 'Çizgi', cember3d: 'Çember', spline3d: 'Spline', polyline: 'Polyline', curveSmooth: 'Yumuşatılmış Eğri', corner: 'Köşe (Corner)', isoparametrik: 'Iso Eğri', combine3d: 'Birleşik Eğri', equationCurve: 'Denklem Eğrisi', spine3d: 'Omurga (Spine)', helix3d: 'Helis', duzlem3d: 'Düzlem', spiral3d: 'Spiral', paralelEgri: 'Paralel Eğri', connectEgri: 'Bağlantı', yuzeyKenar: 'Yüzey Sınırı', conic3d: 'Konik', kesisim3d: 'Kesişim' };
    (mesh as any).userData['FikoArayuz'] = { ad: telAd[f.tip] || 'Tel', agactaGorunur: false, listeGoster: false };
  }
  // ═════════ MERKEZİ TÜR-BAZLI SEÇİM (CATIA tarzı: tipe göre seç) ═════════
  // Tüm 3D referans/öğe seçimleri TEK API'den geçer. tipler[] = hangi türler seçilebilir:
  // wireframe: nokta3d/cizgi3d/cember3d/spline3d/helix3d/spiral3d/duzlem3d
  // (Faz 2'de eklenecek: kenar/yuz/vertex/profil/govdeNokta/govdeNormal)
  private static WIRE_TIPLER = new Set(['nokta3d', 'cizgi3d', 'cember3d', 'spline3d', 'polyline', 'curveSmooth', 'corner', 'isoparametrik', 'combine3d', 'equationCurve', 'spine3d', 'helix3d', 'spiral3d', 'duzlem3d', 'conic3d', 'paralelEgri', 'connectEgri', 'kesisim3d', 'yuzeyKenar', 'yuzey', 'yuzeyJoin', 'yuzeyTrim', 'yuzeyExtract', 'yuzeyBlend', 'yuzeyExtrapolate']);
  // Seçili-yüz op'ları: secYuzNok (mesh yüz merkezi) ile OCCT yüzlerini eşleyen feature'lar. Aktifken final UnifySameDomain ATLANIR.
  private static SEC_YUZ_OP_TIPLER = new Set(['thickness', 'draft', 'shell', 'removeFace', 'faceFillet', 'triFillet', 'replaceFace']);
  private aktifSecim: ISecimTalebi | null = null;
  private _secimRc = new THREE.Raycaster();   // PERFORMANS: tek paylaşılan raycaster (tıklama başına allocation yok)
  /** Aktif seçim var mı — merkezi wireframe VEYA eski CAD pick modlarından biri. Pointer zinciri tek bunu sorar. */
  get secimAktif(): boolean {
    return !!this.aktifSecim || this.holeSecAktif || this.limitSecAktif || this.yonSecAktif
      || this.projekteAktif || this.profilSecimAktif || this.vertexSecimAktif || this.kenarSecimAktif || this.yuzSecimAktif;
  }
  /** Aktif seçim hedefi (feat'e yazılacak anahtar) — düzlem/ref seçici vurgusu için. */
  secimHedef(): string { return this.aktifSecim?.anahtar || ''; }
  /** Merkezi seçim başlat. talep.tipler = seçilebilir türler. */
  secimBaslat(talep: ISecimTalebi): void {
    this.aktifSecim = talep;
    const T = talep.tipler;
    const ad = T.includes('duzlem3d') ? 'düzlem' : T.includes('cizgi3d') ? 'çizgi' : T.includes('cember3d') ? 'çember' : T.includes('yuz') ? 'yüz' : T.includes('kenar') ? 'kenar' : 'nokta';
    this.d.bildir?.('3D sahneden veya ürün ağacından bir ' + ad + ' seç' + (talep.coklu ? ' (çoklu)' : ''));
  }
  /** Ortak raycast yardımcısı: pointer event → {ndc, rc} (tek raycaster mantığı). */
  private secimRay(e: PointerEvent | MouseEvent): { rc: THREE.Raycaster; canvas: any; kamera: any } | null {
    const canvas = this.d.canvasAl(); const kamera = this.d.kameraAl(); if (!canvas || !kamera) return null;
    const r = canvas.getBoundingClientRect();
    const ndc = new THREE.Vector2((((e as any).clientX - r.left) / r.width) * 2 - 1, -(((e as any).clientY - r.top) / r.height) * 2 + 1);
    this._secimRc.setFromCamera(ndc, kamera as any);
    return { rc: this._secimRc, canvas, kamera };
  }
  secimBitir(): void { this.aktifSecim = null; this.hoverTemizle(); this.hoverSecimFaceId = -2; this.d.ciz(); }

  // ---- MERKEZİ REFERANS SEÇİMİ HOVER (CATIA tarzı — komut penceresinden düzlem/eksen/yüz seçerken anlık vurgu) ----
  private hoverSecimFaceId = -2;
  /** Merkezi referans seçimi aktif mi (pointermove hover dispatcher için). */
  get merkeziSecimAktif(): boolean { return !!this.aktifSecim; }
  /** pointermove → merkezi seçimde imleç altındaki yüz/tel elemanı vurgular (CATIA görsel geri bildirim). */
  secimHover(e: PointerEvent | MouseEvent): void {
    const t = this.aktifSecim; if (!t) return;
    const now = (e as any).timeStamp || performance.now();
    if (now - this._hoverSon < 16) return; this._hoverSon = now;
    const wireTipleri = t.tipler.filter(x => FikoModellemeYonetici.WIRE_TIPLER.has(x));
    // 1) Tel eleman (duzlem3d/cizgi3d/cember3d/yüzey/…) hover
    if (wireTipleri.length && (this.telMeshler?.length || this.yuzeyMeshler?.length)) {
      const ray = this.secimRay(e);
      if (ray) {
        const hits = ray.rc.intersectObjects([...this.telMeshler, ...this.yuzeyMeshler], false);
        for (const h of hits) {
          const tf = (h.object as any)._telFeat as IOzellik | undefined;
          if (tf && wireTipleri.includes(tf.tip) && tf !== t.feat) {
            this.hoverTemizle(); this.hoverSecimFaceId = -2;
            const ov = this.telVurguObj(tf, h.point);
            if (ov) { this.hoverObj = ov; this.d.sahne.add(ov); }
            this.d.ciz(); return;
          }
        }
      }
    }
    // 2) Gövde yüzü hover (govdeNokta/govdeNormal/yuz)
    if (t.tipler.includes('govdeNokta') || t.tipler.includes('govdeNormal') || t.tipler.includes('yuz')) {
      const faceId = this.yuzPickFaceId(e);
      if (faceId === this.hoverSecimFaceId) return;
      this.hoverSecimFaceId = faceId; this.hoverTemizle();
      if (faceId < 0) { this.d.ciz(); return; }
      const obj = this.yuzGorselMesh(fid => fid === faceId, 0x4db8ff, 0.42, 996);   // CATIA hover: açık mavi
      if (obj) { this.hoverObj = obj; this.d.sahne.add(obj); }
      this.d.ciz(); return;
    }
    this.hoverTemizle(); this.d.ciz();
  }
  /** Tel feature hover vurgusu: çizgi=fat-line, diğerleri=hit noktasında parlak işaret. */
  private telVurguObj(f: IOzellik, hitPoint?: THREE.Vector3): THREE.Object3D | null {
    try { if (f.tip === 'cizgi3d') { const [a, b] = this.cizgiUclar(f); return this.fatVurgu([a.x, a.y, a.z, b.x, b.y, b.z], 0x22d3ee, 5, 998); } } catch { }
    if (hitPoint) {
      const g = new THREE.SphereGeometry(2.2, 10, 10);
      const m = new THREE.MeshBasicMaterial({ color: 0x22d3ee, depthTest: false });
      const s = new THREE.Mesh(g, m); s.position.copy(hitPoint); s.renderOrder = 998; (s as any).raycast = () => { };
      return s;
    }
    return null;
  }
  /** Seçilen feature'ı aktif talebe yaz (3D raycast VEYA ağaç tıklaması ortak yolu). */
  private secimTamamla(secilen: IOzellik): boolean {
    const t = this.aktifSecim; if (!t) return false;
    const idx = this.ozellikler.indexOf(secilen);
    if (t.feat && t.anahtar) {
      if (t.coklu) { const arr = ((t.feat as any)[t.anahtar] ||= []) as number[]; arr.push(idx); }
      else (t.feat as any)[t.anahtar] = idx;
      this.rebuild(); this.featureNodeAdGuncelle(t.feat); this.d.agacGuncelle?.();
    }
    const cb = t.geriCagri; this.secimBitir(); cb?.(idx); return true;
  }
  /** Gövde/sahne raycast: yüze tıkla → {nokta, normal, mesh} → geriCagri. (Point on surface, 3D yerleştirme) */
  private secimGovdePick(e: PointerEvent | MouseEvent, t: ISecimTalebi): boolean {
    const ray = this.secimRay(e); if (!ray) { this.secimBitir(); return false; }
    const hedefler: any[] = [];
    if (this.govdeMesh) hedefler.push(this.govdeMesh);
    if (this.telMeshler?.length) hedefler.push(...this.telMeshler);   // tel öğelere de snap (nokta/çizgi)
    if (!hedefler.length) return false;
    const hit = ray.rc.intersectObjects(hedefler, true)[0] as any;
    if (!hit?.point) return false;   // boşluk — modda kal
    let n = new THREE.Vector3(0, 0, 1);
    if (hit.face && hit.object) n = hit.face.normal.clone().transformDirection(hit.object.matrixWorld).normalize();
    const sonuc = { nokta: { x: hit.point.x, y: hit.point.y, z: hit.point.z }, normal: { x: n.x, y: n.y, z: n.z } };
    const cb = t.geriCagri; if (!t.coklu) this.secimBitir(); cb?.(sonuc); return true;   // coklu (ardışık çizim) → modda kal, sağ-tık bitirir
  }
  /** Nokta dialog'dan: yüze/3D'ye tıklayarak noktayı yerleştir (CATIA Point on surface). */
  noktaYuzeyeKoy(feat: IOzellik, geriCagri?: () => void): void {
    this.secimBaslat({
      tipler: ['govdeNokta'], feat,
      geriCagri: (r: any) => {
        if (r?.nokta) {
          feat.noktaTip = 'koordinat'; feat.p1x = r.nokta.x; feat.p1y = r.nokta.y; feat.p1z = r.nokta.z; feat.yuzeyNormal = r.normal;
          this.rebuild(); this.featureNodeAdGuncelle(feat); this.d.agacGuncelle?.();
        }
        geriCagri?.();
      },
    });
  }
  /** Bir koordinat üçlüsünü (ax/ay/az alanları) 3D'ye/yüze tıklayarak doldur (CATIA tarzı yerleştirme). */
  koord3dYerlestir(feat: IOzellik, ax: string, ay: string, az: string, geriCagri?: () => void): void {
    this.secimBaslat({
      tipler: ['govdeNokta'], feat,
      geriCagri: (r: any) => {
        if (r?.nokta) { (feat as any)[ax] = r.nokta.x; (feat as any)[ay] = r.nokta.y; (feat as any)[az] = r.nokta.z; if (r.normal) feat.yuzeyNormal = r.normal; this.rebuild(); this.featureNodeAdGuncelle(feat); this.d.agacGuncelle?.(); }
        geriCagri?.();
      },
    });
  }
  /** Spline: 3D'ye/yüze ardışık tıklayarak nokta ekle (CATIA tarzı eğri çizimi; sağ-tık bitirir). */
  splineArdisik3dBaslat(feat: IOzellik, geriCagri?: () => void): void {
    this.secimBaslat({
      tipler: ['govdeNokta'], feat, coklu: true,
      geriCagri: (r: any) => {
        if (r?.nokta) {
          const node = this.ozellikNode('nokta3d', null);
          const nk: IOzellik = { tip: 'nokta3d', ekleme: true, noktaTip: 'koordinat', p1x: r.nokta.x, p1y: r.nokta.y, p1z: r.nokta.z, node };
          this.ozellikler.push(nk); this.featureNodeBagla(nk);
          const idx = this.ozellikler.indexOf(nk);
          (feat.refler ||= []).push(idx);
          this.rebuild(); this.featureNodeAdGuncelle(feat); this.d.agacGuncelle?.();
        }
        geriCagri?.();
      },
    });
  }
  /** Ürün ağacından seçim: secim aktifken bir feature node'una tıklanınca (CATIA spec-tree seçimi). */
  secimAgactanSec(feat: IOzellik): boolean {
    const t = this.aktifSecim; if (!t || !feat) return false;
    const wireTipleri = t.tipler.filter(x => FikoModellemeYonetici.WIRE_TIPLER.has(x));
    if (!wireTipleri.includes(feat.tip) || feat === t.feat) return false;
    return this.secimTamamla(feat);
  }
  /** Pointer: aktif seçim varsa türe göre raycast + sonucu feat'e yaz / geriCagri'ya ver.
   *  TEK giriş noktası — wireframe merkezi, eski CAD pick modları (kenar/yüz/vertex/profil/hedef/hole) buraya delege. */
  secimPick(e: PointerEvent | MouseEvent): boolean {
    const t = this.aktifSecim;
    if (t) {
      const wireTipleri = t.tipler.filter(x => FikoModellemeYonetici.WIRE_TIPLER.has(x));
      if (wireTipleri.length) {
        const ray = this.secimRay(e); if (!ray) { this.secimBitir(); return false; }
        const hits = ray.rc.intersectObjects([...this.telMeshler, ...this.yuzeyMeshler], false);   // tel + yüzey elemanları
        for (const h of hits) {
          const tf = (h.object as any)._telFeat as IOzellik | undefined;
          if (tf && wireTipleri.includes(tf.tip) && tf !== t.feat) return this.secimTamamla(tf);
        }
      }
      // GÖVDE/SAHNE NOKTASI: yüze (veya tel öğeye) tıkla → 3D konum + normal (CATIA: Point on surface, 3D yerleştirme)
      if (t.tipler.includes('govdeNokta') || t.tipler.includes('govdeNormal')) return this.secimGovdePick(e, t);
      return false;   // boşluğa tıklandı — modda kal
    }
    // ── Eski CAD pick modları (kendi flag'leriyle aktif) — merkezi pointer'dan delege ──
    if (this.holeSecAktif) return this.holeYerPick(e);
    if (this.limitSecAktif) return this.limitHedefPick(e);
    if (this.yonSecAktif) return this.yonHedefPick(e);
    if (this.projekteAktif) return this.projekte3dPick(e);
    if (this.profilSecimAktif) return this.profilPick(e);
    if (this.vertexSecimAktif) return this.vertexPick(e);
    if (this.kenarSecimAktif) return this.kenarPick(e);
    if (this.yuzSecimAktif) return this.yuzPick(e);
    return false;
  }
  // ── Geriye uyumlu refSec API'si (artık merkezi secim'e delege) ──
  get refSecAktif(): boolean { return this.secimAktif; }
  refSecHedef(): string { return this.secimHedef(); }
  refSecBaslat(feat: IOzellik, anahtar: string, tipler: string[], geriCagri?: () => void): void {
    this.secimBaslat({ tipler, feat, anahtar, geriCagri: geriCagri ? () => geriCagri() : undefined });
  }
  refSecListeBaslat(feat: IOzellik, anahtar: string, tipler: string[], geriCagri?: () => void): void {
    this.secimBaslat({ tipler, feat, anahtar, coklu: true, geriCagri: geriCagri ? () => geriCagri() : undefined });
  }
  refSecBitir(): void { this.secimBitir(); }
  refSecPick(e: PointerEvent | MouseEvent): boolean { return this.secimPick(e); }

  /** Komut penceresinde aktif taslak feature (FikoDuzlemSecici 'eksen' modu için). */
  get taslakFeatAktif(): IOzellik | null { return this.taslakFeat; }

  /** Komut penceresi 3D referans: yüze/datum düzleme tıkla → en yakın standart düzlem (aynaTip) + ofset.
   *  OCCT mirror/symmetry yalnız xy/xz/yz eksen düzlemini, split ayrıca ofseti destekler. */
  komutDuzlemYuzSec(feat: IOzellik, anahtar = 'aynaTip', geriCagri?: () => void): void {
    if (!feat) { geriCagri?.(); return; }
    this.secimBaslat({
      tipler: ['govdeNokta', 'duzlem3d'], feat, anahtar: '_refEksen',   // _refEksen: yok sayılan dummy (vurgu nabzı için)
      geriCagri: (r: any) => {
        let n: { x: number; y: number; z: number } | null = null;
        let nok: { x: number; y: number; z: number } | null = null;
        if (r && typeof r === 'object' && r.normal) { n = r.normal; nok = r.nokta; }
        else if (typeof r === 'number' && r >= 0 && this.ozellikler[r]) {
          const nn = this.aynaNormal(this.ozellikler[r]); n = { x: nn.x, y: nn.y, z: nn.z };
          const kok = this.duzlemKok(this.ozellikler[r]); nok = { x: kok.merkez.x, y: kok.merkez.y, z: kok.merkez.z };
        }
        if (n) {
          const ax = Math.abs(n.x), ay = Math.abs(n.y), az = Math.abs(n.z);
          const tip: 'xy' | 'xz' | 'yz' = (az >= ax && az >= ay) ? 'xy' : (ay >= ax ? 'xz' : 'yz');
          feat.duzlemRef = -1; (feat as any)[anahtar] = tip;
          if (nok && feat.tip === 'split') feat.splitOfset = tip === 'xy' ? nok.z : tip === 'xz' ? nok.y : nok.x;
          this.rebuild(); this.featureNodeAdGuncelle(feat); this.d.agacGuncelle?.();
        }
        delete (feat as any)._refEksen;
        geriCagri?.();
      },
    });
  }

  /** User Pattern: 3D'de ardışık noktalara tıkla → her nokta gövde kopyası (ilk nokta=çapa). Sağ-tık bitirir. */
  private userPatternSecBaslat(feat: IOzellik): void {
    feat.secNoktalar = [];
    this.secimBaslat({
      tipler: ['govdeNokta'], coklu: true,
      geriCagri: (r: any) => {
        if (r?.nokta) { (feat.secNoktalar ||= []).push({ x: r.nokta.x, y: r.nokta.y, z: r.nokta.z }); this.rebuild(); this.featureNodeAdGuncelle(feat); this.d.agacGuncelle?.(); }
      },
    });
    this.d.bildir?.('İlk tık = çapa, sonraki tıklar = kopya konumu (gövde her noktaya kopyalanır). Bitince sağ-tık.');
  }
  get userPatternNoktaSayisi(): number { return (this.taslakFeat?.tip === 'userPattern' ? this.taslakFeat.secNoktalar?.length : 0) || 0; }

  /** Komut penceresi genel 3D referans seçimi (nokta/çizgi/yüz vb.) — feat[anahtar]=seçilen idx. */
  komutRefSec(feat: IOzellik, anahtar: string, tipler: string[], geriCagri?: () => void): void {
    if (!feat) { geriCagri?.(); return; }
    this.refSecBaslat(feat, anahtar, tipler, () => geriCagri?.());
  }
  /** Açık seçim-listesi panelleri (FikoSecimListesi) — 3D'de yüz/kenar seçilince canlı tazelenir. */
  private secimDinleyiciler = new Set<() => void>();
  secimDegisimDinle(cb: () => void): void { this.secimDinleyiciler.add(cb); }
  secimDegisimBirak(cb: () => void): void { this.secimDinleyiciler.delete(cb); }
  private secimBildir(): void { this.secimDinleyiciler.forEach(cb => { try { cb(); } catch { } }); }
  /** Seçili referansın görünen adı (komut penceresi 'ref' alanı etiketi). */
  refAdi(feat: IOzellik, anahtar: string): string {
    if (!feat) return '';
    const idx = (feat as any)[anahtar];
    if (idx == null || idx < 0) return '';
    const r = this.ozellikler[idx];
    return (((r?.node as any)?.name || r?.tip || 'Referans') as string).split(' · ')[0];
  }
  /** Bir yön vektörünün en yakın standart ekseni (x/y/z). */
  private enYakinEksen(d: THREE.Vector3): 'x' | 'y' | 'z' {
    const ax = Math.abs(d.x), ay = Math.abs(d.y), az = Math.abs(d.z);
    return (ax >= ay && ax >= az) ? 'x' : (ay >= az ? 'y' : 'z');
  }
  /** Dialog etiketi: seçili eksen adı — eksenRef(3D çizgi) öncelikli, yoksa eksenTip (X/Y/Z). */
  eksenAdi(f: IOzellik, anahtar = 'eksenTip'): string {
    if (f && f.eksenRef != null && f.eksenRef >= 0) { const d = this.ozellikler[f.eksenRef]; return (((d?.node as any)?.name || 'Çizgi') as string).split(' · ')[0]; }
    return (((f as any)?.[anahtar] || 'z') as string).toUpperCase();
  }
  /** Eksen standart seçimi (X/Y/Z) — eksenRef temizlenir. */
  eksenStandartSec(f: IOzellik, anahtar: string, tip: 'x' | 'y' | 'z'): void {
    if (!f) return;
    if ('eksenRef' in (f as any) || f.tip === 'rotate') f.eksenRef = -1;
    (f as any)[anahtar] = tip;
    this.rebuild(); this.featureNodeAdGuncelle(f); this.d.agacGuncelle?.();
  }
  /** Komut penceresi 3D eksen referansı: 3D'de çizgiye veya yüze tıkla.
   *  rotate keyfi ekseni (eksenRef → cizgi3d) destekler; diğerleri en yakın standart eksene (eksenTip x/y/z) oturtur. */
  komutEksenSec(feat: IOzellik, anahtar = 'eksenTip', geriCagri?: () => void): void {
    if (!feat) { geriCagri?.(); return; }
    const eksenRefDestek = feat.tip === 'rotate' || feat.tip === 'translate';
    this.secimBaslat({
      tipler: ['cizgi3d', 'govdeNokta'], feat, anahtar: '_refEksenA',
      geriCagri: (r: any) => {
        if (typeof r === 'number' && r >= 0 && this.ozellikler[r]?.tip === 'cizgi3d') {
          if (eksenRefDestek) { feat.eksenRef = r; }                                   // keyfi eksen (rotate)
          else { const [a, b] = this.cizgiUclar(this.ozellikler[r]); feat.eksenRef = -1; (feat as any)[anahtar] = this.enYakinEksen(b.clone().sub(a)); }
        } else if (r && typeof r === 'object' && r.normal) {
          const n = r.normal; (feat as any)[anahtar] = this.enYakinEksen(new THREE.Vector3(n.x, n.y, n.z));
          if (eksenRefDestek) feat.eksenRef = -1;
        }
        this.rebuild(); this.featureNodeAdGuncelle(feat); this.d.agacGuncelle?.();
        delete (feat as any)._refEksenA;
        geriCagri?.();
      },
    });
  }

  /** Wireframe 3D nokta ekle (varsayılan origin). */
  nokta3dEkle(): void {
    const node = this.ozellikNode('nokta3d', null);
    const feat: IOzellik = { tip: 'nokta3d', ekleme: true, p1x: 0, p1y: 0, p1z: 30, node };
    this.ozellikler.push(feat); this.featureNodeBagla(feat); this.rebuild(); this.d.agacGuncelle?.(); this.gecmisKaydet();
    setTimeout(() => this.featureMenu(feat), 60);
  }
  /** Wireframe 3D çizgi ekle (varsayılan X ekseni boyu). */
  cizgi3dEkle(): void {
    const node = this.ozellikNode('cizgi3d', null);
    const feat: IOzellik = { tip: 'cizgi3d', ekleme: true, cizgiTip: 'iki-nokta', ref1: -1, ref2: -1, p1x: 0, p1y: 0, p1z: 0, p2x: 40, p2y: 0, p2z: 0, eksenTip: 'x', param: 40, node };
    this.ozellikler.push(feat); this.featureNodeBagla(feat); this.rebuild(); this.d.agacGuncelle?.(); this.gecmisKaydet();
    setTimeout(() => this.featureMenu(feat), 60);
  }

  /** Bir profil setinin (elemanlar+duzlem) en büyük loop dünya noktaları (loft kesiti). */
  private profilSetiDunya(elemanlar: any[], duzlem: ISketchDuzlem): { x: number; y: number; z: number }[] {
    const loops = this.looplar(elemanlar); if (!loops.length) return [];
    const big = loops.reduce((a, b) => b.alan > a.alan ? b : a);
    return this.noktaDunya(big.pts, duzlem);
  }

  /** Sweep yolu: sketch çizgi/yay zincirinin AÇIK dünya noktaları (loop değil, polyline). */
  private sketchYolDunya(elemanlar: any[], duzlem: ISketchDuzlem): { x: number; y: number; z: number }[] {
    // kapalı loop varsa onu kullan (kapalı yol da geçerli); yoksa çizgi/yay uçlarını sırayla
    const loops = this.looplar(elemanlar);
    if (loops.length) { const big = loops.reduce((a, b) => b.alan > a.alan ? b : a); return this.noktaDunya(big.pts, duzlem); }
    const pts2d: THREE.Vector2[] = [];
    for (const el of (elemanlar || [])) {
      if (el.yapi) continue;
      if (el.tip === 'cizgi') { const [a, b] = el.noktalar; if (!pts2d.length) pts2d.push(new THREE.Vector2(a.x, a.y)); pts2d.push(new THREE.Vector2(b.x, b.y)); }
      else if (el.tip === 'yay') { const yp = FikoSketch.yayNoktalari(el.noktalar[0], el.noktalar[1], el.noktalar[2]); for (const p of yp) pts2d.push(new THREE.Vector2(p.x, p.y)); }
    }
    return pts2d.length >= 2 ? this.noktaDunya(pts2d, duzlem) : [];
  }

  /** Flat [x,y,z,...] dizisini {x,y,z}[] noktalarına çevirir (OCCT eşleştirme). */
  private flatNok(arr: number[]): { x: number; y: number; z: number }[] {
    const out: { x: number; y: number; z: number }[] = [];
    for (let i = 0; i + 2 < arr.length; i += 3) out.push({ x: arr[i], y: arr[i + 1], z: arr[i + 2] });
    return out;
  }

  /** PERSISTENT NAMING önce: seçili yüzleri kalıcı stableId ile çöz (radius/op eklense de doğru yüz),
   *  çözülemezse centroid (secYuzNok) eşleştirmeye düş. Tüm seçili-yüz op'ları (fillet/draft/shell/...) bunu kullanır. */
  private occtSecYuz(shape: any, f: IOzellik): any[] {
    if (f.secYuzKimlik && f.secYuzKimlik.length) {
      const k = fikoOCCT.seciliYuzlerKimlik(shape, f.secYuzKimlik);
      if (k.length) return k;   // kimlik çözüldü → en güvenilir
    }
    return (f.secYuzNok && f.secYuzNok.length >= 3) ? this.occtSecYuz(shape, f) : [];
  }

  /** CATIA: mesh-modunda seçilen kenarları (dünya koord.) OCCT edge'lerine eşler. undefined→tüm kenarlar. */
  private occtSecKenar(shape: any, f: IOzellik): any[] | undefined {
    if (f.secKenarlar === undefined) return undefined;        // LEGACY (hiç set edilmedi) → tüm kenar
    if (f.secKenarlar.length < 3) return [];                  // SEÇİM MODU, hiç seçili yok → BOŞ (fillet uygulanmaz)
    return fikoOCCT.seciliEdgeler(shape, this.flatNok(f.secKenarlar));   // seçili edge'ler (boş olabilir)
  }

  /** GERÇEK kernel rebuild: feature zincirini OCCT B-Rep shape kurar → kusursuz fillet/chamfer → mesh. */
  private async rebuildOCCT(): Promise<void> {
    if (!fikoOCCT.hazirMi()) { try { await fikoOCCT.init(); } catch { return; } }
    this._occtIslemBasarisiz = '';   // bu rebuild'de fillet/chamfer başarısızlığını sıfırla
    const govdeShapeler = new Map<number, any>();   // çoklu-gövde: bodyId → B-Rep shape
    govdeShapeler.set(0, this.occtBazShape || null);   // ana PartBody (STEP import bazı varsa)
    // PERSISTENT NAMING: ana gövdenin (body 0) yüz kimliğini takibe başla. Taban varsa hemen,
    // yoksa ilk solid üretildiğinde tabanlanır. Replay deterministik → stableId rebuild'ler arası sabit.
    fikoOCCT.kimlikDurdur();
    if (this.occtBazShape) try { fikoOCCT.kimlikBaslat(this.occtBazShape); } catch { }
    for (const f of this.ozellikler) {
      if (f.bastirilmis) { this.featureDurumGuncelle(f); continue; }   // suppress: üretime girmez
      // Aktif YÜZ seçimi sırasında bu op'u canlı UYGULAMA → gövde sabit kalır, faceId'ler kaymaz
      // (highlight/seçim başka yüzlere atlamaz). Tamam'da seçim modu kapanınca normal uygulanır.
      if (f === this.taslakFeat && this.yuzSecModu && FikoModellemeYonetici.SEC_YUZ_OP_TIPLER.has(f.tip)) { this.featureDurumGuncelle(f); continue; }
      const _bid = f.bodyId || 0;
      if (f.tip === 'bool') {   // CATIA BOOLEAN: kaynak gövdeyi ana gövdeye uygula (Assemble/Add/Remove/Intersect)
        try {
          const hedef = govdeShapeler.get(0), kaynak = govdeShapeler.get(f.kaynakBodyId ?? -1);
          if (hedef && kaynak) {
            const op = f.boolOp;
            govdeShapeler.set(0, op === 'remove' ? fikoOCCT.cikar(hedef, kaynak) : op === 'intersect' ? fikoOCCT.kesistir(hedef, kaynak) : fikoOCCT.birlestir(hedef, kaynak));
            govdeShapeler.delete(f.kaynakBodyId ?? -1);   // kaynak gövde tüketildi (artık ayrı gösterilmez)
          }
        } catch (e) { console.warn('[OCCT bool]', e); }
        continue;
      }
      let shape: any = govdeShapeler.get(_bid) || null;   // bu feature'ın gövdesinin mevcut shape'i
      if (_bid === 0 && shape) try { fikoOCCT.kimlikSenkron(shape); } catch { }   // PERSISTENT NAMING: kimliği gövdeyle hizala
      try {
        let s: any = null;
        if (f.tip === 'primitif') {
          // mesh-mod ile aynı dünya konumu: kutu X/Y merkez Z=0 taban, silindir zaten merkez, küre Z=0 taban
          if (f.primitifTip === 'kutu') { const en = f.pEn || 40, boy = f.pBoy || 40; s = fikoOCCT.oteleKopya(fikoOCCT.makeBox(en, boy, f.pYuk || 30), -en / 2, -boy / 2, 0); }
          else if (f.primitifTip === 'silindir') s = fikoOCCT.makeCylinder((f.pCap || 30) / 2, f.pYuk || 30);
          else if (f.primitifTip === 'kure') s = fikoOCCT.oteleKopya(fikoOCCT.makeSphere((f.pCap || 30) / 2), 0, 0, (f.pCap || 30) / 2);
          else if (f.primitifTip === 'koni') s = fikoOCCT.makeCone((f.pCap || 30) / 2, 0, f.pYuk || 30);   // koni: alt R, üst 0
          else if (f.primitifTip === 'torus') s = fikoOCCT.makeTorus((f.pCap || 40) / 2, (f.pTbCap || 12) / 2);   // OCCT torus
          else if (f.primitifTip === 'wedge') { const en = f.pEn || 40, boy = f.pBoy || 40; s = fikoOCCT.oteleKopya(fikoOCCT.makeWedge(en, f.pYuk || 30, boy, f.wedgeLtx ?? 15), -en / 2, 0, -boy / 2); }   // OCCT kama (y=yükseklik)
        } else if (f.tip === 'pipe3d') {   // OCCT Pipe: bir 3D eğri (ref1) boyunca dairesel kesitli boru (solid)
          s = this.pipe3dShape(f);
        } else if (f.tip === 'makro') {   // Meslek makrosu (boru/dirsek/yüzük/taş/bezel) → GERÇEK OCCT katı (gövdeye kaynaşır, yüz seçilebilir)
          s = this.occtMakroShape(f);
        } else if (f.tip === 'pad' || f.tip === 'pocket') {
          s = this.occtPadShape(f);   // CATIA Pad Definition: tüm limit/yön/thin/simetri seçenekleri
        } else if (f.tip === 'shaft' || f.tip === 'groove') {
          s = this.occtShaftShape(f);   // CATIA Shaft Definition: First/Second Angle + eksen + reverse
        } else if (f.tip === 'loft') {   // LOFT: çoklu kesit arası pürüzsüz katı (ThruSections)
          const profiller = (f.secProfiller || []).map(p => this.profilSetiDunya(p.elemanlar, p.duzlem)).filter(a => a.length >= 3);
          if (profiller.length >= 2) s = fikoOCCT.makeLoft(profiller, true);
        } else if (f.tip === 'sweep' || f.tip === 'slot') {   // SWEEP/SLOT: profili yol boyunca süpür (slot=cut, ekleme=false)
          const sp = f.secProfiller || [];
          if (sp.length >= 2) {
            const profil = this.profilSetiDunya(sp[0].elemanlar, sp[0].duzlem);
            const yol = this.sketchYolDunya(sp[1].elemanlar, sp[1].duzlem);
            if (profil.length >= 3 && yol.length >= 2) s = fikoOCCT.makeSweep(profil, yol, true);
          }
        } else if (f.tip === 'combine') {   // SOLID COMBINE: iki profilin tam-extrude kesişimi (Common)
          const sp = f.secProfiller || [];
          if (sp.length >= 2) {
            const e1 = this.occtProfilExtrudeFull(sp[0]), e2 = this.occtProfilExtrudeFull(sp[1]);
            if (e1 && e2) s = fikoOCCT.kesistir(e1, e2);
          }
        } else if (f.tip === 'rib') {   // RIB (Kaburga): profili kalınlaştırıp normal boyunca uzat
          s = this.occtRibShape(f);
        } else if (f.tip === 'stiffener') {   // STIFFENER (Feder): açık profil + kalınlık, Z=0 tabana kadar
          s = this.occtStiffenerShape(f);
        } else if (f.tip === 'thickSurface') {   // GSD Thick Surface: bir yüzeyi (shell) kalınlaştır → katı
          const hedef = this.ozellikler[f.hedefIndex ?? -1];
          if (hedef && hedef.tip === 'yuzey') s = this.occtYuzeyKalinlikShape(hedef, f.kalinlik ?? 3);   // GÜVENİLİR profil-offset yöntemi
        } else if (f.tip === 'closeSurface') {   // CLOSE SURFACE: tüm açık yüzeyleri dik → kapalı katı
          const shps = this.ozellikler.filter(o => o.tip === 'yuzey' && !o.bastirilmis).map(o => this.occtYuzeyShape(o)).filter(Boolean);
          if (shps.length) s = fikoOCCT.katiYap(shps);
        }
        if (s) {
          if (shape) { shape = f.ekleme ? fikoOCCT.birlestir(shape, s) : fikoOCCT.cikar(shape, s); }
          else { shape = s; if (_bid === 0) try { fikoOCCT.kimlikBaslat(shape); } catch { } }   // ilk solid → kimlik tabanla
          govdeShapeler.set(_bid, shape); continue;
        }
        // gövde-değiştirici (kusursuz)
        if (!shape) continue;
        if (f.tip === 'fillet3d') {
          const e = this.occtSecKenar(shape, f);
          if (f.degiskenFillet && e && e.length) {   // CATIA Variable Fillet: r1→r2 (seçili kenar zorunlu)
            try { const vs = fikoOCCT.filletDegisken(shape, f.kenarMesafe ?? 4, f.kenarMesafe2 ?? (f.kenarMesafe ?? 4), e); if (vs) shape = vs; } catch (er) { const r2 = fikoOCCT.fillet(shape, f.kenarMesafe ?? 4, e); if (r2) shape = r2; else this._occtIslemBasarisiz = 'fillet'; }
          } else if (e === undefined || e.length) { const r = fikoOCCT.fillet(shape, f.kenarMesafe ?? 4, e); if (r) shape = r; else this._occtIslemBasarisiz = 'fillet'; }   // null→eski gövde korunur + uyarı; boş seçim→ATLA
        }
        else if (f.tip === 'chamfer3d') {
          const e = this.occtSecKenar(shape, f);
          if (e === undefined || e.length) {
            const d1 = f.kenarMesafe ?? 4; let r: any;
            if (f.chamferMod === 'length2') r = fikoOCCT.chamferAsim(shape, d1, f.kenarMesafe2 ?? d1, e, false) || fikoOCCT.chamfer(shape, d1, e);
            else if (f.chamferMod === 'aci') r = fikoOCCT.chamferAsim(shape, d1, f.chamferAci ?? 45, e, true) || fikoOCCT.chamfer(shape, d1, e);
            else r = fikoOCCT.chamfer(shape, d1, e);
            if (r) shape = r; else this._occtIslemBasarisiz = 'chamfer';   // null→eski gövde korunur + uyarı
          }
        }
        else if (f.tip === 'faceFillet') {   // FACE-FACE FILLET: seçili yüzlerin paylaştığı kenarlara fillet
          if (f.secYuzNok && f.secYuzNok.length >= 3) { const faces = this.occtSecYuz(shape, f); const r = fikoOCCT.yuzFillet(shape, faces, f.kenarMesafe ?? 4); if (r) shape = r; else this.d.bildir?.('Face-Face fillet: komşu (kenar paylaşan) 2+ yüz seç'); }
        }
        else if (f.tip === 'triFillet') {   // TRITANGENT FILLET: orta/şerit yüzü oto-yarıçapla tangent yuvarla
          if (f.secYuzNok && f.secYuzNok.length >= 3) { const faces = this.occtSecYuz(shape, f); if (faces.length) { const r = fikoOCCT.triFillet(shape, faces[0], f.kenarMesafe || 0); if (r) shape = r; else this.d.bildir?.('Tritangent: şerit/orta bir yüz seç'); } }
        }
        else if (f.tip === 'replaceFace') {   // REPLACE FACE (pratik): seçili yüzü hedef düzleme taşı
          if (f.secYuzNok && f.secYuzNok.length >= 3) {
            const faces = this.occtSecYuz(shape, f);
            if (faces.length) {
              const at = f.aynaTip || 'xy', o = f.splitOfset ?? 0;
              const pO = at === 'yz' ? { x: o, y: 0, z: 0 } : at === 'xz' ? { x: 0, y: o, z: 0 } : { x: 0, y: 0, z: o };
              const pN = at === 'yz' ? { x: 1, y: 0, z: 0 } : at === 'xz' ? { x: 0, y: 1, z: 0 } : { x: 0, y: 0, z: 1 };
              const r = fikoOCCT.yuzDuzlemeTasi(shape, faces[0], pO, pN); if (r) shape = r; else this.d.bildir?.('Replace Face: yüz hedef düzleme paralel olmalı');
            }
          }
        }
        else if (f.tip === 'shell') {   // KUSURSUZ içi boş kabuk; seçili yüz(ler) açık kalır
          const acik = (f.secYuzNok && f.secYuzNok.length >= 3)
            ? this.occtSecYuz(shape, f)
            : undefined;
          const r = fikoOCCT.shell(shape, f.kalinlik ?? 4, acik); if (r) shape = r; else this.d.bildir?.('Kabuk oluşturulamadı (kalınlığı azalt veya açık yüz seç)');   // null→gövde KORUNUR
        }
        else if (f.tip === 'draft') {   // KUSURSUZ eğim: seçili yüzleri çekme ekseni etrafında açıyla eğ
          if (f.secYuzNok && f.secYuzNok.length >= 3) {
            const faces = this.occtSecYuz(shape, f);
            const ek = f.eksenTip || 'z';
            const pull = ek === 'x' ? { x: 1, y: 0, z: 0 } : ek === 'y' ? { x: 0, y: 1, z: 0 } : { x: 0, y: 0, z: 1 };
            const r = fikoOCCT.draft(shape, f.aci ?? 5, faces, pull, { x: 0, y: 0, z: 0 }); if (r) shape = r; else this.d.bildir?.('Eğim uygulanamadı (yüz çekme eksenine paralel olmamalı)');   // null→gövde KORUNUR
          }
        }
        else if (f.tip === 'removeLump') { const r = fikoOCCT.enBuyukLump(shape); if (r) shape = r; }   // REMOVE LUMP: en büyük solid'i tut (kopukları at)
        else if (f.tip === 'healing') { const r = fikoOCCT.iyilestir(shape, f.param ?? 0.01); if (r) shape = r; }   // HEALING: ShapeFix onarım
        else if (f.tip === 'simplify') { const r = fikoOCCT.sadelestir(shape); if (r) shape = r; }   // SURFACE SIMPLIFICATION: aynı-domain birleştir
        else if (f.tip === 'offsetShape') { const r = fikoOCCT.offsetSolid(shape, f.param ?? 5); if (r) shape = r; }   // OCCT 3D Offset (büyüt/küçült)
        else if (f.tip === 'removeFace') {   // REMOVE FACE (defeaturing): seçili yüzleri kaldır
          if (f.secYuzNok && f.secYuzNok.length >= 3) { const faces = this.occtSecYuz(shape, f); const r = fikoOCCT.yuzKaldir(shape, faces); if (r) shape = r; else this.d.bildir?.('Yüz kaldırılamadı (yüzü yeniden seçin)'); }
        }
        else if (f.tip === 'hole') { const tool = this.occtHoleTool(f); if (tool) shape = fikoOCCT.cikar(shape, tool); }   // CATIA Hole: yüzeye matkap (silindir + havşa/V-dip)
        else if (f.tip === 'thickness') {   // CATIA Thickness: seçili yüz(ler)e kalınlık ekle/çıkar (yüz yoksa tüm gövde)
          const t = (f.tersYon ? -1 : 1) * (f.kalinlik ?? 2);   // negatif kalınlık = içe (eksi offset)
          if (Math.abs(t) > 0.001) {
            if (f.secYuzNok && f.secYuzNok.length >= 3) {   // YÜZ SEÇİLDİ → sadece o yüz(ler)
              const faces = this.occtSecYuz(shape, f);
              if (faces.length) {
                const r = fikoOCCT.yuzeKalinlikEkle(shape, faces, t);
                if (r) shape = r;
                else { shape = fikoOCCT.kalinlikEkle(shape, t); this.d.bildir?.('Thickness: fillet komşusu yüz yırtıyordu → tüm gövdeye uygulandı'); }   // yırtık fallback
              }
              else this.d.bildir?.('Thickness: seçilen yüz eşleşmedi (yüzü yeniden seçin)');
            }
            else shape = fikoOCCT.kalinlikEkle(shape, t);   // hiç yüz seçilmedi → klasik tüm-gövde offset
          }
        }
        else if (f.tip === 'split') {   // CATIA Split: düzlemle böl, bir tarafı tut (yarı-uzay kutu cut)
          const K = this.govdeKapsam(), o = f.splitOfset ?? 0, alt = !f.tersYon;   // alt=negatif tarafı sil (pozitif tarafı tut)
          const box = fikoOCCT.makeBox(2 * K, 2 * K, 2 * K);
          const tool = f.aynaTip === 'yz' ? fikoOCCT.oteleKopya(box, alt ? o - 2 * K : o, -K, -K)
            : f.aynaTip === 'xz' ? fikoOCCT.oteleKopya(box, -K, alt ? o - 2 * K : o, -K)
              : fikoOCCT.oteleKopya(box, -K, -K, alt ? o - 2 * K : o);   // xy varsayılan
          shape = fikoOCCT.cikar(shape, tool);
        }
        else if (f.tip === 'splitSurface') {   // GSD Split-by-Surface: katıyı yüzeyle böl
          const hedef = this.ozellikler[f.hedefIndex ?? -1];
          if (hedef && hedef.tip === 'yuzey') { const tool = this.occtSplitSurfaceTool(hedef, !!f.tersYon); if (tool) shape = fikoOCCT.cikar(shape, tool); }
        }
        else if (f.tip === 'translate') {   // CATIA Translate — Vektör Tanımı: yön+mesafe veya koordinat
          if (f.trMod === 'koordinat') shape = fikoOCCT.oteleKopya(shape, f.trX ?? 0, f.trY ?? 0, f.trZ ?? 0);
          else {
            let dir: { x: number; y: number; z: number };
            if (f.eksenRef != null && f.eksenRef >= 0 && this.ozellikler[f.eksenRef]?.tip === 'cizgi3d') { const [a, b] = this.cizgiUclar(this.ozellikler[f.eksenRef]); const d = b.clone().sub(a).normalize(); dir = { x: d.x, y: d.y, z: d.z }; }
            else { const ek = f.trYon || 'x'; dir = ek === 'x' ? { x: 1, y: 0, z: 0 } : ek === 'y' ? { x: 0, y: 1, z: 0 } : { x: 0, y: 0, z: 1 }; }
            const m = f.trMesafe ?? 0; shape = fikoOCCT.oteleKopya(shape, dir.x * m, dir.y * m, dir.z * m);
          }
        }
        else if (f.tip === 'rotate') {   // CATIA Rotate — eksen: X/Y/Z veya 3D çizgi referansı
          let ax: any = null, merkez = { x: 0, y: 0, z: 0 };
          if (f.eksenRef != null && f.eksenRef >= 0) { const ln = this.ozellikler[f.eksenRef]; if (ln?.tip === 'cizgi3d') { const [a, b] = this.cizgiUclar(ln); const d = b.clone().sub(a).normalize(); ax = { x: d.x, y: d.y, z: d.z }; merkez = { x: a.x, y: a.y, z: a.z }; } }
          if (!ax) { const ek = f.eksenTip || 'z'; ax = ek === 'x' ? { x: 1, y: 0, z: 0 } : ek === 'y' ? { x: 0, y: 1, z: 0 } : { x: 0, y: 0, z: 1 }; }
          shape = fikoOCCT.dondurKopya(shape, ax, (f.aci ?? 45) * Math.PI / 180, merkez);
        }
        else if (f.tip === 'symmetry') {   // CATIA Symmetry — referans: düzlem (mirror) / nokta (ölçek −1) / çizgi (180° döndür)
          const r = (f.symRef != null && f.symRef >= 0) ? this.ozellikler[f.symRef] : null;
          if (r?.tip === 'nokta3d') { const p = this.noktaKonum(r); shape = fikoOCCT.olcekle(shape, { x: p.x, y: p.y, z: p.z }, -1); }
          else if (r?.tip === 'cizgi3d') { const [a, b] = this.cizgiUclar(r); const d = b.clone().sub(a).normalize(); shape = fikoOCCT.dondurKopya(shape, { x: d.x, y: d.y, z: d.z }, Math.PI, { x: a.x, y: a.y, z: a.z }); }
          else shape = fikoOCCT.mirror(shape, f.aynaTip ?? 'yz');
        }
        else if (f.tip === 'scaling') shape = fikoOCCT.olcekle(shape, { x: 0, y: 0, z: 0 }, f.scaleFaktor ?? 1);   // CATIA Scaling
        else if (f.tip === 'affinity') shape = fikoOCCT.afinDonusum(shape, f.affX ?? 1, f.affY ?? 1, f.affZ ?? 1);   // CATIA Affinity (orantısız ölçek)
        else if (f.tip === 'axisToAxis') {   // CATIA Axis to Axis — origin+Z'den hedef nokta(ref1)+çizgi-yön(eksenRef)'e taşı
          const hp = (f.ref1 != null && f.ref1 >= 0) ? this.refNoktaKonum(f.ref1) : new THREE.Vector3(f.trX ?? 0, f.trY ?? 0, f.trZ ?? 0);
          let yon = { x: 0, y: 0, z: 1 };
          if (f.eksenRef != null && f.eksenRef >= 0 && this.ozellikler[f.eksenRef]?.tip === 'cizgi3d') { const [a, b] = this.cizgiUclar(this.ozellikler[f.eksenRef]); const d = b.clone().sub(a).normalize(); yon = { x: d.x, y: d.y, z: d.z }; }
          shape = fikoOCCT.ekseneYerlestir(shape, { x: hp.x, y: hp.y, z: hp.z }, yon);
        }
        else if (f.tip === 'mirror') shape = fikoOCCT.birlestir(shape, fikoOCCT.mirror(shape, f.aynaTip ?? 'xy'));
        else if (f.tip === 'patternDik') {
          const adet = Math.max(1, f.adet ?? 2), ar = f.aralik ?? 40;
          const u = (this.ozellikler[f.hedefIndex ?? 0]?.duzlem?.eksenU) || new THREE.Vector3(1, 0, 0);
          let acc = shape; for (let i = 1; i < adet; i++) acc = fikoOCCT.birlestir(acc, fikoOCCT.oteleKopya(shape, u.x * ar * i, u.y * ar * i, u.z * ar * i));
          shape = acc;
        }
        else if (f.tip === 'patternDairesel') {
          const adet = Math.max(1, f.adet ?? 6), aci = (f.aci ?? 60) * Math.PI / 180;
          const hedef = this.ozellikler[f.hedefIndex ?? 0];
          const eks = hedef?.duzlem?.normal || new THREE.Vector3(0, 0, 1);
          const o = hedef?.duzlem?.origin || new THREE.Vector3();
          let acc = shape; for (let i = 1; i < adet; i++) acc = fikoOCCT.birlestir(acc, fikoOCCT.dondurKopya(shape, { x: eks.x, y: eks.y, z: eks.z }, aci * i, { x: o.x, y: o.y, z: o.z }));
          shape = acc;
        }
        else if (f.tip === 'patternIzgara') {   // 2-eksen ızgara dizi (U×V)
          const a1 = Math.max(1, f.adet ?? 2), ar1 = f.aralik ?? 40, a2 = Math.max(1, f.adet2 ?? 2), ar2 = f.aralik2 ?? 40;
          const dz = this.ozellikler[f.hedefIndex ?? 0]?.duzlem;
          const u = dz?.eksenU || new THREE.Vector3(1, 0, 0), v = dz?.eksenV || new THREE.Vector3(0, 1, 0);
          let acc = shape;
          for (let i = 0; i < a1; i++) for (let j = 0; j < a2; j++) {
            if (i === 0 && j === 0) continue;
            const dx = u.x * ar1 * i + v.x * ar2 * j, dy = u.y * ar1 * i + v.y * ar2 * j, dz2 = u.z * ar1 * i + v.z * ar2 * j;
            acc = fikoOCCT.birlestir(acc, fikoOCCT.oteleKopya(shape, dx, dy, dz2));
          }
          shape = acc;
        }
        else if (f.tip === 'userPattern') {   // CATIA User Pattern: gövdeyi noktalarda çoğalt (tıklanan secNoktalar → yoksa tüm nokta3d). İlk nokta=çapa
          const nk = (f.secNoktalar && f.secNoktalar.length)
            ? f.secNoktalar.map(p => new THREE.Vector3(p.x, p.y, p.z))
            : this.ozellikler.filter(o => o.tip === 'nokta3d').map(o => this.noktaKonum(o));
          if (nk.length >= 2) { const b = nk[0]; let acc = shape; for (let i = 1; i < nk.length; i++) acc = fikoOCCT.birlestir(acc, fikoOCCT.oteleKopya(shape, nk[i].x - b.x, nk[i].y - b.y, nk[i].z - b.z)); shape = acc; }
        }
      } catch (e) { console.warn('[OCCT rebuild]', f.tip, e); }
      govdeShapeler.set(_bid, shape);   // gövde-değiştirici sonrası bu gövdeyi yaz
    }
    // boolean op'larla tüketilmemiş gövdeleri göster (ana + kalan ayrı gövdeler union ile birleştir)
    let shape: any = govdeShapeler.get(0) || null;
    for (const [bid, bs] of govdeShapeler) { if (bid !== 0 && bs) { try { shape = shape ? fikoOCCT.birlestir(shape, bs) : bs; } catch { } } }
    // CATIA gibi TEK sürekli katı: 2+ unsur birleşmişse iç dikiş kenarlarını/aynı-domain yüzleri birleştir (UnifySameDomain)
    // ANCAK: aktif bir SEÇİLİ-YÜZ op'u (thickness/draft/shell/removeFace/faceFillet...) varsa final unify YAPMA —
    // unify gösterilen mesh'in yüz topolojisini/merkezlerini değiştirir; bir sonraki seçimde secYuzNok ara-shape'le
    // eşleşmez ve op sessizce tüm-gövdeye düşerdi (thickness bug'ı). Bu op'lar zaten kendi içinde unify yapar.
    if (shape) {
      const SEC_YUZ_OP = FikoModellemeYonetici.SEC_YUZ_OP_TIPLER;
      // Aktif yüz seçimi sırasında (İLK pick'ten önce dahi) unify ATLA — yoksa komşu coplanar yüzler
      // tek faceId'de birleşir, kullanıcı bir yüze tıklayınca yanındaki de seçili görünür.
      const seciliModda = this.yuzSecModu && this.taslakFeat != null && SEC_YUZ_OP.has(this.taslakFeat.tip);
      const seciliYuzOpAktif = seciliModda || this.ozellikler.some(o => !o.bastirilmis && SEC_YUZ_OP.has(o.tip) && (o.secYuzNok?.length ?? 0) >= 3);
      const govdeFeatSay = this.ozellikler.filter(o => !o.bastirilmis && FikoModellemeYonetici.OCCT_DESTEKLI.has(o.tip) && !FikoModellemeYonetici.WIRE_TIPLER.has(o.tip)).length;
      if (govdeFeatSay >= 2 && !seciliYuzOpAktif) { try { const u = fikoOCCT.sadelestir(shape); if (u) shape = u; } catch (e) { } }
    }
    this.occtSonShapeRef = shape;   // export (STEP) için son B-Rep shape
    // fillet/chamfer OCCT'de başarısız olduysa (yarıçap kenardan büyük) kullanıcıyı uyar — gövde eski haliyle korundu
    if (this._occtIslemBasarisiz) {
      const msg = this._occtIslemBasarisiz === 'fillet'
        ? '⚠️ Yuvarlatma uygulanamadı — yarıçap kenara/deliğe göre çok büyük. Daha küçük bir yarıçap deneyin.'
        : '⚠️ Pah uygulanamadı — mesafe kenara/deliğe göre çok büyük. Daha küçük bir mesafe deneyin.';
      if (this._sonBasarisizUyari !== msg) { this._sonBasarisizUyari = msg; this.d.bildir?.(msg); }
    } else this._sonBasarisizUyari = '';
    this.yuzeyMeshKur();   // GSD yüzeyleri (solid'den bağımsız ayrı mesh)
    // makrolar artık gövde kaynağı (govdeShapeler) — ayrı mesh yok; eski makro mesh'leri temizle
    if (this.makroMeshler.length) { for (const o of this.makroMeshler) { (o.parent || this.d.sahne).remove(o); o.geometry?.dispose?.(); } this.makroMeshler = []; }
    this.telKafesKur();    // GSD wireframe (nokta/çizgi)
    if (!shape) { if (this.govdeMesh) { (this.govdeMesh.parent||this.d.sahne).remove(this.govdeMesh); this.govdeMesh = null; } this.d.ciz(); return; }
    try {
      const geo = fikoOCCT.shapeToGeo(shape, 0.05, this.displayKalite.aci, this.displayKalite.oran);   // CATIA 3D Accuracy: kullanıcı kalite seviyesi
      if (geo) { if (!(geo as any)._occtFaceGruplu) this.kati.yuzGrupla(geo); this.govdeMeshKur(geo); }   // OCCT yüz grupları varsa geometrik gruplama YAPMA
    } catch (e) { console.warn('[OCCT] tessellation hatası', e); }
  }

  /**
   * GERÇEK OCCT modelleme testi: OCCT B-Rep kutu + KUSURSUZ fillet → tessellation →
   * mevcut gövde akışına (yuzGrupla + metadata + kenar + seçim) sokar. Mesh-CSG'yi BOZMAZ.
   */
  async occtKutuFillet(en = 40, boy = 40, yuk = 40, r = 6): Promise<boolean> {
    if (!await this.occtYukle()) return false;
    try {
      const box = fikoOCCT.makeBox(en, boy, yuk);
      const filleted = fikoOCCT.fillet(box, r);     // TÜM kenarlara kusursuz radius
      const geo = fikoOCCT.shapeToGeo(filleted, 0.05, this.displayKalite.aci, this.displayKalite.oran);   // CATIA 3D Accuracy
      if (!geo) return false;
      geo.translate(-en / 2, -boy / 2, 0);          // OCCT köşe-origin → merkeze
      this.parametrikTemizle();
      this.modBaslat();
      this.occtSonShapeRef = filleted; this.occtModu = true;   // B-Rep: modBaslat SONRASI, govdeMeshKur ÖNCESİ (faceGroups analitik zenginleşsin + yuzBrepKontur erişsin)
      if (!(geo as any)._occtFaceGruplu) this.kati.yuzGrupla(geo);   // OCCT yüz grupları (26 yüz) varsa geometrik gruplama YAPMA — yoksa faceId 26→2'ye çöker
      this.govdeMeshKur(geo);
      this.kenarGuncelle();   // GERÇEK B-Rep kenarları (analitik curveType/center/radius) → ölçüm/seçim B-Rep snap
      return true;
    } catch (e) { console.warn('[OCCT] kutuFillet hatası', e); return false; }
  }

  /** govdeGeo'yu govdeMesh'e bağlar + BREP metadata + kenar + seçim tazele (rebuild + OCCT ortak). */
  private govdeMeshKur(govdeGeo: THREE.BufferGeometry): void {
    if (!this.govdeMesh) {
      const mat = new THREE.MeshStandardMaterial({
        color: RENK_KATI, metalness: 0.15, roughness: 0.6,
        polygonOffset: true, polygonOffsetFactor: 1, polygonOffsetUnits: 1,
      });
      this.govdeMesh = new THREE.Mesh(govdeGeo, mat);
      this.govdeMesh.name = 'FikoGovde';
      this.govdeMesh.userData['FikoArayuz'] = { ad: 'Katı', agactaGorunur: false, listeGoster: false };   // body node katıyı temsil eder (CATIA) → ayrı satır yok ama orphan da değil
      // CATIA: 3D geometriden edit — raycast dblclick→ciftTiklandi, uzun bas→uzunBasildi, sağ-tık→sagMenu (FikoSecililer dispatch eder)
      (this.govdeMesh as any).ciftTiklandi = () => this.govdeCiftTik();
      (this.govdeMesh as any).uzunBasildi = () => this.govdeUzunBas();
      const sf = () => this.sonDuzenlenebilirFeat();
      (this.govdeMesh as any).sagMenu = [
        { baslik: 'Düzenle (Pencere)', simge: 'sliders horizontal', renkSinif: 'blue', tiklandi: () => { const f = sf(); if (f) this.featureMenu(f); } },
        { baslik: 'Hızlı Ayar', simge: 'bolt', renkSinif: 'teal', tiklandi: () => { const f = sf(); if (f) this.featureHizliMenu(f); } },
        { baslik: 'Grafik Özellikleri', simge: 'paint brush', renkSinif: 'purple', tiklandi: () => this.grafikDialogAc(null) },
        { tipi: 'Ayraç' },
        { baslik: 'Bastır / Aktifleştir', simge: 'ban', renkSinif: 'orange', tiklandi: () => { const f = sf(); if (f) this.featureBastirToggle(f); } },
        { baslik: 'Son Özelliği Sil', simge: 'trash alternate', renkSinif: 'red', tiklandi: () => { const f = sf(); if (f) this.featureSil(f); } },
      ];
      this.govdeGrupAl().ekle!(this.govdeMesh as any);   // PartBody altına bağlı → sahne kökünde DEĞİL
    } else { this.govdeMesh.geometry.dispose(); this.govdeMesh.geometry = govdeGeo; }
    (this.govdeMesh.material as THREE.MeshStandardMaterial).color.setHex(this.aktifMalzeme.renk);   // malzeme rengi
    this.grafikUygula(this.govdeMesh, this.govdeGrafik, this.aktifMalzeme.renk);   // CATIA Graphics Properties (body-level override)
    this.d.topo.kaydetMesh(this.govdeMesh);
    try {
      const fg = faceGruplariUret(govdeGeo);
      // CATIA: B-Rep varsa yüzey tiplerini ANALİTİK değerle zenginleştir (silindir-eksen/küre merkezi GERÇEK — mesh-türevli değil)
      const s = this.brepShape;
      if (s) {
        try {
          const yt = fikoOCCT.brepYuzeyTipleri(s);   // faceId sırasıyla
          if (yt.length) fg.forEach((g: any, i: number) => { const a = yt[i]; if (a && (a.tip === 'CYLINDER' || a.tip === 'SPHERE')) { g.surfaceType = a.tip; g.surfaceData = { center: a.center, axis: a.axis, radius: a.radius }; } });
        } catch (e2) { }
      }
      (this.govdeMesh as any).occt_faceGroups = fg;
      (this.govdeMesh as any).brep_faces = fg;
    } catch (e) { console.warn('[Modelleme] faceGroups üretilemedi', e); }
    this.kenarGuncelle();
    this._govdeVertexCache = null;   // gövde değişti → vertex cache invalidate
    this.govdeBilgiGuncelle();       // hacim/ağırlık → ağaç node adı
    this.secimiTazele();
    if (this.kesitAktif) this.kesitUygula();   // kesit açıksa yeni gövdeye yeniden uygula
    this.d.ciz();
    this.govdeDegisti?.();   // CATIA associativity: gövde değişti → CAM toolpath'leri bayat işaretlenir (component bağlar)
  }
  /** Gövde mesh her yeniden kurulduğunda çağrılır (CAM associativity kancası — component cam.modelDegisti'ye bağlar). */
  govdeDegisti?: () => void;

  // ---- MALZEME + AĞIRLIK (CATIA: yoğunluk → kütle, görsel renk) ----
  private aktifMalzeme: IMalzeme = MALZEMELER[0];
  get malzemeListesi(): { ad: string }[] { return MALZEMELER.map(m => ({ ad: m.ad })); }
  /** Malzeme seçenekleri + yoğunluk (g/cm³) — dialog/dış tüketiciler için. */
  get malzemeSecenekleri(): { ad: string; yogunluk: number }[] { return MALZEMELER.map(m => ({ ad: m.ad, yogunluk: m.yogunluk })); }
  get aktifMalzemeAd(): string { return this.aktifMalzeme.ad; }
  /** Tüm KATI gövdeler (PartDesign + katı makrolar). Yüzey/tel hacimsiz → hariç. */
  private katiGovdeler(): THREE.Mesh[] { return [this.govdeMesh, ...(this.makroMeshler || [])].filter(Boolean) as THREE.Mesh[]; }
  /** Tek mesh işaretli hacmi (cm³) — kapalı mesh, dünya matrisli (makro konum/ölçek uyumlu). */
  private _meshHacimCm3(mesh: THREE.Mesh): number {
    const geo = mesh.geometry; const pos = geo.attributes['position'] as THREE.BufferAttribute; if (!pos) return 0;
    mesh.updateMatrixWorld(); const m = mesh.matrixWorld;
    const idx = geo.index; let v = 0;
    const a = new THREE.Vector3(), b = new THREE.Vector3(), c = new THREE.Vector3();
    const tri = (i0: number, i1: number, i2: number) => { a.fromBufferAttribute(pos, i0).applyMatrix4(m); b.fromBufferAttribute(pos, i1).applyMatrix4(m); c.fromBufferAttribute(pos, i2).applyMatrix4(m); v += a.dot(b.clone().cross(c)) / 6; };
    if (idx) { for (let i = 0; i < idx.count; i += 3) tri(idx.getX(i), idx.getX(i + 1), idx.getX(i + 2)); }
    else { for (let i = 0; i < pos.count; i += 3) tri(i, i + 1, i + 2); }
    return Math.abs(v) / 1000;   // mm³ → cm³
  }
  /** Gövde hacmi (cm³) — TÜM katı gövdelerin toplamı (PartDesign + makrolar). */
  govdeHacim(): number { return this.katiGovdeler().reduce((s, m) => s + this._meshHacimCm3(m), 0); }
  /** Gövde ağırlığı (gram) = hacim(cm³) × yoğunluk(g/cm³). */
  govdeAgirlik(): number { return this.govdeHacim() * this.aktifMalzeme.yogunluk; }
  /** Gövde bilgi özeti (UI readout): malzeme · hacim · ağırlık · katı gövde adedi. */
  get govdeBilgi(): { malzeme: string; hacimCm3: number; agirlikG: number; govdeAdet: number } | null {
    const liste = this.katiGovdeler(); if (!liste.length) return null;
    return { malzeme: this.aktifMalzeme.ad, hacimCm3: +this.govdeHacim().toFixed(2), agirlikG: +this.govdeAgirlik().toFixed(1), govdeAdet: liste.length };
  }
  /** Gövde sınırlayıcı kutu ölçüleri (mm) + yüzey alanı (cm²) — TÜM katı gövdeleri kapsar. */
  govdeOlcu(): { x: number; y: number; z: number; alanCm2: number } | null {
    const liste = this.katiGovdeler(); if (!liste.length) return null;
    const box = new THREE.Box3(); for (const m of liste) { m.updateMatrixWorld(); box.expandByObject(m); }
    if (box.isEmpty()) return null;
    const s = box.getSize(new THREE.Vector3());
    let alan = 0; const a = new THREE.Vector3(), b = new THREE.Vector3(), c = new THREE.Vector3(), ab = new THREE.Vector3(), ac = new THREE.Vector3();
    for (const mesh of liste) {
      const geo = mesh.geometry, pos = geo.attributes['position'] as THREE.BufferAttribute, idx = geo.index; if (!pos) continue;
      mesh.updateMatrixWorld(); const mm = mesh.matrixWorld;
      const tri = (i0: number, i1: number, i2: number) => { a.fromBufferAttribute(pos, i0).applyMatrix4(mm); b.fromBufferAttribute(pos, i1).applyMatrix4(mm); c.fromBufferAttribute(pos, i2).applyMatrix4(mm); alan += ab.subVectors(b, a).cross(ac.subVectors(c, a)).length() / 2; };
      if (idx) { for (let i = 0; i < idx.count; i += 3) tri(idx.getX(i), idx.getX(i + 1), idx.getX(i + 2)); }
      else { for (let i = 0; i < pos.count; i += 3) tri(i, i + 1, i + 2); }
    }
    return { x: +s.x.toFixed(2), y: +s.y.toFixed(2), z: +s.z.toFixed(2), alanCm2: +(alan / 100).toFixed(2) };
  }
  /** Malzeme ata → renk + ağırlık güncelle. */
  malzemeAta(ad: string): void {
    const m = MALZEMELER.find(x => x.ad === ad); if (!m) return;
    this.aktifMalzeme = m;
    if (this.govdeMesh) {
      const mat = this.govdeMesh.material as THREE.MeshStandardMaterial;
      mat.color.setHex(m.renk);
      if (m.metalik != null) mat.metalness = m.metalik;
      if (m.puruz != null) mat.roughness = m.puruz;
      mat.needsUpdate = true;
    }
    this.govdeBilgiGuncelle(); this.d.ciz(); this.d.agacGuncelle?.();
  }
  /** Gövde ağaç node adına malzeme + ağırlık yaz (CATIA gibi). */
  private govdeBilgiGuncelle(): void {
    if (!this.govdeGrup || !this.govdeMesh) return;
    const ag = this.govdeAgirlik();
    const agStr = ag >= 1000 ? (ag / 1000).toFixed(2) + ' kg' : ag.toFixed(0) + ' g';
    const ad = 'Gövde (PartBody) — ' + this.aktifMalzeme.ad + ' · ' + agStr;
    (this.govdeGrup as any)._bodyTaban = ad;   // aktif işareti bunu temel alır
    this.bodyVurgula();   // ◆ prefix + ad güncelle
    this.d.agacGuncelle?.();
  }

  /** Gövde geometrisi değişince mevcut seçim sisteminin önbelleklerini tazele (Pad sonrası seçim sapmasını önler). */
  private secimiTazele(): void {
    try {
      const s = this.d.sahne as any;
      const sec = s?.secimler || s?.secililer || s?.anaGosterim?.secililer || s?.anaGosterim?.secimler;
      sec?.cadGeometriDegisti?.();
    } catch { /* sessiz */ }
  }

  /** Bir sketch-tabanlı feature'ın dünya-uzayı tool geometrisini üretir. */
  private toolGeo(f: IOzellik): THREE.BufferGeometry | null {
    if (f.tip === 'makro') return this.makroGeo(f);
    if (f.tip === 'primitif') {
      let geo: THREE.BufferGeometry;
      const cap = f.pCap || 30, yuk = f.pYuk || 30;
      if (f.primitifTip === 'silindir') { geo = new THREE.CylinderGeometry(cap / 2, cap / 2, yuk, 48); geo.rotateX(Math.PI / 2); geo.translate(0, 0, yuk / 2); }
      else if (f.primitifTip === 'kure') { geo = new THREE.SphereGeometry(cap / 2, 48, 32); geo.translate(0, 0, cap / 2); }
      else if (f.primitifTip === 'koni') { geo = new THREE.ConeGeometry(cap / 2, yuk, 48); geo.rotateX(Math.PI / 2); geo.translate(0, 0, yuk / 2); }
      else { const en = f.pEn || 40, boy = f.pBoy || 40; geo = new THREE.BoxGeometry(en, boy, yuk); geo.translate(0, 0, yuk / 2); }
      const d = f.duzlem;
      if (d) geo.applyMatrix4(new THREE.Matrix4().makeBasis(d.eksenU, d.eksenV, d.normal).setPosition(d.origin));
      geo.computeVertexNormals();
      return geo;
    }
    if (f.tip === 'yazi3d') {
      if (!f.duzlem) return null;
      const geo = yaziGeometri(f.metin || '', f.fontAd || 'helvetiker', f.boyut || 10, f.param || 4, false, f.harfBosluk || 0);
      if (!geo) return null;   // font henüz yüklenmedi
      const d = f.duzlem;
      geo.applyMatrix4(new THREE.Matrix4().makeBasis(d.eksenU, d.eksenV, d.normal).setPosition(d.origin));
      return geo;
    }
    if (f.tip === 'rib') {
      if (!f.elemanlar || !f.duzlem) return null;
      const shape = this.ribShape(f.elemanlar, f.kalinlik || 6);
      if (!shape) return null;
      const uz = Math.max(0.2, f.param || 30);
      const cift = f.yon === 'cift';
      const derinlik = cift ? uz : uz;
      const geo = new THREE.ExtrudeGeometry(shape, { depth: derinlik, bevelEnabled: false, steps: 1, curveSegments: 32 });
      // yön: + normal (varsayılan extrude +Z), - normal, ya da çift (merkeze ortala)
      if (f.yon === 'eksi') geo.translate(0, 0, -derinlik);
      else if (cift) geo.translate(0, 0, -derinlik / 2);
      geo.computeVertexNormals();
      const d = f.duzlem;
      geo.applyMatrix4(new THREE.Matrix4().makeBasis(d.eksenU, d.eksenV, d.normal).setPosition(d.origin));
      return geo;
    }
    if (!f.elemanlar || !f.duzlem) return null;
    let geo: THREE.BufferGeometry;
    if (f.tip === 'pad' || f.tip === 'pocket') {
      const shapes = this.profilShapelar(f.elemanlar); if (!shapes.length) return null;   // ÇOKLU bölge (ayrı kapalı çizgiler) → hepsi
      const h = f.param || 20;   // CATIA: negatif değer = ters yöne KATI (içini gösterme) → abs ile extrude + ters öteleme
      geo = new THREE.ExtrudeGeometry(shapes, { depth: Math.abs(h), bevelEnabled: false, steps: 1, curveSegments: 48 });
      if (h < 0) geo.translate(0, 0, h);   // negatif: aşağı taşı, normaller dışa kalır
    } else {
      const prof = this.profilNoktalari(f.elemanlar); if (!prof || prof.length < 2) return null;
      geo = FikoKatiMotoru.lathe(prof);
    }
    geo.computeVertexNormals();
    const d = f.duzlem;
    geo.applyMatrix4(new THREE.Matrix4().makeBasis(d.eksenU, d.eksenV, d.normal).setPosition(d.origin));
    return geo;
  }

  // ---- MAKRO (meslek komutları): mevcut primitif/lathe'lerden parametrik montaj ----
  // Tüm makrolar düzlem-yereldir; sonunda f.duzlem ile dünya konumuna taşınır.
  // Çoklu parça → ham CSG union ile TEK gövde geometrisi (rebuild sonunda finalize edilir).

  /** Bir makro feature'ı tek bir kapalı (watertight) gövde geometrisine derler. */
  private makroGeo(f: IOzellik): THREE.BufferGeometry | null {
    let geo: THREE.BufferGeometry | null = null;
    try {
      if (f.makroTip === 'boru') geo = this.makroBoru(f);
      else if (f.makroTip === 'dirsek') geo = this.makroDirsek(f);
      else if (f.makroTip === 'yuzuk') geo = this.makroYuzuk(f);
      else if (f.makroTip === 'tasYuvasi') geo = this.makroTasYuvasi(f);
      else if (f.makroTip === 'bezel') geo = this.makroBezel(f);
    } catch (e) { console.warn('[Modelleme] makroGeo hatası', f.makroTip, e); geo = null; }
    if (!geo) return null;
    geo.computeVertexNormals();
    const d = f.duzlem;
    if (d) geo.applyMatrix4(new THREE.Matrix4().makeBasis(d.eksenU, d.eksenV, d.normal).setPosition(d.origin));
    return geo;
  }

  /** İki ham geometriyi birleştirir (tool montajı için kısa yol; finalize YOK). */
  private makroBirlik(a: THREE.BufferGeometry, b: THREE.BufferGeometry): THREE.BufferGeometry {
    const r = this.kati.birlestirHam(a, b); a.dispose(); b.dispose(); return r;
  }
  private makroFark(a: THREE.BufferGeometry, b: THREE.BufferGeometry): THREE.BufferGeometry {
    const r = this.kati.cikarHam(a, b); a.dispose(); b.dispose(); return r;
  }

  /** +Z boyunca, tabanı orijinde içi boş/dolu silindir (boru segmenti). */
  private boruSilindir(disCap: number, etKal: number, uzun: number): THREE.BufferGeometry {
    const dis = new THREE.CylinderGeometry(disCap / 2, disCap / 2, uzun, 32);
    dis.rotateX(Math.PI / 2); dis.translate(0, 0, uzun / 2);
    if (etKal > 0.05 && etKal < disCap / 2 - 0.05) {
      const icR = disCap / 2 - etKal;
      const ic = new THREE.CylinderGeometry(icR, icR, uzun + 2, 32);
      ic.rotateX(Math.PI / 2); ic.translate(0, 0, uzun / 2);
      return this.makroFark(dis, ic);
    }
    return dis;
  }

  /** TESİSAT — Boru: içi boş silindir + iki uçta flanş halkası (parametrik). */
  private makroBoru(f: IOzellik): THREE.BufferGeometry {
    const cap = Math.max(2, f.mCap || 30), et = Math.max(0.5, f.mEtKal ?? 3), uz = Math.max(2, f.mUzun || 120);
    let g = this.boruSilindir(cap, et, uz);
    // iki uçta ince flanş (dış çap +%30, kalınlık küçük) — gerçek tesisat görünümü
    const flKal = Math.min(4, uz * 0.06);
    const flCap = cap * 1.3;
    for (const z of [0, uz - flKal]) {
      const fl = this.boruSilindir(flCap, et + (flCap - cap) / 2, flKal);
      fl.translate(0, 0, z);
      g = this.makroBirlik(g, fl);
    }
    return g;
  }

  /** TESİSAT — Dirsek/Te: iki boru kolu `mAci` açıyla, ortak merkezde tork (90° vb.). */
  private makroDirsek(f: IOzellik): THREE.BufferGeometry {
    const cap = Math.max(2, f.mCap || 30), et = Math.max(0.5, f.mEtKal ?? 3);
    const kol = Math.max(cap, f.mUzun || 60);
    const aci = (f.mAci || 90) * Math.PI / 180;
    // 1. kol: +Z
    let g = this.boruSilindir(cap, et, kol);
    // 2. kol: merkezde (orijin) `aci` ile döndürülmüş +Z boru → merkezi birleşim küresi ile yumuşat
    const kol2 = this.boruSilindir(cap, et, kol);
    kol2.rotateX(aci);   // XZ düzleminde sap (Z→ açı yönü)
    g = this.makroBirlik(g, kol2);
    // birleşim küresi (köşeyi doldur, sızdırmazlık görünümü)
    const kure = new THREE.SphereGeometry(cap / 2, 24, 16);
    g = this.makroBirlik(g, kure);
    // küre içini de boşalt (akış kanalı sürsün)
    if (et > 0.05) { const icK = new THREE.SphereGeometry(cap / 2 - et, 20, 14); g = this.makroFark(g, icK); }
    return g;
  }

  /** TAKI — Yüzük: torus band (lathe-eşdeğeri) + opsiyonel düz iç (parmak deliği). */
  private makroYuzuk(f: IOzellik): THREE.BufferGeometry {
    const icCap = Math.max(8, f.mCap || 18);          // parmak iç çapı
    const band = Math.max(1, f.mBand || 4);            // band kesit kalınlığı (et)
    const yuk = Math.max(1, f.mUzun || 5);             // band genişliği (eksenel)
    const merkezR = icCap / 2 + band / 2;              // torus tüp merkez yarıçapı
    // dikdörtgen kesitli band → lathe profili (iç-dış duvar + üst-alt), Z ekseni etrafında
    const ri = icCap / 2, ro = icCap / 2 + band;
    const z0 = -yuk / 2, z1 = yuk / 2;
    const profil = [ { x: ri, y: z0 }, { x: ro, y: z0 }, { x: ro, y: z1 }, { x: ri, y: z1 }, { x: ri, y: z0 } ];
    const g = FikoKatiMotoru.lathe(profil, 48);
    return g;
  }

  /** Prong (tırnak) dizisi: merkez yarıçaptan eşit açılı dik silindirler. */
  private prongDizi(merkezR: number, adet: number, prongCap: number, prongYuk: number): THREE.BufferGeometry | null {
    let g: THREE.BufferGeometry | null = null;
    for (let i = 0; i < adet; i++) {
      const a = i / adet * Math.PI * 2;
      const p = new THREE.CylinderGeometry(prongCap / 2, prongCap / 2 * 0.7, prongYuk, 24);
      p.rotateX(Math.PI / 2); p.translate(0, 0, prongYuk / 2);   // +Z dik tırnak
      p.translate(Math.cos(a) * merkezR, Math.sin(a) * merkezR, 0);
      g = g ? this.makroBirlik(g, p) : p;
    }
    return g;
  }

  /** TAKI — Taş Yuvası (prong setting): taban kaide + N tırnak (taşı tutar). */
  private makroTasYuvasi(f: IOzellik): THREE.BufferGeometry {
    const tasCap = Math.max(2, f.mTasCap || 8);
    const adet = Math.max(3, Math.round(f.mProngAdet || 4));
    const prongCap = Math.max(0.4, f.mProngCap || 1.2);
    const prongYuk = Math.max(1, f.mProngYuk || tasCap * 0.8);
    const merkezR = tasCap / 2 + prongCap * 0.25;   // taşın hemen dışında
    // kaide: küçük konik kaide (taş oturma yuvası)
    const kaideYuk = Math.max(1, tasCap * 0.35);
    let g: THREE.BufferGeometry = new THREE.CylinderGeometry(merkezR * 0.95, merkezR * 0.6, kaideYuk, 48);
    g.rotateX(Math.PI / 2); g.translate(0, 0, kaideYuk / 2);
    // kaide ortasını oy (taşın altı görünsün, hafif huni)
    const oyuk = new THREE.ConeGeometry(merkezR * 0.7, kaideYuk + 1, 40);
    oyuk.rotateX(-Math.PI / 2); oyuk.translate(0, 0, kaideYuk * 0.6);
    g = this.makroFark(g, oyuk);
    // tırnaklar (kaide üstünden yukarı)
    const prongs = this.prongDizi(merkezR, adet, prongCap, prongYuk);
    if (prongs) { prongs.translate(0, 0, kaideYuk * 0.4); g = this.makroBirlik(g, prongs); }
    return g;
  }

  /** TAKI — Bezel (çerçeve yuva): taşı saran ince halka + taban tabla. */
  private makroBezel(f: IOzellik): THREE.BufferGeometry {
    const tasCap = Math.max(2, f.mTasCap || 8);
    const et = Math.max(0.4, f.mEtKal ?? 1);
    const yuk = Math.max(1, f.mProngYuk || tasCap * 0.7);
    const tablaKal = Math.max(0.6, et);
    // dış çeperli ince halka (taşı çevreler) — lathe ile L kesit
    const ri = tasCap / 2, ro = tasCap / 2 + et;
    const profil = [
      { x: ri, y: 0 }, { x: ro, y: 0 }, { x: ro, y: yuk }, { x: ri, y: yuk }, { x: ri, y: 0 },
    ];
    let g = FikoKatiMotoru.lathe(profil, 96);
    // taban tabla (dolu disk, taşı alttan tutar)
    const tabla = new THREE.CylinderGeometry(ro, ro, tablaKal, 64);
    tabla.rotateX(Math.PI / 2); tabla.translate(0, 0, tablaKal / 2);
    g = this.makroBirlik(g, tabla);
    return g;
  }

  /** Gövde geometrisini bir temel düzleme göre aynalar (winding düzeltmeli). */
  private aynaGeo(geo: THREE.BufferGeometry, tip: 'xy' | 'xz' | 'yz'): THREE.BufferGeometry {
    const g = geo.clone();
    const s = tip === 'xy' ? [1, 1, -1] : tip === 'xz' ? [1, -1, 1] : [-1, 1, 1];
    g.applyMatrix4(new THREE.Matrix4().makeScale(s[0], s[1], s[2]));
    // ayna winding'i ters çevirir → düzelt (non-indexed: her üçgenin 2.-3. vertex'ini takasla)
    const ng = g.index ? g.toNonIndexed() : g;
    if (g.index) g.dispose();
    const p = ng.attributes.position as THREE.BufferAttribute;
    for (let t = 0; t < p.count; t += 3) {
      const bx = p.getX(t + 1), by = p.getY(t + 1), bz = p.getZ(t + 1);
      p.setXYZ(t + 1, p.getX(t + 2), p.getY(t + 2), p.getZ(t + 2));
      p.setXYZ(t + 2, bx, by, bz);
    }
    p.needsUpdate = true;
    ng.computeVertexNormals();
    return ng;
  }

  /** Rib profili → THREE.Shape. Kapalı kontur varsa onu (pad gibi) kullanır;
   * yoksa açık çizgi/yay zincirini `t` kadar kalınlaştırıp kapalı bant üretir. */
  private ribShape(els: any[], t: number): THREE.Shape | null {
    // 1) Sketch'te kapalı bir loop varsa (dikdörtgen/çember/kapalı zincir) → doğrudan profil
    const kapali = this.profilShape(els);
    if (kapali) {
      // kapalı loop gerçekten alanlı mı? (çember/dikdörtgen daima alanlı)
      const pts = kapali.getPoints(8);
      const alan = Math.abs(this.poliAlan(pts.map(p => new THREE.Vector2(p.x, p.y))));
      if (alan > t * t * 0.25) return kapali;
    }
    // 2) Açık çizgi zinciri → orta hattı çıkar, ±t/2 ötele, kapalı bant kur
    const cizgiler = els.filter((e: any) => !e.yapi && (e.tip === 'cizgi' || e.tip === 'yay'));
    if (!cizgiler.length) return null;
    const segs = cizgiler.map((c: any) => c.tip === 'yay'
      ? FikoSketch.yayNoktalari(c.noktalar[0], c.noktalar[1], c.noktalar[2])
      : [{ x: c.noktalar[0].x, y: c.noktalar[0].y }, { x: c.noktalar[1].x, y: c.noktalar[1].y }]);
    // orta hat noktaları (uç-uca bitiştir)
    const orta: { x: number; y: number }[] = [segs[0][0]];
    for (const s of segs) for (let i = 1; i < s.length; i++) orta.push(s[i]);
    if (orta.length < 2) return null;
    const sol = this.offsetPolygon(orta, t / 2, false);
    const sag = this.offsetPolygon(orta, -t / 2, false);
    if (!sol || !sag) return null;
    const halka = [...sol, ...sag.slice().reverse()].map(p => new THREE.Vector2(p.x, p.y));
    if (this.poliAlan(halka) < 0) halka.reverse();   // CCW dış kontur
    return new THREE.Shape(halka);
  }

  /** Kabuk iç gövdesi: gövdeyi bbox merkezine göre içe ölçekler (kalınlık ≈ t).
   * BASİT YAKLAŞIM — gerçek paralel offset değil; ince/uzun parçalarda et kalınlığı
   * yönlere göre değişir. Kutu benzeri kütlelerde iyi sonuç verir. */
  /** Seçili faceId'lerin vertex pozisyon anahtar seti (gerçek shell açık-yüz için, Q=0.3 kabukIcGeoGercek ile aynı). */
  private yuzNoktaSeti(geo: THREE.BufferGeometry, faceIds: number[]): Set<string> {
    const set = new Set<string>();
    const grup = geo.attributes['grup'] as THREE.BufferAttribute | undefined;
    const pos = geo.attributes['position'] as THREE.BufferAttribute | undefined;
    if (!grup || !pos) return set;
    const fset = new Set(faceIds); const Q = 0.3;
    for (let i = 0; i < grup.count; i++) if (fset.has(grup.getZ(i))) set.add(`${Math.round(pos.getX(i) / Q)},${Math.round(pos.getY(i) / Q)},${Math.round(pos.getZ(i) / Q)}`);
    return set;
  }

  private kabukIcGeo(geo: THREE.BufferGeometry, t: number): THREE.BufferGeometry | null {
    geo.computeBoundingBox();
    const bb = geo.boundingBox; if (!bb) return null;
    const merkez = new THREE.Vector3(); bb.getCenter(merkez);
    const boyut = new THREE.Vector3(); bb.getSize(boyut);
    const sx = boyut.x > 1e-3 ? Math.max(0.02, (boyut.x - 2 * t) / boyut.x) : 1;
    const sy = boyut.y > 1e-3 ? Math.max(0.02, (boyut.y - 2 * t) / boyut.y) : 1;
    const sz = boyut.z > 1e-3 ? Math.max(0.02, (boyut.z - 2 * t) / boyut.z) : 1;
    const ic = geo.clone();
    const m = new THREE.Matrix4().makeTranslation(merkez.x, merkez.y, merkez.z)
      .multiply(new THREE.Matrix4().makeScale(sx, sy, sz))
      .multiply(new THREE.Matrix4().makeTranslation(-merkez.x, -merkez.y, -merkez.z));
    ic.applyMatrix4(m);
    ic.computeVertexNormals();
    return ic;
  }

  /** Eğim (draft): vertex'leri çekme ekseni boyunca, nötr düzleme uzaklıkla
   * orantılı yatay kaydırır (taper). BASİT VERSİYON — tüm gövdeye global koniklik
   * uygular, yüz-bazlı seçim yoktur. Yerinde çalışır (CSG yok). */
  private draftUygula(geo: THREE.BufferGeometry, f: IOzellik): void {
    const pos = geo.attributes.position as THREE.BufferAttribute; if (!pos) return;
    geo.computeBoundingBox();
    const bb = geo.boundingBox; if (!bb) return;
    const eks = f.eksenTip || 'z';
    const al = (i: number) => eks === 'x' ? pos.getX(i) : eks === 'y' ? pos.getY(i) : pos.getZ(i);
    const min = eks === 'x' ? bb.min.x : eks === 'y' ? bb.min.y : bb.min.z;
    const max = eks === 'x' ? bb.max.x : eks === 'y' ? bb.max.y : bb.max.z;
    const notr = f.taban === 'ust' ? max : f.taban === 'orta' ? (min + max) / 2 : min;
    const k = Math.tan((f.aci || 0) * Math.PI / 180);
    const ctr = new THREE.Vector3(); bb.getCenter(ctr);   // yan boyutlar bu merkez etrafında genleşir (parça kaymaz)
    // CATIA: secYuzler verilmişse SADECE o yüzlerin vertex'lerine eğim (grup attribute faceId ile filtre)
    const grup = geo.attributes['grup'] as THREE.BufferAttribute | undefined;
    const secSet = (f.secYuzler && f.secYuzler.length && grup) ? new Set(f.secYuzler) : null;
    for (let i = 0; i < pos.count; i++) {
      if (secSet && !secSet.has(grup!.getZ(i))) continue;   // seçili yüz değil → dokunma
      const h = al(i) - notr;
      const s = 1 + (k * h) / Math.max(1e-3, max - min);   // yüksekliğe oranlı genleşme
      if (eks === 'z') { pos.setX(i, ctr.x + (pos.getX(i) - ctr.x) * s); pos.setY(i, ctr.y + (pos.getY(i) - ctr.y) * s); }
      else if (eks === 'y') { pos.setX(i, ctr.x + (pos.getX(i) - ctr.x) * s); pos.setZ(i, ctr.z + (pos.getZ(i) - ctr.z) * s); }
      else { pos.setY(i, ctr.y + (pos.getY(i) - ctr.y) * s); pos.setZ(i, ctr.z + (pos.getZ(i) - ctr.z) * s); }
    }
    pos.needsUpdate = true;
    geo.computeVertexNormals();
  }

  private duzlemKlon(d: ISketchDuzlem): ISketchDuzlem {
    return { origin: d.origin.clone(), normal: d.normal.clone(), eksenU: d.eksenU.clone(), eksenV: d.eksenV.clone() };
  }

  /** OCCT B-Rep gerçek kenarları → occt_edgeGroups formatında ({pts} polyline). */
  private brepKenarGruplari(): any[] | null {
    try {
      const e = fikoOCCT.shapeToEdges(this.occtSonShapeRef); if (!e || !e.length) return null;
      const ek = this.holeEkKenarlari();   // boolean'ın üretmediği koni-silindir teğet geçiş çemberleri (havşa/V-dip/kademeli)
      return ek.length ? e.concat(ek) : e;
    } catch { return null; }
  }

  /** Merkez+normal+yarıçaptan kapalı çember polyline'ı (occt_edgeGroup formatı). */
  private cemberPolyline(merkez: { x: number; y: number; z: number }, normal: { x: number; y: number; z: number }, r: number, N = 48): { pts: { x: number; y: number; z: number }[] } {
    const ax = new THREE.Vector3(normal.x, normal.y, normal.z).normalize();
    const u = Math.abs(ax.x) > 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0);
    const e1 = new THREE.Vector3().crossVectors(ax, u).normalize();
    const e2 = new THREE.Vector3().crossVectors(ax, e1).normalize();
    const pts: { x: number; y: number; z: number }[] = [];
    for (let i = 0; i <= N; i++) { const a = (i / N) * Math.PI * 2; pts.push({ x: merkez.x + e1.x * Math.cos(a) * r + e2.x * Math.sin(a) * r, y: merkez.y + e1.y * Math.cos(a) * r + e2.y * Math.sin(a) * r, z: merkez.z + e1.z * Math.cos(a) * r + e2.z * Math.sin(a) * r }); }
    return { pts };
  }

  /** Delik konilerinde boolean'ın atladığı geçiş çemberleri (koni alt yarıçapı = bore yarıçapı → teğet, kenar üretmez). */
  private holeEkKenarlari(): any[] {
    const out: any[] = [];
    for (const f of this.ozellikler) {
      if (f.tip !== 'hole' || f.bastirilmis || !f.holeMerkez || !f.holeNormal) continue;
      const c = f.holeMerkez, nrm = f.holeNormal; const L = Math.hypot(nrm.x, nrm.y, nrm.z) || 1;
      const dir = { x: -nrm.x / L, y: -nrm.y / L, z: -nrm.z / L };
      const r = Math.max(0.1, (f.holeCap ?? 10) / 2);
      const through = f.holeLimit === 'son' || f.holeLimit === 'sonraki' || f.holeLimit === 'tumu';
      const cember = (derinlik: number) => out.push(this.cemberPolyline({ x: c.x + dir.x * derinlik, y: c.y + dir.y * derinlik, z: c.z + dir.z * derinlik }, dir, r));
      if (f.holeTur === 'countersunk') {   // havşa konisi → düz bore geçişi
        const csR = Math.max(r + 0.2, (f.csCap ?? (f.holeCap ?? 8) * 2) / 2);
        cember(Math.max(0.1, (csR - r) / Math.tan(((f.csAci ?? 90) / 2) * Math.PI / 180)));
      }
      if (f.holeAlt === 'vdip' && !through) cember(Math.max(0.1, f.holeDerinlik ?? 20));   // V-dip: düz bore → koni başlangıcı
      if (f.holeTur === 'counterdrilled') {   // kademeli koni dibi → düz bore
        const cbH = Math.max(0.1, f.cbDerinlik ?? 6); const cbR = Math.max(r + 0.2, (f.cbCap ?? (f.holeCap ?? 8) * 1.8) / 2);
        cember(cbH + (cbR - r));
      }
    }
    return out;
  }

  /** CATIA tarzı B-Rep kenar çizgileri: keskin yüz-geçiş kenarlarını çizer. */
  private kenarGuncelle(): void {
    if (!this.govdeMesh) return;
    if (this.govdeKenar) { this.govdeMesh.remove(this.govdeKenar); this.govdeKenar.geometry.dispose(); this.govdeKenar = null; }   // material REUSE (dispose etme — GC baskısı azalt)
    let posKopya: number[]; let segEdgeId: Int32Array | null = null;
    // OCCT modunda GERÇEK B-Rep kenarları (fillet teğet kenarlar dahil); değilse geometrik 30° çıkarım
    const occtEdges = (this.occtModu && this.occtSonShapeRef) ? this.brepKenarGruplari() : null;
    if (occtEdges && occtEdges.length) {
      (this.govdeMesh as any).occt_edgeGroups = occtEdges;
      const segPts: number[] = [], ids: number[] = [];
      occtEdges.forEach((g: any, eid: number) => { const np = this.edgeGroupNoktalari(g); for (let k = 1; k < np.length; k++) { segPts.push(np[k - 1].x, np[k - 1].y, np[k - 1].z, np[k].x, np[k].y, np[k].z); ids.push(eid); } });
      posKopya = segPts; segEdgeId = Int32Array.from(ids);
      if (!posKopya.length) { this.d.ciz(); return; }
    } else {
      // Pozisyon-tabanlı kenar çıkarımı (CSG vertex kaosuna dayanıklı, 30° eşik)
      const eg = this.kati.kenarCikar(this.govdeMesh.geometry, 30);
      const posAttr = eg.getAttribute('position') as THREE.BufferAttribute | undefined;
      try { const r = edgeGruplariUret(eg); (this.govdeMesh as any).occt_edgeGroups = r.gruplar; segEdgeId = r.segEdgeId; } catch (e) { console.warn('[Modelleme] edgeGroups üretilemedi', e); }
      if (!posAttr || posAttr.count === 0) { eg.dispose(); this.d.ciz(); return; }
      posKopya = Array.from(posAttr.array as Float32Array);
      eg.dispose();
    }
    // FikoCizimYalin tarzı: LineSegments2 fat-line + mesh polygonOffset ile temiz kenar
    const lsg = new LineSegmentsGeometry();
    lsg.setPositions(posKopya);
    // edgeId instanced attribute → aynı mantıksal kenarın tüm segment'leri birlikte seçilir (BREP)
    if (segEdgeId && segEdgeId.length === posKopya.length / 6) {
      lsg.setAttribute('edgeId', new THREE.InstancedBufferAttribute(new Float32Array(segEdgeId), 1));
    }

    // 🎯 ÖLÇÜM/SEÇİM UYUMU: import edilen FikoCizimYalin ile BİREBİR aynı 'edgesGeom' (position + 'grup')
    // üret + govdeMesh'e bağla. FikoSecililer.kesistiRegularLineSegments bu formatı bekler; böylece
    // modellenen gövdede de kenar highlight/seçim/ölçüm import gibi ÇALIŞIR. ('grup' = her vertex için
    // [edgeRun başlangıç vertex, edgeRun vertex sayısı, grupId])
    try {
      const oldEG = (this.govdeMesh as any).edgesGeom as THREE.BufferGeometry | undefined;
      if (oldEG && oldEG !== (this.govdeMesh as any).geometry) oldEG.dispose();
      const vcount = posKopya.length / 3;
      const grupArr = new Float32Array(vcount * 3);
      if (segEdgeId && segEdgeId.length === posKopya.length / 6) {
        let s = 0; const segN = segEdgeId.length;
        while (s < segN) {                       // aynı edgeId'li ardışık segment'leri tek run yap
          const id = segEdgeId[s]; let e = s;
          while (e < segN && segEdgeId[e] === id) e++;
          const startV = s * 2, vCnt = (e - s) * 2;
          for (let v = startV; v < startV + vCnt; v++) { grupArr[v * 3] = startV; grupArr[v * 3 + 1] = vCnt; grupArr[v * 3 + 2] = id; }
          s = e;
        }
      } else {                                   // segEdgeId yoksa her segment ayrı grup
        const segC = vcount / 2;
        for (let s2 = 0; s2 < segC; s2++) { const sv = s2 * 2; for (let v = sv; v < sv + 2; v++) { grupArr[v * 3] = sv; grupArr[v * 3 + 1] = 2; grupArr[v * 3 + 2] = s2; } }
      }
      const edgesGeom = new THREE.BufferGeometry();
      edgesGeom.setAttribute('position', new THREE.BufferAttribute(new Float32Array(posKopya), 3));
      edgesGeom.setAttribute('grup', new THREE.BufferAttribute(grupArr, 3));
      if (segEdgeId && segEdgeId.length === posKopya.length / 6) {   // vertex-bazlı edgeId (eğrisel kenar toplama)
        const eidArr = new Float32Array(vcount);
        for (let s = 0; s < segEdgeId.length; s++) { eidArr[s * 2] = segEdgeId[s]; eidArr[s * 2 + 1] = segEdgeId[s]; }
        edgesGeom.setAttribute('edgeId', new THREE.BufferAttribute(eidArr, 1));
      }
      (this.govdeMesh as any).edgesGeom = edgesGeom;
    } catch (e) { /* edgesGeom üretilemezse highlight import yoluna düşemez, sessiz */ }
    const canvas = this.d.canvasAl();
    if (!this._govdeLineMat) this._govdeLineMat = new LineMaterial({ worldUnits: false, alphaToCoverage: false, dashed: false, transparent: false });   // PERFORMANS: tek material, her rebuild'de yeniden YARATMA
    const mat = this._govdeLineMat;
    mat.color.setHex(this.govdeGrafik.kenarRenk ?? 0x0b1119); mat.linewidth = this.govdeGrafik.kenarKalinlik ?? 2;   // CATIA: kenar rengi/kalınlığı grafik özelliklerinden
    mat.resolution.set(canvas?.width || 1920, canvas?.height || 1080);
    this.govdeKenar = new LineSegments2(lsg, mat);
    this.govdeKenar.name = 'FikoGovdeKenar';
    this.govdeKenar.renderOrder = 1;
    (this.govdeKenar as any).raycast = () => { };  // picking'i engellemesin (yüze sketch çalışsın)
    this.govdeMesh.add(this.govdeKenar);
  }

  // ---- MERKEZİ parametrik feature düzenleme (ürün ağacı + 3dHizliMenu) ----

  /** Feature'ın düzenlenebilir parametre listesi (panel + ağaç için tek kaynak). */
  featureParametreleri(feat: IOzellik): { anahtar: string; etiket: string; deger: number; birim: string; tamSayi?: boolean }[] {
    switch (feat.tip) {
      case 'pad': return [{ anahtar: 'param', etiket: 'Yükseklik', deger: feat.param ?? 20, birim: 'mm' }];
      case 'pocket': return [{ anahtar: 'param', etiket: 'Derinlik', deger: feat.param ?? 20, birim: 'mm' }];
      case 'rib': return [{ anahtar: 'kalinlik', etiket: 'Kalınlık', deger: feat.kalinlik ?? 6, birim: 'mm' }, { anahtar: 'param', etiket: 'Uzunluk', deger: feat.param ?? 30, birim: 'mm' }];
      case 'shell': return [{ anahtar: 'kalinlik', etiket: 'Et Kalınlığı', deger: feat.kalinlik ?? 4, birim: 'mm' }];
      case 'draft': return [{ anahtar: 'aci', etiket: 'Eğim Açısı', deger: feat.aci ?? 5, birim: '°' }];
      case 'patternDik': return [{ anahtar: 'adet', etiket: 'Adet', deger: feat.adet ?? 4, birim: '×', tamSayi: true }, { anahtar: 'aralik', etiket: 'Aralık', deger: feat.aralik ?? 40, birim: 'mm' }];
      case 'patternDairesel': return [{ anahtar: 'adet', etiket: 'Adet', deger: feat.adet ?? 6, birim: '×', tamSayi: true }, { anahtar: 'aci', etiket: 'Açı', deger: feat.aci ?? 60, birim: '°' }];
      case 'patternIzgara': return [{ anahtar: 'adet', etiket: 'Sütun', deger: feat.adet ?? 3, birim: '×', tamSayi: true }, { anahtar: 'adet2', etiket: 'Satır', deger: feat.adet2 ?? 3, birim: '×', tamSayi: true }, { anahtar: 'aralik', etiket: 'Yatay', deger: feat.aralik ?? 40, birim: 'mm' }, { anahtar: 'aralik2', etiket: 'Dikey', deger: feat.aralik2 ?? 40, birim: 'mm' }];
      case 'yazi3d': return [{ anahtar: 'boyut', etiket: 'Boyut', deger: feat.boyut ?? 10, birim: 'mm' }, { anahtar: 'param', etiket: 'Kalınlık', deger: feat.param ?? 4, birim: 'mm' }, { anahtar: 'harfBosluk', etiket: 'Harf Aralığı', deger: feat.harfBosluk ?? 0, birim: 'mm' }];
      case 'primitif':
        if (feat.primitifTip === 'kutu') return [{ anahtar: 'pEn', etiket: 'En', deger: feat.pEn ?? 40, birim: 'mm' }, { anahtar: 'pBoy', etiket: 'Boy', deger: feat.pBoy ?? 40, birim: 'mm' }, { anahtar: 'pYuk', etiket: 'Yükseklik', deger: feat.pYuk ?? 30, birim: 'mm' }];
        if (feat.primitifTip === 'kure') return [{ anahtar: 'pCap', etiket: 'Çap', deger: feat.pCap ?? 30, birim: 'mm' }];
        return [{ anahtar: 'pCap', etiket: 'Çap', deger: feat.pCap ?? 30, birim: 'mm' }, { anahtar: 'pYuk', etiket: 'Yükseklik', deger: feat.pYuk ?? 30, birim: 'mm' }];
      case 'makro':
        if (feat.makroTip === 'boru') return [{ anahtar: 'mCap', etiket: 'Dış Çap', deger: feat.mCap ?? 30, birim: 'mm' }, { anahtar: 'mEtKal', etiket: 'Et Kalınlığı', deger: feat.mEtKal ?? 3, birim: 'mm' }, { anahtar: 'mUzun', etiket: 'Uzunluk', deger: feat.mUzun ?? 120, birim: 'mm' }];
        if (feat.makroTip === 'dirsek') return [{ anahtar: 'mCap', etiket: 'Çap', deger: feat.mCap ?? 30, birim: 'mm' }, { anahtar: 'mEtKal', etiket: 'Et Kalınlığı', deger: feat.mEtKal ?? 3, birim: 'mm' }, { anahtar: 'mUzun', etiket: 'Kol Boyu', deger: feat.mUzun ?? 60, birim: 'mm' }, { anahtar: 'mAci', etiket: 'Açı', deger: feat.mAci ?? 90, birim: '°' }];
        if (feat.makroTip === 'yuzuk') return [{ anahtar: 'mCap', etiket: 'İç Çap', deger: feat.mCap ?? 18, birim: 'mm' }, { anahtar: 'mBand', etiket: 'Band Kalınlığı', deger: feat.mBand ?? 4, birim: 'mm' }, { anahtar: 'mUzun', etiket: 'Band Genişliği', deger: feat.mUzun ?? 5, birim: 'mm' }];
        if (feat.makroTip === 'tasYuvasi') return [{ anahtar: 'mTasCap', etiket: 'Taş Çapı', deger: feat.mTasCap ?? 8, birim: 'mm' }, { anahtar: 'mProngAdet', etiket: 'Tırnak Adedi', deger: feat.mProngAdet ?? 4, birim: '×', tamSayi: true }, { anahtar: 'mProngCap', etiket: 'Tırnak Çapı', deger: feat.mProngCap ?? 1.2, birim: 'mm' }, { anahtar: 'mProngYuk', etiket: 'Tırnak Boyu', deger: feat.mProngYuk ?? 6, birim: 'mm' }];
        if (feat.makroTip === 'bezel') return [{ anahtar: 'mTasCap', etiket: 'Taş Çapı', deger: feat.mTasCap ?? 8, birim: 'mm' }, { anahtar: 'mEtKal', etiket: 'Çeper', deger: feat.mEtKal ?? 1, birim: 'mm' }, { anahtar: 'mProngYuk', etiket: 'Yükseklik', deger: feat.mProngYuk ?? 6, birim: 'mm' }];
        return [];
      case 'fillet3d': return [{ anahtar: 'kenarMesafe', etiket: 'Yarıçap', deger: feat.kenarMesafe ?? 4, birim: 'mm' }, { anahtar: 'kenarAci', etiket: 'Kenar Eşiği', deger: feat.kenarAci ?? 30, birim: '°' }];
      case 'chamfer3d': return [{ anahtar: 'kenarMesafe', etiket: 'Mesafe', deger: feat.kenarMesafe ?? 4, birim: 'mm' }, { anahtar: 'kenarAci', etiket: 'Kenar Eşiği', deger: feat.kenarAci ?? 30, birim: '°' }];
      default: return [];
    }
  }

  /** Feature parametresini değiştir → parametrik rebuild + ağaç güncelle (MERKEZİ). */
  /** CATIA Ribbon Pad/Pocket: aktif eskiz profilini direkt pad/pocket yap → Pad Definition penceresi açılır. Profil yoksa false. */
  padPocketBaslat(tip: 'pad' | 'pocket'): boolean {
    if (!this.komutBaslat(tip, { param: 20 })) return false;
    const f = this.taslakFeat;
    const profilVar = !!f && (f.elemanlar === undefined || (f.elemanlar?.length || 0) > 0);
    if (!profilVar) { this.komutIptal(); return false; }   // profil yok → boş feature kalmasın
    this.komutOnayla();   // pad/pocket oluşur + komutOnayla içinde Pad Definition penceresi açılır
    return true;
  }

  /** CATIA Ribbon Shaft/Groove: aktif eskiz profilini direkt döndür → Shaft Definition penceresi. Profil yoksa false. */
  shaftGrooveBaslat(tip: 'shaft' | 'groove'): boolean {
    if (!this.komutBaslat(tip, {})) return false;
    const f = this.taslakFeat;
    const profilVar = !!f && (f.elemanlar === undefined || (f.elemanlar?.length || 0) > 0);
    if (!profilVar) { this.komutIptal(); return false; }
    this.komutOnayla();
    return true;
  }

  /** Pad Definition penceresinden herhangi bir alanı (sayı/bool/string) güncelle → rebuild + önizleme. */
  padGuncelle(feat: IOzellik, anahtar: string, deger: any): void {
    (feat as any)[anahtar] = deger;
    this.rebuild(); this.featureNodeAdGuncelle(feat); this.d.agacGuncelle?.();
    this.gecmisKaydet();   // doğrudan param değişimi (FikoDuzlemSecici/FikoFeatureDialog) → geçmiş
  }

  featureParamGuncelle(feat: IOzellik, anahtar: string, deger: number): void {
    if (deger == null || isNaN(deger)) return;
    const tamSay = anahtar === 'adet' || anahtar === 'adet2' || anahtar === 'mProngAdet';
    (feat as any)[anahtar] = tamSay ? Math.max(1, Math.round(deger)) : deger;
    this.featureNodeAdGuncelle(feat);   // ağaç etiketi anlık (ucuz)
    this.rebuildDebounce();             // PERFORMANS: slider sürüklerken her değerde değil, durunca tek rebuild
  }
  private _rebuildTimer: any = null;
  /** Parametre düzenlemede rebuild'i geciktir (ardışık değişimlerde sadece SON rebuild çalışır — ultra performans). */
  private rebuildDebounce(ms = 70): void {
    if (this._rebuildTimer) clearTimeout(this._rebuildTimer);
    this._rebuildTimer = setTimeout(() => { this._rebuildTimer = null; this.rebuild(); this.d.agacGuncelle?.(); this.gecmisKaydet(); }, ms);
  }

  /** Feature referans alanları (index ile başka feature'a bağlanan linkler). */
  private static readonly REF_ALANLAR = ['duzlemRef', 'eksenRef', 'symRef', 'hedefIndex', 'ref1', 'ref2', 'ref3'] as const;

  /** Dizi mutasyonu (sil/yeniden-sırala) sonrası TÜM feature linklerini KİMLİKLE yeniden eşler.
   *  Index kaymasından bağımsız: her ref, eski dizideki hedefin YENİ konumuna güncellenir.
   *  Hedef silinmişse -1 (kopuk link → mevcut `>= 0` guard'ları koordinat moduna düşer). */
  private referanslariRemap(eski: IOzellik[]): void {
    const yeni = (idx: any): number => {
      if (typeof idx !== 'number' || idx < 0 || idx >= eski.length) return idx;   // -1/koord sentinel korunur
      const hedef = eski[idx];
      return hedef ? this.ozellikler.indexOf(hedef) : -1;
    };
    for (const f of this.ozellikler) {
      for (const a of FikoModellemeYonetici.REF_ALANLAR) {
        if (typeof (f as any)[a] === 'number') (f as any)[a] = yeni((f as any)[a]);
      }
      if (Array.isArray(f.refler)) f.refler = f.refler.map(yeni).filter(i => i >= 0);
      if (Array.isArray(f.secProfiller)) { /* profil snapshot — index ref tutmaz */ }
    }
  }

  /** Bir feature'ın doğrudan bağımlı olduğu feature index'leri (link hedefleri). DAG kenarları. */
  private bagimliIndexler(f: IOzellik): number[] {
    const out: number[] = [];
    for (const a of FikoModellemeYonetici.REF_ALANLAR) {
      const v = (f as any)[a];
      if (typeof v === 'number' && v >= 0) out.push(v);
    }
    if (Array.isArray(f.refler)) for (const v of f.refler) if (typeof v === 'number' && v >= 0) out.push(v);
    return out;
  }

  /** Feature'ı sil → rebuild (parametrik geçmişten çıkar). */
  featureSil(feat: IOzellik): void {
    const i = this.ozellikler.indexOf(feat); if (i < 0) return;
    const eski = this.ozellikler.slice();   // mutasyon öncesi (referans remap için)
    feat.node?.parent?.remove(feat.node as any);
    this.ozellikler.splice(i, 1);
    this.referanslariRemap(eski);           // linkleri kimlikle koru (orphan knowledge param intrinsikSenkron'da temizlenir)
    this.rebuild(); this.parametreAgaciGuncelle(); this.d.agacGuncelle?.();
    (this.d.sahne as any).anaGosterim?.Dialog3DHizliMenu?.kapa?.();
    this.gecmisKaydet();   // silme sonrası geçmişe kaydet
  }

  /** Ağaç node etiketi için parametre özeti. */
  private featureOzet(feat: IOzellik): string {
    switch (feat.tip) {
      case 'pad': case 'pocket': {
        const tipAd: any = { sonraki: 'Sonraki', son: 'Son', duzleme: 'Düzlem', yuzeye: 'Yüzey' };
        const ana = (feat.limitTip && feat.limitTip !== 'boyut') ? tipAd[feat.limitTip] : (feat.param ?? 20) + 'mm';
        return ana + (feat.simetrik ? ' ⇆' : '') + (feat.limit2Aktif ? ' +' + (feat.param2 ?? 0) : '') + (feat.incePad ? ' ◫' : '') + ((feat.draftAci || 0) > 0 ? ' ∠' + feat.draftAci : '');
      }
      case 'shaft': case 'groove': return (feat.aci ?? 360) + '°' + (feat.limit2Aktif ? ' +' + (feat.aci2 ?? 0) + '°' : '') + (feat.tersYon ? ' ⇄' : '');
      case 'hole': {
        const turS: any = { counterbored: ' ⊔', countersunk: ' ⌄', counterdrilled: ' ⊻' };
        const lim = feat.holeLimit && feat.holeLimit !== 'boyut' ? '⤓' : (feat.holeDerinlik ?? 20) + 'mm';
        return (feat.holeThread ? (feat.holeThreadAd || 'M') : 'Ø' + (feat.holeCap ?? 10)) + ' · ' + lim + (turS[feat.holeTur || ''] || '') + (feat.holeAlt === 'vdip' ? ' V' : '');
      }
      case 'rib': return (feat.kalinlik ?? 6) + 'mm · ' + (feat.param ?? 30) + 'mm';
      case 'stiffener': return (feat.kalinlik ?? 6) + 'mm feder';
      case 'shell': return (feat.kalinlik ?? 4) + 'mm';
      case 'thickness': return (feat.tersYon ? '−' : '+') + (feat.kalinlik ?? 2) + 'mm';
      case 'bool': { const ad: any = { assemble: 'Birleştir', add: 'Ekle', remove: 'Çıkar', intersect: 'Kesiştir' }; return (ad[feat.boolOp || ''] || 'Boolean') + ' ← Gövde.' + ((feat.kaynakBodyId ?? 0) + 1); }
      case 'split': return (feat.aynaTip || 'xy').toUpperCase() + (feat.splitOfset ? '@' + feat.splitOfset : '') + (feat.tersYon ? ' −' : ' +');
      case 'yuzey': { const ad: any = { extrude: 'Uzat', revolve: 'Döndür', fill: 'Dolgu', loft: 'Çok-Kesit', sweep: 'Süpür', sphere: 'Küre Yüzey', cylinder: 'Silindir Yüzey' }; return (ad[feat.yuzeyTip || ''] || 'Yüzey') + (feat.yuzeyTip === 'extrude' ? ' ' + (feat.param ?? 40) + 'mm' : feat.yuzeyTip === 'revolve' ? ' ' + (feat.aci ?? 360) + '°' : feat.yuzeyTip === 'sphere' ? ' Ø' + (feat.pCap ?? 50) : feat.yuzeyTip === 'cylinder' ? ' Ø' + (feat.pCap ?? 50) : ''); }
      case 'yuzeyJoin': return (feat.refler?.length ?? 0) + ' yüzey birleşik';
      case 'yuzeyTrim': return 'kırp ' + (feat.aynaTip || 'yz').toUpperCase() + (feat.tersYon ? ' −' : ' +');
      case 'yuzeyExtract': return 'yüz #' + (feat.yuzIndex ?? 0);
      case 'yuzeyBlend': return 'iki eğri arası';
      case 'yuzeyExtrapolate': return 'uzat +' + (feat.param ?? 20) + 'mm';
      case 'thickSurface': return (feat.kalinlik ?? 3) + 'mm kalınlık';
      case 'splitSurface': return 'Yüzeyle böl' + (feat.tersYon ? ' −' : ' +');
      case 'closeSurface': return 'Katı (kapat)';
      case 'removeLump': return 'En büyük parça';
      case 'healing': return 'Onar (Healing)';
      case 'simplify': return 'Sadeleştir';
      case 'offsetShape': return 'Offset ' + (feat.param ?? 5) + 'mm';
      case 'pipe3d': return 'Boru Ø' + (feat.pCap ?? 16);
      case 'untrim': return 'Untrim';
      case 'multiExtract': return 'Çoklu Çıkar';
      case 'disassemble': return 'Parçala';
      case 'removeFace': return (feat.secYuzler?.length || 0) + ' yüz silindi';
      case 'faceFillet': return 'R' + (feat.kenarMesafe ?? 4) + ' yüz-fillet';
      case 'triFillet': return (feat.kenarMesafe ? 'R' + feat.kenarMesafe : 'oto') + ' tritangent';
      case 'replaceFace': return 'yüz→' + (feat.aynaTip || 'xy').toUpperCase() + (feat.splitOfset ? '@' + feat.splitOfset : '');
      case 'nokta3d': { const ad: any = { arasi: 'Arası', egriUzerinde: 'Eğride', extremum: 'Uç', duzlemde: 'Düzlemde', projeksiyon: 'İzdüşüm' }; const p = this.noktaKonum(feat); return (ad[feat.noktaTip || ''] ? ad[feat.noktaTip!] + ' ' : '') + `(${Math.round(p.x)},${Math.round(p.y)},${Math.round(p.z)})`; }
      case 'cizgi3d': { const [a, b] = this.cizgiUclar(feat); const ad: any = { 'nokta-yon': 'Yön ' + (feat.eksenTip || 'x').toUpperCase() + ' ', 'aci': (feat.aci ?? 45) + '° ', 'bisecting': 'Açıortay ', 'tanjant': 'Teğet ', 'normal': 'Normal ' }; return (ad[feat.cizgiTip || ''] || '') + Math.round(a.distanceTo(b)) + 'mm'; }
      case 'cember3d': { const cb = this.cemberBilgi(feat); const e: any = { 'uc-nokta': ' (3 nokta)', 'merkez-nokta': ' (mrk+nok)', 'iki-nokta-cap': ' (çap)', 'merkez-eksen': ' (mrk+eksen)', 'merkez-teget': ' (teğet)', 'bitangent': ' (2-teğet)', 'tritangent': ' (3-teğet)' }; return 'R' + (cb ? Math.round(cb.r) : (feat.yaricap ?? 20)) + (e[feat.cemberTip || ''] || ''); }
      case 'spline3d': return (feat.refler?.length ?? 0) + ' nokta';
      case 'polyline': return (feat.refler?.length ?? 0) + ' nokta (düz)';
      case 'curveSmooth': return 'Yumuşat ×' + (feat.param ?? 3);
      case 'corner': return 'Köşe R' + (feat.yaricap ?? 10);
      case 'isoparametrik': return 'Iso ' + Math.round((feat.oran ?? 0.5) * 100) + '%';
      case 'combine3d': return 'Birleşik Eğri';
      case 'equationCurve': return 'f(t) Eğrisi';
      case 'spine3d': return 'Omurga (' + (feat.refler?.length ?? 0) + ' kesit)';
      case 'helix3d': return 'R' + (feat.yaricap ?? 20) + ' ' + (feat.helixTur ?? 3) + 'tur';
      case 'spiral3d': return 'R' + (feat.yaricap ?? 5) + '→ ' + (feat.helixTur ?? 3) + 'tur';
      case 'paralelEgri': return 'ofset ' + (feat.param ?? 10) + 'mm';
      case 'connectEgri': return 'bağlantı';
      case 'yuzeyKenar': return 'sınır eğrisi';
      case 'kesisim3d': return 'kesişim eğrisi';
      case 'conic3d': { const r = feat.param ?? 0.5; return r < 0.5 ? 'elips' : r > 0.5 ? 'hiperbol' : 'parabol'; }
      case 'duzlem3d': { const ad: any = { ofset: 'Ofset', paralelNokta: 'Paralel', 'uc-nokta': '3 Nokta', egriNormal: 'Eğri⊥', 'iki-cizgi': '2 Çizgi' }; return (ad[feat.duzlemTip || 'ofset'] || 'Düzlem') + (feat.duzlemTip === 'ofset' ? ' ' + (feat.splitOfset ?? 0) + 'mm' : ''); }
      case 'draft': return (feat.aci ?? 5) + '° · ' + (feat.eksenTip || 'z').toUpperCase();
      case 'patternDik': return (feat.adet ?? 4) + '× / ' + (feat.aralik ?? 40) + 'mm';
      case 'patternDairesel': return (feat.adet ?? 6) + '× / ' + (feat.aci ?? 60) + '°';
      case 'patternIzgara': return (feat.adet ?? 3) + '×' + (feat.adet2 ?? 3);
      case 'userPattern': return ((feat.secNoktalar?.length) || this.ozellikler.filter(o => o.tip === 'nokta3d').length) + ' nokta';
      case 'mirror': return (feat.aynaTip || '').toUpperCase();
      case 'symmetry': return (feat.aynaTip || 'yz').toUpperCase();
      case 'translate': return feat.trMod === 'koordinat' ? `${feat.trX ?? 0},${feat.trY ?? 0},${feat.trZ ?? 0}` : ((feat.eksenRef != null && feat.eksenRef >= 0 ? '⟂çizgi' : (feat.trYon || 'x').toUpperCase()) + ' ' + (feat.trMesafe ?? 0) + 'mm');
      case 'rotate': return (feat.aci ?? 45) + '° ' + (feat.eksenTip || 'z').toUpperCase();
      case 'scaling': return '×' + (feat.scaleFaktor ?? 1.5);
      case 'affinity': return `${feat.affX ?? 1}×${feat.affY ?? 1}×${feat.affZ ?? 1}`;
      case 'axisToAxis': return 'Eksen→Eksen';
      case 'fillet3d': return 'R' + (feat.kenarMesafe ?? 4);
      case 'chamfer3d': return (feat.kenarMesafe ?? 4) + 'mm' + (feat.chamferMod === 'length2' ? '×' + (feat.kenarMesafe2 ?? 4) : feat.chamferMod === 'aci' ? '∠' + (feat.chamferAci ?? 45) : '');
      case 'yazi3d': return '"' + (feat.metin || '') + '"';
      case 'primitif': return ({ kutu: 'Kutu', silindir: 'Silindir', kure: 'Küre', koni: 'Koni', torus: 'Torus', wedge: 'Kama' } as any)[feat.primitifTip || ''] || 'Primitif';
      case 'makro':
        if (feat.makroTip === 'boru') return 'Ø' + (feat.mCap ?? 30) + ' · ' + (feat.mUzun ?? 120) + 'mm';
        if (feat.makroTip === 'dirsek') return 'Ø' + (feat.mCap ?? 30) + ' · ' + (feat.mAci ?? 90) + '°';
        if (feat.makroTip === 'yuzuk') return 'iç Ø' + (feat.mCap ?? 18);
        if (feat.makroTip === 'tasYuvasi') return (feat.mProngAdet ?? 4) + ' tırnak · Ø' + (feat.mTasCap ?? 8);
        if (feat.makroTip === 'bezel') return 'bezel Ø' + (feat.mTasCap ?? 8);
        return '';
      default: return '';
    }
  }

  private featureNodeAdGuncelle(feat: IOzellik): void {
    if (!feat.node) return;
    const ozet = this.featureOzet(feat);
    (feat.node as any).name = (feat.node as any)._taban + (ozet ? ' · ' + ozet : '');
  }

  /** Feature node'una çift-tık/sağ-tık → parametre panelini (FikoFeatureHizliEk) aç. */
  // ───────────────────── CATIA Çoklu-Gövde (Multi-Body) + Boolean ─────────────────────
  private aktifBodyId = 0;       // yeni feature'lar bu gövdeye gider (0 = ana PartBody)
  private bodyIdSayac = 0;       // yeni gövde id üreteci
  get aktifBodyIdDeger(): number { return this.aktifBodyId; }
  /** GSD Thick Surface: son yüzeyi (shell) kalınlaştırıp katıya çevir. */
  thickSurfaceBaslat(): boolean {
    let idx = -1;
    for (let i = this.ozellikler.length - 1; i >= 0; i--) if (this.ozellikler[i].tip === 'yuzey' && !this.ozellikler[i].bastirilmis) { idx = i; break; }
    if (idx < 0) { this.d.bildir?.('Önce bir yüzey oluştur (Yüzey Uzat/Dön/Dolgu)'); return false; }
    const node = this.ozellikNode('thickSurface', null);
    const feat: IOzellik = { tip: 'thickSurface', ekleme: true, hedefIndex: idx, kalinlik: 3, node };
    this.ozellikler.push(feat); this.featureNodeBagla(feat);
    this.rebuild(); this.d.agacGuncelle?.(); this.d.degisti?.(this.durum, this.sketch);
    setTimeout(() => this.featureMenu(feat), 60);
    return true;
  }

  /** GSD Offset Surface: son profil-tabanlı yüzeyin paralelini oluştur (ribbon). */
  yuzeyOffsetSonBaslat(): boolean {
    for (let i = this.ozellikler.length - 1; i >= 0; i--) { const y = this.ozellikler[i]; if (y.tip === 'yuzey' && y.yuzeyTip !== 'sweep' && y.yuzeyTip !== 'loft' && !y.bastirilmis) { this.yuzeyOffsetFor(y); return true; } }
    this.d.bildir?.('Önce profil-tabanlı yüzey oluştur (Uzat/Dön/Dolgu)'); return false;
  }

  /** GSD Split-by-Surface: son (extrude) yüzeyle ana gövdeyi böl, bir tarafı tut. */
  splitSurfaceBaslat(): boolean {
    if (!this.govdeMesh) { this.d.bildir?.('Bölmek için bir katı gerekli'); return false; }
    let idx = -1;
    for (let i = this.ozellikler.length - 1; i >= 0; i--) { const y = this.ozellikler[i]; if (y.tip === 'yuzey' && y.yuzeyTip === 'extrude' && !y.bastirilmis) { idx = i; break; } }
    if (idx < 0) { this.d.bildir?.('Önce bir Yüzey Uzat (extrude) oluştur'); return false; }
    const node = this.ozellikNode('splitSurface', null);
    const feat: IOzellik = { tip: 'splitSurface', ekleme: false, hedefIndex: idx, tersYon: false, node };
    this.ozellikler.push(feat); this.featureNodeBagla(feat);
    this.rebuild(); this.d.agacGuncelle?.(); this.d.degisti?.(this.durum, this.sketch);
    setTimeout(() => this.featureMenu(feat), 60);
    return true;
  }

  /** CATIA "Insert New Body": yeni boş gövde oluştur + aktif yap. Sonraki feature'lar bu gövdeye gider. */
  yeniGovde(): void {
    this.aktifBodyId = ++this.bodyIdSayac;
    this.d.bildir?.('Yeni gövde aktif (Gövde.' + (this.aktifBodyId + 1) + ') — modelle, sonra Boolean ile ana gövdeye birleştir');
    this.d.degisti?.(this.durum, this.sketch);
  }
  /** CATIA Boolean: aktif (kaynak) gövdeyi ana gövdeye uygula (Assemble/Add/Remove/Intersect). */
  boolBaslat(op: 'assemble' | 'add' | 'remove' | 'intersect'): boolean {
    const kaynak = this.aktifBodyId;
    if (kaynak === 0) { this.d.bildir?.('Önce "Yeni Gövde" ile 2. gövde oluşturup modelle'); return false; }
    if (!this.ozellikler.some(f => (f.bodyId || 0) === kaynak)) { this.d.bildir?.('Kaynak gövde boş — önce modelle'); return false; }
    const node = this.ozellikNode('bool', null);
    const feat: IOzellik = { tip: 'bool', ekleme: true, boolOp: op, kaynakBodyId: kaynak, bodyId: 0, node };
    this.ozellikler.push(feat); this.featureNodeBagla(feat);
    this.aktifBodyId = 0;   // ana gövdeye dön
    this.rebuild(); this.d.agacGuncelle?.(); this.d.degisti?.(this.durum, this.sketch);
    return true;
  }

  private featureNodeBagla(feat: IOzellik): void {
    if (feat.bodyId === undefined) feat.bodyId = this.aktifBodyId;   // çoklu-gövde: feature'ı aktif gövdeye damgala
    const node: any = feat.node; if (!node) return;
    if (node._taban === undefined) node._taban = node.name;   // yalnız ilk bağlamada (tekrar bağlamada ozet birikmesin)
    node._feat = feat;            // node→feature eşlemesi (suppress/reorder)
    node.siralanabilir = true;    // ürün ağacında sürükle-bırak yeniden sıralanabilir
    node.dtRename = true;         // çift-tık → yeniden adlandır (param için inline chip + sağ menü var)
    this.featureNodeAdGuncelle(feat);
    node.tiklandi = () => { if (this.secimAktif) this.secimAgactanSec(feat); };   // CATIA: seçim modunda ağaçtan da seç
    node.ciftTiklandi = () => this.featureMenu(feat);
    node.sagMenu = [
      { baslik: 'Parametreleri Düzenle', simge: 'sliders horizontal', renkSinif: 'blue', tiklandi: () => this.featureMenu(feat) },
      { baslik: 'Grafik Özellikleri', simge: 'paint brush', renkSinif: 'purple', tiklandi: () => this.grafikDialogAc(feat) },
      { baslik: feat.bastirilmis ? 'Aktifleştir' : 'Bastır (Deactivate)', simge: feat.bastirilmis ? 'check circle' : 'ban', renkSinif: 'orange', tiklandi: () => this.featureBastirToggle(feat) },
      { tipi: 'Ayraç' },
      { baslik: 'Sil', simge: 'trash alternate', renkSinif: 'red', tiklandi: () => this.featureSil(feat) },
    ];
    if (feat.tip === 'yazi3d') node.sagMenu.unshift({ baslik: 'CAM Gravür / Takım', simge: 'cut', renkSinif: 'orange', tiklandi: () => this.yaziGravurAc(feat) });
    node.userData['FikoParametreler'] = this.featureInlineParametreler(feat);   // ağaçta inline düzenleme (CATIA'da dialog gerekir)
    this.featureDurumGuncelle(feat);
    this.parametreAgaciGuncelle();   // CATIA "Parameters" grubunu tazele
  }

  /** Feature node'un durum rozetini güncelle (bastirilmis/hata → FikoArayuz.durum). */
  private featureDurumGuncelle(feat: IOzellik): void {
    const node: any = feat.node; if (!node?.userData?.['FikoArayuz']) return;
    node.userData['FikoArayuz'].durum = feat.bastirilmis ? 'bastirilmis' : (feat.hata ? 'hata' : null);
  }

  /** Feature suppress (deactivate) aç/kapa → rebuild (üretimden çıkar/girer). CATIA Deactivate. */
  featureBastirToggle(feat: IOzellik): void {
    feat.bastirilmis = !feat.bastirilmis;
    if (feat.node) { (feat.node as any).sagMenu = null; this.featureNodeBagla(feat); }   // menü etiketini tazele
    this.rebuild();
    this.d.agacGuncelle?.();
    (this.d.sahne as any).anaGosterim?.Dialog3DHizliMenu?.kapa?.();
  }

  /** Ürün ağacında sürükle-bırak: kaynak feature node'unu hedefin konumuna taşı → ozellikler[] yeniden sırala + rebuild. */
  ozellikSiralaTasi(kaynakNode: any, hedefNode: any): boolean {
    const fk = kaynakNode?._feat as IOzellik, fh = hedefNode?._feat as IOzellik;
    if (!fk || !fh || fk === fh) return false;
    const i = this.ozellikler.indexOf(fk), j = this.ozellikler.indexOf(fh);
    if (i < 0 || j < 0) return false;
    const eski = this.ozellikler.slice();   // mutasyon öncesi (referans remap + geri alma için)
    this.ozellikler.splice(i, 1);
    this.ozellikler.splice(this.ozellikler.indexOf(fh) + (i < j ? 1 : 0), 0, fk);   // hedefin alt/üstüne
    this.referanslariRemap(eski);   // yeni sıraya göre linkleri kimlikle güncelle
    // DAG koruması: bir feature, link verdiği (bağımlı olduğu) feature'dan ÖNCE gelemez (CATIA kuralı)
    let ihlal = false;
    for (let k = 0; k < this.ozellikler.length && !ihlal; k++)
      for (const dep of this.bagimliIndexler(this.ozellikler[k])) if (dep > k) { ihlal = true; break; }
    if (ihlal) {
      const yeniDizi = this.ozellikler.slice();   // ref'ler şu an bu (yeni) sıraya göre — geri remap için
      this.ozellikler.length = 0; this.ozellikler.push(...eski);
      this.referanslariRemap(yeniDizi);   // refleri eski sıraya geri eşle
      this.d.bildir?.('Bu sıralama bir bağımlılığı kırardı (feature, bağlı olduğu öğeden önce gelemez)');
      return false;
    }
    // body grubundaki node sırasını da güncelle (ağaç görünümü)
    const par = kaynakNode.parent;
    if (par && kaynakNode !== hedefNode) {
      par.remove(kaynakNode);
      const hi = par.children.indexOf(hedefNode);
      if (hi >= 0) { par.children.splice(hi + (i < j ? 1 : 0), 0, kaynakNode); kaynakNode.parent = par; }
      else par.add(kaynakNode);
    }
    this.rebuild(); this.parametreAgaciGuncelle();
    return true;
  }

  // ───────── GSD Yüzey Analizi (vertex-renkli overlay) ─────────
  private _analizEskiMat: any = null;
  private _analizAktif: string | null = null;
  get analizAktif(): string | null { return this._analizAktif; }
  /** Analiz için gövde mesh'ine vertex-renk uygula + materyali değiştir (eski materyali sakla). */
  private analizUygula(renk: Float32Array, tip: string): boolean {
    const mesh = this.govdeMesh as any; if (!mesh) { this.d.bildir?.('Önce bir katı/gövde oluştur'); return false; }
    const geo = mesh.geometry as THREE.BufferGeometry;
    geo.setAttribute('color', new THREE.BufferAttribute(renk, 3));
    if (!this._analizEskiMat) this._analizEskiMat = mesh.material;
    mesh.material = new THREE.MeshBasicMaterial({ vertexColors: true, side: THREE.DoubleSide });
    this._analizAktif = tip; this.d.ciz(); return true;
  }
  /** Analizi kapat → eski materyali geri yükle. */
  private _analizOverlay: THREE.Object3D[] = [];
  analizKapat(): void {
    const mesh = this.govdeMesh as any;
    if (mesh) {
      if (mesh._analizEskiGeo) { mesh.geometry = mesh._analizEskiGeo; mesh._analizEskiGeo = null; }
      if (this._analizEskiMat) { mesh.material = this._analizEskiMat; this._analizEskiMat = null; }
    }
    for (const o of this._analizOverlay) { (o.parent || this.d.sahne).remove(o); (o as any).geometry?.dispose?.(); }
    this._analizOverlay = [];
    this._analizAktif = null; this.d.ciz();
  }
  /** GSD Connect Checker: son iki yüzey arasındaki min/maks boşluğu ölçer (raporlar). */
  connectChecker(): boolean {
    const ms = this.yuzeyMeshler; if (ms.length < 2) { this.d.bildir?.('Connect Checker için en az 2 yüzey gerekli'); return false; }
    const A = ms[ms.length - 2], B = ms[ms.length - 1];
    const pa = A.geometry.getAttribute('position'), pb = B.geometry.getAttribute('position');
    const stepA = Math.max(1, Math.floor(pa.count / 400)), stepB = Math.max(1, Math.floor(pb.count / 400));
    let mn = Infinity, mx = 0; const va = new THREE.Vector3(), vb = new THREE.Vector3();
    for (let i = 0; i < pa.count; i += stepA) { va.set(pa.getX(i), pa.getY(i), pa.getZ(i)); A.localToWorld(va); let dl = Infinity;
      for (let j = 0; j < pb.count; j += stepB) { vb.set(pb.getX(j), pb.getY(j), pb.getZ(j)); B.localToWorld(vb); const d = va.distanceTo(vb); if (d < dl) dl = d; }
      if (dl < mn) mn = dl; if (dl > mx) mx = dl; }
    this.d.bildir?.(`Connect Checker: min boşluk ${mn.toFixed(3)} mm · maks ${mx.toFixed(2)} mm`);
    this._analizAktif = 'connect'; return true;
  }
  /** GSD Curve Connect Checker: son iki eğri ucu arası G0 boşluk + G1 teğet açısı (raporlar). */
  egriBaglantiChecker(): boolean {
    const egriT = new Set(['cizgi3d', 'cember3d', 'spline3d', 'polyline', 'curveSmooth', 'corner', 'helix3d', 'spiral3d', 'conic3d', 'paralelEgri', 'connectEgri', 'combine3d']);
    const eg = this.ozellikler.filter(o => egriT.has(o.tip) && !o.bastirilmis);
    if (eg.length < 2) { this.d.bildir?.('Eğri Bağlantı Kontrolü için 2 eğri gerekli'); return false; }
    const p1 = this.egriNoktalari(eg[eg.length - 2]), p2 = this.egriNoktalari(eg[eg.length - 1]);
    if (p1.length < 2 || p2.length < 2) return false;
    // en yakın uç çiftini bul
    const uc1 = [p1[0], p1[p1.length - 1]], uc2 = [p2[0], p2[p2.length - 1]];
    let best = Infinity, bi = 0, bj = 0;
    for (let i = 0; i < 2; i++) for (let j = 0; j < 2; j++) { const d = uc1[i].distanceTo(uc2[j]); if (d < best) { best = d; bi = i; bj = j; } }
    const t1 = (bi === 0 ? p1[1].clone().sub(p1[0]) : p1[p1.length - 2].clone().sub(p1[p1.length - 1])).normalize();
    const t2 = (bj === 0 ? p2[1].clone().sub(p2[0]) : p2[p2.length - 2].clone().sub(p2[p2.length - 1])).normalize();
    const aci = Math.acos(Math.max(-1, Math.min(1, Math.abs(t1.dot(t2))))) * 180 / Math.PI;
    const sure = best < 0.01 ? (aci < 1 ? 'G1 (teğet)' : 'G0 (konum)') : 'Boşluk var';
    this.d.bildir?.(`Eğri Bağlantı: G0 boşluk ${best.toFixed(3)} mm · teğet farkı ${aci.toFixed(1)}° → ${sure}`);
    this._analizAktif = 'egriBaglanti'; return true;
  }
  /** GSD Reflect Line: gövdenin bir yöne göre silüet (kontur) çizgileri — komşu yüz normalleri yön etrafında işaret değiştiren kenarlar. */
  reflectLineAnaliz(eksen: 'x' | 'y' | 'z' = 'z'): boolean {
    const mesh = this.govdeMesh as any; if (!mesh) { this.d.bildir?.('Önce bir katı/gövde oluştur'); return false; }
    const g: THREE.BufferGeometry = mesh.geometry.index ? mesh.geometry.toNonIndexed() : mesh.geometry;
    const pos = g.getAttribute('position');
    const dir = eksen === 'x' ? new THREE.Vector3(1, 0, 0) : eksen === 'y' ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(0, 0, 1);
    const triSay = pos.count / 3; const faceN: THREE.Vector3[] = [];
    const a = new THREE.Vector3(), b = new THREE.Vector3(), c = new THREE.Vector3();
    // KONUM-hash komşuluk (mergeVertices keskin kenarda normal farkıyla birleştirmez) → konuma göre kenar paylaşımı
    const anahtar = (i: number) => Math.round(pos.getX(i) * 1000) + ',' + Math.round(pos.getY(i) * 1000) + ',' + Math.round(pos.getZ(i) * 1000);
    const gv = (i: number, t: THREE.Vector3) => t.set(pos.getX(i), pos.getY(i), pos.getZ(i));
    const kenarYuz = new Map<string, { f: number[]; pa: number; pb: number }>();
    for (let t = 0; t < triSay; t++) {
      const i0 = t * 3, i1 = t * 3 + 1, i2 = t * 3 + 2;
      gv(i0, a); gv(i1, b); gv(i2, c);
      faceN[t] = b.clone().sub(a).cross(c.clone().sub(a)).normalize();
      for (const [u, v] of [[i0, i1], [i1, i2], [i2, i0]]) {
        const ku = anahtar(u), kv = anahtar(v); const k = ku < kv ? ku + '|' + kv : kv + '|' + ku;
        const e = kenarYuz.get(k) || (kenarYuz.set(k, { f: [], pa: u, pb: v }).get(k)!); e.f.push(t);
      }
    }
    const seg: number[] = []; const eps = 1e-3;
    for (const [, e] of kenarYuz) {
      if (e.f.length < 2) continue;
      const s0 = faceN[e.f[0]].dot(dir), s1 = faceN[e.f[1]].dot(dir);
      if ((s0 > eps) !== (s1 > eps)) seg.push(pos.getX(e.pa), pos.getY(e.pa), pos.getZ(e.pa), pos.getX(e.pb), pos.getY(e.pb), pos.getZ(e.pb));
    }
    if (!seg.length) { this.d.bildir?.('Bu yönde silüet kenarı bulunamadı'); return false; }
    const lg = new THREE.BufferGeometry(); lg.setAttribute('position', new THREE.Float32BufferAttribute(seg, 3));
    const line = new THREE.LineSegments(lg, new THREE.LineBasicMaterial({ color: 0x00e5ff, linewidth: 2 }));
    line.name = 'FikoReflectLine'; (line as any).raycast = () => { };
    this.d.sahne.add(line); this._analizOverlay.push(line);
    this.d.bildir?.('Reflect Line: ' + (seg.length / 6) + ' silüet kenarı (' + eksen.toUpperCase() + ' yönü)'); this._analizAktif = 'reflect'; this.d.ciz(); return true;
  }
  /** OCCT Kütle Özellikleri: hacim, yüzey alanı, ağırlık merkezi (raporlar). */
  kutleAnaliz(): boolean {
    if (!this.occtSonShapeRef) { this.d.bildir?.('Önce bir katı oluştur'); return false; }
    const k = fikoOCCT.kutleOzellikleri(this.occtSonShapeRef);
    const yog = 7.85e-6;   // çelik kg/mm³ (varsayılan)
    this.d.bildir?.(`Hacim ${(k.hacim / 1000).toFixed(2)} cm³ · Alan ${(k.alan / 100).toFixed(2)} cm² · Ağırlık~${(k.hacim * yog).toFixed(1)} g · COG (${k.merkez.x.toFixed(1)},${k.merkez.y.toFixed(1)},${k.merkez.z.toFixed(1)})`);
    // ağırlık merkezi işareti
    const g = new THREE.SphereGeometry(2.5, 16, 12); const mt = new THREE.MeshBasicMaterial({ color: 0xffd000, depthTest: false });
    const s = new THREE.Mesh(g, mt); s.position.set(k.merkez.x, k.merkez.y, k.merkez.z); s.renderOrder = 999; (s as any).raycast = () => { };
    this.d.sahne.add(s); this._analizOverlay.push(s); this._analizAktif = 'kutle'; this.d.ciz(); return true;
  }
  /** OCCT Hassas Mesafe: son iki yüzey/gövde arası en kısa mesafe (BRepExtrema) + çizgi. */
  mesafeAnaliz(): boolean {
    const shapes: any[] = [];
    const ms = this.yuzeyMeshler;
    if (ms.length >= 2) { /* yüzey shape'leri yeniden üret */ }
    // kaynak: son 2 yüzey feature shape'i veya gövde+son yüzey
    const yuzFeat = this.ozellikler.filter(o => o.tip === 'yuzey' && !o.bastirilmis);
    if (yuzFeat.length >= 2) { shapes.push(this.occtYuzeyShape(yuzFeat[yuzFeat.length - 2]), this.occtYuzeyShape(yuzFeat[yuzFeat.length - 1])); }
    else if (yuzFeat.length === 1 && this.occtSonShapeRef) { shapes.push(this.occtSonShapeRef, this.occtYuzeyShape(yuzFeat[0])); }
    if (shapes.length < 2 || !shapes[0] || !shapes[1]) { this.d.bildir?.('Hassas mesafe için 2 yüzey (veya gövde+yüzey) gerekli'); return false; }
    const r = fikoOCCT.minMesafe(shapes[0], shapes[1]); if (!r) { this.d.bildir?.('Mesafe hesaplanamadı'); return false; }
    this.d.bildir?.(`Hassas Mesafe: ${r.mesafe.toFixed(3)} mm`);
    const lg = new THREE.BufferGeometry(); lg.setAttribute('position', new THREE.Float32BufferAttribute([r.p1.x, r.p1.y, r.p1.z, r.p2.x, r.p2.y, r.p2.z], 3));
    const line = new THREE.LineSegments(lg, new THREE.LineBasicMaterial({ color: 0xff00ff, depthTest: false })); line.renderOrder = 999; (line as any).raycast = () => { };
    this.d.sahne.add(line); this._analizOverlay.push(line); this._analizAktif = 'mesafe'; this.d.ciz(); return true;
  }
  /** OCCT Geometri Kontrol: gövde topolojik/geometrik geçerli mi (BRepCheck). */
  kontrolAnaliz(): boolean {
    if (!this.occtSonShapeRef) { this.d.bildir?.('Önce bir katı oluştur'); return false; }
    const ok = fikoOCCT.gecerliMi(this.occtSonShapeRef);
    this.d.bildir?.(ok ? '✅ Geometri geçerli (kapalı, tutarlı topoloji)' : '⚠️ Geometri hatalı — Healing (Onar) deneyin');
    return true;
  }
  /** OCCT Sınır Kutusu: gövde AABB ölçüleri + kutu çizimi. */
  bboxAnaliz(): boolean {
    if (!this.occtSonShapeRef) { this.d.bildir?.('Önce bir katı oluştur'); return false; }
    const b = fikoOCCT.sinirKutu(this.occtSonShapeRef); if (!b) { this.d.bildir?.('Sınır kutusu hesaplanamadı'); return false; }
    this.d.bildir?.(`Sınır Kutusu: ${b.boyut.x.toFixed(1)} × ${b.boyut.y.toFixed(1)} × ${b.boyut.z.toFixed(1)} mm`);
    const g = new THREE.BoxGeometry(b.boyut.x, b.boyut.y, b.boyut.z);
    const eg = new THREE.EdgesGeometry(g); const line = new THREE.LineSegments(eg, new THREE.LineBasicMaterial({ color: 0x00ff88 }));
    line.position.set((b.min.x + b.maks.x) / 2, (b.min.y + b.maks.y) / 2, (b.min.z + b.maks.z) / 2); (line as any).raycast = () => { };
    this.d.sahne.add(line); this._analizOverlay.push(line); this._analizAktif = 'bbox'; this.d.ciz(); return true;
  }
  /** GSD Porcupine: son eğride eğrilik tarağı (her örnekte eğriliğe orantılı dik diken). */
  porcupineAnaliz(olcek = 30): boolean {
    let src: IOzellik | null = null; const egri = new Set(['cizgi3d', 'cember3d', 'spline3d', 'polyline', 'curveSmooth', 'corner', 'helix3d', 'spiral3d', 'conic3d', 'paralelEgri', 'connectEgri', 'combine3d']);
    for (const o of this.ozellikler) if (egri.has(o.tip) && !o.bastirilmis) src = o;
    if (!src) { this.d.bildir?.('Porcupine için bir eğri gerekli'); return false; }
    const pts = this.egriNoktalari(src); if (pts.length < 3) return false;
    const segPts: number[] = [];
    for (let i = 1; i < pts.length - 1; i++) {
      const a = pts[i - 1], b = pts[i], c = pts[i + 1];
      const v1 = b.clone().sub(a), v2 = c.clone().sub(b); const l1 = v1.length(), l2 = v2.length(); if (l1 < 1e-6 || l2 < 1e-6) continue;
      const aci = v1.angleTo(v2); const ds = (l1 + l2) / 2; const k = aci / Math.max(ds, 1e-6);   // ~eğrilik
      let nrm = v2.clone().sub(v1); if (nrm.lengthSq() < 1e-9) continue; nrm.normalize();
      const tip = b.clone().addScaledVector(nrm, Math.min(k * olcek * 100, 60));
      segPts.push(b.x, b.y, b.z, tip.x, tip.y, tip.z);
    }
    if (!segPts.length) return false;
    const g = new THREE.BufferGeometry(); g.setAttribute('position', new THREE.Float32BufferAttribute(segPts, 3));
    const seg = new THREE.LineSegments(g, new THREE.LineBasicMaterial({ color: 0xff3366 }));
    seg.name = 'FikoPorcupine'; (seg as any).raycast = () => { };
    this.d.sahne.add(seg); this._analizOverlay.push(seg);
    this.d.bildir?.('Porcupine: eğrilik tarağı (kırmızı diken = yüksek eğrilik)'); this._analizAktif = 'porcupine'; this.d.ciz(); return true;
  }
  /** GSD Draft Analysis: yüz normallerinin çekme yönüne göre eğimi (yeşil=pozitif, kırmızı=undercut, sarı=dikey). */
  draftAnaliz(eksen: 'x' | 'y' | 'z' = 'z', minAci = 3): boolean {
    const mesh = this.govdeMesh as any; if (!mesh) { this.d.bildir?.('Önce bir katı/gövde oluştur'); return false; }
    const geo = mesh.geometry as THREE.BufferGeometry;
    if (!geo.getAttribute('normal')) geo.computeVertexNormals();
    const N = geo.getAttribute('normal');
    const pull = eksen === 'x' ? new THREE.Vector3(1, 0, 0) : eksen === 'y' ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(0, 0, 1);
    const esik = Math.sin(minAci * Math.PI / 180);
    const renk = new Float32Array(N.count * 3); const v = new THREE.Vector3();
    for (let i = 0; i < N.count; i++) {
      v.set(N.getX(i), N.getY(i), N.getZ(i)); const s = v.dot(pull);
      let r: number, g: number, b: number;
      if (s > esik) { r = 0.12; g = 0.78; b = 0.25; }        // pozitif draft (releasable)
      else if (s < -esik) { r = 0.88; g = 0.12; b = 0.12; }  // undercut (kalıptan çıkmaz)
      else { r = 0.96; g = 0.82; b = 0.10; }                  // dikey duvar (marjinal)
      renk[i * 3] = r; renk[i * 3 + 1] = g; renk[i * 3 + 2] = b;
    }
    this.d.bildir?.('Draft Analizi: 🟢 pozitif · 🟡 dikey · 🔴 undercut (' + eksen.toUpperCase() + ')');
    return this.analizUygula(renk, 'draft');
  }
  /** GSD Surfacic Curvature: Gauss eğriliği (açı açığı / Gauss-Bonnet) → renk haritası (mavi=düz, kırmızı=yüksek). */
  egrilikAnaliz(): boolean {
    const mesh = this.govdeMesh as any; if (!mesh) { this.d.bildir?.('Önce bir katı/gövde oluştur'); return false; }
    let geo = mesh.geometry as THREE.BufferGeometry;
    // paylaşımlı köşe gerek (açı açığı için) — birleştir
    let m: THREE.BufferGeometry;
    try { m = mergeVertices(geo.index ? geo.toNonIndexed() : geo); } catch (e) { m = geo; }
    const pos = m.getAttribute('position'); const idx = m.getIndex();
    const n = pos.count;
    const aciToplam = new Float32Array(n); const alanToplam = new Float32Array(n);
    const a = new THREE.Vector3(), b = new THREE.Vector3(), c = new THREE.Vector3();
    const ab = new THREE.Vector3(), ac = new THREE.Vector3(), ba = new THREE.Vector3(), bc = new THREE.Vector3(), ca = new THREE.Vector3(), cb = new THREE.Vector3();
    const triSay = idx ? idx.count / 3 : n / 3;
    const gv = (i: number, t: THREE.Vector3) => t.set(pos.getX(i), pos.getY(i), pos.getZ(i));
    for (let t = 0; t < triSay; t++) {
      const i0 = idx ? idx.getX(t * 3) : t * 3, i1 = idx ? idx.getX(t * 3 + 1) : t * 3 + 1, i2 = idx ? idx.getX(t * 3 + 2) : t * 3 + 2;
      gv(i0, a); gv(i1, b); gv(i2, c);
      const alan = ab.subVectors(b, a).cross(ac.subVectors(c, a)).length() * 0.5;
      const A0 = ab.subVectors(b, a).angleTo(ac.subVectors(c, a));
      const A1 = ba.subVectors(a, b).angleTo(bc.subVectors(c, b));
      const A2 = ca.subVectors(a, c).angleTo(cb.subVectors(b, c));
      aciToplam[i0] += A0; aciToplam[i1] += A1; aciToplam[i2] += A2;
      alanToplam[i0] += alan; alanToplam[i1] += alan; alanToplam[i2] += alan;
    }
    // Gauss eğriliği K = (2π - Σθ) / (alan/3); köşe sınırında yaklaşık
    const K = new Float32Array(n); let kMax = 1e-6;
    for (let i = 0; i < n; i++) { const ar = alanToplam[i] / 3 || 1e-6; K[i] = (2 * Math.PI - aciToplam[i]) / ar; const ak = Math.abs(K[i]); if (ak > kMax && isFinite(ak)) kMax = ak; }
    // renk: |K| normalize → mavi(0)→yeşil→kırmızı(max)
    const renk = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) {
      const tNorm = Math.min(Math.abs(K[i]) / kMax, 1);
      const r = tNorm, g = 1 - Math.abs(tNorm - 0.5) * 2, bl = 1 - tNorm;
      renk[i * 3] = r; renk[i * 3 + 1] = Math.max(0, g); renk[i * 3 + 2] = bl;
    }
    // merged geo'yu mesh'e uygula (renkli)
    m.setAttribute('color', new THREE.BufferAttribute(renk, 3));
    if (!this._analizEskiMat) this._analizEskiMat = mesh.material;
    (mesh as any)._analizEskiGeo = mesh.geometry;
    mesh.geometry = m;
    mesh.material = new THREE.MeshBasicMaterial({ vertexColors: true, side: THREE.DoubleSide });
    this._analizAktif = 'egrilik'; this.d.bildir?.('Eğrilik Analizi: 🔵 düz → 🔴 yüksek eğrilik'); this.d.ciz();
    return true;
  }

  /** Generic DialogCore penceresi için feature parametreleri (Sayı/Açılır). FikoFeatureDialog kullanır. */
  featureDialogParametreler(feat: IOzellik): any[] { return this.featureInlineParametreler(feat); }

  // ───────── Dialog'lardan geometri yeniden-seçimi (mevcut feature'a bağlı) ─────────
  /** Fillet/Chamfer dialog'undan: bu feature için kenarları yeniden seç (canlı). */
  featureKenarSec(feat: IOzellik): void { this.taslakFeat = feat; this.kenarSecBaslat(); this.d.bildir?.('Kenarlara tıkla (tekrar=kaldır); yüze tıkla=tüm sınır kenarları'); }
  /** Draft/Shell dialog'undan: bu feature için yüzleri yeniden seç (canlı). */
  featureYuzSec(feat: IOzellik): void { this.taslakFeat = feat; this.yuzSecBaslat(); this.d.bildir?.('Yüzlere tıkla (tekrar=kaldır)'); }
  /** Dialog kapanınca / Tamam: aktif kenar/yüz seçim modunu bitir. */
  featureSecimBitir(): void { if (this.kenarSecModu) this.kenarSecBitir(); if (this.yuzSecModu) this.yuzSecBitir(); this.taslakFeat = null; }
  /** KÖKTEN temizleme — TÜM seçim modları + vurgular + hover. Dialog ngOnDestroy'dan çağrılır (X dâhil her kapanış).
   *  feat verilirse SADECE o feature aktif taslaksa temizler (yeni komut açılırken yarış önlemek için). */
  dialogSecimTemizle(feat?: IOzellik): void {
    if (feat && this.taslakFeat && this.taslakFeat !== feat) return;   // başka komut aktif → dokunma
    if (this.kenarSecModu) this.kenarSecBitir();
    if (this.yuzSecModu) this.yuzSecBitir();
    if (this.vertexSecModu) this.vertexSecBitir();
    if (this.profilSecModu) this.profilSecBitir();
    if (this.aktifSecim) this.secimBitir();                 // merkezi ref seçimi (nokta/çizgi/yüzey 3D-pick)
    if (this.limitSecModu) this.limitHedefSecBitir();
    this.yuzVurguTemizle(); this.hoverTemizle();
    if (!feat || this.taslakFeat === feat) this.taslakFeat = null;
    this.d.ciz?.();
  }

  // ───────── CATIA tarzı SEÇİM LİSTESİ (dialogda seçili yüz/kenar listele + sil + ekle) ─────────
  /** Bir feature'ın seçili yüz/kenar listesi (her öğe = 1 satır, merkez koord etiketli). */
  secimListesi(feat: IOzellik, tip: 'yuz' | 'kenar'): { idx: number; etiket: string }[] {
    if (!feat) return [];
    const nok = (tip === 'yuz' ? feat.secYuzNok : feat.secKenarlar) || [];
    const ad = tip === 'yuz' ? 'Yüz' : 'Kenar';
    const out: { idx: number; etiket: string }[] = [];
    for (let i = 0; i * 3 + 2 < nok.length; i++) out.push({ idx: i, etiket: `${ad} ${i + 1} · ${Math.round(nok[i * 3])},${Math.round(nok[i * 3 + 1])},${Math.round(nok[i * 3 + 2])}` });
    return out;
  }
  /** Listeden bir öğeyi sil → rebuild. */
  secimListeSil(feat: IOzellik, tip: 'yuz' | 'kenar', idx: number): void {
    if (!feat) return;
    if (tip === 'yuz') { feat.secYuzNok?.splice(idx * 3, 3); feat.secYuzler?.splice(idx, 1); feat.secYuzKimlik?.splice(idx, 1); feat.secYuzImza?.splice(idx * 7, 7); }
    else { feat.secKenarlar?.splice(idx * 3, 3); feat.secKenarImza?.splice(idx * 7, 7); }
    this.rebuild(); this.featureNodeAdGuncelle(feat); this.d.agacGuncelle?.(); this.gecmisKaydet();
  }
  /** Listeyi tümüyle temizle → rebuild (boş seçim = işlem uygulanmaz). */
  secimListeTemizle(feat: IOzellik, tip: 'yuz' | 'kenar'): void {
    if (!feat) return;
    if (tip === 'yuz') { feat.secYuzNok = []; feat.secYuzler = []; feat.secYuzKimlik = []; feat.secYuzImza = []; } else { feat.secKenarlar = []; feat.secKenarImza = []; }
    this.rebuild(); this.featureNodeAdGuncelle(feat); this.d.agacGuncelle?.(); this.gecmisKaydet();
  }
  /** 3D'den daha fazla yüz/kenar ekle (mevcut seçim korunarak seçim moduna gir). */
  secimListeEkle(feat: IOzellik, tip: 'yuz' | 'kenar'): void {
    if (tip === 'yuz') this.featureYuzSec(feat); else this.featureKenarSec(feat);
  }
  /** Listede bir öğeye gelince 3D'de o yüz/kenar merkezinde parlak işaret (CATIA hover). */
  secimListeVurgula(feat: IOzellik, tip: 'yuz' | 'kenar', idx: number): void {
    const nok = (tip === 'yuz' ? feat?.secYuzNok : feat?.secKenarlar); if (!nok || idx * 3 + 2 >= nok.length) return;
    this.hoverTemizle();
    const g = new THREE.SphereGeometry(tip === 'yuz' ? 3 : 2.2, 12, 12);
    const mt = new THREE.MeshBasicMaterial({ color: tip === 'yuz' ? 0xff8c1a : 0x00ff88, depthTest: false, transparent: true, opacity: 0.85 });
    const s = new THREE.Mesh(g, mt); s.position.set(nok[idx * 3], nok[idx * 3 + 1], nok[idx * 3 + 2]); s.renderOrder = 999; (s as any).raycast = () => { };
    this.hoverObj = s; this.d.sahne.add(s); this.d.ciz();
  }
  secimListeVurguBitir(): void { this.hoverTemizle(); this.d.ciz(); }

  /** Feature'ın ürün ağacında inline düzenlenebilir anahtar parametreleri (chip). CATIA: çift-tık dialog; biz: ağaçta direkt. */
  private featureInlineParametreler(feat: IOzellik): any[] {
    const sayi = (isim: string, anahtar: string, ayar: any = {}): any => ({
      turu: 'Sayı', isim, degeri: (feat as any)[anahtar],
      ayar: { adim: 1, birim: 'mm', ...ayar },
      degisti: (v: any) => this.featureParamGuncelle(feat, anahtar, +v),
    });
    const acilir = (isim: string, anahtar: string, secenekler: { deger: any, etiket: string }[]): any => ({
      turu: 'Açılır', isim, degeri: (feat as any)[anahtar], ayar: { secenekler },
      degisti: (v: any) => { (feat as any)[anahtar] = v; this.rebuild(); this.featureNodeAdGuncelle(feat); this.d.agacGuncelle?.(); },
    });
    const duzlemSec = (isim = 'Düzlem'): any => ({ turu: 'Düzlem', isim });   // FikoDuzlemSecici (datum: duzlemRef)
    const duzlemSnapSec = (isim = 'Düzlem', anahtar = 'aynaTip'): any => ({ turu: 'DuzlemSnap', isim, anahtar });   // 3D yüz/düzlem → standart (aynaTip)
    const eksenRefSec = (isim = 'Eksen', anahtar = 'eksenTip'): any => ({ turu: 'Eksen', isim, anahtar });   // 3D çizgi/yüz → eksen (eksenTip/eksenRef)
    // 3D-seçilebilir referans (dropdown + "3D'den Seç" butonu) — eğri/nokta/yüzey
    const EGRI_TIP = ['cizgi3d', 'cember3d', 'spline3d', 'polyline', 'curveSmooth', 'corner', 'isoparametrik', 'combine3d', 'equationCurve', 'spine3d', 'helix3d', 'spiral3d', 'conic3d', 'paralelEgri', 'connectEgri', 'kesisim3d', 'yuzeyKenar'];
    const NOKTA_TIP = ['nokta3d'];
    const YUZEY_TIP = ['yuzey', 'yuzeyJoin', 'yuzeyTrim', 'yuzeyExtract', 'yuzeyBlend', 'yuzeyExtrapolate'];
    const ref3dSec = (isim: string, anahtar: string, secenekler: any[], tipler: string[]): any => ({
      turu: 'Ref3D', isim, anahtar, tipler, degeri: (feat as any)[anahtar], ayar: { secenekler },
      degisti: (v: any) => { (feat as any)[anahtar] = +v; this.rebuild(); this.featureNodeAdGuncelle(feat); this.d.agacGuncelle?.(); },
    });
    const aksiyon = (etiket: string, fn: () => void): any => ({ turu: 'Aksiyon', isim: etiket, tiklandi: fn });   // dialog içi buton (3D seçim başlat vb.)
    const secimListe = (tip: 'yuz' | 'kenar'): any => ({ turu: 'SecimListe', isim: tip === 'yuz' ? 'Yüzler' : 'Kenarlar', secimTip: tip });   // CATIA seçim listesi (listele/sil/ekle)
    const metin = (isim: string, anahtar: string, varsayilan = ''): any => ({   // serbest metin (denklem) girişi
      turu: 'Metin', isim, degeri: (feat as any)[anahtar] ?? varsayilan,
      degisti: (v: any) => { (feat as any)[anahtar] = String(v); this.rebuild(); this.featureNodeAdGuncelle(feat); this.d.agacGuncelle?.(); },
    });
    switch (feat.tip) {
      case 'pad': case 'pocket': return [sayi('Derinlik', 'param', { min: 0.1 })];
      case 'shaft': case 'groove': return [sayi('Açı', 'aci', { min: 1, maks: 360, birim: '°' })];
      case 'hole': return [sayi('Çap', 'holeCap', { min: 0.1 }), sayi('Derinlik', 'holeDerinlik', { min: 0.1 })];
      case 'rib': return [sayi('Kalınlık', 'kalinlik', { min: 0.1 }), sayi('Uzunluk', 'param', { min: 0.1 }), acilir('Yön', 'yon', [{ deger: 'arti', etiket: 'İleri' }, { deger: 'eksi', etiket: 'Geri' }, { deger: 'cift', etiket: 'Çift Yön' }])];
      case 'shell': return [sayi('Et Kal.', 'kalinlik', { min: 0.1 })];
      case 'thickness': return [sayi('Kalınlık (±)', 'kalinlik', { min: -500 }), acilir('Yön', 'tersYon', [{ deger: false, etiket: 'Ekle (dışa)' }, { deger: true, etiket: 'Çıkar (içe)' }]), secimListe('yuz')];
      case 'split': return [duzlemSnapSec('Kesme Düzlemi', 'aynaTip'), sayi('Ofset', 'splitOfset'), acilir('Tutulan Taraf', 'tersYon', [{ deger: false, etiket: 'Pozitif' }, { deger: true, etiket: 'Negatif' }])];
      case 'yuzey': {
        if (feat.yuzeyTip === 'sphere') return [sayi('Çap', 'pCap', { min: 0.1 })];
        if (feat.yuzeyTip === 'cylinder') return [sayi('Çap', 'pCap', { min: 0.1 }), sayi('Yükseklik', 'pYuk', { min: 0.1 }), duzlemSnapSec('Eksen Düzlemi', 'aynaTip')];
        const tb: any[] = feat.yuzeyTip === 'extrude' ? [sayi('Uzunluk', 'param', { min: 0.1 })] : feat.yuzeyTip === 'revolve' ? [sayi('Açı', 'aci', { min: 1, maks: 360, birim: '°' })] : [];
        if (feat.yuzeyTip !== 'sweep' && feat.yuzeyTip !== 'loft') tb.push(sayi('Offset', 'ofset1'));
        return tb;
      }
      case 'thickSurface': return [sayi('Kalınlık', 'kalinlik', { min: 0.1 })];
      case 'helix3d': return [sayi('Yarıçap', 'yaricap', { min: 0.1 }), sayi('Yükseklik', 'helixYukseklik', { min: 0.1 }), sayi('Tur', 'helixTur', { min: 0.1 }), duzlemSec('Düzlem')];
      case 'spiral3d': return [sayi('Başlangıç R', 'yaricap', { min: 0 }), sayi('Tur Başına Artış', 'helixAdim'), sayi('Yükseklik (konik)', 'helixYukseklik', { min: 0 }), sayi('Tur', 'helixTur', { min: 0.1 }), duzlemSec('Düzlem')];
      case 'paralelEgri': return [ref3dSec('Kaynak Eğri', 'ref1', this.tumEgriSecenekleri(feat), EGRI_TIP), sayi('Mesafe', 'param'), duzlemSec('Düzlem')];
      case 'connectEgri': return [ref3dSec('Eğri 1', 'ref1', this.tumEgriSecenekleri(feat), EGRI_TIP), ref3dSec('Eğri 2', 'ref2', this.tumEgriSecenekleri(feat), EGRI_TIP), sayi('Gerginlik', 'param', { adim: 0.1, birim: '' })];
      case 'yuzeyKenar': return [ref3dSec('Yüzey', 'ref1', this.yuzeySecenekleri(feat), YUZEY_TIP)];
      case 'conic3d': return [ref3dSec('Başlangıç', 'ref1', this.noktaSecenekleri(feat), NOKTA_TIP), ref3dSec('Bitiş', 'ref2', this.noktaSecenekleri(feat), NOKTA_TIP), ref3dSec('Tepe (kontrol)', 'ref3', this.noktaSecenekleri(feat), NOKTA_TIP), sayi('ρ (0.5=parabol)', 'param', { adim: 0.05, birim: '' })];
      case 'kesisim3d': return [ref3dSec('1. Eleman', 'ref1', this.kesisimSecenekleri(feat), YUZEY_TIP), ref3dSec('2. Eleman', 'ref2', this.kesisimSecenekleri(feat), YUZEY_TIP)];
      case 'yuzeyTrim': return [ref3dSec('Yüzey', 'ref1', this.yuzeySecenekleri(feat), YUZEY_TIP), acilir('Düzlem', 'aynaTip', [{ deger: 'xy', etiket: 'XY (Z)' }, { deger: 'xz', etiket: 'XZ (Y)' }, { deger: 'yz', etiket: 'YZ (X)' }]), sayi('Ofset', 'splitOfset'), acilir('Tutulan Taraf', 'tersYon', [{ deger: false, etiket: 'Pozitif' }, { deger: true, etiket: 'Negatif' }])];
      case 'yuzeyExtract': return [ref3dSec('Kaynak', 'ref1', this.kesisimSecenekleri(feat), YUZEY_TIP), sayi('Yüz No', 'yuzIndex', { adim: 1, birim: '' })];
      case 'yuzeyBlend': return [ref3dSec('Eğri 1', 'ref1', this.tumEgriSecenekleri(feat), EGRI_TIP), ref3dSec('Eğri 2', 'ref2', this.tumEgriSecenekleri(feat), EGRI_TIP)];
      case 'yuzeyExtrapolate': return [ref3dSec('Yüzey', 'ref1', this.yuzeySecenekleri(feat), YUZEY_TIP), sayi('Uzatma', 'param', { min: 0.1 })];
      case 'splitSurface': return [acilir('Tutulan Taraf', 'tersYon', [{ deger: false, etiket: 'Pozitif' }, { deger: true, etiket: 'Negatif' }])];
      case 'nokta3d': return [sayi('X', 'p1x'), sayi('Y', 'p1y'), sayi('Z', 'p1z')];
      case 'cizgi3d': return [sayi('X1', 'p1x'), sayi('Y1', 'p1y'), sayi('Z1', 'p1z'), sayi('X2', 'p2x'), sayi('Y2', 'p2y'), sayi('Z2', 'p2z')];
      case 'draft': return [sayi('Açı', 'aci', { min: 0, maks: 89, birim: '°' }), eksenRefSec('Eksen', 'eksenTip')];
      case 'patternDik': return [sayi('Adet', 'adet', { min: 1, birim: '×' }), sayi('Aralık', 'aralik', { min: 0.1 })];
      case 'patternDairesel': return [sayi('Adet', 'adet', { min: 1, birim: '×' }), sayi('Açı', 'aci', { min: 1, maks: 360, birim: '°' })];
      case 'patternIzgara': return [sayi('Sıra', 'adet', { min: 1 }), sayi('Sütun', 'adet2', { min: 1 }), sayi('Aralık', 'aralik', { min: 0.1 })];
      case 'mirror': case 'symmetry': return [duzlemSnapSec('Düzlem', 'aynaTip')];
      case 'translate': return [acilir('Vektör', 'trMod', [{ deger: 'yon', etiket: 'Yön + Mesafe' }, { deger: 'koordinat', etiket: 'Koordinat' }]), eksenRefSec('Yön', 'trYon'), sayi('Mesafe', 'trMesafe'), sayi('X', 'trX'), sayi('Y', 'trY'), sayi('Z', 'trZ')];
      case 'rotate': return [sayi('Açı', 'aci', { min: -360, maks: 360, birim: '°' }), eksenRefSec('Eksen', 'eksenTip')];
      case 'scaling': return [sayi('Katsayı', 'scaleFaktor', { min: 0.01, adim: 0.1, birim: '×' })];
      case 'affinity': return [sayi('X Oran', 'affX', { min: 0.01, adim: 0.1, birim: '×' }), sayi('Y Oran', 'affY', { min: 0.01, adim: 0.1, birim: '×' }), sayi('Z Oran', 'affZ', { min: 0.01, adim: 0.1, birim: '×' })];
      case 'axisToAxis': return [ref3dSec('Hedef Nokta', 'ref1', this.noktaSecenekleri(feat), NOKTA_TIP), eksenRefSec('Hedef Eksen (çizgi)', 'eksenRef')];
      case 'fillet3d': return [sayi('Yarıçap', 'kenarMesafe', { min: 0.1 })];
      case 'chamfer3d': return [acilir('Mod', 'chamferMod', [{ deger: 'simetrik', etiket: 'Simetrik' }, { deger: 'length2', etiket: 'Mes/Mes' }, { deger: 'aci', etiket: 'Mes/Açı' }]), sayi('Mesafe', 'kenarMesafe', { min: 0.1 }), sayi('Mesafe 2', 'kenarMesafe2', { min: 0.1 }), sayi('Açı', 'chamferAci', { min: 1, maks: 89, birim: '°' })];
      case 'closeSurface': case 'removeLump': case 'simplify': case 'disassemble': return [];
      case 'healing': return [sayi('Tolerans', 'param', { min: 0.001, adim: 0.01 })];
      case 'offsetShape': return [sayi('Offset (±)', 'param', { adim: 0.5 })];
      case 'untrim': return [ref3dSec('Kaynak', 'ref1', this.kesisimSecenekleri(feat), YUZEY_TIP), sayi('Yüz No', 'yuzIndex', { adim: 1, birim: '' })];
      case 'multiExtract': return [ref3dSec('Kaynak', 'ref1', this.kesisimSecenekleri(feat), YUZEY_TIP)];
      case 'curveSmooth': return [ref3dSec('Kaynak Eğri', 'ref1', this.tumEgriSecenekleri(feat), EGRI_TIP), sayi('Yumuşatma', 'param', { adim: 1, birim: '×', min: 1 })];
      case 'corner': return [ref3dSec('Çizgi 1', 'ref1', this.egriSecenekleri(feat), ['cizgi3d']), ref3dSec('Çizgi 2', 'ref2', this.egriSecenekleri(feat), ['cizgi3d']), sayi('Yarıçap', 'yaricap', { min: 0.1 })];
      case 'isoparametrik': return [ref3dSec('Yüzey', 'ref1', this.kesisimSecenekleri(feat), YUZEY_TIP), acilir('Yön', 'tersYon', [{ deger: false, etiket: 'U (X)' }, { deger: true, etiket: 'V (Y)' }]), sayi('Konum (0-1)', 'oran', { adim: 0.05, birim: '', min: 0, maks: 1 })];
      case 'combine3d': return [ref3dSec('Eğri 1', 'ref1', this.tumEgriSecenekleri(feat), EGRI_TIP), ref3dSec('Eğri 2', 'ref2', this.tumEgriSecenekleri(feat), EGRI_TIP)];
      case 'equationCurve': return [metin('x(t)', 'eqX', '40*cos(t)'), metin('y(t)', 'eqY', '40*sin(t)'), metin('z(t)', 'eqZ', '6*t'), sayi('t baş', 'eqTMin', { adim: 0.1, birim: '' }), sayi('t bit', 'eqTMax', { adim: 0.1, birim: '' }), sayi('Örnek', 'eqAdim', { adim: 10, birim: '', min: 8 })];
      case 'spine3d': return [];   // refler otomatik toplanır (eğri/düzlem kesitleri)
      case 'pipe3d': return [ref3dSec('Yol Eğrisi', 'ref1', this.tumEgriSecenekleri(feat), EGRI_TIP), sayi('Çap', 'pCap', { min: 0.1 })];
      case 'removeFace': return [secimListe('yuz')];
      case 'faceFillet': return [sayi('Yarıçap', 'kenarMesafe', { min: 0.1 }), secimListe('yuz')];
      case 'triFillet': return [sayi('Yarıçap (0=oto)', 'kenarMesafe', { min: 0 }), secimListe('yuz')];
      case 'replaceFace': return [duzlemSnapSec('Hedef Düzlem', 'aynaTip'), sayi('Ofset', 'splitOfset'), secimListe('yuz')];
      case 'primitif':
        if (feat.primitifTip === 'kutu') return [sayi('En', 'pEn', { min: 0.1 }), sayi('Boy', 'pBoy', { min: 0.1 }), sayi('Yük', 'pYuk', { min: 0.1 })];
        if (feat.primitifTip === 'silindir' || feat.primitifTip === 'koni') return [sayi('Çap', 'pCap', { min: 0.1 }), sayi('Yük', 'pYuk', { min: 0.1 })];
        if (feat.primitifTip === 'kure') return [sayi('Çap', 'pCap', { min: 0.1 })];
        if (feat.primitifTip === 'torus') return [sayi('Ana Çap', 'pCap', { min: 0.1 }), sayi('Boru Çapı', 'pTbCap', { min: 0.1 })];
        if (feat.primitifTip === 'wedge') return [sayi('En', 'pEn', { min: 0.1 }), sayi('Boy', 'pBoy', { min: 0.1 }), sayi('Yük', 'pYuk', { min: 0.1 }), sayi('Üst Daralma', 'wedgeLtx', { min: 0 })];
        return [];
      default: return [];
    }
  }

  /** Feature parametre panelini ekranın ortasında aç (3dHizliMenu + FikoFeatureHizliEk). */
  /** Son düzenlenebilir (bastırılmamış, node'lu) feature — 3D gövde tıklamasından edit için. */
  private sonDuzenlenebilirFeat(): IOzellik | null {
    for (let i = this.ozellikler.length - 1; i >= 0; i--) { const f = this.ozellikler[i]; if (f.node && !f.bastirilmis) return f; }
    return null;
  }
  /** 3D gövdeye çift-tık → son feature'ı düzenle (CATIA: geometriden edit). Pad → Definition penceresi. */
  govdeCiftTik(): void { const f = this.sonDuzenlenebilirFeat(); if (f) this.featureMenu(f); }
  /** 3D gövdeye uzun-bas → son feature'ın hızlı parametre paneli (hizli menü). */
  govdeUzunBas(): void { const f = this.sonDuzenlenebilirFeat(); if (f) this.featureHizliMenu(f); }

  featureMenu(feat: IOzellik): void {
    // CATIA: TÜM feature'lar DialogCore Definition penceresiyle düzenlenir; uzun-bas hızlı param ayrı.
    // CATIA Pad/Pocket/Shaft/Groove → tam Definition PENCERESİ (DialogCore). Uzun bas → hızlı param (hizli menü) ayrı kalır.
    const df = (this.d.sahne as any).anaGosterim?.DialogFiko;
    if ((feat.tip === 'pad' || feat.tip === 'pocket') && df?.ac) {
      df.ac({ bilesen: FikoPadDialog, parametreler: { element: feat, yonetici: this }, ayarlar: { baslik: (feat.tip === 'pad' ? 'Pad' : 'Pocket') + ' Tanımı', simgeResim: './simgeler/3d.webp' } }); return;
    }
    if ((feat.tip === 'shaft' || feat.tip === 'groove') && df?.ac) {
      df.ac({ bilesen: FikoShaftDialog, parametreler: { element: feat, yonetici: this }, ayarlar: { baslik: (feat.tip === 'shaft' ? 'Shaft (Döndür)' : 'Groove (Oluk)') + ' Tanımı', simgeResim: './simgeler/3d.webp' } }); return;
    }
    if (feat.tip === 'hole' && df?.ac) {
      df.ac({ bilesen: FikoHoleDialog, parametreler: { element: feat, yonetici: this }, ayarlar: { baslik: 'Delik Tanımı', simgeResim: './simgeler/3d.webp' } }); return;
    }
    if (feat.tip === 'nokta3d' && df?.ac) {
      df.ac({ bilesen: FikoNoktaDialog, parametreler: { element: feat, yonetici: this }, ayarlar: { baslik: 'Nokta Tanımı', simgeResim: './simgeler/3d.webp' } }); return;
    }
    if (feat.tip === 'cizgi3d' && df?.ac) {
      df.ac({ bilesen: FikoCizgiDialog, parametreler: { element: feat, yonetici: this }, ayarlar: { baslik: 'Çizgi Tanımı', simgeResim: './simgeler/3d.webp' } }); return;
    }
    if (feat.tip === 'cember3d' && df?.ac) {
      df.ac({ bilesen: FikoCemberDialog, parametreler: { element: feat, yonetici: this }, ayarlar: { baslik: 'Çember Tanımı', simgeResim: './simgeler/3d.webp' } }); return;
    }
    if ((feat.tip === 'spline3d' || feat.tip === 'polyline') && df?.ac) {
      df.ac({ bilesen: FikoSplineDialog, parametreler: { element: feat, yonetici: this }, ayarlar: { baslik: feat.tip === 'polyline' ? 'Polyline Tanımı' : 'Spline Tanımı', simgeResim: './simgeler/3d.webp' } }); return;
    }
    if (feat.tip === 'duzlem3d' && df?.ac) {
      df.ac({ bilesen: FikoDuzlemDialog, parametreler: { element: feat, yonetici: this }, ayarlar: { baslik: 'Düzlem Tanımı', simgeResim: './simgeler/3d.webp' } }); return;
    }
    // CATIA uyumu: HER feature kendi tam Definition PENCERESİYLE (DialogCore) düzenlenir. Uzun-bas → hızlı param ayrı.
    if (df?.ac) {
      const ozel: Record<string, { b: any; baslik: string }> = {
        fillet3d: { b: FikoFilletDialog, baslik: 'Yuvarlatma (Fillet)' }, chamfer3d: { b: FikoFilletDialog, baslik: 'Pah (Chamfer)' },
        draft: { b: FikoDraftDialog, baslik: 'Eğim (Draft)' }, shell: { b: FikoShellDialog, baslik: 'Kabuk (Shell)' },
        patternDik: { b: FikoPatternDialog, baslik: 'Doğrusal Dizi' }, patternDairesel: { b: FikoPatternDialog, baslik: 'Dairesel Dizi' }, patternIzgara: { b: FikoPatternDialog, baslik: 'Izgara Dizi' },
        translate: { b: FikoTransformDialog, baslik: 'Taşı (Translate)' }, rotate: { b: FikoTransformDialog, baslik: 'Döndür (Rotate)' }, symmetry: { b: FikoTransformDialog, baslik: 'Simetri' }, scaling: { b: FikoTransformDialog, baslik: 'Ölçekle (Scaling)' }, mirror: { b: FikoTransformDialog, baslik: 'Aynala (Mirror)' },
      };
      const sec = ozel[feat.tip];
      const ad: Record<string, string> = { rib: 'Kaburga', loft: 'Loft', sweep: 'Sweep', slot: 'Slot', combine: 'Birleştirme', primitif: 'Primitif', yazi3d: 'Yazı', makro: 'Makro' };
      df.ac({ bilesen: sec ? sec.b : FikoFeatureDialog, parametreler: { element: feat, yonetici: this }, ayarlar: { baslik: (sec ? sec.baslik : (ad[feat.tip] || 'Özellik') + ' Tanımı'), simgeResim: './simgeler/3d.webp' } }); return;
    }
    this.featureHizliMenu(feat);
  }

  /** Uzun-bas menüsünden Parça Takip panelini aç — Fiko3DComponent 'fiko:parca-takip' event'ini dinler (lib→app decoupled). */
  parcaTakibiAc(): void { try { window.dispatchEvent(new CustomEvent('fiko:parca-takip')); } catch { } }

  /** Hızlı parametre paneli (hizli menü, FikoFeatureHizliEk) — uzun bas + pad olmayan feature edit. */
  featureHizliMenu(feat: IOzellik): void {
    const adlar: Record<string, string> = { pad: 'Pad', pocket: 'Pocket', shaft: 'Shaft', groove: 'Groove', hole: 'Delik', mirror: 'Ayna', symmetry: 'Simetri', translate: 'Taşı', rotate: 'Döndür', scaling: 'Ölçekle', patternDik: 'Doğrusal Dizi', patternDairesel: 'Dairesel Dizi', patternIzgara: 'Izgara Dizi', rib: 'Kaburga', shell: 'Kabuk', draft: 'Eğim', yuzey: 'Yüzey', thickSurface: 'Yüzey Kalınlık', splitSurface: 'Yüzeyle Böl' };
    const makroAd: Record<string, string> = { boru: 'Boru', dirsek: 'Dirsek/Te', yuzuk: 'Yüzük', tasYuvasi: 'Taş Yuvası', bezel: 'Bezel' };
    const obj: any = {
      name: feat.tip === 'makro' ? (makroAd[feat.makroTip || ''] || 'Makro') : (adlar[feat.tip] || feat.tip),
      userData: { FikoArayuz: { simge: './simgeler/3d.webp' } },
      hizliMenu3DBilgi: this.featureOzet(feat),
      hizliMenu3DEk: FikoFeatureHizliEkSekmeli,
      hizliMenu3DEkParametreler: { element: feat, yonetici: this, icBilesen: FikoFeatureHizliEk },
      hizli3DMenu: [
        { baslik: 'Takip', simge: 'box', renkSinif: 'blue', tiklandi: () => this.parcaTakibiAc() },
        { baslik: 'Sil', simge: 'trash alternate', renkSinif: 'red', tiklandi: () => this.featureSil(feat) },
      ],
    };
    const hm = (this.d.sahne as any).anaGosterim?.Dialog3DHizliMenu;
    const cx = (typeof window !== 'undefined' ? window.innerWidth : 800) / 2;
    const cy = (typeof window !== 'undefined' ? window.innerHeight : 600) / 2;
    if (hm?.ac) hm.ac(obj, { type: 'pointerdown', clientX: cx, clientY: cy });
  }

  private ithalNode: FikoGrup | null = null;
  /** CATIA "Result of import": içe aktarılan B-Rep için ürün ağacında görünür öğe (PartBody altında). */
  private ithalGovdeNodeKur(): void {
    if (!this.occtBazShape) return;
    if (this.ithalNode && this.ithalNode.parent) { this.d.agacGuncelle?.(); return; }   // zaten var
    const node = new FikoGrup(this.d.sahne as any, 'İçe Aktarılan Gövde');
    node.userData['FikoArayuz'].simge = './simgeler/3d.webp';
    node.userData['FikoArayuz'].listeGoster = true;
    (node as any).sagMenu = [
      { baslik: 'STEP Olarak Dışa Aktar', simge: 'download', renkSinif: 'blue', tiklandi: async () => {
          try { const b = await this.stepDisaAktar(); if (b) { const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([b as BlobPart])); a.download = 'fikoyt-govde.step'; a.click(); URL.revokeObjectURL(a.href); } } catch (e) { console.warn('[OCCT] export', e); } } },
    ];
    this.govdeGrupAl().ekle!(node);
    this.ithalNode = node;
    this.d.agacGuncelle?.();
  }

  private ozellikNode(tip: string, sketchNode: FikoGrup | null): FikoGrup {
    const etiket: Record<string, string> = { pad: 'Pad', pocket: 'Pocket', shaft: 'Shaft', groove: 'Groove', hole: 'Delik', slot: 'Slot', combine: 'Birleştirme', thickness: 'Kalınlık', bool: 'Boolean', split: 'Böl', yuzey: 'Yüzey', thickSurface: 'Yüzey Kalınlık', splitSurface: 'Yüzeyle Böl', nokta3d: 'Nokta', cizgi3d: 'Çizgi', cember3d: 'Çember', spline3d: 'Spline', helix3d: 'Helis', duzlem3d: 'Düzlem', spiral3d: 'Spiral', paralelEgri: 'Paralel Eğri', connectEgri: 'Bağlantı Eğrisi', yuzeyKenar: 'Yüzey Sınırı', conic3d: 'Konik', yuzeyJoin: 'Yüzey Birleştir', kesisim3d: 'Kesişim', yuzeyTrim: 'Yüzey Kırp', yuzeyExtract: 'Yüzey Çıkar', yuzeyBlend: 'Yüzey Blend', yuzeyExtrapolate: 'Yüzey Uzat', mirror: 'Ayna', symmetry: 'Simetri', translate: 'Taşı', rotate: 'Döndür', scaling: 'Ölçekle', patternDik: 'Doğrusal Dizi', patternDairesel: 'Dairesel Dizi', patternIzgara: 'Izgara Dizi', yazi3d: 'Yazı', primitif: 'Primitif', rib: 'Kaburga', shell: 'Kabuk', draft: 'Eğim', makro: 'Makro', fillet3d: 'Yuvarlat', chamfer3d: 'Pah', loft: 'Loft', sweep: 'Sweep' };
    // 🎯 KOMUTA ÖZEL SİMGE: her feature tipine uygun ikon (eskiden hepsi 3d.webp idi). Eşleşmeyen → 3d.
    const SIMGE: Record<string, string> = {
      pad: 'profil-kontur', pocket: 'profil-kontur', rib: 'profil-kontur', slot: 'profil-kontur', stiffener: 'profil-kontur', loft: 'profil-kontur', sweep: 'profil-kontur',
      shaft: 'xyz', groove: 'xyz', translate: 'xyz', rotate: 'xyz', scaling: 'xyz', affinity: 'xyz', axisToAxis: 'xyz', symmetry: 'xyz',
      hole: 'matkap-ucu', primitif: 'kupler',
      shell: 'gizli-kaplama', thickness: 'gizli-kaplama', thickSurface: 'gizli-kaplama', draft: 'plan',
      patternDik: 'besgen-vektor', patternDairesel: 'besgen-vektor', patternIzgara: 'besgen-vektor', userPattern: 'besgen-vektor',
      yazi3d: 'fikoyt_yazi',
      yuzey: 'plan', thickSurfaceY: 'plan', splitSurface: 'plan', duzlem3d: 'plan', yuzeyExtrude: 'plan', yuzeyRevolve: 'plan', yuzeyFill: 'plan', yuzeyLoft: 'plan', yuzeySweep: 'plan',
      cizgi3d: 'line', spline3d: 'line', conic3d: 'line', helix3d: 'line', spiral3d: 'line', paralelEgri: 'line', cember3d: 'line', connectEgri: 'line',
      nokta3d: 'punta',
    };
    const node = new FikoGrup(this.d.sahne as any, (etiket[tip] || tip) + '.' + (this.ozellikSayac++));
    node.userData['FikoArayuz'].simge = './simgeler/' + (SIMGE[tip] || '3d') + '.webp';
    node.userData['FikoArayuz'].listeGoster = true;
    if (sketchNode) { sketchNode.parent?.remove(sketchNode); node.ekle!(sketchNode); }
    this.govdeGrupAl().ekle!(node);
    return node;
  }

  // ───────────────────── AKTİF BODY (CATIA: özellikler aktif Body altında toplanır) ─────────────────────
  private aktifBody: FikoGrup | null = null;
  private tumBodyler: FikoGrup[] = [];
  private bodySayac = 1;
  get aktifBodyAd(): string { return ((this.aktifBody as any)?._bodyTaban) || 'Gövde (PartBody)'; }

  /** Yeni özelliklerin ekleneceği AKTİF body'i döndürür (ilk çağrıda PartBody oluşturur). */
  private govdeGrupAl(): FikoGrup {
    if (this.aktifBody && this.aktifBody.parent) return this.aktifBody;
    // ilk body = PartBody (govdeGrup ile aynı, ağırlık adı buna yazılır)
    const kok = (this.d.sahne as any).urunAgaci as FikoGrup | undefined;
    const g = new FikoGrup(this.d.sahne as any, 'Gövde (PartBody)');
    g.userData['FikoArayuz'].listeGoster = true;
    g.userData['FikoArayuz'].simge = './simgeler/3d.webp';
    this.bodyKur(g, 'Gövde (PartBody)');
    if (kok?.ekle) kok.ekle(g); else this.d.sahne.add(g);
    this.govdeGrup = g; this.tumBodyler = [g]; this.aktifBody = g; this.bodyVurgula();
    return g;
  }

  /** Yeni Body ekle (CATIA Insert > Body) → otomatik AKTİF olur, sonraki özellikler buraya gider. */
  bodyEkle(): FikoGrup {
    this.govdeGrupAl();   // PartBody garanti
    const kok = (this.d.sahne as any).urunAgaci as FikoGrup | undefined;
    const ad = 'Body.' + (++this.bodySayac);
    const g = new FikoGrup(this.d.sahne as any, ad);
    g.userData['FikoArayuz'].listeGoster = true; g.userData['FikoArayuz'].simge = './simgeler/3d.webp';
    this.bodyKur(g, ad);
    if (kok?.ekle) kok.ekle(g); else this.d.sahne.add(g);
    this.tumBodyler.push(g); this.bodyAktifYap(g);
    return g;
  }

  private bodyKur(g: FikoGrup, taban: string): void {
    (g as any)._bodyTaban = taban;
    (g as any).ciftTiklandi = () => this.bodyAktifYap(g);
    (g as any).sagMenu = [{ baslik: 'Aktif Cisim Olarak Tanımla', simge: 'check circle outline', renkSinif: 'blue', tiklandi: () => this.bodyAktifYap(g) }];
  }

  /** Bir body'i aktif yap (CATIA "Define In Work Object") — yeni özellikler buraya eklenir. */
  bodyAktifYap(g: FikoGrup): void {
    if (!this.tumBodyler.includes(g)) this.tumBodyler.push(g);
    this.aktifBody = g; this.bodyVurgula(); this.d.agacGuncelle?.(); this.d.ciz();
  }

  /** Aktif body adına "◆ " işareti (CATIA altçizgi/bold aktif gösterimi). */
  private bodyVurgula(): void {
    for (const b of this.tumBodyler) {
      const taban = (b as any)._bodyTaban || b.name.replace(/^◆ /, '');
      const ad = (b === this.aktifBody ? '◆ ' : '') + taban;
      try { (b as any).adDegistirSessiz?.(ad); b.name = ad; if (b.userData?.['FikoArayuz']) b.userData['FikoArayuz'].ad = ad; } catch { }
    }
  }

  /** Profil 2D noktaları (lathe/shaft için). */
  private profilNoktalari(elemanlar: any[]): { x: number; y: number }[] | null {
    const shape = this.profilShape(elemanlar);
    if (!shape) return null;
    return shape.getPoints(64).map(p => ({ x: p.x, y: p.y }));
  }

  /**
   * Profil → THREE.Shape. CATIA gibi ÇOKLU LOOP: en büyük alanlı loop dış sınır,
   * onun İÇİNDE kalan diğer loop'lar DELİK (shape.holes). Böylece tek sketch'te
   * kare + çember → delikli kare olur.
   */
  private profilShape(els: any[]): THREE.Shape | null {
    const s = this.profilShapelar(els);
    return s.length ? s[0] : null;   // en büyük bölge (lathe/shaft tek profil için)
  }

  /**
   * Profil → ÇOKLU BÖLGE THREE.Shape[]. CATIA gibi: tek eskizde BİRDEN FAZLA ayrı kapalı çizgi
   * → her biri AYRI katı bölge (yan yana 2 kare → 2 pad). İç-içe loop'lar even-odd: çift derinlik
   * = dolu bölge (outer), tek derinlik = delik. ExtrudeGeometry(Shape[]) hepsini tek geo'da işler.
   */
  private profilShapelar(els: any[]): THREE.Shape[] {
    const loops = this.looplar(els);
    if (!loops.length) return [];
    // her loop için nesting derinliği = kaç DAHA BÜYÜK loop onu içeriyor
    const derinlik = loops.map((L, i) => loops.filter((O, j) => j !== i && O.alan > L.alan && this.icerdeMi(L.pts[0], O.pts)).length);
    const shapes: THREE.Shape[] = [];
    loops.forEach((L, i) => {
      if (derinlik[i] % 2 !== 0) return;   // tek derinlik = delik → outer değil
      const shape = new THREE.Shape(L.pts);
      loops.forEach((H, j) => {            // doğrudan içindeki bir-üst derinlik loop'lar = delik
        if (i !== j && derinlik[j] === derinlik[i] + 1 && this.icerdeMi(H.pts[0], L.pts)) shape.holes.push(new THREE.Path(H.pts));
      });
      shapes.push(shape);
    });
    return shapes;
  }

  /** Sketch elemanlarını kapalı loop'lara (2D nokta dizisi + alan) çevirir.
   * Çember/dikdörtgen = standalone kapalı; çizgi+yay = uç-uca zincirlenir. */
  private looplar(els: any[]): { pts: THREE.Vector2[]; alan: number }[] {
    const out: { pts: THREE.Vector2[]; alan: number }[] = [];
    const segmentler: { a: any; b: any; pts: THREE.Vector2[] }[] = [];
    for (const el of els) {
      if (el.yapi) continue;  // construction geometri profile girmez
      if (el.tip === 'cember') {
        const m = el.noktalar[0], r = el.r || 1; const pts: THREE.Vector2[] = [];
        for (let i = 0; i < 48; i++) { const t = i / 48 * Math.PI * 2; pts.push(new THREE.Vector2(m.x + Math.cos(t) * r, m.y + Math.sin(t) * r)); }
        out.push({ pts, alan: Math.abs(this.poliAlan(pts)) });
      } else if (el.tip === 'dikdortgen') {
        const [a, b] = el.noktalar;
        const pts = [new THREE.Vector2(a.x, a.y), new THREE.Vector2(b.x, a.y), new THREE.Vector2(b.x, b.y), new THREE.Vector2(a.x, b.y)];
        out.push({ pts, alan: Math.abs(this.poliAlan(pts)) });
      } else if (el.tip === 'cokgen') {   // PARAMETRİK çokgen → tek kapalı loop
        const pts = FikoSketch.cokgenNoktalari(el).map(p => new THREE.Vector2(p.x, p.y));
        if (pts.length >= 3) out.push({ pts, alan: Math.abs(this.poliAlan(pts)) });
      } else if (el.tip === 'slot') {     // PARAMETRİK slot → tek kapalı loop
        const pts = FikoSketch.slotNoktalari(el).map(p => new THREE.Vector2(p.x, p.y));
        if (pts.length >= 3) out.push({ pts, alan: Math.abs(this.poliAlan(pts)) });
      } else if (el.tip === 'elips') {    // PARAMETRİK elips → tek kapalı loop
        const pts = FikoSketch.elipsNoktalari(el).map(p => new THREE.Vector2(p.x, p.y));
        if (pts.length >= 3) out.push({ pts, alan: Math.abs(this.poliAlan(pts)) });
      } else if (el.tip === 'spline' && el.kapali) {   // KAPALI spline → kapalı loop
        const pts = FikoSketch.splineNoktalari(el).map(p => new THREE.Vector2(p.x, p.y));
        if (pts.length >= 3) out.push({ pts, alan: Math.abs(this.poliAlan(pts)) });
      } else if (el.tip === 'cizgi') {
        const [a, b] = el.noktalar;
        segmentler.push({ a, b, pts: [new THREE.Vector2(a.x, a.y), new THREE.Vector2(b.x, b.y)] });
      } else if (el.tip === 'yay') {
        const yp = FikoSketch.yayNoktalari(el.noktalar[0], el.noktalar[1], el.noktalar[2]);
        segmentler.push({ a: el.noktalar[0], b: el.noktalar[1], pts: yp.map(p => new THREE.Vector2(p.x, p.y)) });
      }
    }
    // çizgi+yay segmentlerini uç-uca zincirle → kapalı loop'lar
    for (const z of this.zincirle(segmentler)) if (z.length >= 3) out.push({ pts: z, alan: Math.abs(this.poliAlan(z)) });
    return out;
  }

  /** Segmentleri (çizgi/yay) uç-uca bağlayıp kapalı loop nokta dizileri üretir. */
  private zincirle(segs: { a: any; b: any; pts: THREE.Vector2[] }[]): THREE.Vector2[][] {
    const tol = 0.8;
    const yakin = (p: any, q: any) => Math.hypot(p.x - q.x, p.y - q.y) < tol;
    const kullanildi = new Array(segs.length).fill(false);
    const loops: THREE.Vector2[][] = [];
    for (let i = 0; i < segs.length; i++) {
      if (kullanildi[i]) continue;
      kullanildi[i] = true;
      let zincir = segs[i].pts.slice();
      let son = segs[i].b;
      let ilerle = true;
      while (ilerle) {
        ilerle = false;
        for (let j = 0; j < segs.length; j++) {
          if (kullanildi[j]) continue;
          const s = segs[j];
          if (yakin(s.a, son)) { zincir = zincir.concat(s.pts.slice(1)); son = s.b; kullanildi[j] = true; ilerle = true; break; }
          if (yakin(s.b, son)) { zincir = zincir.concat(s.pts.slice().reverse().slice(1)); son = s.a; kullanildi[j] = true; ilerle = true; break; }
        }
      }
      loops.push(zincir);
    }
    return loops;
  }

  private poliAlan(pts: THREE.Vector2[]): number {
    let a = 0;
    for (let i = 0; i < pts.length; i++) { const j = (i + 1) % pts.length; a += pts[i].x * pts[j].y - pts[j].x * pts[i].y; }
    return a / 2;
  }

  /** Nokta, polygon içinde mi? (ray-casting) */
  private icerdeMi(p: THREE.Vector2, poly: THREE.Vector2[]): boolean {
    let ic = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
      if (((poly[i].y > p.y) !== (poly[j].y > p.y)) &&
        (p.x < (poly[j].x - poly[i].x) * (p.y - poly[i].y) / (poly[j].y - poly[i].y) + poly[i].x)) ic = !ic;
    }
    return ic;
  }

  private grupEkle(geo: THREE.BufferGeometry): void {
    const pos = geo.attributes.position as THREE.BufferAttribute;
    if (!pos) return;
    const n = pos.count;
    const grup = new Float32Array(n * 3);
    const gruplar = geo.groups.length ? geo.groups : [{ start: 0, count: n, materialIndex: 0 }];
    let fid = 0;
    for (const g of gruplar) {
      const son = g.start + g.count;
      for (let i = g.start; i < son && i < n; i++) { const gi = i * 3; grup[gi] = g.start; grup[gi + 1] = g.count; grup[gi + 2] = fid; }
      fid++;
    }
    geo.setAttribute('grup', new THREE.Float32BufferAttribute(grup, 3));
  }

  // ---- kamera / kontrol ----

  private kameraHizala(d: ISketchDuzlem): void {
    const kam = this.d.kameraAl();
    const k = this.d.kontrolAl?.();
    if (!kam) return;
    const merkez = k ? k.target.clone() : new THREE.Vector3();
    const mesafe = Math.max(50, kam.position.distanceTo(merkez) || 100);
    kam.position.copy(d.origin).addScaledVector(d.normal, mesafe);
    kam.up.copy(d.eksenV);
    if (k) { k.target.copy(d.origin); k.update(); } else { (kam as any).lookAt?.(d.origin); }
    this.d.ciz();
  }

  private yuzSecHandlerKaldir(): void {
    if (this.yuzSecHandler) {
      this.d.canvasAl()?.removeEventListener('pointerdown', this.yuzSecHandler);
      this.yuzSecHandler = null;
    }
  }

  // ---- PARAMETRİK KAYIT / OKUMA (tüm feature geçmişi + sketch + ölçüler) ----

  /** Tüm parametrik modeli JSON'a çevirir (kaydetme için). Boşsa null. */
  /** Her feature'ın kararlı id'si olsun (yoksa üret — bir kez tohumlanır, sonra sabit). */
  private featIdGaranti(f: IOzellik): string {
    if (!f.id) f.id = 'f_' + Math.max(1, this.ozellikSayac) + '_' + (this.ozellikler.indexOf(f) + 1) + '_' + this.ozellikler.length + '_' + Math.floor(Math.random() * 1e6);
    return f.id;
  }

  /** Bir sketch'in boyutsal ölçülerini snapshot olarak kopyalar (yoksa undefined — korumalı). */
  private olculerKopyala(s: any): { id: number; tip: string; elemanId: number; deger: number }[] | undefined {
    return (s?.olculer?.length) ? JSON.parse(JSON.stringify(s.olculer)) : undefined;
  }

  /** Eskiz ölçüsünün değerini feature snapshot geometrisine (f.elemanlar) doğrudan uygular.
   *  olcuUygula matematiğinin BİREBİR replikasıdır (headless FikoSketch gerekmez). Eleman/tip yoksa sessiz geç. */
  private sketchOlcuUygulaSnapshot(f: IOzellik, o: { tip: string; elemanId: number; deger: number }): void {
    const els = f.elemanlar; if (!els || !els.length) return;
    const el = els.find((e: any) => e?.id === o.elemanId); if (!el) return;
    const v = o.deger; if (!(v > 0)) return;
    if (o.tip === 'uzunluk' && el.tip === 'cizgi') {
      const a = el.noktalar[0], b = el.noktalar[1];
      const len = Math.hypot(b.x - a.x, b.y - a.y) || 1;
      el.noktalar[1] = { x: a.x + (b.x - a.x) / len * v, y: a.y + (b.y - a.y) / len * v };
    } else if ((o.tip === 'yaricap' || o.tip === 'cap') && el.tip === 'yay') {
      const yeniR = o.tip === 'cap' ? v / 2 : v;
      const c = FikoSketch.cevreCember(el.noktalar[0], el.noktalar[1], el.noktalar[2]); if (!c) return;
      const sc = yeniR / (c.r || 1);
      el.noktalar = el.noktalar.map((p: any) => ({ x: c.merkez.x + (p.x - c.merkez.x) * sc, y: c.merkez.y + (p.y - c.merkez.y) * sc }));
    } else if (o.tip === 'yaricap') { el.r = v; }
    else if (o.tip === 'cap') { el.r = v / 2; }
    else if (o.tip === 'genislik' && el.tip === 'dikdortgen') {
      const a = el.noktalar[0], b = el.noktalar[1]; el.noktalar[1] = { x: a.x + Math.sign(b.x - a.x || 1) * v, y: b.y };
    } else if (o.tip === 'yukseklik' && el.tip === 'dikdortgen') {
      const a = el.noktalar[0], b = el.noktalar[1]; el.noktalar[1] = { x: b.x, y: a.y + Math.sign(b.y - a.y || 1) * v };
    }
  }

  /** Tüm feature'lar için intrinsic parametreleri depoda güncel tut (yeni → yayın, silinen → kaldır). */
  private intrinsikSenkron(): void {
    const k = this.knowledge.depo;
    const canliId = new Set<string>();
    this.ozellikler.forEach((f, i) => {
      const id = this.featIdGaranti(f); canliId.add(id);
      const featAd = (f.node?.name || f.tip || '').replace(/^◆ /, '') || `${f.tip}.${i + 1}`;
      const fg = k.featGrupAl(id, featAd);
      for (const a of featureAlanlari(f.tip)) {
        const mevcut = (f as any)[a.alan];
        if (mevcut == null) continue;
        k.featAlanYayinla(id, featAd, a.alan, a.etiket, a.tip, mevcut, fg.id);
      }
      const olcuAlanlar: string[] = [];
      for (const o of (f.sketchOlculer || [])) {
        const tip = (o.tip === 'aci') ? 'Aci' : 'Uzunluk';
        const alan = '_sketchOlcu_' + o.id;
        k.featAlanYayinla(id, featAd, alan, 'Ölçü' + o.id + '_' + o.tip, tip, o.deger, fg.id);
        olcuAlanlar.push(alan);
      }
      if (f.node) {
        const ud = ((f.node as any).userData ||= {});
        ud['FikoParametreler'] = [...featureAlanlari(f.tip).map(a => a.alan), ...olcuAlanlar]
          .map(alan => k.getFeatAlan(id, alan)).filter(Boolean)
          .map((p: any) => ({
            isim: p.ad.split('\\').pop(),
            turu: p.formul ? 'Formul' : 'Sayı',
            degeri: p.formul ? p.formul : p.deger,
            paramId: p.id,
            hata: p.hata,
            ayar: { birim: p.tip === 'Aci' ? '°' : (p.tip === 'Uzunluk' ? 'mm' : '') },
            knowledge: this.knowledge,
            degisti: (v: any) => { this.knowledge.parametreGuncelle(p.id, v); },
          }));
      }
    });
    // CAM ad-alanı ('cam_' öneki) FikoCamYonetici tarafından yönetilir — CAD rebuild temizliği ONLARA DOKUNMAZ (parametrik CAM)
    const canliTum = new Set(canliId);
    for (const p of k.parametreler) if (p.feat?.featId?.startsWith('cam_')) canliTum.add(p.feat.featId);
    for (const g of k.gruplar) if (g.featId?.startsWith('cam_')) canliTum.add(g.featId);
    for (const p of [...k.parametreler]) if (p.feat && !canliTum.has(p.feat.featId)) k.featKaldir(p.feat.featId);
    k.featGruplariTemizle(canliTum);
  }

  /** Tüm parametreleri çöz ve intrinsic değerleri feature alanlarına yaz (write-through). */
  private parametreCozUygula(): void {
    this.knowledge.cozumle();
    for (const p of this.knowledge.depo.parametreler) {
      if (!p.feat) continue;
      const f = this.ozellikler.find(x => x.id === p.feat!.featId);
      if (!f) continue;
      if (p.feat.alan.startsWith('_sketchOlcu_')) {
        const olcuId = +p.feat.alan.slice('_sketchOlcu_'.length);
        const o = (f.sketchOlculer || []).find(x => x.id === olcuId);
        if (o && typeof p.deger === 'number') { o.deger = p.deger; this.sketchOlcuUygulaSnapshot(f, o); p._sonYazilan = p.deger; }
      } else if (typeof p.deger === 'number') { (f as any)[p.feat.alan] = p.deger; p._sonYazilan = p.deger; }
    }
  }

  serialize(): any {
    if (!this.ozellikler.length && !this.occtBazStepBytes) return null;
    const dz = (d?: ISketchDuzlem) => d ? { origin: d.origin.toArray(), normal: d.normal.toArray(), eksenU: d.eksenU.toArray(), eksenV: d.eksenV.toArray() } : undefined;
    const ret: any = {
      v: 2,
      eskizSayac: this.eskizSayac,
      ozellikSayac: this.ozellikSayac,
      // TAM-KLON: node hariç tüm feature alanları (linkler ref1/refler/duzlemRef… + GSD wireframe/yüzey/transform dahil)
      // korunur. Vector3 → __v3 işaretli dizi; duzlem ise geriye-uyum için ayrı dizi formatında.
      ozellikler: this.ozellikler.map(f => {
        this.featIdGaranti(f);
        const c = JSON.parse(JSON.stringify(f, (k, v) => {
          if (k === 'node') return undefined;                              // THREE/ağaç referansı atla
          if (v && (v as any).isVector3) return { __v3: [v.x, v.y, v.z] };  // Vector3 → işaretli dizi
          return v;
        }));
        c.duzlem = dz(f.duzlem);   // ISketchDuzlem → dizi formatı (eski sürümle uyumlu)
        return c;
      }),
      parametreler: this.knowledge.depo.parametreler.map(p => ({ ...p })),
      gruplar: this.knowledge.depo.gruplar.map(g => ({ ...g })),
      knowledgeMode: this.knowledge.mode,
      konfigler: this.knowledge.konfigler.map(k => ({ ...k, degerler: { ...k.degerler } })),
      kontroller: this.knowledge.kontroller.map(k => ({ ...k })),
    };
    // CATIA "Result of import": içe aktarılan STEP B-Rep'i taban olarak göm (binary-collect varsa _bin, yoksa base64)
    if (this.occtBazStepBytes) {
      const bi = FikoCizimYalin.collectBinary(this.occtBazStepBytes);
      if (bi >= 0) ret.ithalStepBin = bi;
      else ret.ithalStep = FikoCizimYalin.bytesToBase64(this.occtBazStepBytes);
    }
    return ret;
  }

  /** JSON'dan parametrik modeli geri yükler (okuma için) → tam rebuild. buffers: FikoSahne binary buffer'ları (import B-Rep için). */
  async deserialize(o: any, buffers?: Uint8Array[]): Promise<void> {
    if (!o?.ozellikler?.length && o?.ithalStepBin == null && !o?.ithalStep) return;
    try {
      this.parametrikTemizle();
      this.modBaslat();
      if (this.occtUyumluMu()) { try { await this.occtYukle(); } catch { } }   // B-Rep feature replay'i için OCCT garanti yükle (rebuild senkron occtModu açar)
      // CATIA "Result of import": kaydedilmiş STEP B-Rep tabanını birebir geri kur (varsa)
      if (o.ithalStepBin != null || o.ithalStep) {
        try {
          const bytes = (o.ithalStepBin != null && buffers && buffers[o.ithalStepBin])
            ? buffers[o.ithalStepBin]
            : (o.ithalStep ? FikoCizimYalin.base64ToBytes(o.ithalStep) : null);
          if (bytes && await this.occtYukle()) {
            const shape = await fikoOCCT.stepOku(bytes);
            if (shape) { this.occtBazShape = shape; this.occtBazStepBytes = bytes.slice(); this.occtModu = true; }
            else console.warn('[Modelleme] ithal B-Rep STEP çözülemedi');
          }
        } catch (e) { console.warn('[Modelleme] ithal B-Rep geri yükleme hatası', e); }
      }
      this.eskizSayac = o.eskizSayac || 1;
      this.ozellikSayac = o.ozellikSayac || 1;
      const un = (a: number[]) => new THREE.Vector3().fromArray(a);
      const dz = (d: any): ISketchDuzlem | undefined => d ? { origin: un(d.origin), normal: un(d.normal), eksenU: un(d.eksenU), eksenV: un(d.eksenV) } : undefined;
      // __v3 işaretli dizileri Vector3'e geri çevir (tam-klon serialize ile yazılan tüm alanlar için).
      const canlandir = (v: any): any => {
        if (Array.isArray(v)) return v.map(canlandir);
        if (v && typeof v === 'object') {
          if (Array.isArray((v as any).__v3)) return un((v as any).__v3);
          const o2: any = {}; for (const k in v) o2[k] = canlandir(v[k]); return o2;
        }
        return v;
      };
      const sketchBazli = (t: string) => ['pad', 'pocket', 'shaft', 'groove', 'rib'].includes(t);
      for (const fo of o.ozellikler) {
        const feat: IOzellik = { ...canlandir(fo), duzlem: dz(fo.duzlem), node: null };
        if (feat.tip === 'yazi3d' && feat.fontAd) this.yaziFontHazirla(feat.fontAd);   // yüklenince rebuild
        const sketchNode = sketchBazli(feat.tip) ? this.eskizNodeYukle(feat) : null;
        feat.node = this.ozellikNode(feat.tip, sketchNode);
        if (sketchNode) sketchNode.userData['featObj'] = feat;
        this.ozellikler.push(feat);
        this.featureNodeBagla(feat);
      }
      this.knowledge.depo.parametreler = Array.isArray(o.parametreler) ? o.parametreler.map((p: any) => ({ ...p })) : [];
      this.knowledge.depo.gruplar = Array.isArray(o.gruplar) ? o.gruplar.map((g: any) => ({ ...g })) : [];
      this.knowledge.mode = o.knowledgeMode === 'manuel' ? 'manuel' : 'oto';
      this.knowledge.konfigler = Array.isArray(o.konfigler) ? o.konfigler.map((k: any) => ({ ...k, degerler: { ...k.degerler } })) : [];
      this.knowledge.kontroller = Array.isArray(o.kontroller) ? o.kontroller.map((k: any) => ({ ...k })) : [];
      this.ozellikler.forEach(f => this.featIdGaranti(f));
      this.rebuild();
      this.ithalGovdeNodeKur();   // CATIA: içe aktarılan B-Rep ağaç öğesi (varsa)
      this.durum = 'kapali';
      this.d.agacGuncelle?.();
      this.d.degisti?.(this.durum, null);
      this.gecmisSifirla();   // yüklenen modeli geçmiş tabanı yap
    } catch (e) { console.warn('[Modelleme] deserialize hatası', e); }
  }

  // ════════════════ GEÇMİŞ (Geri Al / İleri Al — tam-doğruluklu snapshot) ════════════════
  private _gecmis: string[] = [];
  private _gecmisIdx = -1;
  private _gecmisKilit = false;
  private readonly GECMIS_MAX = 80;

  /** Tüm feature parametrelerini (Vector3 dahil, node hariç) JSON snapshot'a çevirir. */
  private gecmisSnap(): string {
    return JSON.stringify(this.ozellikler, (k, v) => {
      if (k === 'node') return undefined;                                  // THREE/ağaç referansı atla
      if (v && (v as any).isVector3) return { __v3: [v.x, v.y, v.z] };     // Vector3 → işaretli dizi
      return v;
    });
  }
  get geriAlinabilir(): boolean { return this._gecmisIdx > 0; }
  get ileriAlinabilir(): boolean { return this._gecmisIdx >= 0 && this._gecmisIdx < this._gecmis.length - 1; }

  /** Geçmişi sıfırla + mevcut durumu taban al (yeni model / yükleme sonrası). */
  gecmisSifirla(): void {
    this._gecmis = []; this._gecmisIdx = -1;
    this.gecmisKaydet();   // taban snapshot
  }
  /** Değişiklik sonrası geçmişe kaydet — komut sürerken (taslak) KAYDETMEZ; aynı durumu tekrar yazmaz. */
  gecmisKaydet(): void {
    if (this._gecmisKilit) return;
    if (this.taslakFeat || this.taslakFeatler || this.taslakRefDuzlem || this.taslakCam3d || this.taslakMalzeme || this.taslakSketchSec !== null) return;   // komut sürüyor
    const snap = this.gecmisSnap();
    if (this._gecmisIdx >= 0 && this._gecmis[this._gecmisIdx] === snap) return;   // net değişiklik yok
    this._gecmis = this._gecmis.slice(0, this._gecmisIdx + 1);
    this._gecmis.push(snap);
    if (this._gecmis.length > this.GECMIS_MAX) this._gecmis.shift();
    this._gecmisIdx = this._gecmis.length - 1;
    this.gecmisDurumBildir();
  }
  geriAl(): void { if (this.geriAlinabilir) { this._gecmisIdx--; this.gecmisUygula(this._gecmis[this._gecmisIdx]); this.d.bildir?.('Geri alındı'); } }
  ileriAl(): void { if (this.ileriAlinabilir) { this._gecmisIdx++; this.gecmisUygula(this._gecmis[this._gecmisIdx]); this.d.bildir?.('İleri alındı'); } }

  // ── SKETCH GEÇMİŞİ (sketch modunda ayrı geri/ileri al) ──
  private _skGecmis: string[] = [];
  private _skIdx = -1;
  private _skKilit = false;
  get sketchGeriAlinabilir(): boolean { return this.durum === 'sketch' && this._skIdx > 0; }
  get sketchIleriAlinabilir(): boolean { return this.durum === 'sketch' && this._skIdx >= 0 && this._skIdx < this._skGecmis.length - 1; }
  /** Sketch'e girince geçmişi sıfırla + taban al. */
  sketchGecmisSifirla(): void { this._skGecmis = []; this._skIdx = -1; this.sketchGecmisKaydet(); }
  /** Sketch değişimi sonrası snapshot (elemanlar). */
  sketchGecmisKaydet(): void {
    if (this._skKilit || this.durum !== 'sketch' || !this.sketch) return;
    const s = JSON.stringify(this.sketch.elemanlar);
    if (this._skIdx >= 0 && this._skGecmis[this._skIdx] === s) return;
    this._skGecmis = this._skGecmis.slice(0, this._skIdx + 1);
    this._skGecmis.push(s);
    if (this._skGecmis.length > 120) this._skGecmis.shift();
    this._skIdx = this._skGecmis.length - 1;
    this.gecmisDurumBildir();
  }
  sketchGeriAl(): void { if (this.sketchGeriAlinabilir) { this._skIdx--; this.sketchGecmisUygula(); this.d.bildir?.('Sketch: geri alındı'); } }
  sketchIleriAl(): void { if (this.sketchIleriAlinabilir) { this._skIdx++; this.sketchGecmisUygula(); this.d.bildir?.('Sketch: ileri alındı'); } }
  private sketchGecmisUygula(): void {
    if (!this.sketch) return;
    this._skKilit = true;
    try {
      this.sketch.elemanlar = JSON.parse(this._skGecmis[this._skIdx]);
      (this.sketch as any).secimTemizle?.(); this.sketch.ciz();
      if (this.duzenlenenFeat) { this.duzenlenenFeat.elemanlar = JSON.parse(JSON.stringify(this.sketch.elemanlar)); this.rebuild(); }
      this.entityAgaceGuncelleGuvenli(); this.d.ciz(); this.d.degisti?.(this.durum, this.sketch);
    } catch (e) { console.warn('[Sketch geçmiş]', e); }
    this._skKilit = false;
    this.gecmisDurumBildir();
  }

  // ── BİRLEŞİK (aktif moda göre: sketch ise sketch geçmişi, değilse feature geçmişi) ──
  get geriAlinabilirAktif(): boolean { return this.durum === 'sketch' ? this.sketchGeriAlinabilir : this.geriAlinabilir; }
  get ileriAlinabilirAktif(): boolean { return this.durum === 'sketch' ? this.sketchIleriAlinabilir : this.ileriAlinabilir; }
  geriAlAktif(): void { if (this.durum === 'sketch') this.sketchGeriAl(); else this.geriAl(); }
  ileriAlAktif(): void { if (this.durum === 'sketch') this.sketchIleriAl(); else this.ileriAl(); }
  /** Buton durumlarını aktif moda göre bildir. */
  gecmisDurumBildir(): void { this.d.gecmisDegisti?.(this.geriAlinabilirAktif, this.ileriAlinabilirAktif); }

  /** Snapshot'tan parametrik modeli geri kurar (geri/ileri al ortak yolu). */
  private gecmisUygula(snap: string): void {
    this._gecmisKilit = true;
    try {
      const feats: IOzellik[] = JSON.parse(snap, (k, v) => (v && v.__v3) ? new THREE.Vector3(v.__v3[0], v.__v3[1], v.__v3[2]) : v);
      this.komutIptal();
      this.parametrikTemizle();
      this.modBaslat();
      const sketchBazli = (t: string) => ['pad', 'pocket', 'shaft', 'groove', 'rib'].includes(t);
      for (const feat of feats) {
        feat.node = null as any;
        if (feat.tip === 'yazi3d' && feat.fontAd) this.yaziFontHazirla(feat.fontAd);
        const sketchNode = sketchBazli(feat.tip) ? this.eskizNodeYukle(feat) : null;
        feat.node = this.ozellikNode(feat.tip, sketchNode);
        if (sketchNode) sketchNode.userData['featObj'] = feat;
        this.ozellikler.push(feat);
        this.featureNodeBagla(feat);
      }
      this.rebuild();
      this.ithalGovdeNodeKur();   // CATIA: içe aktarılan B-Rep ağaç öğesi (varsa)
      this.durum = 'kapali';
      this.d.agacGuncelle?.();
      this.d.degisti?.(this.durum, null);
    } catch (e) { console.warn('[Modelleme] geçmiş uygula hatası', e); }
    this._gecmisKilit = false;
    this.gecmisDurumBildir();
  }

  /** Deserialize: bir sketch-tabanlı feature için Eskiz node'u + FikoSketch'i yeniden kurar (düzenlenebilir). */
  private eskizNodeYukle(feat: IOzellik): FikoGrup | null {
    if (!feat.duzlem || !feat.elemanlar) return null;
    const sketch = new FikoSketch(this.duzlemKlon(feat.duzlem));
    sketch.elemanlar = JSON.parse(JSON.stringify(feat.elemanlar));
    sketch.ciz();
    const node = new FikoGrup(this.d.sahne as any, 'Eskiz.' + (this.eskizSayac++));
    node.userData['FikoArayuz'].simge = './simgeler/yalinCizim.webp';
    node.userData['FikoArayuz'].listeGoster = true;
    node.userData['fikoSketch'] = sketch;
    node.userData['sketchDuzlem'] = feat.duzlem;
    (node as any).ciftTiklandi = () => this.sketchDuzenle(node);
    node.ekle!(sketch.nesne);
    return node;
  }

  /** Mevcut parametrik modeli tamamen temizler (yükleme öncesi). */
  private parametrikTemizle(): void {
    this.duzenlemeyiDurdur();
    this.iptalSketch();
    for (const f of this.ozellikler) f.node?.parent?.remove(f.node as any);
    this.ozellikler = [];
    if (this.govdeMesh) { (this.govdeMesh.parent||this.d.sahne).remove(this.govdeMesh); this.govdeMesh.geometry.dispose(); this.govdeMesh = null; }
    this.govdeKenar = null;
    for (const b of this.tumBodyler) { if (b !== this.govdeGrup && b.parent) b.parent.remove(b); }
    if (this.govdeGrup?.parent) { this.govdeGrup.parent.remove(this.govdeGrup); this.govdeGrup = null; }
    this.aktifBody = null; this.tumBodyler = []; this.bodySayac = 1;
    // referans düzlem görselleri + geometrik set temizle
    for (const r of this.referansDuzlemler) { try { (r.mesh.parent||this.d.sahne).remove(r.mesh); (r.mesh.material as any)?.dispose?.(); } catch { } }
    this.referansDuzlemler = [];
    if (this.geometriSeti?.parent) { this.geometriSeti.parent.remove(this.geometriSeti); this.geometriSeti = null; }
    if (this.parametreGrubu?.parent) { this.parametreGrubu.parent.remove(this.parametreGrubu as any); this.parametreGrubu = null; }
    this.occtBazShape = null; this.occtSonShapeRef = null; this.occtBazStepBytes = null; this.ithalNode = null;   // import edilen B-Rep gövdeyi de temizle
    this.eskizSayac = 1; this.ozellikSayac = 1; this.refSayac = 1;
    this._sonFeatSayisi = 0;   // temizlik sonrası ilk feature yeniden odaklanabilsin
    this.d.ciz();
  }

  // ───────────────────── 3D CAM (mesh tabanlı gerçek toolpath) ─────────────────────
  private cam3dYol: THREE.Group | null = null;
  private sonCam3dTp: IToolpath | null = null;
  private sonCam3dParam: ICamParam | null = null;
  /** Son toolpath'i kütük simülasyonu nokta formatına çevirir (FikoSimilasyonV1 için). */
  cam3dSimNoktalari(): any[] { return (this.sonCam3dTp && this.sonCam3dParam) ? fikoCam3D.simNoktalari(this.sonCam3dTp, this.sonCam3dParam.feeds, this.sonCam3dParam.takim) : []; }
  get govdeMeshRef(): THREE.Mesh | null { return this.govdeMesh; }

  // ── CAM 3D'yi GERÇEK ürün ağacı yapısına ekle: Fikoperasyon → Nc Programlar → Fikogram → FikoTakımYoluv1 ──
  private cam3dOperasyon: any = null;
  private cam3dProgSayac = 1;
  private sonCam3dYolNode: any = null;
  get cam3dYolNode(): any { return this.sonCam3dYolNode; }
  private fikoperasyonlarBul(p: any): any {
    if (!p?.children) return null;
    for (const c of p.children) { if (c.type === 'Fikoperasyonlar') return c; const f = this.fikoperasyonlarBul(c); if (f) return f; }
    return null;
  }
  /** Ağaçta seçili (aktifItem) öğeden Fikoperasyon çöz (parent zinciri). */
  private seciliOperasyonBul(): any {
    let n: any = (this.d.sahne as any)?.anaGosterim?.aktifItem;
    while (n) { if (n.type === 'Fikoperasyon') return n; n = n.parent; }
    return null;
  }
  /** Son ağaca-ekleme hangi operasyona gitti + yeni mi oluştu (component bildirim için). */
  sonCam3dOpAd = ''; sonCam3dOpYeni = false;
  /** Son toolpath'i CATIA tarzı NC Program node'u olarak ürün ağacına ekler (FikoTakımYoluv1 döndürür). */
  cam3dAgacaEkle(opAd: string): any {
    const noktalar = this.cam3dSimNoktalari(); if (!noktalar.length) return null;
    const sahne: any = this.d.sahne;
    // CATIA: NC program AĞAÇTA SEÇİLİ operasyona gider; yoksa "3D CAM Operasyon" (oluştur/yeniden kullan)
    this.sonCam3dOpYeni = false;
    let hedefOp = this.seciliOperasyonBul();
    if (!hedefOp) {
      if (!this.cam3dOperasyon || !this.cam3dOperasyon.parent) {
        this.cam3dOperasyon = new FikoOperasyon(sahne, '3D CAM Operasyon');
        const kap = sahne.operasyonlar || this.fikoperasyonlarBul(sahne) || sahne.urunAgaci;
        (kap?.ekle ? kap.ekle(this.cam3dOperasyon) : sahne.add(this.cam3dOperasyon));
        this.sonCam3dOpYeni = true;
      }
      hedefOp = this.cam3dOperasyon;
    }
    this.sonCam3dOpAd = (hedefOp.name || '3D CAM Operasyon').replace(/^◆ /, '');
    // NC Program (Fikogram) + içine FikoTakımYoluv1
    hedefOp.ekleFikogram(opAd);
    const prog = hedefOp.programlar.children[hedefOp.programlar.children.length - 1];
    const yol = new FikoTakimYoluv1(sahne, opAd, noktalar);
    prog.ekle!(yol);
    this.sonCam3dYolNode = yol;
    this.cam3dYolTemizle();   // önizleme toolpath'i sahneden kalkar — toolpath SADECE simülasyonda görünür
    // CATIA bildirim: hangi operasyona gitti / yeni mi oluştu (seçili operasyon yoksa)
    this.d.bildir?.(this.sonCam3dOpYeni
      ? ('Seçili operasyon yoktu → "' + this.sonCam3dOpAd + '" oluşturuldu. Farklı operasyona eklemek için ağaçtan seçin.')
      : ('"' + opAd + '" → ' + this.sonCam3dOpAd + ' altına eklendi'));
    this.d.agacGuncelle?.(); sahne.anaGosterim?.FikoYT?.degisti?.emit?.(); this.d.ciz();
    return yol;
  }
  /** 3D CAM operasyonu çalıştır → toolpath görselleştir + istatistik döndür. */
  cam3dCalistir(op: 'parallel' | 'roughing' | 'waterline' | 'pencil', param: ICamParam): IToolpath['meta'] | null {
    if (!this.govdeMesh) return null;
    this.cam3dYolTemizle();
    try {
      if (!fikoCam3D.kur(this.govdeMesh)) return null;
      this.sonCam3dParam = param;
      const tp = op === 'parallel' ? fikoCam3D.parallelFinish(param)
        : op === 'roughing' ? fikoCam3D.zLevelRough(param)
          : op === 'waterline' ? fikoCam3D.waterline(param)
            : fikoCam3D.pencil(param);
      this.sonCam3dTp = tp;
      this.cam3dYol = fikoCam3D.gorsellestir(tp);
      this.d.sahne.add(this.cam3dYol);
      this.d.ciz();
      return tp.meta;
    } catch (e) { console.warn('[CAM3D] hata', e); return null; }
  }
  /** Dialog flat parametrelerini ICamParam yapısına çevirir (canlı önizleme + onay). */
  cam3dParamYap(op: string, p: Record<string, any>): ICamParam {
    const tip = (p.takimTip || (op === 'roughing' ? 'duz' : 'kure')) as any;
    return {
      takim: { tip, cap: +p.takimCap || (op === 'roughing' ? 10 : 6), kose: +p.takimKose || undefined },
      feeds: { devir: +p.devir || 10000, ilerleme: +p.ilerleme || 1500, dalma: +p.dalma || 400, rapid: 5000 },
      guvenliZ: +p.guvenliZ || 120,
      scallop: +p.scallop || 0.08,
      stepover: +p.stepover || undefined,
      stepdown: +p.stepdown || (op === 'roughing' ? 5 : 3),
      stockLeave: p.stockLeave != null ? +p.stockLeave : (op === 'roughing' ? 0.3 : 0),
      eksen: (p.eksen || 'x') as any, zigzag: p.zigzag !== false,
      yaklasmaZ: p.yaklasmaZ != null ? +p.yaklasmaZ : 2,
      girisTip: (p.girisTip || 'dik') as any, cikisTip: (p.cikisTip || p.girisTip || 'dik') as any,
      girisBoy: +p.girisBoy || undefined,
    };
  }
  get cam3dSonParamYap(): ICamParam | null { return this.taslakCam3d ? this.cam3dParamYap(this.taslakCam3d.op, this.taslakCam3d.p) : null; }
  get cam3dSonMeta(): IToolpath['meta'] | null { return this.sonCam3dTp ? this.sonCam3dTp.meta : null; }

  /** Son toolpath'in ISO G-code'u. */
  cam3dGcode(param: ICamParam): string { return this.sonCam3dTp ? fikoCam3D.gcode(this.sonCam3dTp, param) : ''; }
  cam3dYolTemizle(): void {
    if (this.cam3dYol) { this.d.sahne.remove(this.cam3dYol); this.cam3dYol.traverse(o => { (o as any).geometry?.dispose?.(); (o as any).material?.dispose?.(); }); this.cam3dYol = null; this.d.ciz(); }
  }

  dispose(): void { this.iptalSketch(); fikoCam3D.dispose(); }
}
